import { Fragment, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { doubleTapProps, followsDoubleTap, isDoubleTap } from '@/shared/device/doubleTap'
import { Caret } from '@/shared/components/ui/Caret'
import { createPortal } from 'react-dom'
import { AutofillCellEditor } from './AutofillCellEditor'
import { HeaderAlignmentControls } from './HeaderAlignmentControls'
import { resolveHeaderRule } from './headerAlignment'
import { patchColumnFormats, resolveColumnFormatBase } from './bulkColumnFormats'
import { columnCellColorChanges, effectiveFillColor, normalizeColumnColors, withColumnColors } from './columnColors'
import { useGridConfig } from './GridConfigProvider'
import { useOptionalSummaryCards, type GroupSubtotalsDefault } from './SummaryCardsContext'
import { SummaryCardEditDialog } from './SummaryCardEditDialog'
import { aggregateSummaryNumber, calculateSummaryCardValue, type SummaryCardConfig } from './summaryCards'
import { withHeader, moveColumnBand, columnKey, columnInsertAnchor, resolveColumnOrder, resolveRowOrder, frozenKeyFor, frozenScopeOf, withFrozenOn, type GridSortRef, type GridGroupRef } from './headers'
import { scopeIncludes, type DeviceScope } from '@/shared/device/device'
import { useDevice } from '@/shared/device/useDevice'
import { DeviceSwitch } from '@/shared/device/DeviceSwitch'
import { textClass, contentStyle, LINE_HEIGHT, ruleWrapMode } from './alignment'
import type {
  CellParams,
  CellRule,
  CellType,
  DropdownOption,
  GridColumn,
} from './types'
import { serialToDate } from '@/shared/formula/dateSerial'
import { patronACanonico, patronADisplay } from '@/shared/formula/excelFormat'
import {
  clipboardGridData,
  editableGridNumber,
  formatGridData,
  gridCellDataFormat,
  parseGridCellInput,
  withGridCellFormats,
  GRID_CELL_DATA_TYPES,
  type GridCellFormat,
  numberColor,
  parseGridDate,
  parseGridNumber,
  getGridDecimalSeparator,
  patternHasCents,
  withGridDataFormat,
  GRID_DATE_FORMATS,
  GRID_NUMBER_FORMATS,
  type GridDataFormat,
  type GridDataOption,
  type GridOptionsLink,
  type GridFormatKey,
  type GridFormats,
  isGridErrorsSearch,
  normalizeGridCellValue,
  isGridListMismatch,
  isGridTypeMismatch,
  parseGridNumberCell,
  resolveGridExchangeRate,
  columnBorderShadows,
  GRID_BORDER_WEIGHTS,
  GRID_BORDER_WIDTHS,
  lastBorderStyle,
  rememberBorderStyle,
} from './formats'
import { EXCHANGE_RATE_TODAY, exchangeRateAutoField, exchangeRateChanges } from '@/shared/exchangeRates/rule'
import { FX_HISTORY_CURRENCIES, FX_MARKETS, FX_SIDES, loadFxHistory, onFxHistoryLoaded, refreshSheetExchangeRates, type FxHistory } from '@/shared/exchangeRates/history'
import { useI18n } from '@/shared/i18n/useT'
import type { MessageKey } from '@/shared/i18n/messages'
import { GridStatusBar, computeSelectionStats } from './GridStatusBar'
import { useOptionalEditor } from '@/shared/editing/EditorContext'
import { useDropdownKeyboard } from './dropdownKeyboard'
import { ResizeCorner, useResizableSize } from './ResizeCorner'
import FormulaBar, { NEW_COLUMN, NameBox, type FormulaBarMode, type FormulaTarget } from './FormulaBar'
import FormulaEditor from '@/shared/components/Formula/FormulaEditor'
import { localizedFunctionMetadata } from '@/shared/components/Formula/formulaFunctionMetadata'
import { historyValueMatches, pushHistoryTask, replaySettingChange, type GridHistoryEntry, type GridReversible, type HistoryState, type HistoryTask } from './history'
import { ColorPicker } from '@/shared/components/ui/ColorPicker'
import { Dialog } from '@/shared/components/ui/Dialog'
import { Icon } from '@/shared/components/ui/Icon'
import { ChipListEditor } from '@/shared/components/ui/ChipListEditor'
import { ListChipCell, ListChipEditor } from './ListChipCell'
import { pasteExtent } from './pasteTiling'
import { useLiveLinkedLists } from '@/shared/folders'
import {
  computeSheetValues,
  ensureLinkedListRegistrations,
  formulaFieldsOf,
  refreshDirtyLinkedLists,
  refreshLinkedLists,
  referencesSheet,
  registerLinkedList,
  saveSheetComputed,
  unregisterLinkedList,
} from './computedValues'
import {
  optionsLinkKey,
  sortLinkedValues,
  tableKey,
  useLinkableTables,
  useLinkedValues,
  type WorkbookSnapshot,
} from './linkedOptions'
import { RollupColumnDialog } from './RollupColumnDialog'
import { createRollupValueGetter } from '@/shared/formula/rollups'
import { type FuenteChips, type ItemChip } from '@/shared/components/ui/ChipSelect'
import { ToolbarOverflow, useIsMobile, type ToolbarMenuEntry } from './ToolbarOverflow'
import { ToolbarPopover } from './ToolbarPopover'
import { CopySplitButton } from './CopySplitButton'
import { PasteSplitButton } from './PasteSplitButton'
import { GridSidePanel } from './GridSidePanel'
import { SplitPaneContext, useSplitPane } from './splitPane'
import { GridChartPanel, type ChartPoint, type GridChartSettings } from './GridChartPanel'
import { useCopyFormulasPreference, useCopyHeadersPreference, useCopyNumberFormatPreference } from './useCopyHeadersPreference'
import GridContextMenu, { type GridMenuItem } from './GridContextMenu'
import { DEFAULT_FORMAT, createFormulaEngine, editedAtField, formulaField, formulaIdOf, gridScope, isFormulaField, validateFormulas } from '@/shared/formula/engine'
import { createCellFormulaRuntime } from '@/shared/formula/cellRuntime'
import type { FormulaCompletionSchema, FormulaCompletionSheet, FormulaOperandRef } from '@/shared/components/Formula/formulaCompletion'
import { STORED_VERSION, fromStored, isRawFormula, storedFromCanonical, toStored, type StoredFormula } from '@/shared/formula/stored'

/** Code-authored canonical text → the stored form the rest of the formula layer speaks. */
const storedDefaults = (defaults?: Record<string, string>): Record<string, StoredFormula> =>
  Object.fromEntries(Object.entries(defaults ?? {}).map(([field, text]) => [field, storedFromCanonical(text)]))
import {
  EMPTY_FORMULA_CONFIG,
  cellOverridesAllowed,
  newFormulaId,
  overrideTargetOf,
  withCellOverridesAllowed,
  withFormulaColumn,
  withFormulaOverride,
  withOverrideTarget,
} from '@/shared/formula/storage'
import { SAME_COLUMN, goalSeekCandidates, goalSeekFormula } from '@/shared/formula/goalSeek'
import {
  columnScope,
  localeFor,
  localizeErrorCode,
  normaliseContainerName,
  parseCellFormula,
  serializeCanonical,
  serializeCellFormula,
  type CellFormulaScope,
} from '@/shared/formula/language'
import { GRID_SETTING_KEYS, type GridSettingKey } from './settings'
import { GridHistoryEntryContext } from './historyContext'
import type { Currency } from '@/shared/currencies'
import {
  cellColorTargetsForRange,
  cellColorKey,
  loadCellColors,
  normalizeCellColor,
  saveCellColors,
  type CellColorChange,
  type CellColorTarget,
} from './cellColors'
import { cellChangeKey, changeTimeField, changeTimeSource, loadCellChanges, recordCellValues, serializeCellValue, type CellValueReport } from './cellChanges'
import type { Json } from '@/lib/database.types'
import {
  blankOverride,
  clearOverride,
  loadOverrides,
  overrideKey,
  saveFormulaOverride,
  saveOverrideValue,
  type CellOverrideState,
} from './cellOverrides'
import { normalizeText } from './textNormalize'
import { signChanges, signedValue, type GridSignRule } from './signRule'
import { SignRuleDialog } from './SignRuleDialog'
import { matchesGridSearch, normalizeGridSearchText } from './search'
import { firstVisibleIndex, offsetOf, readGridViewport, resolveAnchorIndex, saveGridViewport } from './gridViewport'
import { AttachmentCell } from './attachments/AttachmentCell'
import { normalizeAttachments } from './attachments/types'
import { usePinchZoom } from '@/shared/hooks/usePinchZoom'
import { useSystemTextScale } from '@/shared/theme/systemTextScale'
import { useMediaQuery } from '@/shared/hooks/useMediaQuery'
import { addColumnGroup, collapsedColumnKeys, columnGroupSpans, removeColumnGroup, updateColumnGroup, type ColumnGroup, type ColumnGroupSpan } from './columnGroups'
import { ACCEPT_ATTRIBUTE, UploadError, uploadAttachment } from '@/shared/files/attachments'

/** The only grid toolbar items that stay in the bar on phones. */
const MOBILE_TOOLBAR_KEYS = ['add-rows', 'refresh', 'search', 'filtersPanel']

/**
 * Custom spreadsheet-style grid (replaces AG Grid Community). Matches the column
 * contract used by pages (field/headerName/minWidth/editable/valueFormatter/
 * valueGetter/cellRenderer) so column definitions remain unchanged.
 *
 * Column filter: list of unique values with checkboxes (equivalent to AG Grid
 * Enterprise Set Filter, which is not available in Community).
 */

// The public contract lives in `./types` so the formula engine can name a
// column without importing this component.
export type {
  CellParams,
  CellType,
  ColDef,
  DropdownOption,
  GridColumn,
} from './types'

interface DeleteActionConfig {
  label: string
  title?: string
  titulo?: string
  message?: (count: number) => string
  mensaje?: (count: number) => string
}

interface AutoRowsConfig<T> {
  min?: number
  minimo?: number
  create?: () => T
  nueva?: () => T
  isEmpty?: (row: T) => boolean
  estaVacia?: (row: T) => boolean
}

export interface GridFormulaWorkbookColumn {
  field: string
  name: string
  /** Surfaced as the autocomplete entry's `detail`. Optional: not every caller knows it. */
  type?: CellType
}

export interface GridFormulaWorkbookSheet {
  id: string
  name: string
  columns: readonly GridFormulaWorkbookColumn[]
  rows: readonly Record<string, unknown>[]
}

/** Optional workbook snapshot. Module grids omit it; Sheets supplies every tab so formulas can
 * aggregate whole columns from another sheet. Results are cached to
 * `sheet_rows.computed` for readers of a single column, never read back here. */
export interface GridFormulaWorkbook {
  currentSheetId: string
  /** The document these sheets belong to. Lets the completion rank by proximity and keeps the
   *  current document out of the "other documents" list. */
  documentId?: string
  sheets: readonly GridFormulaWorkbookSheet[]
}

interface GridProps<T extends object> {
  gridKey: string
  /**
   * What else the toolbar's Refresh button refreshes, besides the grid's own
   * exchange rates (a module's prices and volumes). Its presence shows the button.
   */
  onRefresh?: () => Promise<void>
  /** Hover text for Refresh, e.g. when the module's data was last updated. */
  refreshTitle?: string
  /**
   * Key used to persist column WIDTH and visibility when this grid is a variant of
   * another and needs to mirror its layout (e.g. Trash views). Columns unique to
   * the variant retain their own settings because their keys do not match any base column.
   * Other preferences (row/column order, grouping, filters) follow `gridKey`.
   */
  gridKeyColumnas?: string
  columnsGridKey?: string
  rows: T[]
  columns: GridColumn<T>[]
  /** Stable identity used by persisted cell formula references. When omitted, the Grid keeps
   * its existing `id`/`sku`/etc. discovery; an index-only row cannot own a live formula. */
  getRowId?: (row: T) => string
  formulaWorkbook?: GridFormulaWorkbook
  loading?: boolean
  editable?: boolean
  height?: number
  /** 'full' / 'completa' (default) is the full-page grid with complete toolbar.
   * 'embedded' / 'embebida' is the compact version for forms: no toolbar, search,
   * grouping, filters, sorting, virtualization, or row checkbox column. Retains keyboard
   * navigation, range selection, copy/paste, and status bar (without zoom). */
  variante?: 'completa' | 'embebida' | 'full' | 'embedded'
  variant?: 'completa' | 'embebida' | 'full' | 'embedded'
  /** On narrow screens, let the page grow with mobile cards instead of constraining them to
   * the remaining viewport below a tall summary/header. Desktop keeps the normal full-screen
   * grid behavior. */
  mobileAutoHeight?: boolean
  /** Allows a parent form to place the formula toggle outside the embedded grid while the
   * formula editor itself remains directly above the table headers. Only programmers can see it. */
  formulaBarOpen?: boolean
  /** Same external control contract as `formulaBarOpen`; both bars share exactly one slot. */
  formatBarOpen?: boolean
  /** Built-in formulas for existing columns. Persisted formulas override these defaults. */
  /** Formulas the page's own code supplies, written as canonical TEXT because that is what is
   *  readable in a source file. They are encoded to the stored form at the boundary below. */
  formulaDefaults?: Record<string, string>
  /** Existing-column formulas whose persistence is owned by the parent instead of `formulas_grilla`. */
  externallyManagedFormulaFields?: readonly string[]
  onExternallyManagedFormulaSave?: (field: string, canonical: string) => Promise<Error | null>
  /** Embedded variant only: grid automatically appends blank rows as the user types in the
   * last row, avoiding an "add row" button. `min` is initial row count. Object must be stable. */
  autoFilas?: AutoRowsConfig<T>
  autoRows?: AutoRowsConfig<T>
  /** Field to group rows by in collapsible bands (replaces Enterprise row grouping). */
  groupBy?: string
  /** Keeps grouping bands, but limits visible rows to a single page. */
  paginateGrouped?: boolean
  onCellChange?: (row: T, editedField?: string) => void | Promise<void>
  /**
   * Re-files dragged rows into the group of `target` when that takes more than writing
   * the grouped cell (a pivot's rows, whose values live in other sheets). Resolves false
   * when nothing moved. Present, rows can be dragged between groups even if the grid is read-only.
   */
  onMoveRowsToGroup?: (rows: T[], target: T, groupFields: string[]) => boolean | Promise<boolean>
  /** Saves several rows changed together (a paste, or undoing one) in one go.
   * When defined it replaces `onCellChange` for any change spanning more than one row. */
  onCellsChange?: (rows: T[]) => void | Promise<void>
  /** Sets the toolbar search from outside (e.g. "Show errors"); a new `seq` applies it again. */
  searchRequest?: { text: string; seq: number } | undefined
  /** How to find flagged cells, shown in the notice after a paste with type errors.
   * Defaults to the search box hint; a host with its own error count can say more. */
  typeErrorsHint?: string | undefined
  /** Set when the rows shown may be out of date; shows a notice with a button that calls it. */
  onStaleRefresh?: (() => void) | undefined
  /** Reports how many cells hold a value their column rejects (see `cellError`). */
  onErrorCountChange?: ((count: number) => void) | undefined
  /** Marks, counts and filters values their column rejects. Off for read-only views such as a trash. */
  flagErrors?: boolean
  onRowSelected?: (row: T) => void
  /** If defined, renders "Activate" button when rows are selected. */
  onBulkActivate?: (rows: T[]) => void | Promise<void>
  /** If defined, renders "Deactivate" button when rows are selected. */
  onBulkDeactivate?: (rows: T[]) => void | Promise<void>
  /** If defined, renders "Restore" button when rows are selected (for trash). */
  onBulkRestore?: (rows: T[]) => void | Promise<void>
  /** If defined, renders "Delete" button (with confirmation modal) when rows are selected. */
  onBulkDelete?: (rows: T[]) => void | Promise<void>
  /** Candidate sheets/tabs for moving rows via bulk action. */
  availableSheets?: Array<{ id: string; name: string; columns: readonly { id?: string; name: string }[] }> | undefined
  /** Invoked when user chooses to move selected rows to another sheet. */
  onBulkMoveToSheet?: ((rows: T[], targetSheetId: string) => void | Promise<void>) | undefined
  /** Button and modal texts for `onBulkDelete` when action is not standard delete
   * (e.g. "Move to Trash"). Defaults to irreversible deletion. */
  accionEliminar?: DeleteActionConfig
  deleteAction?: DeleteActionConfig
  /** Called when pasting values not matching `cellEditorOptions` (those values are discarded).
   * Receives unique rejected values to notify user. */
  onPasteInvalid?: (rejectedValues: string[]) => void
  toolbarAppend?: React.ReactNode
  /** How `toolbarAppend` is offered in the overflow menu (icon + label). */
  toolbarAppendMenu?: ToolbarMenuEntry[]
  /** False hides grouping entirely (toolbar, panel, saved grouping): for computed sheets such as a pivot. */
  groupable?: boolean
  /** Rows nobody stores (a pivot's result): "Add column" starts a calculated column in the formula bar. */
  calculatedColumnsOnly?: boolean
  /** Group subtotals before anyone has chosen (a pivot starts with them shown, below each group). */
  defaultGroupSubtotals?: GroupSubtotalsDefault
  /** Fields to group by (one or two levels) until someone chooses; clearing the grouping then sticks. */
  defaultGroupFields?: readonly string[]
  /** Told whether rows are currently grouped. */
  onGroupedChange?: (grouped: boolean) => void
  /** Items at the very end of a cell's context menu, offered on every cell whatever its column allows. */
  /** Outline grouping: columns whose value still shows on every row of a group. */
  repeatGroupLabelFields?: readonly string[] | undefined
  /** Grouping columns whose groups close without a subtotal row, whatever the subtotals toggle says. */
  noSubtotalFields?: readonly string[] | undefined
  extraCellMenuItems?: (cell: { row: T | null; field: string | null }) => readonly Extract<GridMenuItem, { kind: 'action' }>[]
  /** Sheets the split view can open beside this grid, and how to render one. */
  splitView?: {
    sheets: ReadonlyArray<{ id: string; name: string }>
    /** This grid's own sheet name, shown in its title bar while sidebars are open. */
    title?: string
    render: (sheetId: string, view: SplitSheetView) => React.ReactNode
  }
  /** Only the cells: no toolbar, bars, side panels or footer. Used inside the split view. */
  bare?: boolean
  /** In the split view: which columns this sidebar shows, and where its column menu goes. */
  splitSheetView?: SplitSheetView
  onAddRow?: () => void | Promise<void>
  onAddRows?: (count: number) => void | Promise<void>
  onAddColumn?: ((type?: 'text' | 'checkbox') => void | Promise<void>) | undefined
  /** Inserts a blank row directly above/below `row`. The context-menu item is hidden when absent. */
  onInsertRow?: (row: T, where: 'above' | 'below') => void | Promise<void | GridReversible>
  /** Deletes the single right-clicked row. The context-menu item is hidden when absent. */
  onDeleteRow?: (row: T) => void | Promise<void | GridReversible>
  /**
   * Inserts a blank column immediately after the column with `afterField` in the stored
   * column list; `null` means "before every column".
   *
   * The anchor is a stable field, never an index: the grid's own `columns` array is
   * derived (formula columns appended, `formulaOnly` ones filtered out), so its indices
   * are a different space from the caller's stored list. The grid also resolves
   * left/right in DISPLAY order before calling, so the new column's data predecessor is
   * its display predecessor and it renders exactly where the user asked.
   */
  onInsertColumn?: (afterField: string | null) => void | Promise<void | GridReversible>
  /** Deletes the column with this stable field. Confirmed before it runs. */
  onDeleteColumn?: (field: string) => void | Promise<void | GridReversible>
  /** A change the host made (a pivot's fields, say), put on this grid's undo stack. */
  historyEntry?: GridHistoryEntry | undefined
}

/** 14px stroke icon for the toolbar overflow menu. */
function MenuIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  )
}

/**
 * Icons for the overflow menu rows. Controls that carry an icon in the toolbar reuse
 * the same drawing; the ones the toolbar shows as plain text (Group, fx, Columns,
 * Reset view) get one here so every menu row reads icon + label.
 */
/** Sheets the split view shows beside the grid at once. */
const MAX_SPLIT_SHEETS = 2

/** What a split view sidebar hands the grid inside it. */
export interface SplitSheetView {
  /** Fields this sidebar hides; the sheet itself is untouched. */
  hiddenFields: readonly string[]
  /** Absent for someone who may not change the shared view. */
  onHiddenFieldsChange?: ((fields: string[]) => void) | undefined
  /** The sidebar's title bar, where the grid puts its column menu. */
  menuSlot: HTMLElement | null
}

const MENU_ICONS = {
  // The grid view's glyph, as the view switcher draws it.
  splitView: <svg aria-hidden="true" width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h11A1.5 1.5 0 0 1 15 2.5v11a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 1 13.5v-11zM2.5 2a.5.5 0 0 0-.5.5V6h5V2H2.5zm5.5 0v4h6V2.5a.5.5 0 0 0-.5-.5H8zm6 5H8v4h6V7zm0 5H8v2h4.5a.5.5 0 0 0 .5-.5V12zm-7 2v-2H2v1.5a.5.5 0 0 0 .5.5H7zm-5-3h5V7H2v4z" /></svg>,
  /** A parent bar with two indented children: rows folded into groups. */
  group: <MenuIcon><path d="M4 6h16" /><path d="M4 6v12" /><path d="M9 12h11" /><path d="M9 18h11" /></MenuIcon>,
  collapse: <MenuIcon><path d="m6 15 6-6 6 6" /></MenuIcon>,
  expand: <MenuIcon><path d="m6 9 6 6 6-6" /></MenuIcon>,
  copy: <MenuIcon><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></MenuIcon>,
  paste: <MenuIcon><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-2" /><path d="M16 4h2a2 2 0 0 1 2 2v4" /><path d="M21 14H11" /><path d="m15 10-4 4 4 4" /></MenuIcon>,
  filters: <MenuIcon><g fill="currentColor" stroke="none"><rect x="1" y="5" width="22" height="3" /><rect x="6" y="11" width="13" height="2" /><rect x="10" y="16" width="4" height="3" /></g></MenuIcon>,
  /** An "fx" glyph drawn with strokes: the italic f of the formula bar plus a times sign. */
  formula: <MenuIcon><path d="M14 3h-1.5A2.5 2.5 0 0 0 10 5.5V17a3 3 0 0 1-3 3" /><path d="M7 10h6" /><path d="m15 13 5 6" /><path d="m20 13-5 6" /></MenuIcon>,
  summary: <MenuIcon><path d="M18 4H6l7 8-7 8h12" /></MenuIcon>,
  link: (
    <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1 1" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1-1" />
    </svg>
  ),
  format: (
    <svg aria-hidden="true" width="15" height="15" viewBox="0 0 26 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <g transform="translate(4.5 0)">
        <path d="m9.06 11.9 8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08" />
        <path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z" />
      </g>
      <text x="0" y="11" fill="currentColor" stroke="none" fontSize="11" fontWeight="600">%</text>
    </svg>
  ),
  /** Three upright bars: the columns panel. */
  columns: <MenuIcon><rect x="3" y="4" width="5" height="16" rx="1" /><rect x="9.5" y="4" width="5" height="16" rx="1" /><rect x="16" y="4" width="5" height="16" rx="1" /></MenuIcon>,
  /** Axis with three bars: the chart panel. */
  chart: <MenuIcon><path d="M3 3v18h18" /><path d="M8 17v-5" /><path d="M13 17V8" /><path d="M18 17v-8" /></MenuIcon>,
  /** Counter-clockwise arrow: undo the local view tweaks. */
  resetView: <MenuIcon><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></MenuIcon>,
  /** Replace: search with replace arrow. */
  replace: <MenuIcon><path d="M19 11V9a2 2 0 0 0-2-2H4" /><path d="m7 4-3 3 3 3" /><path d="M5 13v2a2 2 0 0 0 2 2h13" /><path d="m17 14 3 3-3 3" /></MenuIcon>,
  /** Eraser: clear the cell contents. */
  clear: <MenuIcon><path d="m7 21-4.3-4.3a1 1 0 0 1 0-1.4l10-10a1 1 0 0 1 1.4 0l5.6 5.6a1 1 0 0 1 0 1.4L11 21" /><path d="M22 21H7" /><path d="m5 11 9 9" /></MenuIcon>,
  insertRow: <MenuIcon><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 12h18" /><path d="M12 6v3" /><path d="M10.5 7.5h3" /></MenuIcon>,
  insertColumn: <MenuIcon><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M12 3v18" /><path d="M7.5 10.5v3" /><path d="M6 12h3" /></MenuIcon>,
  arrowUp: <MenuIcon><path d="M12 19V5" /><path d="m5 12 7-7 7 7" /></MenuIcon>,
  arrowDown: <MenuIcon><path d="M12 5v14" /><path d="m19 12-7 7-7-7" /></MenuIcon>,
  arrowLeft: <MenuIcon><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></MenuIcon>,
  arrowRight: <MenuIcon><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></MenuIcon>,
  trash: <MenuIcon><path d="M4 7h16" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" /><path d="M9 7V4h6v3" /></MenuIcon>,
  hide: <MenuIcon><path d="m3 3 18 18" /><path d="M10.6 5.1A10 10 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-2.9 3.8" /><path d="M6.6 6.6C3.9 8.4 2 12 2 12s3.6 7 10 7a9.7 9.7 0 0 0 5.4-1.6" /><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" /></MenuIcon>,
  show: <MenuIcon><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></MenuIcon>,
  rename: <MenuIcon><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></MenuIcon>,
  note: <MenuIcon><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></MenuIcon>,
  clock: <MenuIcon><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></MenuIcon>,
  rollup: <MenuIcon><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /><path d="M8 11h6" /></MenuIcon>,
  unlink: <MenuIcon><path d="M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1-1" /><path d="m3 3 18 18" /></MenuIcon>,
}

/** Narrowest useful search field; below it the toolbar shows the search icon instead. */
const SEARCH_MIN_WIDTH = 144

// Stable references for empty settings so hooks do not re-create on each render.
const EMPTY_OVERRIDES: Record<string, string> = {}
const EMPTY_WIDTHS: Record<string, number> = {}
const EMPTY_HIDDEN: string[] = []
const EMPTY_COLUMN_GROUPS: ColumnGroup[] = []
const EMPTY_COLUMN_DEVICES: Record<string, 'desktop' | 'mobile'> = {}
const EMPTY_CHANGE_TIME_FIELDS: string[] = []
const EMPTY_HEADER_NOTES: Record<string, string> = {}

// Number cell sizing adjustments so numbers fit on a single line without truncation.
// Base values: `p-2` (8px) and `text-xs` (12px), matching regular cells.
const CELL_PAD = 8
const CELL_PAD_MIN = 2
const CELL_FONT_SIZE = 14
/** Readability floor: font size will not shrink below this minimum. */
const CELL_FONT_SIZE_MIN = 10
/** Canvas measurement cushion: cell `tabular-nums` is not replicated exactly by canvas. */
const MEASURE_MARGIN = 2

// Off-screen canvas text measurement (no DOM layout thrashing / reflows). Font family
// is read once from <body> from which cells inherit.
let measurementContext: CanvasRenderingContext2D | null | undefined
let measurementFontFamily = ''
function measureText(text: string, fontPx: number, bold = false): number {
  if (measurementContext === undefined) {
    measurementContext = document.createElement('canvas').getContext('2d')
    measurementFontFamily = getComputedStyle(document.body).fontFamily || 'sans-serif'
  }
  if (!measurementContext) return 0
  measurementContext.font = `${bold ? 'bold ' : ''}${fontPx}px ${measurementFontFamily}`
  return measurementContext.measureText(text).width
}

/** Minimum column width for non-numeric columns when resizing. */
const MIN_COLUMN_WIDTH = 60
/** The alignment kind of one cell in a by-cell column: its own type, else what its value is. */
function byCellKind(value: unknown, format: GridDataFormat): 'text' | 'number' | 'date' {
  if (format.type === 'number' || format.type === 'date') return format.type
  if (format.type === 'text') return 'text'
  if (typeof value === 'number' && Number.isFinite(value)) return 'number'
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T.*)?$/.test(value.trim())) return 'date'
  return 'text'
}

/** The history menu's name for a change to each shared grid setting. */
const SETTING_HISTORY_LABELS: Partial<Record<GridSettingKey, MessageKey>> = {
  [GRID_SETTING_KEYS.headers]: 'grid.historySetting.headers',
  [GRID_SETTING_KEYS.headerNotes]: 'grid.historySetting.headerNotes',
  [GRID_SETTING_KEYS.columnGroups]: 'grid.historySetting.columnGroups',
  [GRID_SETTING_KEYS.columnColors]: 'grid.historySetting.columnColors',
  [GRID_SETTING_KEYS.headerColors]: 'grid.historySetting.columnColors',
  [GRID_SETTING_KEYS.columnOrder]: 'grid.historySetting.columnOrder',
  [GRID_SETTING_KEYS.rowOrder]: 'grid.historySetting.rowOrder',
  [GRID_SETTING_KEYS.sort]: 'grid.historySetting.sort',
  [GRID_SETTING_KEYS.grouping]: 'grid.historySetting.grouping',
  [GRID_SETTING_KEYS.widths]: 'grid.historySetting.widths',
  [GRID_SETTING_KEYS.hiddenColumns]: 'grid.historySetting.hiddenColumns',
  [GRID_SETTING_KEYS.frozenColumns]: 'grid.historySetting.frozenColumns',
  [GRID_SETTING_KEYS.columnDevices]: 'grid.historySetting.columnDevices',
  [GRID_SETTING_KEYS.dataFormats]: 'grid.historySetting.dataFormats',
  [GRID_SETTING_KEYS.formulas]: 'grid.historySetting.formulas',
  [GRID_SETTING_KEYS.chart]: 'grid.historySetting.chart',
  [GRID_SETTING_KEYS.summaryCards]: 'grid.historySetting.summaryCards',
}

const CELL_TYPE_LABELS = {
  auto: 'grid.dataTypeAuto',
  text: 'grid.dataTypeText',
  date: 'grid.dataTypeDate',
  number: 'grid.dataTypeNumber',
  checkbox: 'grid.dataTypeCheckbox',
} as const

const ID_CANDIDATES = ['id', 'sku', 'clave', 'email', 'dni_cuit'] as const

// Selection range border color (Excel/Sheets style), independent of brand theme.
const SELECTION_COLOR = 'var(--grid-selection)'
/** The key a group subtotal's fill is stored under, beside the rows' own keys. */
const subtotalRowKey = (groupKey: string) => `subtotal:${groupKey}`
/** The totals row selects like one more subtotal band, under this key. */
const TOTALS_BAND_KEY = '__totals__'

// Virtual column index for the leading row checkbox column (not in `columns`),
// enabling keyboard navigation and range selection to include it seamlessly.
const CHECKBOX_COL = -1

function readValue<T>(column: GridColumn<T>, row: T): unknown {
  if (column.valueGetter) return column.valueGetter({ data: row })
  if (column.field) return (row as Record<string, unknown>)[column.field]
  return undefined
}

function isColumnEditable<T>(column: GridColumn<T>, row: T): boolean {
  return typeof column.editable === 'function' ? column.editable(row) : Boolean(column.editable)
}

/** Visible text of a `dropdown` column value: option label or raw value. */
function dropdownLabel(options: readonly DropdownOption[], value: unknown): string {
  const key = value === null || value === undefined ? '' : String(value)
  if (key === '') return ''
  return options.find((o) => o.value === key)?.label ?? key
}

/**
 * A subtotal or totals-row cell. A sum, average, min or max reads exactly like the rows
 * above it, through the column's own formatter; a card's format, prefix and decimals
 * belong to the summary cards, not to the table. Counts keep the card's formatting.
 */
function aggregateCellText<T>(
  column: GridColumn<T>,
  card: SummaryCardConfig | undefined,
  isNumber: boolean,
  label: string,
  rows: readonly Record<string, unknown>[],
  formats: GridFormats,
): string {
  if (!column.field || (!card && !isNumber)) return ''
  if (!card && column.aggregate) {
    const own = column.aggregate(rows as unknown as readonly T[])
    if (own === null) return ''
    return column.valueFormatter ? column.valueFormatter({ value: own, data: {} as T }) : formatGridData(own, column.dataFormat ?? { type: 'number', formatKey: 'integer' }, formats)
  }
  const effective: SummaryCardConfig = card ?? {
    id: `auto-${column.field}`,
    label,
    columnId: column.field,
    calculationType: 'SUM',
    format: column.dataFormat?.formatKey ?? 'integer',
  }
  const value = aggregateSummaryNumber(effective, rows, formats)
  if (value === null) return calculateSummaryCardValue(effective, rows, formats)
  if (column.valueFormatter) return column.valueFormatter({ value, data: {} as T })
  return formatGridData(value, column.dataFormat ?? { type: 'number', formatKey: 'integer' }, formats)
}

/** The number behind {@link aggregateCellText}, for the status bar's sum; `null` for counts and text. */
function aggregateCellNumber<T>(
  column: GridColumn<T>,
  card: SummaryCardConfig | undefined,
  isNumber: boolean,
  rows: readonly Record<string, unknown>[],
  formats: GridFormats,
): number | null {
  if (!column.field || (!card && !isNumber)) return null
  if (!card && column.aggregate) {
    const own = column.aggregate(rows as unknown as readonly T[])
    return typeof own === 'number' && Number.isFinite(own) ? own : null
  }
  return aggregateSummaryNumber(card ?? { id: `auto-${column.field}`, label: '', columnId: column.field, calculationType: 'SUM' }, rows, formats)
}

/** Padding and font size that fit `text` into a column `width`, down to the minimums. */
/** The size cells actually render at: `text-sm` on phones, `text-xs` from `sm` up. */
function renderedCellFontSize(): number {
  if (typeof window === 'undefined' || !window.matchMedia) return CELL_FONT_SIZE
  return window.matchMedia('(min-width: 640px)').matches ? 12 : CELL_FONT_SIZE
}

function fitShrunkText(text: string, width: number): { padding: number; fontSize: number; reduced: boolean; minWidth: number } {
  // Shrinking never enlarges: the cell's own font size is the ceiling.
  const base = renderedCellFontSize()
  const needed = measureText(text, base) + MEASURE_MARGIN
  const minWidth = Math.ceil(needed * (Math.min(CELL_FONT_SIZE_MIN, base) / base)) + 2 * CELL_PAD_MIN
  let padding = CELL_PAD
  let fontSize = base
  let reduced = false
  if (needed > width - 2 * CELL_PAD) {
    padding = Math.max(CELL_PAD_MIN, Math.floor((width - needed) / 2))
    const available = width - 2 * padding
    if (needed > available) {
      fontSize = Math.min(base, Math.max(CELL_FONT_SIZE_MIN, Math.floor(base * (available / needed) * 10) / 10))
      reduced = true
    }
  }
  return { padding, fontSize, reduced, minWidth }
}

/** The toolbar search: ordinary text matching, or the errors code, which keeps
 * the rows holding a value their column's type rejects. */
function rowMatchesSearch<T>(
  search: string,
  row: T,
  searchCols: readonly GridColumn<T>[],
  text: (column: GridColumn<T>) => string,
  hasError: (column: GridColumn<T>, row: T) => boolean,
): boolean {
  if (!isGridErrorsSearch(search)) return matchesGridSearch(search, searchCols.map(text))
  return searchCols.some((column) => hasError(column, row))
}

function cellText<T>(column: GridColumn<T>, row: T): string {
  const value = readValue(column, row)
  if (column.valueFormatter) return column.valueFormatter({ value, data: row })
  // Copy/paste, search, and filter must match rendered cell text: the label.
  if (column.dropdown) return dropdownLabel(column.dropdown.options, value)
  if (column.cellRenderer) {
    const result = column.cellRenderer({ value, data: row })
    if (typeof result === 'string') return result
  }
  if (value === null || value === undefined) return ''
  return String(value)
}

function getCellUrl<T>(column: GridColumn<T>, row: T, tdElement?: HTMLElement | null): string {
  if (tdElement) {
    const anchorEl = tdElement.querySelector('a[href]')
    const href = anchorEl?.getAttribute('href')
    if (href) return href
  }
  const field = column.field
  const rowRec = row as Record<string, unknown>
  if (field) {
    const candidateFields = [`${field}Url`, `${field}_url`, `${field}Link`, `${field}_link`, 'url', 'link', 'href']
    for (const cand of candidateFields) {
      const val = rowRec[cand]
      if (typeof val === 'string' && val.trim()) return val.trim()
    }
  }
  const currentVal = readValue(column, row)
  const textVal = currentVal === null || currentVal === undefined ? '' : String(currentVal).trim()
  if (/^https?:\/\//i.test(textVal) || /^(mailto|tel):/i.test(textVal)) {
    return textVal
  }
  return ''
}

// Date text regex matching dd/mm/yyyy (with optional weekday prefix) used to infer right alignment.
const DATE_REGEX = /^(?:\D+\s)?\d{2}\/\d{2}\/\d{4}$/

function rowKey<T extends object>(row: T, index: number): string {
  const record = row as Record<string, unknown>
  for (const field of ID_CANDIDATES) if (record[field] !== undefined && record[field] !== null) return `${field}:${String(record[field])}`
  return `idx:${index}`
}

function toISODate(d: Date): string {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function getInitialIsoDate(value: unknown): string {
  if (!value) return ''
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return toISODate(value)
  }
  if (typeof value === 'number' && value > 1000 && value < 100000) {
    const d = serialToDate(value)
    return toISODate(d)
  }
  const str = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str
  const parsed = parseGridDate(str)
  return parsed ? toISODate(parsed) : ''
}

function formatNumber(n: number): string {
  return n.toLocaleString('es-AR', { maximumFractionDigits: 2 })
}

function compareValues(a: unknown, b: unknown): number {
  if (a === b) return 0
  if (a === null || a === undefined) return 1
  if (b === null || b === undefined) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b)
  return String(a).localeCompare(String(b), 'es', { numeric: true, sensitivity: 'base' })
}

function compareFilterMenuValues(a: string, b: string): number {
  const isAEmpty = a === '(vacío)' || a === '(sin categoría)' || a === '(empty)' || a === '(uncategorized)'
  const isBEmpty = b === '(vacío)' || b === '(sin categoría)' || b === '(empty)' || b === '(uncategorized)'
  if (isAEmpty && !isBEmpty) return 1
  if (!isAEmpty && isBEmpty) return -1
  return compareValues(a, b)
}

function compareGroupLabels(a: string, b: string): number {
  // An empty value groups under a blank label, sorted last.
  const isAEmpty = a === '' || a === '(sin categoría)' || a === '(vacío)' || a === '(uncategorized)' || a === '(empty)'
  const isBEmpty = b === '' || b === '(sin categoría)' || b === '(vacío)' || b === '(uncategorized)' || b === '(empty)'
  if (isAEmpty && !isBEmpty) return 1
  if (!isAEmpty && isBEmpty) return -1
  return 0
}

interface CellHistoryChange { rowKey: string; field: string; previous: unknown; next: unknown }

// Dotted border ("marching ants") around copied selection perimeter.
function getCopiedBorderStyles(
  rowPos: number,
  colPos: number,
  rect: { rowMin: number; rowMax: number; colMin: number; colMax: number } | null,
): React.CSSProperties | null {
  if (!rect || rowPos < rect.rowMin || rowPos > rect.rowMax || colPos < rect.colMin || colPos > rect.colMax) return null
  const hasTop = rowPos === rect.rowMin
  const hasBottom = rowPos === rect.rowMax
  const hasLeft = colPos === rect.colMin
  const hasRight = colPos === rect.colMax
  if (!hasTop && !hasBottom && !hasLeft && !hasRight) return null

  return {
    ...(hasTop ? { borderTop: `2px dotted ${SELECTION_COLOR}` } : {}),
    ...(hasBottom ? { borderBottom: `2px dotted ${SELECTION_COLOR}` } : {}),
    ...(hasLeft ? { borderLeft: `2px dotted ${SELECTION_COLOR}` } : {}),
    ...(hasRight ? { borderRight: `2px dotted ${SELECTION_COLOR}` } : {}),
  }
}

function getCopiedColumnBorderStyles(
  rowPos: number,
  columnPos: number,
  index: number,
  copiedCols: Set<number> | null,
  totalRows: number,
  visibleCols: readonly number[],
): React.CSSProperties | null {
  if (!copiedCols || !copiedCols.has(index)) return null
  const hasTop = rowPos === 0
  const hasBottom = rowPos === totalRows - 1
  const hasLeft = columnPos === 1 || !copiedCols.has(visibleCols[columnPos - 2] ?? -1)
  const hasRight = columnPos === visibleCols.length || !copiedCols.has(visibleCols[columnPos] ?? -1)
  if (!hasTop && !hasBottom && !hasLeft && !hasRight) return null

  return {
    ...(hasTop ? { borderTop: `2px dotted ${SELECTION_COLOR}` } : {}),
    ...(hasBottom ? { borderBottom: `2px dotted ${SELECTION_COLOR}` } : {}),
    ...(hasLeft ? { borderLeft: `2px dotted ${SELECTION_COLOR}` } : {}),
    ...(hasRight ? { borderRight: `2px dotted ${SELECTION_COLOR}` } : {}),
  }
}

/**
 * The option list of a `smartchip` / `autofill` column. Two ways to fill it:
 * write the values by hand — the same `ChipListEditor` panel `ChipSelect` opens
 * from its pencil — or link the column to another column, anywhere the user can
 * open, and let its distinct values be the list. Colours are stored either way;
 * a linked list keeps only the colours, the values follow the source.
 */
function ColumnOptionsPanel({
  initialOptions,
  initialLink,
  suggestions,
  title,
  orgId,
  workbook,
  renames,
  calculated,
  resolveCell,
  onPersist,
  onClose,
}: {
  initialOptions: readonly GridDataOption[]
  initialLink: GridOptionsLink | undefined
  suggestions: readonly string[]
  title: string
  orgId: string | null
  workbook: WorkbookSnapshot | undefined
  renames: Readonly<Record<string, Record<string, string>>>
  calculated: Readonly<Record<string, readonly { field: string; name: string }[]>>
  resolveCell: (tabId: string) => (row: Record<string, unknown>, field: string) => unknown
  onPersist: (next: { options: GridDataOption[]; link: GridOptionsLink | undefined }) => void
  onClose: () => void
}) {
  const { t } = useI18n()
  const [mode, setMode] = useState<'manual' | 'link'>(initialLink ? 'link' : 'manual')
  const [link, setLink] = useState<GridOptionsLink | undefined>(initialLink)
  const [items, setItems] = useState<ItemChip[]>(() => initialOptions.map((option, index) => ({
    clave: option.value,
    texto: option.value,
    color: option.color ?? null,
    orden: index,
  })))
  const [error, setError] = useState<string | null>(null)
  const itemsRef = useRef(items)
  itemsRef.current = items

  const colorOf = useCallback(
    (value: string) => itemsRef.current.find((item) => item.texto === value)?.color ?? null,
    [],
  )

  const optionsOf = useCallback((list: readonly ItemChip[]) => list.map((item) => (
    item.color ? { value: item.texto, color: item.color } : { value: item.texto }
  )), [])

  // Every mutation rewrites the whole list, which is what the stored format holds.
  const commit = useCallback((next: ItemChip[], nextLink: GridOptionsLink | undefined) => {
    setItems(next.map((item, index) => ({ ...item, orden: index })))
    onPersist({ options: optionsOf(next), link: nextLink })
    return null
  }, [onPersist, optionsOf])

  const fuente = useMemo<FuenteChips>(() => ({
    cargar: async () => itemsRef.current,
    guardarColor: async (clave, color) => commit(
      itemsRef.current.some((item) => item.clave === clave)
        ? itemsRef.current.map((item) => (item.clave === clave ? { ...item, color } : item))
        // A linked value has no stored row until it is given a colour.
        : [...itemsRef.current, { clave, texto: clave, color, orden: itemsRef.current.length }],
      link,
    ),
    ...(mode === 'link' ? {} : {
      guardarTexto: async (clave: string, texto: string) => {
        if (itemsRef.current.some((item) => item.clave !== clave && item.texto === texto)) {
          return { error: t('grid.optionDuplicate'), nuevaClave: clave }
        }
        commit(itemsRef.current.map((item) => (item.clave === clave ? { ...item, clave: texto, texto } : item)), link)
        return { error: null, nuevaClave: texto }
      },
      agregar: async (texto: string) => {
        if (itemsRef.current.some((item) => item.texto === texto)) return t('grid.optionDuplicate')
        return commit([...itemsRef.current, { clave: texto, texto, color: null, orden: itemsRef.current.length }], link)
      },
      eliminar: async (clave: string) => commit(itemsRef.current.filter((item) => item.clave !== clave), link),
      reordenar: async (clavesEnOrden: string[]) => commit(
        clavesEnOrden
          .map((clave) => itemsRef.current.find((item) => item.clave === clave))
          .filter((item): item is ItemChip => item !== undefined),
        link,
      ),
    }),
  }), [commit, link, mode, t])

  const { tables, loading: tablesLoading, error: tablesError } = useLinkableTables(mode === 'link', orgId, workbook, renames, calculated)
  const linkList = useMemo(() => (link && link.field !== '' ? [link] : []), [link])
  // The author of a link can read the source, so the preview reads it live
  // rather than waiting for the first push.
  const { values: linkedValues, loading: valuesLoading, error: valuesError } = useLinkedValues(
    linkList,
    workbook,
    resolveCell,
    { mode: 'live' },
  )

  const currentTableKey = workbook ? tableKey(workbook.documentId ?? '', workbook.currentSheetId) : ''
  const selectedTableKey = link ? tableKey(link.documentId ?? workbook?.documentId ?? '', link.tabId) : currentTableKey
  const selectedTable = tables.find((table) => table.key === selectedTableKey)
  const currentDocumentId = workbook?.documentId ?? ''
  const selectedDocumentId = link?.documentId ?? currentDocumentId
  // One entry per document that has a linkable table, the current one first.
  const documents = tables.reduce<{ id: string; name: string }[]>((list, table) => {
    if (!list.some((document) => document.id === table.documentId)) {
      list.push({
        id: table.documentId,
        name: table.documentId === currentDocumentId ? t('grid.optionsLinkCurrentDocument') : table.documentName,
      })
    }
    return list
  }, [])
  const documentTables = tables.filter((table) => table.documentId === selectedDocumentId)

  const chooseDocument = (documentId: string) => {
    const first = tables.find((table) => table.documentId === documentId)
    if (first) chooseTable(first.key)
  }

  const applyLink = (next: GridOptionsLink | undefined) => {
    setLink(next)
    onPersist({ options: optionsOf(itemsRef.current), link: next })
  }

  const chooseTable = (key: string) => {
    const table = tables.find((candidate) => candidate.key === key)
    if (!table) return
    // The field has to be picked again: columns are per table.
    applyLink(undefined)
    setLink({
      ...(table.documentId && table.documentId !== (workbook?.documentId ?? '') ? { documentId: table.documentId } : {}),
      tabId: table.tabId,
      field: '',
      sort: link?.sort ?? 'alpha',
    })
  }

  const chooseField = (field: string) => {
    if (!link) return
    if (field === '') {
      applyLink(undefined)
      setLink({ ...link, field: '' })
      return
    }
    applyLink({ ...link, field })
  }

  const linkedList = link && link.field !== ''
    ? sortLinkedValues(linkedValues.get(optionsLinkKey(link)) ?? [], link.sort)
    : []

  const linkedItems = linkedList.map<ItemChip>((value, index) => ({
    clave: value,
    texto: value,
    color: colorOf(value),
    orden: index,
  }))

  const missing = suggestions.filter((value) => !items.some((item) => item.texto === value))

  const shownError = error ?? tablesError ?? valuesError

  return (<>
    <div className="flex shrink-0 items-start justify-between gap-2 border-b border-grid-line p-3">
      <h2 className="text-sm font-semibold">{title}</h2>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('common.close')}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-text-muted hover:bg-surface-3 hover:text-text"
      >
        <Icon name="close" className="h-4 w-4" />
      </button>
    </div>
    <div className="min-h-0 flex-1 overflow-y-auto p-3">
      <p className="mb-3 text-xs text-muted">{mode === 'link' ? t('grid.optionsLinkDescription') : t('grid.optionsDescription')}</p>
      {shownError && <p role="alert" className="mb-3 rounded-lg border border-danger px-3 py-2 text-xs text-danger">{shownError}</p>}
      <div className="mb-3 inline-flex rounded-full border border-border p-0.5" role="group" aria-label={t('grid.optionsSourceLabel')}>
        {([
          { key: 'manual', label: t('grid.optionsWriteList') },
          { key: 'link', label: t('grid.optionsLinkColumn') },
        ] as const).map(({ key, label }) => (
          <button
            key={key}
            type="button"
            aria-pressed={mode === key}
            onClick={() => {
              setMode(key)
              // Leaving link mode drops the link; the hand-written list stays.
              if (key === 'manual' && link) applyLink(undefined)
              if (key === 'link' && !link && workbook) {
                setLink({ tabId: workbook.currentSheetId, field: '', sort: 'alpha' })
              }
            }}
            className={`h-8 rounded-full px-3 text-sm transition-colors ${mode === key ? 'bg-accent text-accent-fg font-medium' : 'text-fg hover:bg-surface-3'}`}
          >{label}</button>
        ))}
      </div>

      {mode === 'link' && <div className="mb-3 flex flex-col gap-2">
        <label className="flex flex-col gap-1 text-xs font-bold uppercase text-muted">
          {t('grid.optionsLinkDocument')}
          <select
            value={selectedDocumentId}
            onChange={(event) => chooseDocument(event.target.value)}
            className="h-9 rounded-lg border border-border bg-surface px-2 text-sm font-normal normal-case text-fg outline-none focus:border-accent"
          >
            {documents.map((document) => (
              <option key={document.id} value={document.id}>{document.name}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-bold uppercase text-muted">
          {t('grid.optionsLinkTable')}
          <select
            value={selectedTableKey}
            onChange={(event) => chooseTable(event.target.value)}
            className="h-9 rounded-lg border border-border bg-surface px-2 text-sm font-normal normal-case text-fg outline-none focus:border-accent"
          >
            {documentTables.map((table) => (
              <option key={table.key} value={table.key}>{table.tabName}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-bold uppercase text-muted">
          {t('grid.optionsLinkColumnLabel')}
          <select
            value={link?.field ?? ''}
            disabled={!selectedTable}
            onChange={(event) => chooseField(event.target.value)}
            className="h-9 rounded-lg border border-border bg-surface px-2 text-sm font-normal normal-case text-fg outline-none focus:border-accent disabled:opacity-50"
          >
            <option value="">{t('grid.optionsLinkPickColumn')}</option>
            {(selectedTable?.columns ?? []).map((column) => (
              <option key={column.field} value={column.field}>{column.name}</option>
            ))}
          </select>
        </label>
        {tablesLoading && <p className="text-xs text-muted">{t('common.loading')}</p>}
      </div>}

      {mode === 'link' && link && link.field !== '' && <>
        <div className="mb-2 inline-flex rounded-full border border-border p-0.5" role="group" aria-label={t('grid.optionsSortLabel')}>
          {([
            { key: 'alpha', label: t('grid.optionsSortAlpha') },
            { key: 'source', label: t('grid.optionsSortSource') },
          ] as const).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              aria-pressed={link.sort === key}
              onClick={() => applyLink({ ...link, sort: key })}
              className={`h-7 rounded-full px-3 text-xs transition-colors ${link.sort === key ? 'bg-accent text-accent-fg font-medium' : 'text-fg hover:bg-surface-3'}`}
            >{label}</button>
          ))}
        </div>
        {valuesLoading && linkedItems.length === 0
          ? <p className="text-sm text-muted">{t('common.loading')}</p>
          : linkedItems.length === 0
            ? <p className="text-sm text-muted">{t('grid.optionsLinkEmpty')}</p>
            : <ChipListEditor
              items={linkedItems}
              fuente={fuente}
              onItemsChange={() => { /* linked values are read-only here; only colours are saved */ }}
              onReload={() => setItems([...itemsRef.current])}
              onError={setError}
            />}
      </>}

      {mode === 'manual' && <>
        {items.length === 0 && <p className="mb-2 text-sm text-muted">{t('grid.optionsEmpty')}</p>}
        <ChipListEditor
          items={items}
          fuente={fuente}
          onItemsChange={setItems}
          onReload={() => setItems([...itemsRef.current])}
          onError={setError}
        />
        {missing.length > 0 && <div className="mt-2 flex flex-wrap items-center gap-2">
          {missing.length > 1 && <button
            type="button"
            onClick={() => commit([
              ...itemsRef.current,
              ...missing.map((value, index) => ({ clave: value, texto: value, color: null, orden: itemsRef.current.length + index })),
            ], link)}
            className="h-8 rounded-full border border-border bg-surface px-3 text-sm font-medium text-fg transition-colors hover:bg-surface-3"
          >{t('grid.optionsAddAll')}</button>}
          {missing.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => void fuente.agregar?.(value)}
              className="h-8 rounded-full border border-dashed border-border px-3 text-sm text-muted transition-colors hover:bg-surface-3"
            >+ {value}</button>
          ))}
        </div>}
      </>}
    </div>
  </>)
}

/** A cell's raw date as YYYY-MM-DD: ISO text or a Date; anything else is not a day. */
function chartIsoDay(value: unknown): string | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
  return null
}

export default function Grid<T extends object>({
  gridKey,
  gridKeyColumnas,
  columnsGridKey,
  rows,
  // The page's own columns. Calculated formula columns are appended below,
  // and from then on `columns` refers to both kinds for every consumer.
  columns: columnsProp,
  getRowId: getRowIdProp,
  formulaWorkbook,
  loading = false,
  editable = false,
  onRefresh,
  refreshTitle,
  height,
  variante = 'completa',
  variant,
  mobileAutoHeight: _mobileAutoHeight = false,
  formulaBarOpen,
  formatBarOpen,
  formulaDefaults,
  externallyManagedFormulaFields,
  onExternallyManagedFormulaSave,
  autoFilas,
  autoRows,
  groupBy,
  onMoveRowsToGroup,
  onCellChange,
  onCellsChange,
  searchRequest,
  typeErrorsHint,
  onStaleRefresh,
  onErrorCountChange,
  flagErrors = true,
  onRowSelected,
  onBulkActivate,
  onBulkDeactivate,
  onBulkRestore,
  onBulkDelete,
  availableSheets,
  onBulkMoveToSheet,
  accionEliminar,
  deleteAction: deleteActionProp,
  onPasteInvalid,
  toolbarAppend,
  toolbarAppendMenu,
  groupable = true,
  calculatedColumnsOnly = false,
  defaultGroupSubtotals,
  defaultGroupFields,
  onGroupedChange,
  extraCellMenuItems,
  repeatGroupLabelFields,
  noSubtotalFields,
  splitView,
  bare = false,
  splitSheetView,
  onAddRow,
  onAddRows,
  onAddColumn,
  onInsertRow,
  onDeleteRow,
  onInsertColumn,
  onDeleteColumn,
  historyEntry,
}: GridProps<T>) {
  const [rowsToAdd, setRowsToAdd] = useState('10')
  const editor = useOptionalEditor()
  const editorRef = useRef(editor)
  editorRef.current = editor
  const editorSetHistory = editor?.setHistory
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const markSaved = useCallback(() => {
    editorRef.current?.setSaveState('saved')
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      editorRef.current?.setSaveState('clean')
    }, 2500)
  }, [])

  const markSaving = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    editorRef.current?.setSaveState('saving')
  }, [])

  const markFailed = useCallback((_msg?: string) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    editorRef.current?.setSaveState('failed')
  }, [])

  const [fabMenuOpen, setFabMenuOpen] = useState(false)
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  /** The header whose note is being written, and the draft. */
  const [noteEditor, setNoteEditor] = useState<{ field: string; text: string } | null>(null)
  const [groupingDialogOpen, setGroupingDialogOpen] = useState(false)
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkDialogValue, setLinkDialogValue] = useState('')
  const [linkDialogCopied, setLinkDialogCopied] = useState(false)
  const [copiedCellKey, setCopiedCellKey] = useState<string | null>(null)
  const [uploadingCellKey, setUploadingCellKey] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [copyHeaders, setCopyHeaders] = useCopyHeadersPreference()
  const [copyNumberFormat, setCopyNumberFormat] = useCopyNumberFormatPreference()
  const [copyFormulas, setCopyFormulas] = useCopyFormulasPreference()
  const fabMenuRef = useRef<HTMLDivElement>(null)
  // The floating Add button sits just above the grid's own bottom edge, so the
  // formula bar, sheet tabs and status bar below it never end up underneath.
  const [fabBottom, setFabBottom] = useState(72)
  // Hidden while a cell is being edited on a phone, where the keyboard is up.
  const [keyboardOpen, setKeyboardOpen] = useState(false)

  useEffect(() => {
    if (!fabMenuOpen) return
    const onDocClick = (e: MouseEvent) => {
      if (fabMenuRef.current && !fabMenuRef.current.contains(e.target as Node)) {
        setFabMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [fabMenuOpen])

  const runWithSaveState = useCallback(async <R,>(action: () => R | Promise<R>): Promise<R> => {
    markSaving()
    try {
      const result = await action()
      markSaved()
      return result
    } catch (err) {
      markFailed()
      throw err
    }
  }, [markSaving, markSaved, markFailed])

  const resolvedVariant = variant ?? variante
  const isEmbedded = resolvedVariant === 'embebida' || resolvedVariant === 'embedded'
  const fullScreen = resolvedVariant === 'full' || resolvedVariant === 'completa' || (!isEmbedded && !height)
  const resolvedAutoRows = autoRows ?? autoFilas

  // Rows actually rendered: in embedded variant, padded with blank rows up to `min`
  // and adds one more when the last is used. Without `autoRows`, uses `rows` directly.
  const effectiveRows = useMemo(() => {
    if (!resolvedAutoRows) return rows
    const minCount = resolvedAutoRows.min ?? resolvedAutoRows.minimo ?? 0
    const createFn = resolvedAutoRows.create ?? resolvedAutoRows.nueva ?? (() => ({} as T))
    const isEmptyFn = resolvedAutoRows.isEmpty ?? resolvedAutoRows.estaVacia ?? (() => false)

    const out = [...rows]
    while (out.length < minCount) out.push(createFn())
    if (out.length === 0 || !isEmptyFn(out[out.length - 1]!)) out.push(createFn())
    return out
  }, [rows, resolvedAutoRows])

  // In embedded variant, rowKey is index because rows in forms may be empty or duplicate IDs.
  // Visual order equals index directly.
  const getRowKey = useCallback(
    (row: T, index: number) => isEmbedded
      ? `idx:${index}`
      : getRowIdProp
        ? getRowIdProp(row)
        : rowKey(row, index),
    [getRowIdProp, isEmbedded],
  )
  const [internalRows, setInternalRows] = useState<T[]>(effectiveRows)
  const internalRowsRef = useRef<T[]>(effectiveRows)
  // Edits whose save has not come back yet. The host may store something other
  // than what was typed (a stray "+" in a number column comes back empty), so
  // each edit's history entry takes the stored value as its own; otherwise undo
  // would see a mismatch and call it a conflict. Settled on the first rows
  // after the save resolves, so later edits from elsewhere still conflict.
  const unsettledEditsRef = useRef(new Set<{ changes: CellHistoryChange[]; side: 'previous' | 'next'; saved: boolean }>())
  useEffect(() => {
    let rows = effectiveRows
    for (const edit of unsettledEditsRef.current) {
      for (const change of edit.changes) {
        const index = rows.findIndex((candidate, rowIndex) => getRowKey(candidate, rowIndex) === change.rowKey)
        if (index < 0) continue
        if (edit.saved) {
          change[edit.side] = (rows[index] as Record<string, unknown>)[change.field]
        } else {
          // Rows that arrive before the save lands don't have the edit yet: keep it on screen.
          if (rows === effectiveRows) rows = [...effectiveRows]
          rows[index] = { ...rows[index]!, [change.field]: change[edit.side] } as T
        }
      }
      if (edit.saved) unsettledEditsRef.current.delete(edit)
    }
    internalRowsRef.current = rows
    setInternalRows(rows)
  }, [effectiveRows, getRowKey])

  // When rows change, prune selected keys no longer present in new dataset,
  // while preserving active ones across refreshes.
  useEffect(() => {
    if (effectiveRows.length === 0 && loading !== false) return
    setSelected((current) => {
      if (current.size === 0) return current
      const validKeys = new Set(effectiveRows.map((r, i) => getRowKey(r, i)))
      const next = new Set([...current].filter((k) => validKeys.has(k)))
      return next.size === current.size ? current : next
    })
  }, [effectiveRows, getRowKey, loading])

  // Invalidate selection range only if row no longer exists in updated dataset.
  useEffect(() => {
    if (effectiveRows.length === 0) return
    const validKeys = new Set(effectiveRows.map((r, i) => getRowKey(r, i)))
    setAnchor((current) => current && !validKeys.has(current.rowKey) ? null : current)
    setEnd((current) => current && !validKeys.has(current.rowKey) ? null : current)
    setCopied((current) => current && (!validKeys.has(current.anchor.rowKey) || !validKeys.has(current.end.rowKey)) ? null : current)
  }, [effectiveRows, getRowKey])

  // Key for column widths & visibility: base grid key if this is a variant (e.g. Trash).
  const columnsKey = columnsGridKey ?? gridKeyColumnas ?? gridKey

  // Embedded grid does not persist view preferences in localStorage.
  const readPrefFrom = (key: string, suffix: string) => isEmbedded ? null : localStorage.getItem(`tms-grilla-${key}-${suffix}`)
  const savePrefTo = useCallback((key: string, suffix: string, value: string | null) => {
    if (isEmbedded) return
    if (value === null) localStorage.removeItem(`tms-grilla-${key}-${suffix}`)
    else localStorage.setItem(`tms-grilla-${key}-${suffix}`, value)
  }, [isEmbedded])
  const readPref = (suffix: string) => readPrefFrom(gridKey, suffix)
  const sizeKey = (suffix: string) => isEmbedded ? null : `tms-grilla-${gridKey}-${suffix}`
  const savePref = useCallback(
    (suffix: string, value: string | null) => savePrefTo(gridKey, suffix, value),
    [savePrefTo, gridKey],
  )
  // Legacy width preferences use `columnsKey` (shared with base grid).
  const savePrefCols = useCallback(
    (suffix: string, value: string | null) => savePrefTo(columnsKey, suffix, value),
    [savePrefTo, columnsKey],
  )

  const [search, setSearch] = useState(() => readPref('busqueda') ?? '')
  const [replaceText, setReplaceText] = useState('')
  /** Values the last paste or edit kept although their column rejects them; drives the yellow notice. */
  const [typeNotice, setTypeNotice] = useState<{ count: number; rejected?: number; typed: boolean } | null>(null)
  const [replaceVisible, setReplaceVisible] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const replaceInputRef = useRef<HTMLInputElement>(null)
  /** Second row holding the search field while the toolbar is too narrow for it. */
  const [searchRowOpen, setSearchRowOpen] = useState(() => Boolean(readPref('busqueda')))
  /** Toolbar items currently rendered as their compact stand-in (today only the search field). */
  const [collapsedToolbarKeys, setCollapsedToolbarKeys] = useState<string[]>([])
  // A sheet in the split view keeps its own zoom, apart from the same sheet opened on its own.
  const zoomPref = bare ? 'zoom-dividida' : 'zoom'
  // Grid text ignores the phone's text size; pinch zoom is its only scale.
  const textScale = useSystemTextScale()
  const [zoom, setZoom] = useState(() => {
    const saved = readPref(zoomPref)
    return saved ? Number(saved) : 100
  })
  const [filters, setFilters] = useState<Record<number, Set<string>>>(() => {
    try {
      const saved = JSON.parse(readPref('filtros') ?? 'null') as Record<string, string[]> | null
      return saved ? Object.fromEntries(Object.entries(saved).map(([i, values]) => [i, new Set(values)])) : {}
    } catch { return {} }
  })
  // A filter change is a step of its own on the undo stack, as in Sheets.
  const filtersRef = useRef(filters)
  filtersRef.current = filters
  const filterWithHistory = (update: (current: Record<number, Set<string>>) => Record<number, Set<string>>) => {
    const before = filtersRef.current
    const after = update(before)
    if (after === before) return
    filtersRef.current = after
    setFilters(after)
    addHistoryTaskRef.current({
      label: t('grid.historySetting.filter'),
      undo: () => { setFilters(before); return true },
      redo: () => { setFilters(after); return true },
    })
  }
  // Column filter controls toggle visibility, persisted per gridKey.
  const [filterControlsVisible, setFilterControlsVisible] = useState(() => readPref('filtros-visibles') === '1')
  /** Where the add-row button lives, per device: the toolbar on desktop and a floating button in the grid on phones,
   *  unless the user moved it. The menu's device switch picks which devices a move applies to. */
  const addButtonPhone = useIsMobile()
  const [addButtonPlacement, setAddButtonPlacement] = useState(() => ({
    desktop: readPref('boton-agregar-escritorio') ?? 'barra',
    mobile: readPref('boton-agregar-movil') ?? 'grilla',
  }))
  const [addButtonMoveScope, setAddButtonMoveScope] = useState<'all' | 'desktop' | 'mobile'>('all')
  const addButtonInGrid = (addButtonPhone ? addButtonPlacement.mobile : addButtonPlacement.desktop) === 'grilla'
  const moveAddButton = (toGrid: boolean) => {
    const value = toGrid ? 'grilla' : 'barra'
    const next = { ...addButtonPlacement }
    if (addButtonMoveScope !== 'mobile') { next.desktop = value; savePref('boton-agregar-escritorio', value) }
    if (addButtonMoveScope !== 'desktop') { next.mobile = value; savePref('boton-agregar-movil', value) }
    setAddButtonPlacement(next)
  }
  const [activeHeaderIndex, setActiveHeaderIndex] = useState<number | null>(null)
  const [openFilter, setOpenFilter] = useState<number | null>(null)
  const [filterSearch, setFilterSearch] = useState('')
  useEffect(() => setFilterSearch(''), [openFilter])

  // Header filter menu size, resizable by dragging bottom-right corner.
  const { panelRef: filterMenuRef, size: filterMenuSize, startResize: startFilterResize } = useResizableSize(
    { width: 208, height: 256 },
    { key: sizeKey('filtro-tam'), minWidth: 180, minHeight: 140 },
  )
  const [filterMenuPosition, setFilterMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 })

  useLayoutEffect(() => {
    if (openFilter === null || !filterRef.current) return
    const updatePosition = () => {
      const rect = filterRef.current?.getBoundingClientRect()
      if (!rect) return
      const width = filterMenuSize?.width ?? 208
      const height = filterMenuSize?.height ?? 256
      const pad = 8

      // Horizontal: align to rect.left, but flip to rect.right - width if overflowing right edge
      let left = rect.left
      if (left + width > window.innerWidth - pad) {
        left = rect.right - width
      }
      left = Math.max(pad, Math.min(left, window.innerWidth - width - pad))

      // Vertical: place below header, or above if overflowing viewport bottom
      let top = rect.bottom + 4
      if (top + height > window.innerHeight - pad && rect.top - height - 4 >= pad) {
        top = rect.top - height - 4
      } else if (top + height > window.innerHeight - pad) {
        top = Math.max(pad, window.innerHeight - height - pad)
      }

      setFilterMenuPosition({ top, left })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [openFilter, filterMenuSize?.width, filterMenuSize?.height])

  // Visibility is a shared grid setting. Clear legacy browser-only keys.
  useEffect(() => {
    savePref('ocultas', null)
    savePrefCols('columnas-ocultas', null)
  }, [savePref, savePrefCols])

  // Right sidebar columns panel.
  const [columnsPanel, setColumnsPanel] = useState(() => readPref('columnas') === '1')
  const [columnsSearch, setColumnsSearch] = useState('')
  const [columnsPanelWidth, setColumnsPanelWidth] = useState<number>(() => {
    const saved = readPref('columnas-ancho')
    return saved ? Math.max(160, Math.min(600, Number(saved))) : 224
  })
  const resizeColumnsPanel = (width: number) => { setColumnsPanelWidth(width); savePref('columnas-ancho', String(width)) }

  // Right sidebar for a smartchip / autofill column's options.
  const [optionsPanelWidth, setOptionsPanelWidth] = useState<number>(() => {
    const saved = readPref('opciones-ancho')
    return saved ? Math.max(240, Math.min(900, Number(saved))) : 340
  })
  const resizeOptionsPanel = (width: number) => { setOptionsPanelWidth(width); savePref('opciones-ancho', String(width)) }

  // Right sidebar chart panel. Shares the right side with the columns panel, so only one is open.
  // Whether the chart and the split view are open is shared per sheet (see `persistPanels`).
  const [chartPanel, setChartPanel] = useState(false)
  /** The active-filters panel: a full page on phones, a right sidebar from `sm` up. */
  const [filtersPanel, setFiltersPanel] = useState(false)
  const [addFilterOpen, setAddFilterOpen] = useState(false)
  const [filtersPanelWidth, setFiltersPanelWidth] = useState<number>(() => Number(readPref('filtros-panel-ancho')) || 300)
  useEffect(() => { if (!filtersPanel) setAddFilterOpen(false) }, [filtersPanel])
  const [chartPanelWidth, setChartPanelWidth] = useState<number>(() => {
    const saved = readPref('grafico-ancho')
    return saved ? Math.max(240, Math.min(900, Number(saved))) : 360
  })
  const resizeChartPanel = (width: number) => { setChartPanelWidth(width); savePref('grafico-ancho', String(width)) }

  const toggleChartPanel = () => {
    const next = !chartPanel
    setChartPanel(next)
    if (next) {
      setColumnsPanel(false)
      savePref('columnas', '0')
      setSplitSheetIds([])
    }
    // Opening it on a device its saved scope leaves out shows it everywhere again.
    const openOn = gridConfig.chart[gridKey]?.openOn
    void persistPanels({ open: next, ...(next ? { splits: [], ...(scopeIncludes(openOn, device) ? {} : { openOn: 'all' as const }) } : {}) })
  }

  // Split view: up to two other sheets of the document beside this one, each in
  // its own right sidebar, in the order they were checked.
  const [splitSheetIds, setSplitSheetIds] = useState<string[]>([])
  const [splitMenu, setSplitMenu] = useState<{ left: number; top: number } | null>(null)
  const readSplitWidth = (suffix: string) => {
    const saved = readPref(suffix)
    return saved ? Math.max(160, Math.min(1400, Number(saved))) : 520
  }
  const [splitPanelWidths, setSplitPanelWidths] = useState<[number, number]>(() => [readSplitWidth('dividir-ancho'), readSplitWidth('dividir-ancho-2')])
  const resizeSplitPanel = (index: number, width: number) => {
    setSplitPanelWidths((current) => (index === 0 ? [width, current[1]] : [current[0], width]))
    savePref(index === 0 ? 'dividir-ancho' : 'dividir-ancho-2', String(width))
  }
  const splitSheets = splitSheetIds
    .map((id) => splitView?.sheets.find((sheet) => sheet.id === id))
    .filter((sheet): sheet is { id: string; name: string } => Boolean(sheet))
  // Each sidebar's title bar, for the column menu its grid renders there.
  const [splitMenuSlots, setSplitMenuSlots] = useState<Record<string, HTMLElement | null>>({})
  // One stable ref callback per sheet: a fresh one each render would detach and
  // reattach the slot on every render, and each of those sets state.
  const splitMenuSlotRefs = useRef(new Map<string, (el: HTMLElement | null) => void>())
  const splitMenuSlotRef = (id: string) => {
    let ref = splitMenuSlotRefs.current.get(id)
    if (!ref) {
      ref = (el) => setSplitMenuSlots((current) => (current[id] === el ? current : { ...current, [id]: el }))
      splitMenuSlotRefs.current.set(id, ref)
    }
    return ref
  }
  // The pane last clicked owns the toolbar row: 'main' for this grid, or a sidebar's sheet id.
  const [activePane, setActivePane] = useState<string>('main')
  const [splitToolbarSlot, setSplitToolbarSlot] = useState<HTMLElement | null>(null)
  const sidebarActive = activePane !== 'main' && splitSheets.some((sheet) => sheet.id === activePane)
  // Inside a sidebar this grid's toolbar goes to the page's toolbar row, and only while that sidebar is active.
  const splitPane = useSplitPane()
  const followPane = (event: React.PointerEvent) => {
    const target = event.target as Element
    const pane = target.closest('[data-split-pane]')?.getAttribute('data-split-pane')
    if (pane) setActivePane(pane)
    else if (containerRef.current?.contains(target)) setActivePane('main')
  }
  const toggleSplitSheet = (id: string) => {
    const next = splitSheetIds.includes(id)
      ? splitSheetIds.filter((other) => other !== id)
      : splitSheetIds.length < MAX_SPLIT_SHEETS ? [...splitSheetIds, id] : splitSheetIds
    if (next === splitSheetIds) return
    setSplitSheetIds(next)
    if (next.length > 0) {
      setChartPanel(false)
      setColumnsPanel(false); savePref('columnas', '0')
    }
    const splitsOn = gridConfig.chart[gridKey]?.splitsOn
    void persistPanels({ ...(next.length > 0 ? { open: false } : {}), splits: next, ...(next.length > 0 && !scopeIncludes(splitsOn, device) ? { splitsOn: 'all' as const } : {}) })
  }

  const [groupPanel, setGroupPanel] = useState(() => readPref('agrupar') === '1')
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false)
  const [groupPanelWidth, setGroupPanelWidth] = useState<number>(() => {
    const saved = readPref('agrupar-ancho')
    return saved ? Math.max(160, Math.min(600, Number(saved))) : 224
  })
  const groupPanelResizeDrag = useRef<{ x0: number; w0: number } | null>(null)

  useEffect(() => {
    savePref('agrupar', groupPanel ? '1' : '0')
  }, [groupPanel, savePref])

  useEffect(() => {
    if (!groupPanel || !columnsPanel) return
    setColumnsPanel(false)
    savePref('columnas', '0')
  }, [columnsPanel, groupPanel, savePref])

  const startGroupPanelResize = (event: React.MouseEvent) => {
    event.preventDefault()
    groupPanelResizeDrag.current = { x0: event.clientX, w0: groupPanelWidth }
    const move = (e: MouseEvent) => {
      if (!groupPanelResizeDrag.current) return
      const { x0, w0 } = groupPanelResizeDrag.current
      const newW = Math.max(160, Math.min(600, w0 + (e.clientX - x0)))
      setGroupPanelWidth(newW)
      savePref('agrupar-ancho', String(newW))
    }
    const release = () => {
      groupPanelResizeDrag.current = null
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', release)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', release)
  }

  const { t, locale } = useI18n()
  const effectiveDeleteAction = accionEliminar ?? deleteActionProp
  const deleteAction = useMemo(
    () => ({
      label: effectiveDeleteAction?.label ?? t('grid.moveToTrash'),
      title: effectiveDeleteAction?.title ?? effectiveDeleteAction?.titulo ?? t('grid.moveToTrashConfirmTitle'),
      message: effectiveDeleteAction?.message ?? effectiveDeleteAction?.mensaje ?? ((count: number) => t('grid.moveToTrashConfirmBody', { count })),
    }),
    [effectiveDeleteAction, t],
  )

  const gridConfig = useGridConfig()
  const summaryCardsContext = useOptionalSummaryCards()
  // Cards, totals row and group subtotals are per sheet and shared; the page's
  // provider follows whichever grid is mounted. Without a provider (a bare
  // Grid) subtotals are simply a local toggle.
  const setActiveSummaryGridKey = summaryCardsContext?.setActiveGridKey
  useEffect(() => {
    if (isEmbedded || !setActiveSummaryGridKey) return
    setActiveSummaryGridKey(gridKey)
    return () => setActiveSummaryGridKey(null)
  }, [gridKey, isEmbedded, setActiveSummaryGridKey])
  const setSubtotalsDefault = summaryCardsContext?.setGroupSubtotalsDefault
  const subtotalsDefaultKey = defaultGroupSubtotals ? `${defaultGroupSubtotals.visible}:${defaultGroupSubtotals.position}:${defaultGroupSubtotals.outline}` : ''
  useEffect(() => {
    if (!setSubtotalsDefault || !defaultGroupSubtotals) return
    setSubtotalsDefault(defaultGroupSubtotals)
    return () => setSubtotalsDefault(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSubtotalsDefault, subtotalsDefaultKey])
  const [localGroupSubtotals, setLocalGroupSubtotals] = useState(defaultGroupSubtotals?.visible ?? false)
  const showGroupSubtotals = summaryCardsContext ? summaryCardsContext.groupSubtotalsVisible : localGroupSubtotals
  const toggleGroupSubtotals = useCallback(() => {
    if (summaryCardsContext) summaryCardsContext.toggleGroupSubtotals()
    else setLocalGroupSubtotals((prev) => !prev)
  }, [summaryCardsContext])
  const [localSubtotalsPosition, setLocalSubtotalsPosition] = useState<'top' | 'bottom'>(defaultGroupSubtotals?.position ?? 'top')
  const groupSubtotalsPosition = summaryCardsContext ? summaryCardsContext.groupSubtotalsPosition : localSubtotalsPosition
  const setGroupSubtotalsPosition = summaryCardsContext ? summaryCardsContext.setGroupSubtotalsPosition : setLocalSubtotalsPosition
  const [localGroupOutline, setLocalGroupOutline] = useState(defaultGroupSubtotals?.outline ?? false)
  const groupOutline = summaryCardsContext ? summaryCardsContext.groupOutline : localGroupOutline
  const toggleGroupOutline = useCallback(() => {
    if (summaryCardsContext) summaryCardsContext.toggleGroupOutline()
    else setLocalGroupOutline((prev) => !prev)
  }, [summaryCardsContext])
  // An outline has no header band over an open group, so its subtotals can only close it.
  const subtotalsAtBottom = showGroupSubtotals && (groupSubtotalsPosition === 'bottom' || groupOutline)
  const [localGroupBlankRows, setLocalGroupBlankRows] = useState(false)
  const groupBlankRows = summaryCardsContext ? summaryCardsContext.groupBlankRows : localGroupBlankRows
  const toggleGroupBlankRows = useCallback(() => {
    if (summaryCardsContext) summaryCardsContext.toggleGroupBlankRows()
    else setLocalGroupBlankRows((prev) => !prev)
  }, [summaryCardsContext])
  const [localGroupMergeSingle, setLocalGroupMergeSingle] = useState(false)
  const groupMergeSingle = summaryCardsContext ? summaryCardsContext.groupMergeSingle : localGroupMergeSingle
  const toggleGroupMergeSingle = useCallback(() => {
    if (summaryCardsContext) summaryCardsContext.toggleGroupMergeSingle()
    else setLocalGroupMergeSingle((prev) => !prev)
  }, [summaryCardsContext])
  const {
    formats,
    headers: gridHeadersConfig,
    columnOrder: columnOrderConfig,
    rowOrder: rowOrderConfig,
    sort: sortConfig,
    grouping: groupingConfig,
    widths: widthsConfig,
    hiddenColumns: hiddenColumnsConfig,
    frozenColumns: frozenColumnsConfig,
    columnDevices: columnDevicesConfig,
    alignment: alignmentConfig,
    formulas: formulasConfig,
    dataFormats: dataFormatsConfig,
    decimalSeparator: decimalSeparatorConfig,
    changeTimeColumns: changeTimeColumnsConfig,
    headerNotes: headerNotesConfig,
    hiddenAggregates: hiddenAggregatesConfig,
    reload: reloadConfig,
  } = gridConfig

  // Every shared setting this grid saves — sort, grouping, headers, formats,
  // formulas, column order, widths, visibility, charts — goes through here, so
  // each one lands on the undo stack like a cell edit, as in Excel and Sheets.
  const gridConfigRef = useRef(gridConfig)
  gridConfigRef.current = gridConfig
  // Housekeeping a change does on its own (pinning the order on screen) passes
  // record = false: it is part of that change, not a step of its own.
  const saveSettingRef = useRef<(key: GridSettingKey, value: unknown, record?: boolean) => Promise<Error | null>>(null!)
  saveSettingRef.current = async (key, value, record = true) => {
    const config = gridConfigRef.current
    const before = config.read?.(key)
    const error = await config.save(key, value)
    if (error || !record || !config.read || JSON.stringify(before) === JSON.stringify(value)) return error
    const replay = async (from: unknown, to: unknown) => {
      const latest = gridConfigRef.current
      // A setting first saved by this change had nothing stored before it: undo leaves it empty.
      const failed = await latest.save(key, replaySettingChange(latest.read?.(key), from, to) ?? {})
      if (failed) {
        console.error('Grid: setting change could not be replayed', failed)
        setHistoryError(t('grid.historySaveSettingError'))
        return false
      }
      latest.reload()
      return true
    }
    addHistoryTaskRef.current({
      label: t(SETTING_HISTORY_LABELS[key] ?? 'grid.historySetting.other'),
      undo: () => replay(value, before),
      redo: () => replay(before, value),
    })
    return null
  }


  // Which layout this page is showing: columns, freezes and panels may each be set per device.
  const device = useDevice()
  const isFormulaEditorRole = gridConfig.canEditFormulas
  const canEditSharedConfig = isFormulaEditorRole && !isEmbedded
  const canEditFormats = gridConfig.canEditFormats && !isEmbedded
  // The pencil under a smartchip / autofill list: opens that column's options sidebar.
  const editOptionsOf = (column: { field?: string }) => {
    const field = column.field
    if (!canEditFormats || !field) return undefined
    return () => { setEditing(null); setFormatTarget(field); setOptionsDialog(true) }
  }
  // A personal view (the Numtable library) lets a reader hide columns and pick a
  // chart; the provider keeps both in their own browser, never in shared settings.
  const personalView = gridConfig.personalView === true && !isEmbedded
  const canToggleColumns = canEditFormats || personalView
  const canUseFormulas = isFormulaEditorRole
  const canChooseGrouping = canEditFormats && groupable
  const [cellColors, setCellColors] = useState<Map<string, string>>(new Map())
  const [fillScope, setFillScope] = useState<'cell' | 'column' | 'header'>('cell')
  const columnColorsConfig = useMemo(() => normalizeColumnColors(gridConfig.read?.(GRID_SETTING_KEYS.columnColors)), [gridConfig])
  const columnColors = useMemo(() => columnColorsConfig[gridKey] ?? {}, [columnColorsConfig, gridKey])
  const headerColorsConfig = useMemo(() => normalizeColumnColors(gridConfig.read?.(GRID_SETTING_KEYS.headerColors)), [gridConfig])
  const headerColors = useMemo(() => headerColorsConfig[gridKey] ?? {}, [headerColorsConfig, gridKey])
  const cellColorsRef = useRef(cellColors)
  cellColorsRef.current = cellColors
  const [cellColorLoading, setCellColorLoading] = useState(false)
  const [cellColorBusy, setCellColorBusy] = useState(false)
  const [cellColorError, setCellColorError] = useState<string | null>(null)

  const configuredSummaryCards = useMemo<readonly SummaryCardConfig[]>(() => {
    return gridConfig.summaryCards[gridKey] ?? []
  }, [gridConfig.summaryCards, gridKey])

  const [rowHeightsDraft, setRowHeightsDraft] = useState<Record<string, number>>({})
  const rowResizeDrag = useRef<{ rowKey: string; y0: number; h0: number } | null>(null)
  const [rowDropIndicator, setRowDropIndicator] = useState<{ key: string; position: 'before' | 'after' } | null>(null)
  // Why the row under the cursor won't take the drop, shown beside the browser's no-drop cursor.
  const [rowDropRefusal, setRowDropRefusal] = useState<{ source: string; x: number; y: number; message: string } | null>(null)
  const [columnDropIndicator, setColumnDropIndicator] = useState<{ index: number; position: 'before' | 'after' } | null>(null)
  const [summaryDropdownOpen, setSummaryDropdownOpen] = useState(false)
  const summaryMenuRef = useRef<HTMLDivElement>(null)
  const [isTotalsDialogOpen, setIsTotalsDialogOpen] = useState(false)
  const [editingTotalsColumn, setEditingTotalsColumn] = useState<{ column: GridColumn<T>; card?: SummaryCardConfig | undefined } | null>(null)
  const [totalsContextMenu, setTotalsContextMenu] = useState<{ x: number; y: number; column: GridColumn<T>; card?: SummaryCardConfig | undefined; displayVal: string } | null>(null)

  /** Fields whose subtotal / totals-row value is hidden, shared like every other display choice. */
  const hiddenSubtotalFields = useMemo(() => new Set(hiddenAggregatesConfig[gridKey]?.subtotals ?? []), [hiddenAggregatesConfig, gridKey])
  const hiddenTotalFields = useMemo(() => new Set(hiddenAggregatesConfig[gridKey]?.totals ?? []), [hiddenAggregatesConfig, gridKey])
  const toggleAggregateHidden = useCallback(async (which: 'subtotals' | 'totals', field: string) => {
    const current = hiddenAggregatesConfig[gridKey] ?? {}
    const list = current[which] ?? []
    const nextList = list.includes(field) ? list.filter((f) => f !== field) : [...list, field]
    markSaving()
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.hiddenAggregates, { ...hiddenAggregatesConfig, [gridKey]: { ...current, [which]: nextList } })
    if (error) { markFailed(); return }
    await reloadConfig()
    markSaved()
  }, [hiddenAggregatesConfig, gridKey, gridConfig, markSaving, markFailed, markSaved, reloadConfig])
  const totalsLabel = hiddenAggregatesConfig[gridKey]?.totalsLabel ?? t('grid.grandTotal')
  const [editingTotalsLabel, setEditingTotalsLabel] = useState(false)
  const saveTotalsLabel = useCallback(async (label: string) => {
    setEditingTotalsLabel(false)
    const current = hiddenAggregatesConfig[gridKey] ?? {}
    const next = label.trim()
    if (next === (current.totalsLabel ?? t('grid.grandTotal'))) return
    markSaving()
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.hiddenAggregates, { ...hiddenAggregatesConfig, [gridKey]: { ...current, totalsLabel: next } })
    if (error) { markFailed(); return }
    await reloadConfig()
    markSaved()
  }, [hiddenAggregatesConfig, gridKey, gridConfig, markSaving, markFailed, markSaved, reloadConfig, t])

  const startRowResize = (rowKey: string, event: React.MouseEvent | React.PointerEvent) => {
    if (!canEditFormats) return
    event.preventDefault()
    event.stopPropagation()
    const targetRow = (event.currentTarget as HTMLElement).closest('tr')
    const h0 = targetRow ? targetRow.getBoundingClientRect().height / (zoom / 100 / textScale) : 32
    rowResizeDrag.current = { rowKey, y0: event.clientY, h0 }

    const move = (e: MouseEvent | PointerEvent) => {
      if (!rowResizeDrag.current) return
      const delta = (e.clientY - rowResizeDrag.current.y0) / (zoom / 100 / textScale)
      // Rows shrink down to one line of text plus the cell's padding, never further.
      const nextH = Math.max(Math.ceil(16 + renderedCellFontSize() * LINE_HEIGHT), Math.min(300, Math.round(rowResizeDrag.current.h0 + delta)))
      const keysToResize = selected.has(rowResizeDrag.current.rowKey) && selected.size > 1
        ? Array.from(selected)
        : [rowResizeDrag.current.rowKey]
      setRowHeightsDraft((prev) => {
        const copy = { ...prev }
        keysToResize.forEach((k) => { copy[k] = nextH })
        return copy
      })
    }

    const release = () => {
      rowResizeDrag.current = null
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', release)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', release)
      window.removeEventListener('pointercancel', release)
    }

    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', release)
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', release)
    window.addEventListener('pointercancel', release)
  }

  const resetRowHeight = (rowKey: string) => {
    const keysToReset = selected.has(rowKey) && selected.size > 1 ? Array.from(selected) : [rowKey]
    setRowHeightsDraft((prev) => {
      const copy = { ...prev }
      keysToReset.forEach((k) => delete copy[k])
      return copy
    })
  }

  const handleSaveTotalsCard = useCallback((updatedCard: SummaryCardConfig) => {
    const existing = gridConfig.summaryCards[gridKey] ?? []
    const index = existing.findIndex((c) => c.id === updatedCard.id || (updatedCard.columnId && c.columnId === updatedCard.columnId))
    let next: SummaryCardConfig[]
    if (index >= 0) {
      next = [...existing]
      next[index] = updatedCard
    } else {
      next = [...existing, updatedCard]
    }
    const nextConfig = {
      ...gridConfig.summaryCards,
      [gridKey]: next,
    }
    void saveSettingRef.current(GRID_SETTING_KEYS.summaryCards, nextConfig)
  }, [gridKey, gridConfig])

  const handleDeleteTotalsCard = useCallback((cardId: string) => {
    const existing = gridConfig.summaryCards[gridKey] ?? []
    const next = existing.filter((c) => c.id !== cardId)
    const nextConfig = {
      ...gridConfig.summaryCards,
      [gridKey]: next,
    }
    void saveSettingRef.current(GRID_SETTING_KEYS.summaryCards, nextConfig)
  }, [gridKey, gridConfig])

  useEffect(() => {
    if (isEmbedded || !gridConfig.orgId) {
      setCellColors(new Map())
      setCellColorLoading(false)
      setCellColorError(null)
      return
    }

    let active = true
    setCellColorLoading(true)
    setCellColorError(null)
    void loadCellColors(gridConfig.orgId, gridKey, gridConfig.documentId)
      .then((colors) => {
        if (active) {
          setCellColors(colors)
          setCellColorLoading(false)
        }
      })
      .catch(() => {
        if (active) {
          setCellColors(new Map())
          setCellColorLoading(false)
          setCellColorError(t('grid.cellColorLoadError'))
        }
      })

    return () => { active = false }
  }, [isEmbedded, gridConfig.documentId, gridConfig.orgId, gridKey, t])

  // When each cell's value last changed, for the "last change time" companion columns.
  const [changeTimes, setChangeTimes] = useState<Map<string, Date>>(new Map())
  // Last value the database holds per cell; null until loaded, so nothing is reported early.
  const reportedValuesRef = useRef<Map<string, string> | null>(null)
  const [changesLoaded, setChangesLoaded] = useState(0)
  const changeTimeFields = changeTimeColumnsConfig[gridKey] ?? EMPTY_CHANGE_TIME_FIELDS
  const tracksChanges = !isEmbedded && Boolean(gridConfig.orgId)
  useEffect(() => {
    reportedValuesRef.current = null
    if (!tracksChanges || changeTimeFields.length === 0 || !gridConfig.orgId) {
      setChangeTimes(new Map())
      return
    }
    let active = true
    void loadCellChanges(gridConfig.orgId, gridKey, gridConfig.documentId)
      .then((changes) => {
        if (!active) return
        reportedValuesRef.current = changes.values
        setChangeTimes(changes.times)
        setChangesLoaded((n) => n + 1)
      })
      .catch((err) => { console.error('Grid: last change times could not be loaded', err) })
    return () => { active = false }
  }, [tracksChanges, changeTimeFields.length, gridConfig.documentId, gridConfig.orgId, gridKey])

  const headerOverrides = gridHeadersConfig[gridKey] ?? EMPTY_OVERRIDES

  const [dynamicColumns, setDynamicColumns] = useState<GridColumn<T>[]>([])
  const baseColumns = useMemo(
    () => (dynamicColumns.length > 0 ? [...columnsProp, ...dynamicColumns] : columnsProp),
    [columnsProp, dynamicColumns],
  )

  // ── Formula-calculated columns ─────────────────────────────────────────────
  const savedFormulaConfig = formulasConfig[gridKey] ?? EMPTY_FORMULA_CONFIG
  const externalFormulas = useMemo(() => new Set(externallyManagedFormulaFields ?? []), [externallyManagedFormulaFields])
  // Spread first, then narrow: everything the saved configuration carries reaches the grid by
  // default, and only what this merge genuinely has to change is named. Listing the fields one by
  // one instead silently dropped each newly added one — `overrideTargets` was saved, never read
  // back, and the goal-seek selector reset itself on every render because of it.
  const formulaConfig = useMemo(
    () => ({
      ...savedFormulaConfig,
      overrides: {
        ...storedDefaults(formulaDefaults),
        ...Object.fromEntries(Object.entries(savedFormulaConfig.overrides).filter(([field]) => !externalFormulas.has(field))),
      },
      syntaxVersion: savedFormulaConfig.syntaxVersion ?? STORED_VERSION,
      allowCellOverrides: savedFormulaConfig.allowCellOverrides ?? {},
      overrideTargets: savedFormulaConfig.overrideTargets ?? {},
    }),
    [savedFormulaConfig, formulaDefaults, externalFormulas],
  )
  // FXRATE shows #N/A until the exchange-rate history loads: recompute every formula then.
  const [fxHistoryLoads, setFxHistoryLoads] = useState(0)
  useEffect(() => onFxHistoryLoaded(() => setFxHistoryLoads((n) => n + 1)), [])
  const formulaEngine = useMemo(
    () => createFormulaEngine(baseColumns, formulaConfig, headerOverrides, locale),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fxHistoryLoads only forces the recompute
    [baseColumns, formulaConfig, headerOverrides, locale, fxHistoryLoads],
  )
  const canEditOverrides = gridConfig.canEditFormulas
  const [overridesMap, setOverridesMap] = useState<Map<string, CellOverrideState>>(new Map())
  // Exchange-rate columns tell their own values from overrides by markers kept here,
  // so they wait for it before rewriting anything.
  const [overridesLoaded, setOverridesLoaded] = useState(false)
  const [workbookOverrides, setWorkbookOverrides] = useState<Map<string, Map<string, CellOverrideState>>>(new Map())
  const [workbookOverridesReady, setWorkbookOverridesReady] = useState(true)
  const fetchOverrides = useCallback(async () => {
    if (isEmbedded) return
    setOverridesMap(await loadOverrides(gridConfig.orgId, gridKey, gridConfig.documentId))
    if (gridConfig.orgId) setOverridesLoaded(true)
  }, [isEmbedded, gridKey, gridConfig.documentId, gridConfig.orgId])
  useEffect(() => { void fetchOverrides() }, [fetchOverrides])
  useEffect(() => {
    let active = true
    if (isEmbedded || !formulaWorkbook) {
      setWorkbookOverrides(new Map())
      setWorkbookOverridesReady(true)
      return () => { active = false }
    }
    setWorkbookOverridesReady(false)
    void Promise.all(formulaWorkbook.sheets
      .filter((sheet) => sheet.id !== formulaWorkbook.currentSheetId)
      .map(async (sheet) => [sheet.id, await loadOverrides(gridConfig.orgId, `sheet:${sheet.id}`, gridConfig.documentId)] as const))
      .then((entries) => {
        if (!active) return
        setWorkbookOverrides(new Map(entries))
        setWorkbookOverridesReady(true)
      })
    return () => { active = false }
  }, [formulaWorkbook, gridConfig.documentId, gridConfig.orgId, isEmbedded])
  const [overrideMenu, setOverrideMenu] = useState<{ rowKey: string; field: string; top: number; left: number } | null>(null)
  const [overrideError, setOverrideError] = useState<string | null>(null)

  const cellFormulaSheetId = formulaWorkbook?.currentSheetId ?? gridKey
  const formulaLogicalRows = useMemo(() => {
    const keyed = internalRows.map((row, index) => ({ row, key: getRowKey(row, index) }))
    const savedOrder = rowOrderConfig[gridKey]
    if (!savedOrder || savedOrder.length === 0) return internalRows
    const byKey = new Map(keyed.map((entry) => [entry.key, entry.row]))
    return resolveRowOrder(keyed.map((entry) => entry.key), savedOrder)
      .map((key) => byKey.get(key))
      .filter((row): row is T => row !== undefined)
  }, [getRowKey, gridKey, internalRows, rowOrderConfig])
  const formulaRowIndexMap = useMemo(() => {
    const map = new Map<string, number>()
    formulaLogicalRows.forEach((row, idx) => {
      const key = getRowKey(row, internalRows.indexOf(row))
      if (!key.startsWith('idx:')) {
        map.set(key, idx + 1)
      }
    })
    return map
  }, [formulaLogicalRows, getRowKey, internalRows])
  const cellFormulaRuntime = useMemo(() => {
    const unorderedFields = formulaEngine.columns.flatMap((column) => column.field ? [column.field] : [])
    const currentFields = resolveColumnOrder(columnOrderConfig[gridKey], unorderedFields).map((index) => unorderedFields[index]!)
    const currentRows = formulaLogicalRows as readonly Record<string, unknown>[]
    const formulaForField = (field: string): StoredFormula | undefined => {
      const id = formulaIdOf(field)
      return id === null
        ? formulaConfig.overrides[field]
        : formulaConfig.columns.find((column) => column.id === id)?.formula
    }
    const currentSheet = {
      id: cellFormulaSheetId,
      rows: currentRows,
      fields: currentFields,
      rowId: (row: Record<string, unknown>) => {
        const typedRow = row as T
        const rowIndex = internalRows.indexOf(typedRow)
        const key = getRowKey(typedRow, rowIndex)
        return key.startsWith('idx:') ? undefined : key
      },
      rawValue: (row: Record<string, unknown>, field: string) => {
        const typedRow = row as T
        const column = baseColumns.find((candidate) => candidate.field === field)
        return column ? readValue(column, typedRow) : row[field]
      },
      formulaForField,
      cycleInput: (row: Record<string, unknown>, field: string) => formulaEngine.isCycleInput(field, row as T),
      overrideFor: (rowId: string, field: string) => overridesMap.get(overrideKey(field, rowId)),
      allowCellOverrides: (field: string) => !isFormulaField(field, formulaConfig) || cellOverridesAllowed(formulaConfig, field),
    }
    const externalSheets = (workbookOverridesReady ? formulaWorkbook?.sheets ?? [] : [])
      .filter((sheet) => sheet.id !== cellFormulaSheetId)
      .map((sheet) => {
        const externalConfig = formulasConfig[`sheet:${sheet.id}`] ?? EMPTY_FORMULA_CONFIG
        const unorderedExternalFields = [
          ...sheet.columns.map((column) => column.field),
          ...externalConfig.columns.map((column) => formulaField(column.id)),
        ]
        const fields = resolveColumnOrder(columnOrderConfig[`sheet:${sheet.id}`], unorderedExternalFields)
          .map((index) => unorderedExternalFields[index]!)
        const externalRowKey = (row: Record<string, unknown>) => typeof row.id === 'string' ? `id:${row.id}` : undefined
        const savedRowOrder = rowOrderConfig[`sheet:${sheet.id}`]
        const externalRows = savedRowOrder && savedRowOrder.length > 0
          ? (() => {
            const byKey = new Map(sheet.rows.flatMap((row) => {
              const key = externalRowKey(row)
              return key ? [[key, row] as const] : []
            }))
            return resolveRowOrder([...byKey.keys()], savedRowOrder)
              .map((key) => byKey.get(key))
              .filter((row): row is Record<string, unknown> => row !== undefined)
          })()
          : sheet.rows
        return {
          id: sheet.id,
          rows: externalRows,
          fields,
          rowId: externalRowKey,
          rawValue: (row: Record<string, unknown>, field: string) => row[field],
          formulaForField: (field: string) => {
            const id = formulaIdOf(field)
            return id === null
              ? externalConfig.overrides[field]
              : externalConfig.columns.find((column) => column.id === id)?.formula
          },
          overrideFor: (rowId: string, field: string) => workbookOverrides.get(sheet.id)?.get(overrideKey(field, rowId)),
          allowCellOverrides: (field: string) => !isFormulaField(field, externalConfig) || cellOverridesAllowed(externalConfig, field),
        }
      })
    return createCellFormulaRuntime<Record<string, unknown>>({ sheets: [currentSheet, ...externalSheets] })
  }, [baseColumns, cellFormulaSheetId, columnOrderConfig, formulaConfig, formulaEngine, formulaLogicalRows, formulaWorkbook, formulasConfig, getRowKey, gridKey, internalRows, overridesMap, rowOrderConfig, workbookOverrides, workbookOverridesReady])

  const columns = useMemo(() => {
    const visible = formulaEngine.columns.filter((column) => !column.formulaOnly)
    const withOverrides = isEmbedded ? visible : visible.map((column) => {
      if (!column.field) return column
      const field = column.field
      const formulaGetter = column.valueGetter
      return {
        ...column,
        ...(isFormulaField(field, formulaConfig)
          ? { editable: canEditOverrides && cellOverridesAllowed(formulaConfig, field) }
          : {}),
        valueGetter: (params: { data?: T }) => {
          if (!params.data) return formulaGetter ? formulaGetter(params) : null
          const rowIndex = internalRows.indexOf(params.data)
          const rowId = getRowKey(params.data, rowIndex)
          const override = overridesMap.get(overrideKey(field, rowId))
          if (!isFormulaField(field, formulaConfig) && override?.kind !== 'formula') {
            return formulaGetter ? formulaGetter(params) : (params.data as Record<string, unknown>)[field]
          }
          const result = cellFormulaRuntime.valueOf({ sheetId: cellFormulaSheetId, rowId, field })
          if (result.kind === 'number') return result.n
          if (result.kind === 'text') return result.s
          if (result.kind === 'empty') return null
          if (result.kind === 'error') return localizeErrorCode(result.code, locale)
          return localizeErrorCode('#¡VALOR!', locale)
        },
      }
    })
    const configured = dataFormatsConfig[gridKey] ?? {}
    const formatted = withOverrides.map((column) => {
      let dataFormat = column.field
        ? (configured[column.field] ?? column.dataFormat ?? (
          column.cellType === 'date'
            ? ({ type: 'date' as const, formatKey: 'date' as const })
            : undefined
        ))
        : column.dataFormat
      if (!dataFormat) return column
      if (dataFormat.headerOnly) return { ...column, headerFormat: dataFormat }
      const exchangeRate = column.field ? resolveGridExchangeRate(configured[column.field], column.dataFormat) : undefined
      if (exchangeRate) dataFormat = { ...dataFormat, type: 'number', exchangeRate }
      else if (dataFormat.exchangeRate) { const { exchangeRate: _off, ...rest } = dataFormat; dataFormat = rest }

      let rollupGetter = undefined
      if ((dataFormat.type === 'rollup' || dataFormat.type === 'lookup') && dataFormat.rollup && formulaWorkbook) {
        const targetSheet = formulaWorkbook.sheets.find((s) => s.id === dataFormat.rollup!.targetTab)
        if (targetSheet) {
          rollupGetter = createRollupValueGetter<T>(dataFormat.rollup, targetSheet.rows)
        }
      }

      const resolvedCellType: CellType =
        dataFormat.type === 'rollup'
          ? (['SUM', 'AVG', 'COUNT'].includes(dataFormat.rollup?.agg ?? '') ? 'number' : 'text')
          : dataFormat.type === 'lookup' || dataFormat.type === 'auto'
            ? 'text'
            : dataFormat.type

      return {
        ...column,
        dataFormat,
        headerFormat: dataFormat,
        cellType: resolvedCellType,
        ...(dataFormat.type === 'rollup' || dataFormat.type === 'lookup' ? { editable: false } : {}),
        // An exchange-rate column with overwriting off only ever holds the rate.
        ...(dataFormat.exchangeRate?.allowOverwrite === false ? { editable: false } : {}),
        ...(rollupGetter ? { valueGetter: rollupGetter } : {}),
        ...(dataFormat.align !== undefined ? { align: dataFormat.align } : {}),
        // Unset follows the settings' rule for the type; a by-cell column leaves it to each cell.
        ...(dataFormat.wrapMode !== undefined
          ? { wrapMode: dataFormat.wrapMode }
          : dataFormat.type !== 'auto' && alignmentConfig[resolvedCellType]?.wrapMode
            ? { wrapMode: alignmentConfig[resolvedCellType].wrapMode }
            : {}),
        // Smartchip and attachments render as components further down, which a
        // string formatter would shadow.
        ...(dataFormat.type === 'smartchip' || dataFormat.type === 'attachment' || dataFormat.type === 'checkbox'
          ? {}
          : { valueFormatter: ({ value, data }: CellParams<T>) => formatGridData(
            value,
            dataFormat.cells && data ? gridCellDataFormat(dataFormat, getRowKey(data, internalRows.indexOf(data))) : dataFormat,
            formats,
          ) }),
      }
    })
    if (isEmbedded || changeTimeFields.length === 0) return formatted
    // "Last change time" companions go at the end of the array so no existing
    // column changes index; `columnOrder` places each right after its source.
    const defaultChangeTimeFormat = { type: 'date' as const, formatKey: 'dateTime' as const }
    const companions: GridColumn<T>[] = changeTimeFields.flatMap((sourceField) => {
      const source = formatted.find((column) => column.field === sourceField)
      if (!source) return []
      // A date format chosen for the companion in the format bar wins, like on any column.
      const chosen = configured[changeTimeField(sourceField)]
      const changeTimeFormat = chosen?.type === 'date' ? chosen : defaultChangeTimeFormat
      return [{
        field: changeTimeField(sourceField),
        headerName: t('grid.menu.changeTimeHeader', { header: headerOverrides[sourceField] ?? source.headerName }),
        editable: false,
        cellType: 'date' as CellType,
        dataFormat: changeTimeFormat,
        ...(chosen?.align !== undefined ? { align: chosen.align } : {}),
        ...(chosen?.wrapMode !== undefined ? { wrapMode: chosen.wrapMode } : {}),
        valueGetter: (params: { data?: T }) => {
          if (!params.data) return null
          // An empty cell has no time to show, whatever it held before.
          const value = (params.data as Record<string, unknown>)[sourceField]
          if (value === null || value === '') return null
          const known = source.changedAt?.(params.data)
          if (known !== undefined) return known
          const rowKey = getRowKey(params.data, internalRows.indexOf(params.data))
          return changeTimes.get(cellChangeKey(sourceField, rowKey)) ?? null
        },
        valueFormatter: ({ value }: CellParams<T>) => formatGridData(value, changeTimeFormat, formats),
      }]
    })
    return [...formatted, ...companions]
  }, [formulaEngine, overridesMap, isEmbedded, formulaConfig, canEditOverrides, dataFormatsConfig, gridKey, formats, getRowKey, internalRows, cellFormulaRuntime, cellFormulaSheetId, locale, formulaWorkbook, changeTimeFields, changeTimes, headerOverrides, alignmentConfig, t])

  // field → sign rule, read by `applyCellChanges` without re-creating it. A
  // column's own default (a template's preset, like Investments's Quantity)
  // applies until someone saves a format for that column; then the saved one
  // decides, rule or no rule. `private.apply_sign_rules` reads the same way.
  const signRules = useMemo(() => {
    const stored = dataFormatsConfig[gridKey] ?? {}
    const out: Record<string, GridSignRule> = {}
    for (const column of columns) {
      const sign = column.field && !(column.field in stored) ? column.dataFormat?.sign : undefined
      if (column.field && sign) out[column.field] = sign
    }
    for (const [field, format] of Object.entries(stored)) {
      if (format.sign) out[field] = format.sign
    }
    return out
  }, [columns, dataFormatsConfig, gridKey])
  const signRulesRef = useRef(signRules)
  signRulesRef.current = signRules

  // Exchange-rate columns: field → setup, from the resolved column formats.
  const exchangeRateRules = useMemo(() => {
    const out: Record<string, NonNullable<GridDataFormat['exchangeRate']>> = {}
    for (const column of columns) {
      if (column.field && column.dataFormat?.exchangeRate) out[column.field] = column.dataFormat.exchangeRate
    }
    return out
  }, [columns])
  const hasExchangeRates = Object.keys(exchangeRateRules).length > 0
  const [fxHistory, setFxHistory] = useState<FxHistory | null>(null)
  useEffect(() => {
    if (!hasExchangeRates || isEmbedded) return
    let active = true
    loadFxHistory()
      .then((history) => { if (active) setFxHistory(history) })
      .catch((error: unknown) => console.error('Grid: exchange-rate history unavailable', error))
    return () => { active = false }
  }, [hasExchangeRates, isEmbedded])
  // The last rate the rule wrote into a cell; a different value there is an override.
  const fxAutoOf = useCallback((rowKey: string) => (field: string) => {
    const marker = overridesMap.get(overrideKey(exchangeRateAutoField(field), rowKey))
    return marker?.kind === 'value' && typeof marker.value === 'number' ? marker.value : undefined
  }, [overridesMap])
  const exchangeRateRef = useRef({ rules: exchangeRateRules, history: fxHistory, autoOf: fxAutoOf, ready: overridesLoaded })
  exchangeRateRef.current = { rules: exchangeRateRules, history: fxHistory, autoOf: fxAutoOf, ready: overridesLoaded }
  /** Records the rates the rule just wrote, so later passes know those cells are still linked. */
  const recordAutoRates = useCallback((written: readonly { rowKey: string; field: string; value: number }[]) => {
    const orgId = gridConfig.orgId
    if (!orgId || isEmbedded || written.length === 0) return
    setOverridesMap((previous) => {
      const next = new Map(previous)
      for (const cell of written) next.set(overrideKey(exchangeRateAutoField(cell.field), cell.rowKey), { kind: 'value', value: cell.value })
      return next
    })
    void Promise.all(written.map((cell) => saveOverrideValue(orgId, gridKey, cell.rowKey, exchangeRateAutoField(cell.field), cell.value, gridConfig.documentId)))
      .then((errors) => { const error = errors.find(Boolean); if (error) console.error('Grid: exchange-rate marker could not be saved', error) })
  }, [gridConfig.documentId, gridConfig.orgId, gridKey, isEmbedded])

  // Report what each tracked cell shows — typed or computed — so the database
  // can stamp the ones whose value moved. Debounced: a paste or a price refresh
  // settles into one call.
  useEffect(() => {
    if (!tracksChanges || changeTimeFields.length === 0 || !gridConfig.orgId) return
    const orgId = gridConfig.orgId
    const timer = window.setTimeout(() => {
      const reported = reportedValuesRef.current
      if (!reported) return
      const pending: CellValueReport[] = []
      for (const field of changeTimeFields) {
        const column = columns.find((c) => c.field === field)
        if (!column) continue
        internalRows.forEach((row, index) => {
          const raw = column.valueGetter ? column.valueGetter({ data: row }) : (row as Record<string, unknown>)[field]
          const value: Json = raw === undefined || raw === '' ? null
            : raw instanceof Date ? raw.toISOString()
              : typeof raw === 'number' || typeof raw === 'string' || typeof raw === 'boolean' || raw === null ? raw
                : String(raw)
          const key = cellChangeKey(field, getRowKey(row, index))
          const serialized = serializeCellValue(value)
          if (reported.get(key) === serialized) return
          reported.set(key, serialized)
          pending.push({ rowKey: getRowKey(row, index), field, value })
        })
      }
      if (pending.length === 0) return
      void recordCellValues(orgId, gridKey, pending, gridConfig.documentId)
        .then((stamped) => {
          if (stamped.size === 0) return
          setChangeTimes((current) => new Map([...current, ...stamped]))
        })
        .catch((err) => {
          // Unrecorded cells are reported again next time, not taken as stored.
          for (const cell of pending) reported.delete(cellChangeKey(cell.field, cell.rowKey))
          console.error('Grid: cell values could not be recorded', err)
        })
    }, 1000)
    return () => window.clearTimeout(timer)
  }, [tracksChanges, changeTimeFields, columns, internalRows, getRowKey, gridConfig.orgId, gridConfig.documentId, gridKey, changesLoaded])

  const confirmOverride = useCallback(async (row: T, field: string, text: string) => {
    if (!gridConfig.orgId) return
    const rowIndex = internalRows.indexOf(row)
    const key = getRowKey(row, rowIndex)
    const normalized = normalizeText(text)
    const empty = normalized === ''
    const number = parseGridNumber(normalized, formats, columns.find((c) => c.field === field)?.dataFormat)
    markSaving()
    const error = empty
      ? await blankOverride(gridConfig.orgId, gridKey, key, field, gridConfig.documentId)
      : await saveOverrideValue(gridConfig.orgId, gridKey, key, field, number ?? normalized, gridConfig.documentId)
    if (error) {
      markFailed()
      console.error('Grid: manual value could not be saved', error)
      setOverrideError(t('grid.overrideSaveError'))
      return
    }
    setOverrideError(null)
    await fetchOverrides()
    markSaved()
  }, [gridKey, fetchOverrides, gridConfig, columns, formats, getRowKey, internalRows, markSaving, markSaved, markFailed, t])

  const removeOverride = useCallback(async (rowKeyVal: string, field: string) => {
    setOverrideMenu(null)
    markSaving()
    const error = await clearOverride(gridConfig.orgId ?? '', gridKey, rowKeyVal, field, gridConfig.documentId)
    if (error) {
      markFailed()
      console.error('Grid: manual value could not be removed', error)
      setOverrideError(t('grid.overrideRemoveError'))
      return
    }
    setOverrideError(null)
    await fetchOverrides()
    markSaved()
  }, [gridKey, fetchOverrides, gridConfig.documentId, gridConfig.orgId, markSaving, markSaved, markFailed])

  const clearOverrideCell = useCallback(async (rowKeyVal: string, field: string) => {
    if (!gridConfig.orgId) return
    setOverrideMenu(null)
    markSaving()
    const error = await blankOverride(gridConfig.orgId, gridKey, rowKeyVal, field, gridConfig.documentId)
    if (error) {
      markFailed()
      console.error('Grid: cell could not be cleared', error)
      setOverrideError(t('grid.cellClearError'))
      return
    }
    setOverrideError(null)
    await fetchOverrides()
    markSaved()
  }, [gridKey, fetchOverrides, gridConfig, markSaving, markSaved, markFailed])

  const formulaScope = useMemo(
    () => gridScope(baseColumns, formulaConfig, headerOverrides),
    [baseColumns, formulaConfig, headerOverrides],
  )
  const formulaLocale = useMemo(() => localeFor(decimalSeparatorConfig), [decimalSeparatorConfig])
  const formulaFunctionMetadata = useMemo(() => localizedFunctionMetadata(t), [t])
  const cellFormulaScope = useMemo<CellFormulaScope>(() => {
    const workbookSheets = formulaWorkbook?.sheets ?? []
    const sheetScopes = workbookSheets.map((sheet) => ({
      id: sheet.id,
      name: sheet.name,
      columns: columnScope(sheet.columns),
    }))
    return {
      ...formulaScope,
      ...(formulaWorkbook ? { currentSheetId: formulaWorkbook.currentSheetId } : {}),
      rowIdAt: (oneBasedIndex) => {
        const row = formulaLogicalRows[oneBasedIndex - 1]
        if (!row) return undefined
        const key = getRowKey(row, internalRows.indexOf(row))
        return key.startsWith('idx:') ? undefined : key
      },
      rowIndexOf: (rowId) => {
        const index = formulaLogicalRows.findIndex((row) => getRowKey(row, internalRows.indexOf(row)) === rowId)
        return index < 0 ? undefined : index + 1
      },
      sheetOfName: (name) => {
        // The same folding columns use, so an emoji in a sheet name behaves the way an emoji in a
        // column name already does.
        const wanted = normaliseContainerName(name)
        const matches = sheetScopes.filter((sheet) => normaliseContainerName(sheet.name) === wanted)
        return matches.length === 1 ? matches[0] : matches.length > 1 ? 'ambiguous' : undefined
      },
      sheetNameOfId: (sheetId) => sheetScopes.find((sheet) => sheet.id === sheetId)?.name,
      columnsOfSheet: (sheetId) => sheetScopes.find((sheet) => sheet.id === sheetId)?.columns,
    }
  }, [formulaLogicalRows, formulaScope, formulaWorkbook, getRowKey, internalRows, locale])
  const persistCellFormula = useCallback(async (row: T, originalIndex: number, field: string, source: string): Promise<boolean> => {
    if (!gridConfig.orgId) return false
    if (!gridConfig.canEditFormulas) {
      setOverrideError(t('grid.cellFormulaPermission'))
      return false
    }
    const key = getRowKey(row, originalIndex)
    if (key.startsWith('idx:')) {
      setOverrideError(t('grid.cellFormulaStableRowRequired'))
      return false
    }
    const parsed = parseCellFormula(source.slice(1), cellFormulaScope, formulaLocale)
    if (!parsed.ok) {
      setOverrideError(t('grid.cellFormulaInvalid'))
      return false
    }
    const formula = toStored(parsed.ast)
    const validation = cellFormulaRuntime.validateCandidate({ sheetId: cellFormulaSheetId, rowId: key, field }, formula)
    if (!validation.ok && validation.kind === 'cycle') {
      setOverrideError(t('grid.cellFormulaCycle'))
      return false
    }
    markSaving()
    const error = await saveFormulaOverride(
      gridConfig.orgId,
      gridKey,
      key,
      field,
      formula,
      gridConfig.documentId,
    )
    if (error) {
      markFailed()
      console.error('Grid: cell formula could not be saved', error)
      setOverrideError(t('grid.cellFormulaSaveError'))
      return false
    }
    setOverrideError(null)
    await fetchOverrides()
    formulaEngine.invalidate()
    markSaved()
    return true
  }, [cellFormulaRuntime, cellFormulaScope, cellFormulaSheetId, fetchOverrides, formulaEngine, formulaLocale, getRowKey, gridConfig, gridKey, markFailed, markSaved, markSaving, t])
  useEffect(() => formulaEngine.invalidate(), [internalRows, formulaEngine])

  const headerOf = useCallback((index: number) => {
    const column = columns[index]
    if (!column) return ''
    return (column.field ? headerOverrides[column.field] : undefined) ?? column.headerName
  }, [columns, headerOverrides])

  // Resolve shared grouping configuration from DB (supports 1 or 2 levels).
  const resolveGroupItem = useCallback((refItem: { field: string | null; header?: string; index: number } | null): number | null => {
    if (!refItem) return null
    if (refItem.field) { const i = columns.findIndex((c) => c.field === refItem.field); if (i !== -1) return i }
    if (refItem.header) { const i = columns.findIndex((_, idx) => headerOf(idx) === refItem.header); if (i !== -1) return i }
    return refItem.index < columns.length ? refItem.index : null
  }, [columns, headerOf])

  const resolveGrouping = useCallback((ref: GridGroupRef): [number | null, number | null] => {
    if (!ref) return [null, null]
    if (Array.isArray(ref)) {
      const col1 = resolveGroupItem(ref[0] ?? null)
      const col2 = resolveGroupItem(ref[1] ?? null)
      return [col1, col2]
    }
    return [resolveGroupItem(ref), null]
  }, [resolveGroupItem])

  const groupColumns = useMemo<[number | null, number | null]>(
    () => {
      if (isEmbedded || !groupable) return [null, null]
      if (!(gridKey in groupingConfig) && defaultGroupFields?.length) {
        return resolveGrouping(defaultGroupFields.slice(0, 2).map((field) => ({ field, header: '', index: Number.MAX_SAFE_INTEGER })))
      }
      return resolveGrouping(groupingConfig[gridKey] ?? null)
    },
    [isEmbedded, groupable, resolveGrouping, groupingConfig, gridKey, defaultGroupFields]
  )

  const grouped = groupColumns[0] !== null || groupColumns[1] !== null
  useEffect(() => { onGroupedChange?.(grouped) }, [grouped, onGroupedChange])

  /**
   * The field the first grouping level bands rows by, when that field is one a user
   * could type into anyway. Dragging a row onto another band writes it, which is the
   * only meaning a drag can carry while grouped: the order within a group is decided
   * by the sort, not by hand.
   */
  const groupField = useMemo(() => {
    if (isEmbedded) return null
    const index = groupColumns[0]
    const field = index !== null ? columns[index]?.field ?? null : groupBy ?? null
    if (!field) return null
    const column = columns.find((item) => item.field === field)
    if (!column || column.editable === false || isFormulaField(field, formulaConfig)) return null
    return field
  }, [columns, formulaConfig, groupBy, groupColumns, isEmbedded])

  const [groupSearch, setGroupSearch] = useState('')
  const [highlightedGroupKey, setHighlightedGroupKey] = useState<string | null>(null)
  useEffect(() => {
    setGroupSearch('')
    setHighlightedGroupKey(null)
  }, [groupColumns[0], groupColumns[1]])

  // Row order: defined by programmer dragging rows and shared with everyone,
  // while non-programmer users keep local browser overrides.
  const [localRowOrder, setLocalRowOrder] = useState<string[] | null>(() => {
    try { return JSON.parse(readPref('orden-filas') ?? 'null') } catch { return null }
  })
  useEffect(() => {
    savePref('orden-filas', localRowOrder === null ? null : JSON.stringify(localRowOrder))
  }, [localRowOrder, savePref])
  const effectiveRowOrder = isEmbedded ? null : (localRowOrder ?? rowOrderConfig[gridKey] ?? null)
  // Group order: top-level groups dragged by their header. Kept apart from the row order,
  // so it holds under a column sort too; same two layers as rows, beside them in the setting.
  const groupOrderKey = `groups:${gridKey}`
  const [localGroupOrder, setLocalGroupOrder] = useState<string[] | null>(() => {
    try { return JSON.parse(readPref('orden-grupos') ?? 'null') } catch { return null }
  })
  useEffect(() => {
    savePref('orden-grupos', localGroupOrder === null ? null : JSON.stringify(localGroupOrder))
  }, [localGroupOrder, savePref])
  const groupRank = useMemo(() => {
    const order = isEmbedded ? null : (localGroupOrder ?? rowOrderConfig[groupOrderKey] ?? null)
    return new Map((order ?? []).map((key, index) => [key, index]))
  }, [isEmbedded, localGroupOrder, rowOrderConfig, groupOrderKey])
  // Groups someone placed come first, in that order; the rest keep their natural order after them.
  const compareGroupKeys = useCallback((a: string, b: string) => {
    const rankA = groupRank.get(a) ?? Infinity
    const rankB = groupRank.get(b) ?? Infinity
    return rankA === rankB ? compareGroupLabels(a, b) : rankA - rankB
  }, [groupRank])
  const rowDrag = useRef<string | null>(null)
  /** A whole top-level group being dragged: its rows move as one block, and the groups that were folded before. */
  const groupDrag = useRef<{ key: string; collapsedBefore: Set<string> } | null>(null)
  // While a group moves, only group headers show: no totals or blank rows between them.
  const [movingGroup, setMovingGroup] = useState(false)

  // Column sort with two layers: shared by programmer, local override for users.
  const resolveSort = useCallback((ref: GridSortRef | null): { index: number; dir: 1 | -1 } | null => {
    if (!ref) return null
    if (ref.field) { const i = columns.findIndex((c) => c.field === ref.field); return i === -1 ? null : { index: i, dir: ref.dir } }
    return ref.index < columns.length ? { index: ref.index, dir: ref.dir } : null
  }, [columns])
  const [localSort, setLocalSort] = useState<GridSortRef | 'none' | null>(() => {
    try { return JSON.parse(readPref('sort') ?? 'null') } catch { return null }
  })
  useEffect(() => {
    savePref('sort', localSort === null ? null : JSON.stringify(localSort))
  }, [localSort, savePref])
  const resolvedSharedSort = useMemo(() => resolveSort(sortConfig[gridKey] ?? null), [resolveSort, sortConfig, gridKey])
  const resolvedLocalSort = useMemo<{ index: number; dir: 1 | -1 } | null | undefined>(() => {
    if (localSort === null) return undefined
    if (localSort === 'none') return null
    return resolveSort(localSort)
  }, [localSort, resolveSort])
  const sort = isEmbedded ? null : (resolvedLocalSort !== undefined ? resolvedLocalSort : resolvedSharedSort)

  const applySort = useCallback(async (next: { index: number; dir: 1 | -1 } | null) => {
    const ref: GridSortRef | null = next ? { field: columns[next.index]?.field ?? null, index: next.index, dir: next.dir } : null
    if (canEditSharedConfig) {
      markSaving()
      const nextSort = { ...sortConfig }
      if (ref) nextSort[gridKey] = ref; else delete nextSort[gridKey]
      const error = await saveSettingRef.current(GRID_SETTING_KEYS.sort, nextSort)
      if (error) {
        markFailed()
        setConfigError(`Column sort order could not be saved: ${error.message}`)
        return
      }
      setConfigError(null)
      setLocalSort(null)
      await reloadConfig()
      markSaved()
    } else {
      setLocalSort(next === null ? 'none' : ref)
    }
  }, [columns, canEditSharedConfig, sortConfig, gridKey, gridConfig, markSaving, markSaved, markFailed, reloadConfig])

  // Stable column keys for width and visibility persistence.
  const columnKeys = useMemo(() => {
    const duplicates = new Set<string>()
    const seen = new Set<string>()
    columns.forEach((column) => {
      if (!column.field) return
      if (seen.has(column.field)) duplicates.add(column.field)
      seen.add(column.field)
    })
    return columns.map((column, index) => {
      const base = columnKey(column.field, index)
      return column.field && duplicates.has(column.field) ? `${base}|${column.headerName}` : base
    })
  }, [columns])
  const indexByKey = useMemo(() => {
    const m = new Map<string, number>()
    columnKeys.forEach((key, index) => m.set(key, index))
    return m
  }, [columnKeys])

  const columnOrder = useMemo(() => {
    const saved = columnOrderConfig[gridKey]
    const order = resolveColumnOrder(saved, columnKeys)
    // A companion the saved order has never seen sits right after its source.
    const savedKeys = new Set((saved ?? []).map(String))
    columnKeys.forEach((key, index) => {
      const source = changeTimeSource(key)
      if (source === null || savedKeys.has(key)) return
      const sourceIndex = indexByKey.get(source)
      if (sourceIndex === undefined) return
      order.splice(order.indexOf(index), 1)
      order.splice(order.indexOf(sourceIndex) + 1, 0, index)
    })
    return order
  }, [columnOrderConfig, gridKey, columnKeys, indexByKey])

  const [widthsDraft, setWidthsDraft] = useState<Record<string, number>>({})
  useEffect(() => { setWidthsDraft({}) }, [columnKeys])
  useEffect(() => { savePref('anchos', null) }, [savePref])
  useEffect(() => {
    savePref('orden-columnas', null)
    savePrefCols('anchos-cols', null)
  }, [savePref, savePrefCols])
  const sharedWidths = widthsConfig[columnsKey] ?? EMPTY_WIDTHS
  const widths = useMemo(() => {
    const result: Record<number, number> = {}
    columnKeys.forEach((key, index) => {
      const width = widthsDraft[key] ?? sharedWidths[key]
      if (typeof width === 'number') result[index] = width
    })
    return result
  }, [columnKeys, sharedWidths, widthsDraft])

  const sharedHidden = hiddenColumnsConfig[columnsKey] ?? EMPTY_HIDDEN
  const hidden = useMemo(() => {
    const result = new Set<number>()
    sharedHidden.forEach((key: string) => {
      const index = indexByKey.get(key)
      if (index !== undefined) result.add(index)
    })
    return result
  }, [sharedHidden, indexByKey])

  // Columns shown on one device only, by stable key; a column outside this map shows everywhere.
  const sharedColumnDevices = columnDevicesConfig[columnsKey] ?? EMPTY_COLUMN_DEVICES
  const deviceHidden = useMemo(() => {
    const result = new Set<number>()
    Object.entries(sharedColumnDevices).forEach(([key, only]) => {
      const index = indexByKey.get(key)
      if (index !== undefined && !scopeIncludes(only, device)) result.add(index)
    })
    return result
  }, [sharedColumnDevices, indexByKey, device])
  // Outline groups: a collapsed one hides its columns without touching the shared hidden set.
  const columnGroups = gridConfig.columnGroups?.[columnsKey] ?? EMPTY_COLUMN_GROUPS
  const orderKeys = useMemo(() => columnOrder.map((index) => columnKeys[index] ?? ''), [columnOrder, columnKeys])
  const columnGroupSpanList = useMemo(() => columnGroupSpans(columnGroups, orderKeys), [columnGroups, orderKeys])
  const groupCollapsed = useMemo(() => {
    const result = new Set<number>()
    collapsedColumnKeys(columnGroups, orderKeys).forEach((key) => {
      const index = indexByKey.get(key)
      if (index !== undefined) result.add(index)
    })
    return result
  }, [columnGroups, orderKeys, indexByKey])

  // The device the columns sidebar is editing: its checkboxes and freeze buttons apply there.
  const [columnsPanelScope, setColumnsPanelScope] = useState<DeviceScope>('all')
  const columnScopeOf = (index: number): DeviceScope => {
    const key = columnKeys[index]
    return (key !== undefined ? sharedColumnDevices[key] : undefined) ?? 'all'
  }

  const [editingHeader, setEditingHeader] = useState<number | null>(null)
  const headerNotes = headerNotesConfig[gridKey] ?? EMPTY_HEADER_NOTES
  // A note is shared like a column name: one per column, for everyone reading the grid.
  const saveHeaderNote = useCallback(async (field: string, text: string) => {
    const nextNotes: Record<string, string> = { ...headerNotes }
    if (text.trim()) nextNotes[field] = text.trim()
    else delete nextNotes[field]
    markSaving()
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.headerNotes, { ...headerNotesConfig, [gridKey]: nextNotes })
    if (error) { markFailed(); return false }
    await reloadConfig()
    markSaved()
    return true
  }, [gridConfig, gridKey, headerNotes, headerNotesConfig, markFailed, markSaved, markSaving, reloadConfig])
  const [configError, setConfigError] = useState<string | null>(null)
  // Progress of a column's cell action (a price lookup): shown while it runs, then briefly its outcome.
  const [cellActionStatus, setCellActionStatus] = useState<{ state: 'running' | 'done' | 'error'; label: string; message?: string } | null>(null)
  useEffect(() => {
    if (!cellActionStatus || cellActionStatus.state === 'running') return
    const timer = window.setTimeout(() => setCellActionStatus(null), cellActionStatus.state === 'done' ? 3000 : 6000)
    return () => window.clearTimeout(timer)
  }, [cellActionStatus])

  const confirmHeaderRename = useCallback(async (index: number, raw: string) => {
    setEditingHeader(null)
    const column = columns[index]
    if (!column?.field) return
    const nextHeaders = withHeader(gridHeadersConfig, gridKey, column.field, column.headerName, raw)
    if (JSON.stringify(nextHeaders[gridKey] ?? {}) === JSON.stringify(headerOverrides)) return
    markSaving()
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.headers, nextHeaders)
    if (error) {
      markFailed()
      setConfigError(`Column name could not be saved: ${error.message}`)
      return
    }
    setConfigError(null)
    await reloadConfig()
    markSaved()
  }, [columns, gridHeadersConfig, gridKey, gridConfig, markSaving, markSaved, markFailed, headerOverrides, reloadConfig])

  // ── Formula bar (programmer only) ──────────────────────────────────────────
  const [internalFormulaBar, setInternalFormulaBar] = useState(() => readPref('formulas') === '1')
  const formulaBar = !bare && (formulaBarOpen ?? internalFormulaBar)
  useEffect(() => {
    if (formulaBarOpen === undefined) savePref('formulas', internalFormulaBar ? '1' : '0')
  }, [internalFormulaBar, formulaBarOpen, savePref])

  const [internalFormatBar, setInternalFormatBar] = useState(() => readPref('formato') === '1')
  const formatBar = !bare && (formatBarOpen ?? internalFormatBar)
  useEffect(() => {
    if (formatBarOpen === undefined) savePref('formato', internalFormatBar ? '1' : '0')
  }, [internalFormatBar, formatBarOpen, savePref])


  const [formulaPanel, setFormulaPanel] = useState(false)
  const [formulaPanelWidth, setFormulaPanelWidth] = useState<number>(() => {
    const saved = Number(readPref('formulas-ancho') ?? 0)
    return saved > 0 ? saved : 360
  })
  useEffect(() => { savePref('formulas-ancho', String(formulaPanelWidth)) }, [formulaPanelWidth, savePref])
  const formulaResizeDrag = useRef<{ x0: number; w0: number } | null>(null)

  const startFormulaPanelResize = (event: React.MouseEvent) => {
    event.preventDefault()
    formulaResizeDrag.current = { x0: event.clientX, w0: formulaPanelWidth }
    const move = (e: MouseEvent) => {
      if (!formulaResizeDrag.current) return
      const { x0, w0 } = formulaResizeDrag.current
      setFormulaPanelWidth(Math.max(260, Math.min(720, w0 - (e.clientX - x0))))
    }
    const release = () => {
      formulaResizeDrag.current = null
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', release)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', release)
  }

  const [formulaTarget, setFormulaTarget] = useState<string>(NEW_COLUMN)
  const [formulaSaveError, setFormulaSaveError] = useState<string | null>(null)
  const [formulaBusy, setFormulaBusy] = useState(false)
  const [formulaInsert, setFormulaInsert] = useState<{ field: string; nonce: number } | undefined>(undefined)

  const formulaTargets = useMemo<FormulaTarget[]>(() => {
    const seen = new Set<string>()
    const out: FormulaTarget[] = []
    columns.forEach((column, index) => {
      if (!column.field || seen.has(column.field)) return
      seen.add(column.field)
      out.push({ field: column.field, name: headerOf(index), calculated: formulaIdOf(column.field) !== null })
    })
    return out
  }, [columns, headerOf])

  const [formatTarget, setFormatTarget] = useState('')
  const formatSelectionRef = useRef<readonly string[]>([])
  const [formatBusy, setFormatBusy] = useState(false)
  const [formatSaveError, setFormatSaveError] = useState<string | null>(null)
  useEffect(() => {
    if (formatTarget !== '' && formulaTargets.some((target) => target.field === formatTarget)) return
    setFormatTarget(formulaTargets[0]?.field ?? '')
  }, [formatTarget, formulaTargets])


  const [optionsDialog, setOptionsDialog] = useState(false)
  const [rollupDialog, setRollupDialog] = useState(false)

  const formatBaseOf = useCallback((field: string): GridDataFormat => {
    const column = columns.find((candidate) => candidate.field === field)
    let inferred: GridDataFormat['type'] = 'text'
    const kind = column?.cellType
    if (kind === 'number' || kind === 'date' || kind === 'smartchip' || kind === 'autofill' || kind === 'checkbox' || kind === 'attachment' || kind === 'barcode') inferred = kind
    else if (column && !kind && !column.cellRenderer && !column.dropdown) {
      const values = internalRows.map((row) => readValue(column, row)).filter((value) => value !== null && value !== undefined && value !== '')
      if (values.length > 0 && values.filter((value) => typeof value === 'number').length * 2 > values.length) inferred = 'number'
    }
    return resolveColumnFormatBase(dataFormatsConfig[gridKey]?.[field], column?.dataFormat, inferred)
  }, [columns, internalRows, dataFormatsConfig, gridKey])

  const saveDataFormat = useCallback(async (format: GridDataFormat, headingOnly = false, changedKeys?: readonly (keyof GridDataFormat)[]): Promise<boolean> => {
    if (formatTarget === '' || !gridConfig.canEditFormats) return false
    const fields = changedKeys !== undefined && formatSelectionRef.current.length > 1 ? formatSelectionRef.current : [formatTarget]
    const bases = Object.fromEntries(fields.map((field) => [field, formatBaseOf(field)]))
    const source = formatBaseOf(formatTarget)
    const updates = patchColumnFormats(bases, fields, source, format, changedKeys, headingOnly)
    setFormatBusy(true)
    setFormatSaveError(null)
    markSaving()

    // Every borrow goes on record, including one inside this document. The
    // snapshot is what a reader without access to the source reads, and — for
    // any link at all — it is what remembers which source row each value came
    // from, so an edited value can follow into the cells that chose it.
    const documentId = formulaWorkbook?.documentId ?? gridConfig.documentId
    if (documentId && gridKey.startsWith('sheet:')) {
      const tabId = gridKey.slice('sheet:'.length)
      for (const field of fields) {
        const link = updates[field]?.optionsLink
        const registerError = link
        ? await registerLinkedList(documentId, tabId, field, {
          documentId: link.documentId ?? documentId,
          tabId: link.tabId,
          field: link.field,
        })
        : await unregisterLinkedList(documentId, tabId, field)
      if (registerError && link) {
        markFailed()
        setFormatSaveError(registerError)
        setFormatBusy(false)
        return false
      }
      }
    }

    const error = await saveSettingRef.current(GRID_SETTING_KEYS.dataFormats, { ...dataFormatsConfig, [gridKey]: { ...dataFormatsConfig[gridKey], ...updates } })
    if (error) {
      markFailed()
      setFormatSaveError(`Format could not be saved: ${error.message}`)
    } else {
      await reloadConfig()
      markSaved()
    }
    setFormatBusy(false)
    return !error
  }, [formatTarget, formatBaseOf, dataFormatsConfig, formulaWorkbook, gridConfig, gridKey, markSaving, markSaved, markFailed, reloadConfig])

  const formulaReferenceTargets = useMemo<FormulaTarget[]>(() => {
    const seen = new Set<string>()
    const out: FormulaTarget[] = []
    formulaEngine.columns.forEach((column) => {
      if (!column.field || seen.has(column.field)) return
      seen.add(column.field)
      const visibleIndex = columns.findIndex((candidate) => candidate.field === column.field)
      out.push({
        field: column.field,
        name: visibleIndex >= 0 ? headerOf(visibleIndex) : column.headerName,
        calculated: formulaIdOf(column.field) !== null,
      })
    })
    return out
  }, [columns, headerOf, formulaEngine.columns])

  /**
   * What the editor's autocomplete may offer. The current sheet's entry comes from the effective
   * column list (so calculated columns are offerable too, and headers reflect renames); the other
   * sheets come from the workbook snapshot. Other DOCUMENTS are left `undefined` until something
   * loads them — the source then shows a placeholder and asks, which is the same lazily-loaded
   * shape `workbookOverridesReady` already uses.
   */
  const formulaCompletionSchema = useMemo<FormulaCompletionSchema>(() => {
    const documentId = formulaWorkbook?.documentId ?? gridConfig.documentId ?? ''
    const typeOfField = new Map(baseColumns.map((column) => [column.field, column.cellType]))
    const current: FormulaCompletionSheet = {
      id: cellFormulaSheetId,
      name: formulaWorkbook?.sheets.find((sheet) => sheet.id === cellFormulaSheetId)?.name ?? '',
      documentId,
      columns: formulaReferenceTargets.map((target) => ({
        field: target.field,
        name: target.name,
        ...(typeOfField.get(target.field) === undefined ? {} : { type: typeOfField.get(target.field)! }),
      })),
    }
    const others = (formulaWorkbook?.sheets ?? [])
      .filter((sheet) => sheet.id !== cellFormulaSheetId)
      .map((sheet) => ({
        id: sheet.id,
        name: sheet.name,
        documentId,
        columns: sheet.columns.map((column) => ({
          field: column.field,
          name: column.name,
          ...(column.type === undefined ? {} : { type: column.type }),
        })),
      }))
    return {
      currentDocumentId: documentId,
      currentSheetId: cellFormulaSheetId,
      sheets: [current, ...others],
    }
  }, [baseColumns, cellFormulaSheetId, formulaReferenceTargets, formulaWorkbook, gridConfig.documentId])

  /**
   * Formula results, written back so that reading one column never means
   * loading a document. The grid still evaluates live — this only persists what
   * it just computed, for the tab on screen and for the tabs whose formulas
   * read it, which would otherwise keep a stale value until someone opened
   * them. Only sheet-backed grids take part: a module grid's rows are not
   * `sheet_rows`.
   */
  const lastComputedRef = useRef('')
  const materializedDocumentRef = useRef<string | null>(null)
  useEffect(() => {
    const documentId = formulaWorkbook?.documentId ?? gridConfig.documentId
    if (isEmbedded || !documentId || !formulaWorkbook || !workbookOverridesReady) return
    if (!gridKey.startsWith('sheet:')) return

    // Opening a document is the one moment every tab is in hand: a tab nobody
    // opens would otherwise never have its results stored, and a list borrowing
    // from it would read as empty. After that first pass only the tab being
    // edited, and the tabs whose formulas read it, are rewritten.
    const firstPass = materializedDocumentRef.current !== documentId
    const targets = formulaWorkbook.sheets.flatMap((sheet) => {
      const config = sheet.id === cellFormulaSheetId
        ? formulaConfig
        : formulasConfig[`sheet:${sheet.id}`]
      if (!config) return []
      const fields = formulaFieldsOf(config)
      if (fields.length === 0) return []
      if (!firstPass && sheet.id !== cellFormulaSheetId && !referencesSheet(config, cellFormulaSheetId)) return []
      return [{ sheetId: sheet.id, fields, rows: sheet.rows }]
    })
    if (targets.length === 0) return

    const payload = targets.map(({ sheetId, fields, rows }) => ({
      sheetId,
      rows: computeSheetValues({ runtime: cellFormulaRuntime, sheetId, rows, fields }),
    }))
    const signature = JSON.stringify(payload)
    if (signature === lastComputedRef.current) return

    // Edits arrive one keystroke-confirmed cell at a time; a paste arrives as a
    // burst. Long enough to coalesce the burst, short enough that whoever is
    // watching the borrowing table sees it move.
    const timer = setTimeout(() => {
      lastComputedRef.current = signature
      materializedDocumentRef.current = documentId
      void (async () => {
        for (const { sheetId, rows } of payload) {
          const error = await saveSheetComputed(documentId, sheetId, rows)
          // A reader-only visitor cannot write these, and must not be told off
          // for opening a document: the values stay as whoever last edited left
          // them, and the grid shows its own live evaluation regardless.
          if (error) lastComputedRef.current = ''
        }
        // Storing results flags the lists that borrow them, and those results
        // are what the lists are made of: refresh them now rather than after a
        // debounce nobody is waiting for.
        await refreshDirtyLinkedLists()
      })()
    }, 250)
    return () => clearTimeout(timer)
  }, [
    cellFormulaRuntime,
    cellFormulaSheetId,
    formulaConfig,
    formulaWorkbook,
    formulasConfig,
    gridConfig.documentId,
    gridKey,
    isEmbedded,
    workbookOverridesReady,
  ])

  /**
   * What a linked option list may point at. Sheets supply the whole workbook;
   * a module grid has only itself, which still has to be offerable as the
   * default table.
   */
  const optionsWorkbook = useMemo<WorkbookSnapshot | undefined>(() => {
    if (formulaWorkbook) {
      // The current sheet's headers may have been renamed here; the workbook
      // snapshot still carries the stored column names.
      const renamed = new Map(columns.flatMap((column, index) => (column.field ? [[column.field, headerOf(index)] as const] : [])))
      return {
        ...(formulaWorkbook.documentId === undefined ? {} : { documentId: formulaWorkbook.documentId }),
        currentSheetId: formulaWorkbook.currentSheetId,
        sheets: formulaWorkbook.sheets.map((sheet) => ({
          id: sheet.id,
          name: sheet.name,
          columns: sheet.columns.map((column) => ({
            field: column.field,
            name: (sheet.id === formulaWorkbook.currentSheetId ? renamed.get(column.field) : undefined) ?? column.name,
          })),
          rows: sheet.rows,
        })),
      }
    }
    return {
      ...(gridConfig.documentId === null ? {} : { documentId: gridConfig.documentId }),
      currentSheetId: gridKey,
      sheets: [{
        id: gridKey,
        name: t('grid.optionsThisTable'),
        columns: columns.flatMap((column, index) => (column.field
          ? [{ field: column.field, name: headerOf(index) }]
          : [])),
        rows: internalRows as readonly Record<string, unknown>[],
      }],
    }
  }, [columns, formulaWorkbook, gridConfig.documentId, gridKey, headerOf, internalRows, t])

  /**
   * Reads one cell of a tab in this document the way the grid itself would: a
   * column whose values come from a formula has nothing stored on the row, so
   * asking the runtime is the only way to see what the table shows. Falls back
   * to the stored value when the runtime cannot place the cell (an unstable row
   * id, or a workbook whose overrides have not loaded yet).
   */
  const resolveLinkedCell = useCallback((tabId: string) => (row: Record<string, unknown>, field: string) => {
    const rowId = tabId === cellFormulaSheetId
      ? getRowKey(row as T, internalRows.indexOf(row as T))
      : typeof row.id === 'string' ? `id:${row.id}` : undefined
    if (rowId !== undefined && !rowId.startsWith('idx:')) {
      const result = cellFormulaRuntime.valueOf({ sheetId: tabId, rowId, field })
      if (result.kind === 'number') return result.n
      if (result.kind === 'text') return result.s
      // Empty falls through to the stored value: a row the runtime doesn't model (a tab
      // a module fills, like a investments's Market) is "empty" to it but not on the row.
      // An error is not an option anyone would pick; show nothing for that row.
      if (result.kind === 'error') return null
    }
    return row[field]
  }, [cellFormulaRuntime, cellFormulaSheetId, getRowKey, internalRows])

  /**
   * The rows an aggregate should read: a column whose values come from a formula stores
   * nothing on the row, so summing the raw rows would total an empty column - which is
   * why a group's subtotal and the totals row used to read 0 under a Weight or any other
   * calculated column. Each formula field is resolved through the runtime that painted
   * the cells, so the figure at the foot of a group is the sum of what is above it.
   */
  const aggregationRowsOf = useCallback((rows: readonly Record<string, unknown>[]): Record<string, unknown>[] => {
    const fields = formulaFieldsOf(formulaConfig)
    // A column the page derives - a investments's price and value - is no more present on
    // the row than a formula's result is, and has to be asked for the same way.
    const derived = columns.filter((column): column is typeof column & { field: string; valueGetter: NonNullable<typeof column.valueGetter> } =>
      Boolean(column.field) && typeof column.valueGetter === 'function')
    if (fields.length === 0 && derived.length === 0) return rows as Record<string, unknown>[]
    const resolve = resolveLinkedCell(cellFormulaSheetId)
    return rows.map((row) => {
      const resolved: Record<string, unknown> = { ...row }
      for (const field of fields) resolved[field] = resolve(row, field)
      for (const column of derived) resolved[column.field] = column.valueGetter({ data: row as T })
      return resolved
    })
  }, [cellFormulaSheetId, columns, formulaConfig, resolveLinkedCell])

  /** Calculated columns of every tab in this document, which no stored column list holds. */
  const calculatedColumnsByTab = useMemo(() => {
    const result: Record<string, { field: string; name: string }[]> = {}
    for (const sheet of optionsWorkbook?.sheets ?? []) {
      const sheetFormulas = sheet.id === cellFormulaSheetId
        ? formulaConfig
        : formulasConfig[`sheet:${sheet.id}`]
      const calculated = (sheetFormulas?.columns ?? []).map((column) => ({
        field: formulaField(column.id),
        name: column.title,
      }))
      if (calculated.length > 0) result[sheet.id] = calculated
    }
    return result
  }, [optionsWorkbook, cellFormulaSheetId, formulaConfig, formulasConfig])

  /**
   * Borrowed lists are kept current by a trigger on the source's rows, so this
   * is only a repair: a snapshot written before that trigger existed, or one
   * whose source was changed by something that bypassed the database's own
   * propagation. It refreshes nothing unless the caller holds both accesses.
   */
  const refreshedDocumentRef = useRef<string | null>(null)
  useEffect(() => {
    const documentId = formulaWorkbook?.documentId ?? gridConfig.documentId
    if (isEmbedded || !documentId || !gridKey.startsWith('sheet:')) return
    if (refreshedDocumentRef.current === documentId) return
    refreshedDocumentRef.current = documentId

    void refreshLinkedLists(documentId)
  }, [formulaWorkbook, gridConfig.documentId, gridKey, isEmbedded])



  /** Values of every column that borrows its option list from somewhere else. */
  const optionLinks = useMemo(
    () => columns.map((column) => column.dataFormat?.optionsLink).filter((link): link is GridOptionsLink => link !== undefined),
    [columns],
  )
  /** Which of this grid's columns borrows a given link, so its snapshot is found. */
  const borrowerOfLink = useCallback((link: GridOptionsLink) => {
    const column = columns.find((candidate) => candidate.dataFormat?.optionsLink === link)
    return { tabId: cellFormulaSheetId, field: column?.field ?? '' }
  }, [columns, cellFormulaSheetId])
  const pushedSource = useMemo(() => ({ mode: 'pushed' as const, borrowerOf: borrowerOfLink }), [borrowerOfLink])
  // The Worker refreshes these behind the page's back; this is how it finds out.
  const linkedListRevision = useLiveLinkedLists(formulaWorkbook?.documentId ?? gridConfig.documentId)
  const { values: linkedOptionValues } = useLinkedValues(
    optionLinks,
    optionsWorkbook,
    resolveLinkedCell,
    pushedSource,
    linkedListRevision,
  )

  /**
   * The effective option list of every list column: hand-written, or borrowed
   * from a linked column and coloured here. Built once per render pass rather
   * than per cell — every visible chip needs its colour, and sorting a borrowed
   * list for each of them is work proportional to cells × options.
   */
  const optionsByField = useMemo(() => {
    const result = new Map<string, {
      options: readonly GridDataOption[]
      colorOf: ReadonlyMap<string, string>
      items: ItemChip[]
    }>()
    for (const column of columns) {
      const format = column.dataFormat
      if (!column.field || !format || (format.type !== 'smartchip' && format.type !== 'autofill')) continue
      const stored = format.options ?? []
      const colorOf = new Map(stored.flatMap((option) => (option.color ? [[option.value, option.color] as const] : [])))
      const link = format.optionsLink
      const options = link
        ? sortLinkedValues(linkedOptionValues.get(optionsLinkKey(link)) ?? [], link.sort).map((value) => {
          const color = colorOf.get(value)
          return color ? { value, color } : { value }
        })
        : stored
      // `ItemChip` is what the chip cell and its editor speak — the same shape
      // `ChipSelect` uses, so the colours stay in sync with the options editor.
      const items = options.map((option, index) => ({
        clave: option.value,
        texto: option.value,
        color: option.color ?? null,
        orden: index,
      }))
      result.set(column.field, { options, colorOf, items })
    }
    return result
  }, [columns, linkedOptionValues])

  /** A cell whose value its column rejects: text in a number or date column, or a
   * value outside a list column's options. Kept as entered and flagged, never lost. */
  const cellError = useCallback((column: GridColumn<T>, row: T): boolean => {
    const field = column.field
    if (!flagErrors || !field || isFormulaField(field, formulaConfig)) return false
    const value = (row as Record<string, unknown>)[field]
    const listType = column.dataFormat?.type
    if (listType === 'autofill' || listType === 'smartchip') {
      return isGridListMismatch(value, optionsByField.get(field)?.options ?? [])
    }
    return isGridTypeMismatch(value, column)
  }, [flagErrors, formulaConfig, optionsByField])

  const errorCount = useMemo(() => {
    let count = 0
    for (const row of internalRows) for (const column of columns) if (cellError(column, row)) count += 1
    return count
  }, [cellError, columns, internalRows])
  useEffect(() => { onErrorCountChange?.(errorCount) }, [errorCount, onErrorCountChange])


  /**
   * Links saved before borrows were registered still need their record, or an
   * edit in the source has no way to reach the cells that chose it. Keyed by the
   * links themselves rather than by the document: the columns arrive after the
   * first render, and a link added later has to be caught too.
   */
  const borrowSignature = optionLinks.map(optionsLinkKey).sort().join('|')
  useEffect(() => {
    const documentId = formulaWorkbook?.documentId ?? gridConfig.documentId
    if (isEmbedded || !documentId || !gridKey.startsWith('sheet:') || optionLinks.length === 0) return
    void ensureLinkedListRegistrations(
      documentId,
      gridKey.slice('sheet:'.length),
      columns.flatMap((column) => {
        const link = column.dataFormat?.optionsLink
        return column.field && link
          ? [{
            field: column.field,
            source: { documentId: link.documentId ?? documentId, tabId: link.tabId, field: link.field },
          }]
          : []
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrowSignature, formulaWorkbook?.documentId, gridConfig.documentId, gridKey, isEmbedded])

  /** The stored formula driving `field`, wherever it is configured, or `undefined`. */
  const storedFormulaOf = useCallback((field: string): StoredFormula | undefined => {
    const id = formulaIdOf(field)
    return id === null
      ? formulaConfig.overrides[field]
      : formulaConfig.columns.find((column) => column.id === id)?.formula
  }, [formulaConfig])

  const selectedFormula = useMemo(() => {
    if (formulaTarget === NEW_COLUMN) return { formula: null, title: '' }
    const id = formulaIdOf(formulaTarget)
    if (id !== null) {
      const col = formulaConfig.columns.find((c) => c.id === id)
      return { formula: col?.formula ?? null, title: col?.title ?? '' }
    }
    return { formula: formulaConfig.overrides[formulaTarget] ?? null, title: '' }
  }, [formulaConfig, formulaTarget])

  const saveFormula = useCallback(async (formula: StoredFormula, title: string) => {
    if (!gridConfig.canEditFormulas) return
    setFormulaBusy(true)
    setFormulaSaveError(null)
    const existingId = formulaTarget === NEW_COLUMN ? null : formulaIdOf(formulaTarget)
    const isCalculated = formulaTarget === NEW_COLUMN || existingId !== null
    const id = existingId ?? newFormulaId()
    const field = isCalculated ? formulaField(id) : formulaTarget

    const previousCol = formulaConfig.columns.find((c) => c.id === id)
    const nextFormulas = isCalculated
      ? withFormulaColumn(formulasConfig, gridKey, id, {
        id,
        title: title !== '' ? title : (previousCol?.title ?? 'Calculated'),
        formula,
        format: previousCol?.format ?? DEFAULT_FORMAT,
        ...(previousCol?.minWidth !== undefined ? { minWidth: previousCol.minWidth } : {}),
      })
      : withFormulaOverride(formulasConfig, gridKey, field, formula)

    const nextSaved = nextFormulas[gridKey] ?? EMPTY_FORMULA_CONFIG
    const nextEffective = {
      columns: nextSaved.columns,
      overrides: {
        ...storedDefaults(formulaDefaults),
        ...Object.fromEntries(
          Object.entries(nextSaved.overrides).filter(([overrideField]) => !externalFormulas.has(overrideField) || overrideField === field),
        ),
      },
    }
    // A formula the user chose to save unparsed is a `raw` one. `validateFormulas` would report
    // it as the syntax problem it is and block the save, which is the opposite of the point:
    // keeping the text is what lets them come back to it. Its own cell already shows an error,
    // and it can depend on nothing, so it cannot take part in a cycle either.
    if (!isRawFormula(formula)) {
      const issue = validateFormulas(nextEffective, field, baseColumns, headerOverrides)
      if (issue) { setFormulaSaveError(issue.message); setFormulaBusy(false); return }
    }

    markSaving()
    if (!isCalculated && externalFormulas.has(field) && onExternallyManagedFormulaSave) {
      // This callback belongs to the page, which keeps the formula in its own storage — text is
      // the contract there, so the stored form is rendered back to canonical text for it.
      const error = await onExternallyManagedFormulaSave(field, serializeCanonical(fromStored(formula)))
      if (error) {
        markFailed()
        setFormulaSaveError(`Formula could not be saved: ${error.message}`)
        setFormulaBusy(false)
        return
      }
      await reloadConfig()
      setFormulaTarget(field)
      setFormulaBusy(false)
      markSaved()
      return
    }

    const error = await saveSettingRef.current(GRID_SETTING_KEYS.formulas, nextFormulas)
    if (error) {
      markFailed()
      setFormulaSaveError(`Formula could not be saved: ${error.message}`)
      setFormulaBusy(false)
      return
    }
    await reloadConfig()
    setFormulaTarget(field)
    setFormulaBusy(false)
    markSaved()
  }, [baseColumns, formulaConfig, formulaDefaults, formulaTarget, externalFormulas, formulasConfig, gridKey, gridConfig, markSaving, markSaved, markFailed, onExternallyManagedFormulaSave, headerOverrides, reloadConfig])

  const deleteFormula = useCallback(async () => {
    if (formulaTarget === NEW_COLUMN || !gridConfig.canEditFormulas) return
    setFormulaBusy(true)
    setFormulaSaveError(null)
    markSaving()
    const id = formulaIdOf(formulaTarget)
    const nextFormulas = id !== null
      ? withFormulaColumn(formulasConfig, gridKey, id, null)
      : withFormulaOverride(formulasConfig, gridKey, formulaTarget, null)
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.formulas, nextFormulas)
    if (error) {
      markFailed()
      setFormulaSaveError(`Formula could not be deleted: ${error.message}`)
      setFormulaBusy(false)
      return
    }
    await reloadConfig()
    setFormulaTarget(NEW_COLUMN)
    setFormulaBusy(false)
    markSaved()
  }, [formulaTarget, formulasConfig, gridConfig, gridKey, markSaving, markSaved, markFailed, reloadConfig])

  const setFormulaCellOverridesAllowed = useCallback(async (allowed: boolean) => {
    if (formulaTarget === NEW_COLUMN || !gridConfig.canEditFormulas) return
    setFormulaBusy(true)
    setFormulaSaveError(null)
    markSaving()
    const error = await saveSettingRef.current(
      GRID_SETTING_KEYS.formulas,
      withCellOverridesAllowed(formulasConfig, gridKey, formulaTarget, allowed),
    )
    if (error) {
      markFailed()
      setFormulaSaveError(`Formula could not be saved: ${error.message}`)
    } else {
      await reloadConfig()
      markSaved()
    }
    setFormulaBusy(false)
  }, [formulaTarget, formulasConfig, gridConfig, gridKey, markFailed, markSaved, markSaving, reloadConfig])

  /**
   * The columns of the selected formula a manual entry could be back-solved into, as the
   * formula bar's dropdown offers them. Only same-row references the inverse engine accepts are
   * listed, and only ones that are still real columns of this grid a value can actually be
   * written into: a calculated column has nowhere to put the answer, and a read-only column
   * would refuse it. When that leaves nothing, the bar hides the selector entirely.
   *
   * «Editable» is judged per column, not per row: a policy that varies by row still offers the
   * column, and one that is editable for no row at all — or for no row yet, on an empty sheet —
   * is decided by the only evidence there is.
   */
  const overrideTargetOptions = useMemo<FormulaTarget[]>(() => {
    if (formulaTarget === NEW_COLUMN) return []
    const formula = selectedFormula.formula
    if (formula === null) return []
    const writable = (column: GridColumn<T>): boolean =>
      typeof column.editable === 'function'
        ? internalRows.length === 0 || internalRows.some((row) => isColumnEditable(column, row))
        : Boolean(column.editable)
    return goalSeekCandidates(formula, formulaTarget).flatMap((field) => {
      if (isFormulaField(field, formulaConfig)) return []
      const index = columns.findIndex((column) => column.field === field)
      const column = columns[index]
      if (index < 0 || column === undefined || !writable(column)) return []
      return [{ field, name: headerOf(index), calculated: false }]
    })
  }, [columns, formulaConfig, formulaTarget, headerOf, internalRows, selectedFormula.formula])

  const setFormulaOverrideTarget = useCallback(async (field: string) => {
    if (formulaTarget === NEW_COLUMN || !gridConfig.canEditFormulas) return
    setFormulaBusy(true)
    setFormulaSaveError(null)
    markSaving()
    const error = await saveSettingRef.current(
      GRID_SETTING_KEYS.formulas,
      withOverrideTarget(formulasConfig, gridKey, formulaTarget, field === SAME_COLUMN ? null : field),
    )
    if (error) {
      markFailed()
      setFormulaSaveError(`Formula could not be saved: ${error.message}`)
    } else {
      await reloadConfig()
      markSaved()
    }
    setFormulaBusy(false)
  }, [formulaTarget, formulasConfig, gridConfig, gridKey, markFailed, markSaved, markSaving, reloadConfig])

  const insertRefFromHeader = useCallback((field: string | undefined, event: React.MouseEvent): boolean => {
    if (!field) return false
    const active = document.activeElement
    if (!(active instanceof HTMLElement) || active.dataset.formulaEditor !== '1') return false
    event.preventDefault()
    event.stopPropagation()
    headerRefInsertedRef.current = true
    setFormulaInsert({ field, nonce: Date.now() })
    return true
  }, [])
  // The click that follows a referencing mousedown must not select the column (and end the edit).
  const headerRefInsertedRef = useRef(false)

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState<{ rowKey: string; field: string; initialValue?: string } | null>(null)
  /** A typed value refused by a `reject` column: the edit waits in this dialog for Retry or Cancel. */
  const [rejection, setRejection] = useState<{
    rowKey: string
    field: string
    text: string
    anchor: { rowKey: string; originalCol: number } | null
    end: { rowKey: string; originalCol: number } | null
  } | null>(null)
  const rejectRetryRef = useRef<HTMLButtonElement | null>(null)
  // The edit's own Enter/Tab moves focus to the next cell after this renders; take it back.
  useEffect(() => {
    if (!rejection) return
    const timer = setTimeout(() => rejectRetryRef.current?.focus(), 0)
    return () => clearTimeout(timer)
  }, [rejection])
  const [isEditingCellFormula, setIsEditingCellFormula] = useState(false)
  const cellFormulaDraftRef = useRef('')
  const stopEditingCell = useCallback((rowKeyVal: string, field?: string) => {
    setEditing((actual) => (actual && actual.rowKey === rowKeyVal && actual.field === field ? null : actual))
  }, [])

  // Seed the draft once per edit session. Re-running when overrides or the formula scope change
  // (live rates, background fills, realtime) would reset the draft to its initial text and wipe
  // what the user is typing.
  const seededEditRef = useRef<typeof editing>(null)
  useEffect(() => {
    if (!editing) {
      seededEditRef.current = null
      setIsEditingCellFormula(false)
      cellFormulaDraftRef.current = ''
      return
    }
    if (seededEditRef.current === editing) return
    seededEditRef.current = editing
    const overrideState = overridesMap.get(overrideKey(editing.field, editing.rowKey))
    const initialIsFormula = editing.initialValue !== undefined
      ? editing.initialValue.startsWith('=')
      : overrideState?.kind === 'formula'
    const initialText = editing.initialValue ?? (
      overrideState?.kind === 'formula'
        ? `=${serializeCellFormula(fromStored(overrideState.formula), cellFormulaScope, formulaLocale)}`
        : ''
    )
    cellFormulaDraftRef.current = initialText
    setIsEditingCellFormula(initialIsFormula)
  }, [cellFormulaScope, editing, formulaLocale, overridesMap])

  const [anchor, setAnchor] = useState<{ rowKey: string; originalCol: number } | null>(() => {
    try { return JSON.parse(readPref('ancla') ?? 'null') } catch { return null }
  })
  const [end, setEnd] = useState<{ rowKey: string; originalCol: number } | null>(() => {
    try { return JSON.parse(readPref('fin') ?? 'null') } catch { return null }
  })
  // Subtotal cells form a selection of their own, a rectangle over the group bands: they hold
  // no row, so they can't join `anchor`/`end`, and selecting one clears the other.
  const [subtotalAnchor, setSubtotalAnchor] = useState<{ groupKey: string; originalCol: number } | null>(null)
  const [subtotalEnd, setSubtotalEnd] = useState<{ groupKey: string; originalCol: number } | null>(null)
  const draggingSubtotalSelection = useRef(false)
  const [selectedFullColumn, setSelectedFullColumn] = useState<number | null>(null)
  const [selectedColumns, setSelectedColumns] = useState<Set<number>>(() => new Set())
  const headerButtonRefs = useRef<Map<number, HTMLButtonElement>>(new Map())
  useEffect(() => { savePref('ancla', JSON.stringify(anchor)) }, [anchor, savePref])
  useEffect(() => { savePref('fin', JSON.stringify(end)) }, [end, savePref])

  useEffect(() => {
    if (!formulaBar && !gridConfig.canEditFormats) return
    const activeCell = subtotalEnd ?? end ?? anchor
    if (!activeCell || activeCell.originalCol === CHECKBOX_COL) return
    const field = columns[activeCell.originalCol]?.field
    if (!field || !formulaTargets.some((target) => target.field === field)) return
    if (formulaBar) setFormulaTarget(field)
    // Always tracked: the toolbar's bold toggle acts on the selected column even with the format bar closed.
    if (gridConfig.canEditFormats) setFormatTarget(field)
  }, [anchor, columns, end, subtotalEnd, formulaBar, gridConfig.canEditFormats, formulaTargets])

  const [copied, setCopied] = useState<{ anchor: { rowKey: string; originalCol: number }; end: { rowKey: string; originalCol: number } } | null>(null)
  const [copiedColumns, setCopiedColumns] = useState<Set<number> | null>(null)
  const [clipboardHasText, setClipboardHasText] = useState(false)
  const draggingSelection = useRef(false)
  const draggingColumnSelection = useRef<{ anchorCol: number } | null>(null)
  const cellRefs = useRef(new Map<string, HTMLTableCellElement>())

  const [history, setHistory] = useState<HistoryState>({ undo: [], redo: [] })
  const historyRef = useRef<HistoryState>(history)
  const historyScopeRef = useRef(gridKey)
  const nextHistoryId = useRef(1)
  const addHistoryTaskRef = useRef<(task: Omit<HistoryTask, 'id'>) => void>(() => { })
  const [historyError, setHistoryError] = useState<string | null>(null)

  useEffect(() => {
    if (historyScopeRef.current === gridKey) return
    historyScopeRef.current = gridKey
    historyRef.current = { undo: [], redo: [] }
    setHistory(historyRef.current)
    setHistoryError(null)
  }, [gridKey])

  // Changes the host made, from the prop or the page around the grid. The page's
  // entry already there at mount belongs to an earlier grid's stack; the prop's is
  // this sheet's own (the change that brought the grid up, like a pivot's source).
  const pageHistoryEntry = useContext(GridHistoryEntryContext)
  const handedEntryIds = useRef(new Set([pageHistoryEntry?.id]))
  useEffect(() => {
    for (const entry of [historyEntry, pageHistoryEntry]) {
      if (!entry || handedEntryIds.current.has(entry.id)) continue
      handedEntryIds.current.add(entry.id)
      addHistoryTaskRef.current({ label: entry.label, undo: entry.undo, redo: entry.redo })
    }
  }, [historyEntry, pageHistoryEntry])

  addHistoryTaskRef.current = (draft) => {
    const task: HistoryTask = { id: nextHistoryId.current++, ...draft }
    const next = pushHistoryTask(historyRef.current, task)
    historyRef.current = next
    setHistory(next)
  }

  const [selected, setSelected] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(readPref('seleccion') ?? '[]')) } catch { return new Set() }
  })
  useEffect(() => { savePref('seleccion', JSON.stringify([...selected])) }, [selected, savePref])

  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [processingBulk, setProcessingBulk] = useState(false)
  const columnDrag = useRef<number | null>(null)
  const columnBandDrag = useRef<readonly number[] | null>(null)
  const resizeDrag = useRef<{ index: number; x0: number; width0: number; width?: number } | null>(null)
  const cancelingEdit = useRef(false)

  useEffect(() => { cancelingEdit.current = false }, [editing])
  const editingElRef = useRef<HTMLTextAreaElement | null>(null)
  // Phones edit in a bar above the keyboard, like Sheets: whichever editor a cell or header uses is
  // portaled into the bar's slot, and the cell shows the text as it is typed.
  const isPhone = useMediaQuery('(max-width: 639px)')
  // Touch has no hover, so a header's reorder handle shows once the header is tapped.
  const [touchedHeader, setTouchedHeader] = useState<number | null>(null)
  const [phoneBarSlot, setPhoneBarSlot] = useState<HTMLDivElement | null>(null)
  const [phoneDraft, setPhoneDraft] = useState<string | null>(null)
  /** While a selection corner dot is dragged its dots stop catching touches, so the cell under the finger is found. */
  const [selectionHandleDragging, setSelectionHandleDragging] = useState(false)
  /** Phones: tapping inside the selection shows a small action bar over it, like Sheets and Excel mobile. */
  const [selectionToolbar, setSelectionToolbar] = useState(false)
  const [selectionToolbarMenu, setSelectionToolbarMenu] = useState<'paste' | 'more' | null>(null)
  const touchTapInSelectionRef = useRef(false)
  useEffect(() => { setPhoneDraft(null) }, [editing, editingHeader])
  // A native listener: React bubbles a portaled editor's events to the cell it came from, not to the bar.
  useEffect(() => {
    if (!phoneBarSlot) return
    const onInput = (e: Event) => {
      const target = e.target as HTMLElement
      setPhoneDraft(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement
        ? target.value
        : target.closest('[contenteditable="true"]')?.textContent ?? '')
    }
    phoneBarSlot.addEventListener('input', onInput)
    return () => phoneBarSlot.removeEventListener('input', onInput)
  }, [phoneBarSlot])
  /** On phones the editor goes to the bar and the cell keeps a read-only copy of its text. */
  const phoneEditor = (editor: React.ReactNode, text: string) => !isPhone ? editor : <>
    <div className="min-h-7 w-full truncate bg-bg px-1 text-sm leading-7" style={{ boxShadow: `inset 0 0 0 2px ${SELECTION_COLOR}` }}>
      {phoneDraft ?? text}
    </div>
    {phoneBarSlot && createPortal(editor, phoneBarSlot)}
  </>
  /** The bar's ✓ and ✕ press Enter and Escape in whichever editor it holds, so each keeps its own rules. */
  const pressInPhoneEditor = (key: 'Enter' | 'Escape') => {
    const field = phoneBarSlot?.querySelector<HTMLElement>('input, textarea, [contenteditable="true"]')
    field?.dispatchEvent(new KeyboardEvent('keydown', { key, bubbles: true, cancelable: true }))
  }

  useEffect(() => {
    if (!editing) return
    const flush = () => { editingElRef.current?.blur() }
    window.addEventListener('beforeunload', flush)
    return () => window.removeEventListener('beforeunload', flush)
  }, [editing])

  const cardRef = useRef<HTMLDivElement>(null)
  const filterRef = useRef<HTMLTableCellElement>(null)

  useEffect(() => {
    if (openFilter === null && activeHeaderIndex === null) return
    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node) {
        const targetEl = event.target as HTMLElement
        if (filterRef.current?.contains(event.target)) return
        if (filterMenuRef.current?.contains(event.target)) return
        if (targetEl.closest?.('[data-grid-filter-modal]')) return
        if (targetEl.closest?.('th')) return
        setOpenFilter(null)
        setActiveHeaderIndex(null)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenFilter(null)
        setActiveHeaderIndex(null)
      }
    }
    document.addEventListener('pointerdown', closeOnOutsidePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [openFilter, activeHeaderIndex])

  const prevGridKeyRef = useRef(gridKey)
  useEffect(() => {
    if (isEmbedded) return
    if (prevGridKeyRef.current !== gridKey) {
      prevGridKeyRef.current = gridKey
      const savedZoom = localStorage.getItem(`tms-grilla-${gridKey}-${zoomPref}`)
      setZoom(savedZoom ? Number(savedZoom) : 100)
      const savedFilters = localStorage.getItem(`tms-grilla-${gridKey}-filtros`)
      try {
        const parsed = JSON.parse(savedFilters ?? 'null') as Record<string, string[]> | null
        setFilters(parsed ? Object.fromEntries(Object.entries(parsed).map(([i, values]) => [i, new Set(values)])) : {})
      } catch {
        setFilters({})
      }
      const savedFilterControls = localStorage.getItem(`tms-grilla-${gridKey}-filtros-visibles`)
      setFilterControlsVisible(savedFilterControls === '1')
      return
    }
    localStorage.setItem(`tms-grilla-${gridKey}-${zoomPref}`, String(zoom))
  }, [zoom, zoomPref, gridKey, isEmbedded])

  useEffect(() => {
    savePref('filtros', JSON.stringify(Object.fromEntries(Object.entries(filters).map(([i, values]) => [i, [...values]]))))
  }, [filters, savePref])
  useEffect(() => { savePref('filtros-visibles', filterControlsVisible ? '1' : '0') }, [filterControlsVisible, savePref])
  useEffect(() => { savePref('busqueda', search) }, [search, savePref])
  useEffect(() => {
    if (!searchRequest) return
    setSearch(searchRequest.text)
    setSearchRowOpen(true)
  }, [searchRequest])
  useEffect(() => { savePref('agrupar', groupPanel ? '1' : '0') }, [groupPanel, savePref])

  const splitHiddenFields = splitSheetView?.hiddenFields
  const visibleColumns = useMemo(() => {
    const splitHidden = new Set(splitHiddenFields ?? [])
    return columnOrder.filter((i) => i < columns.length && !hidden.has(i) && !deviceHidden.has(i) && !groupCollapsed.has(i) && !(columns[i]?.field && splitHidden.has(columns[i]!.field!)))
  }, [columnOrder, hidden, deviceHidden, groupCollapsed, columns, splitHiddenFields])
  const [splitColumnsMenu, setSplitColumnsMenu] = useState<{ left: number; top: number } | null>(null)

  const pendingActivateNewRow = useRef(false)
  const previousRowCountRef = useRef(internalRows.length)
  const previousRowKeysRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    const keys = internalRows.map((row, index) => getRowKey(row, index))
    if (pendingActivateNewRow.current && internalRows.length > previousRowCountRef.current) {
      pendingActivateNewRow.current = false
      // A new row is not always last: a tab that leads with the user's own rows (a
      // investments's Market) shows it near the top. Focus the first row that was not there.
      const previousKeys = previousRowKeysRef.current
      const firstNewIndex = keys.findIndex((key) => !previousKeys.has(key))
      const targetIndex = firstNewIndex >= 0 ? firstNewIndex : internalRows.length - 1
      const targetRow = internalRows[targetIndex]
      if (targetRow) {
        const targetRowKey = keys[targetIndex]!
        const firstCol = visibleColumns[0] ?? 0
        setAnchor({ rowKey: targetRowKey, originalCol: firstCol })
        setEnd({ rowKey: targetRowKey, originalCol: firstCol })
        const cellKey = `${targetRowKey}|${firstCol}`
        requestAnimationFrame(() => {
          const el = cellRefs.current.get(cellKey)
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
            el.focus()
          }
        })
      }
    }
    previousRowCountRef.current = internalRows.length
    previousRowKeysRef.current = new Set(keys)
  }, [internalRows, getRowKey, visibleColumns])


  const frozenBoundaryIndex = useMemo(() => {
    const key = frozenKeyFor(frozenColumnsConfig[gridKey], device)
    if (!key) return null
    const index = indexByKey.get(key)
    return index !== undefined && columnOrder.includes(index) ? index : null
  }, [frozenColumnsConfig, gridKey, indexByKey, columnOrder, device])
  const frozenBoundaryPosition = frozenBoundaryIndex === null ? -1 : columnOrder.indexOf(frozenBoundaryIndex)

  const headerBands = useMemo(() => {
    const bands: { group: string | null; cols: number[] }[] = []
    visibleColumns.forEach((index) => {
      const group = columns[index]?.headerGroup ?? null
      const last = bands[bands.length - 1]
      if (group !== null && last && last.group === group) last.cols.push(index)
      else bands.push({ group, cols: [index] })
    })
    return bands
  }, [visibleColumns, columns])
  const hasTwoHeaderRows = useMemo(() => headerBands.some((b) => b.group !== null), [headerBands])

  const bandEdges = useMemo(() => {
    const left = new Set<number>()
    const right = new Set<number>()
    headerBands.forEach((band, i) => {
      if (band.group === null) return
      left.add(band.cols[0]!)
      if (headerBands[i + 1]?.group == null) right.add(band.cols[band.cols.length - 1]!)
    })
    return { left, right }
  }, [headerBands])

  const columnTree = useMemo(() => {
    const nodes: { group: string | null; cols: number[] }[] = []
    columnOrder.filter((i) => i < columns.length).forEach((index) => {
      const group = columns[index]?.headerGroup ?? null
      const last = nodes[nodes.length - 1]
      if (group !== null && last && last.group === group) last.cols.push(index)
      else nodes.push({ group, cols: [index] })
    })
    return nodes
  }, [columnOrder, columns])

  const [viewportWidth, setViewportWidth] = useState(0)

  const stickyRight = useMemo(() => {
    const map = new Map<number, number>()
    let offset = 0
    for (let pos = visibleColumns.length - 1; pos >= 0; pos--) {
      const index = visibleColumns[pos]
      if (!columns[index!]?.pinRight) break
      map.set(index!, offset)
      offset += widths[index!] ?? columns[index!]!.minWidth ?? 140
    }
    return map
  }, [visibleColumns, columns, widths])

  const flexibleIndex = useMemo(
    () => visibleColumns.find((i) => columns[i]?.flexible && widths[i] === undefined),
    [visibleColumns, columns, widths],
  )
  const flexibleWidth = useMemo(() => {
    if (flexibleIndex === undefined || isEmbedded || fullScreen) return undefined
    const own = columns[flexibleIndex]!.minWidth ?? 140
    if (viewportWidth === 0) return own
    const rest = visibleColumns
      .filter((i) => i !== flexibleIndex)
      .reduce((sum, i) => sum + (widths[i] ?? columns[i]!.minWidth ?? 140), 0)
    const available = viewportWidth / (zoom / 100 / textScale) - rest - (isEmbedded ? 0 : 38)
    return Math.max(own, Math.floor(available))
  }, [flexibleIndex, visibleColumns, columns, widths, viewportWidth, zoom, textScale, isEmbedded, fullScreen])

  /**
   * The width an `expand` column needs for its widest value, its total included.
   * Expand is also what a number column does when no wrap mode was chosen.
   */
  const expandWidths = useMemo(() => {
    const map = new Map<number, number>()
    columns.forEach((column, index) => {
      const texts = internalRows.map((row) => cellText(column, row))
      const isNumber = column.cellType === 'number' || column.dataFormat?.type === 'number' || (
        !column.cellType && !column.cellRenderer && !column.dropdown && (() => {
          const filled = texts.filter((text) => text !== '').length
          const numbers = internalRows.filter((row) => {
            const value = readValue(column, row)
            return typeof value === 'number' && Number.isFinite(value)
          }).length
          return filled > 0 && numbers * 2 > filled
        })()
      )
      const headingRule = resolveHeaderRule(column.headerFormat ?? column.dataFormat, alignmentConfig, column.cellType ?? (isNumber ? 'number' : 'text'), column.align, column.wrapMode)
      if (ruleWrapMode(headingRule, 'text') === 'expand') map.set(index, Math.ceil(measureText(headerOf(index), renderedCellFontSize(), true) + 8 + MEASURE_MARGIN + (sort?.index === index ? 12 : 0)))
      if (column.wrapMode !== 'expand' && !(isNumber && column.wrapMode === undefined)) return
      const bold = Boolean(column.dataFormat?.bold)
      if (isNumber) texts.push(aggregateCellText(column, undefined, true, '', internalRows as Record<string, unknown>[], formats))
      const widest = texts.reduce((max, text) => (text ? Math.max(max, measureText(text, CELL_FONT_SIZE, bold)) : max), 0)
      if (widest > 0) map.set(index, Math.max(map.get(index) ?? 0, Math.ceil(widest + MEASURE_MARGIN + 2 * CELL_PAD)))
    })
    return map
  }, [columns, internalRows, formats, alignmentConfig, headerOf, sort])

  const widthOf = useCallback((index: number): number | undefined => {
    const column = columns[index]
    if (!column) return undefined
    const base = widths[index] ?? (index === flexibleIndex ? flexibleWidth : column.minWidth ?? 140)
    const needed = expandWidths.get(index)
    return needed !== undefined && base !== undefined ? Math.max(base, needed) : base
  }, [columns, widths, flexibleIndex, flexibleWidth, expandWidths])

  // A fixed-layout table set to w-full divides the container between its columns, so on a phone
  // every column is crushed to a few characters. Flooring the table at the width its columns
  // actually need keeps spreadsheet geometry at any screen size and lets the surrounding
  // overflow-auto container scroll horizontally instead. It is the table's exact width, not
  // a floor: a table wider than its columns hands the slack back to them, so a column
  // dragged narrower springs back to where it was.
  const naturalTableWidth = useMemo(
    () => visibleColumns.reduce((total, index) => total + (widthOf(index) ?? 140), isEmbedded ? 0 : 38),
    [visibleColumns, widthOf, isEmbedded],
  )

  const stickyLeft = useMemo(() => {
    const map = new Map<number, number>()
    if (frozenBoundaryPosition < 0) return map
    let offset = isEmbedded ? 0 : 38
    for (const index of visibleColumns) {
      if (columnOrder.indexOf(index) > frozenBoundaryPosition) break
      map.set(index, offset)
      offset += widthOf(index) ?? 140
    }
    return map
  }, [widthOf, columnOrder, isEmbedded, frozenBoundaryPosition, visibleColumns])

  const firstStickyRight = useMemo(() => {
    const pinned = [...stickyRight.keys()]
    return pinned.length > 0 ? pinned[pinned.length - 1] : null
  }, [stickyRight])
  const lastStickyLeft = useMemo(() => {
    const pinned = [...stickyLeft.keys()]
    return pinned.length > 0 ? pinned[pinned.length - 1] : null
  }, [stickyLeft])

  const bandEdgeShadows = useCallback((index: number) => [
    bandEdges.left.has(index) || index === firstStickyRight ? 'inset 1px 0 0 0 var(--grid-line-strong)' : '',
    bandEdges.right.has(index) || index === lastStickyLeft ? 'inset -1px 0 0 0 var(--grid-line-strong)' : '',
  ].filter(Boolean), [bandEdges, firstStickyRight, lastStickyLeft])

  const orderedKeyed = useMemo(() => {
    const keyed = internalRows.map((row, i) => ({ row, key: getRowKey(row, i) }))
    if (!effectiveRowOrder || effectiveRowOrder.length === 0) return keyed
    const byKey = new Map(keyed.map((entry) => [entry.key, entry]))
    return resolveRowOrder(keyed.map((entry) => entry.key), effectiveRowOrder)
      .map((key) => byKey.get(key))
      .filter((entry): entry is { row: T; key: string } => entry !== undefined)
  }, [internalRows, getRowKey, effectiveRowOrder])
  const orderedRows = useMemo(() => orderedKeyed.map((k) => k.row), [orderedKeyed])

  const filtered = useMemo(() => {
    if (isEmbedded) return orderedRows
    // Search always spans every column: selecting a column must not hide the rows it found.
    const searchCols = columns
    return orderedRows.filter((row) => {
      if (!rowMatchesSearch(search, row, searchCols, (column) => cellText(column, row), cellError)) return false
      for (const [indexStr, selection] of Object.entries(filters)) {
        const index = Number(indexStr)
        const text = cellText(columns[index]!, row) || '(vacío)'
        if (!selection.has(text) && !selection.has('(empty)')) return false
      }
      return true
    })
  }, [isEmbedded, orderedRows, columns, search, filters, cellError])

  const sorted = useMemo(() => {
    if (!sort) return filtered
    const column = columns[sort.index]
    return [...filtered].sort((a, b) => sort.dir * compareValues(readValue(column!, a), readValue(column!, b)))
  }, [filtered, sort, columns])

  const uniqueValues = useCallback((index: number) => {
    const column = columns[index]
    // Search always spans every column: selecting a column must not hide the rows it found.
    const searchCols = columns
    const counts = new Map<string, number>()
    internalRows.forEach((row) => {
      if (!rowMatchesSearch(search, row, searchCols, (c) => cellText(c, row), cellError)) return
      for (const [indexStr, selection] of Object.entries(filters)) {
        if (Number(indexStr) === index) continue
        const text = cellText(columns[Number(indexStr)]!, row) || '(vacío)'
        if (!selection.has(text) && !selection.has('(empty)')) return
      }
      const value = cellText(column!, row) || '(vacío)'
      counts.set(value, (counts.get(value) ?? 0) + 1)
    })
    return [...counts.entries()].sort(([a], [b]) => compareFilterMenuValues(a, b))
  }, [columns, internalRows, filters, search, cellError])

  type GroupTreeNode = {
    label: string
    key: string
    level: number
    count: number
    children?: GroupTreeNode[]
  }

  const groupTree = useMemo<GroupTreeNode[]>(() => {
    if (!groupPanel) return []
    const [c1, c2] = groupColumns
    if (c1 === null && c2 === null) return []

    const baseRows = sort
      ? [...orderedRows].sort((a, b) => sort.dir * compareValues(readValue(columns[sort.index]!, a), readValue(columns[sort.index]!, b)))
      : orderedRows

    const col1 = c1 !== null && c1 < columns.length ? columns[c1] : null
    const col2 = c2 !== null && c2 < columns.length ? columns[c2] : null

    // Search always spans every column: selecting a column must not hide the rows it found.
    const searchCols = columns

    if (col1 && col2 && c1 !== c2) {
      const mapL1 = new Map<string, { count: number; mapL2: Map<string, number> }>()
      baseRows.forEach((row) => {
        if (!rowMatchesSearch(search, row, searchCols, (c) => cellText(c, row), cellError)) return
        for (const [indexStr, selection] of Object.entries(filters)) {
          if (Number(indexStr) === c1 || Number(indexStr) === c2) continue
          const text = cellText(columns[Number(indexStr)]!, row) || '(vacío)'
          if (!selection.has(text) && !selection.has('(empty)')) return
        }
        const val1 = cellText(col1, row) || '(vacío)'
        const val2 = cellText(col2, row) || '(vacío)'
        if (!mapL1.has(val1)) mapL1.set(val1, { count: 0, mapL2: new Map() })
        const entry1 = mapL1.get(val1)!
        entry1.count++
        entry1.mapL2.set(val2, (entry1.mapL2.get(val2) ?? 0) + 1)
      })

      const sortedL1 = [...mapL1.entries()].sort(([a], [b]) => compareGroupLabels(a, b))
      return sortedL1.map(([val1, data]) => {
        const sortedL2 = [...data.mapL2.entries()].sort(([a], [b]) => compareGroupLabels(a, b))
        return {
          label: val1,
          key: val1,
          level: 0,
          count: data.count,
          children: sortedL2.map(([val2, cnt]) => ({
            label: val2,
            key: `${val1} > ${val2}`,
            level: 1,
            count: cnt,
          })),
        }
      })
    } else {
      const col = col1 || col2
      if (!col) return []
      const counts = new Map<string, number>()
      baseRows.forEach((row) => {
        if (!rowMatchesSearch(search, row, searchCols, (c) => cellText(c, row), cellError)) return
        for (const [indexStr, selection] of Object.entries(filters)) {
          if (Number(indexStr) === (col === col1 ? c1 : c2)) continue
          const text = cellText(columns[Number(indexStr)]!, row) || '(vacío)'
          if (!selection.has(text) && !selection.has('(empty)')) return
        }
        const val = cellText(col, row) || '(vacío)'
        counts.set(val, (counts.get(val) ?? 0) + 1)
      })
      const sortedVals = [...counts.entries()].sort(([a], [b]) => compareGroupLabels(a, b))
      return sortedVals.map(([val, cnt]) => ({
        label: val,
        key: val,
        level: 0,
        count: cnt,
      }))
    }
  }, [groupPanel, groupColumns, columns, orderedRows, sort, filters, search, cellError])

  const columnType = useMemo(() => {
    const sample = sorted.slice(0, 50)
    const map = new Map<number, CellType>()
    columns.forEach((column, index) => {
      if (column.cellType) { map.set(index, column.cellType); return }
      if (column.cellRenderer || column.dropdown) { map.set(index, 'chip'); return }
      let total = 0
      let numbers = 0
      let dates = 0
      for (const row of sample) {
        const value = readValue(column, row)
        if (typeof value === 'boolean') continue
        const text = cellText(column, row)
        if (text === '') continue
        total++
        if (typeof value === 'number' && Number.isFinite(value)) numbers++
        else if (DATE_REGEX.test(text)) dates++
      }
      if (total === 0) { map.set(index, 'text'); return }
      if (numbers * 2 > total) { map.set(index, 'number'); return }
      if (dates * 2 > total) { map.set(index, 'date'); return }
      map.set(index, 'text')
    })
    return map
  }, [columns, sorted])

  // Chart panel: text/date columns give categories, number columns give series.
  const chartCategoryColumns = useMemo(() => visibleColumns
    .filter((index) => {
      const column = columns[index]
      if (!column || column.dataFormat?.type === 'number') return false
      const kind = column.dataFormat?.type === 'date' ? 'date' : columnType.get(index)
      return kind === 'text' || kind === 'date' || kind === 'chip'
    })
    .map((index) => ({
      index,
      key: columnKey(columns[index]?.field, index),
      label: headerOf(index),
      isDate: columns[index]?.dataFormat?.type === 'date' || columnType.get(index) === 'date',
    })), [visibleColumns, columns, columnType, headerOf])
  const chartNumberColumns = useMemo(() => visibleColumns
    .filter((index) => columns[index] && (columns[index].dataFormat?.type === 'number' || columnType.get(index) === 'number'))
    .map((index) => {
      // "Average" alone is ambiguous when several groups have one: keep the group.
      const group = columns[index]?.headerGroup
      return { index, key: columnKey(columns[index]?.field, index), label: group ? group + ' · ' + headerOf(index) : headerOf(index) }
    }), [visibleColumns, columns, columnType, headerOf])

  const numberAdjustments = useMemo(() => {
    const map = new Map<number, { padding: number; fontSize: number; reduced: boolean; minWidth: number }>()
    columns.forEach((column, index) => {
      const isShrink = column.wrapMode === 'shrink'
      if (!isShrink) return
      // Widest by rendered width, not character count: "mon 5/10" outgrows "tue 6/10".
      let longest = ''
      let longestWidth = 0
      const base = renderedCellFontSize()
      sorted.forEach((row) => {
        const text = cellText(column, row)
        if (text === '') return
        const width = measureText(text, base)
        if (width > longestWidth) { longest = text; longestWidth = width }
      })
      if (longest === '') return
      map.set(index, fitShrunkText(longest, widthOf(index) ?? (column.minWidth ?? 140)))
    })
    return map
  }, [columns, sorted, widthOf])

  /** Style for a subtotal or totals cell, shrunk on its own text like a shrink cell. */
  const aggregateFitStyle = useCallback((index: number, text: string): React.CSSProperties => {
    const column = columns[index]
    if (!column || text === '') return {}
    const isShrink = column.wrapMode === 'shrink'
    if (!isShrink) return {}
    const fit = fitShrunkText(text, widthOf(index) ?? (column.minWidth ?? 140))
    return {
      whiteSpace: 'nowrap',
      ...(fit.padding !== CELL_PAD ? { paddingLeft: fit.padding, paddingRight: fit.padding } : {}),
      ...(fit.reduced ? { fontSize: fit.fontSize } : {}),
    }
  }, [columns, widthOf])

  const minWidthOf = useCallback(
    (index: number) => Math.max(MIN_COLUMN_WIDTH, numberAdjustments.get(index)?.minWidth ?? 0),
    [numberAdjustments],
  )

  // A group's header, and with subtotals below or blank rows on, what closes the group.
  type VisualItem<T> =
    | { type: 'row'; row: T; index: number }
    | { type: 'group' | 'subtotal'; label: string; count: number; collapsed: boolean; groupRows: T[]; level?: number; groupKey?: string }
    | { type: 'blank'; label: string; groupKey: string; level: number }

  const closeGroup = useCallback((items: VisualItem<T>[], group: { label: string; count: number; collapsed: boolean; groupRows: T[]; level: number; groupKey: string }) => {
    const levelCol = group.level === 0 ? groupColumns[0] ?? groupColumns[1] : groupColumns[1]
    const levelField = levelCol !== null ? columns[levelCol]?.field : undefined
    const subtotalled = !(levelField && noSubtotalFields?.includes(levelField))
    if (subtotalsAtBottom && subtotalled) items.push({ type: 'subtotal', ...group })
    if (groupBlankRows) items.push({ type: 'blank', label: group.label, groupKey: group.groupKey, level: group.level })
  }, [subtotalsAtBottom, groupBlankRows, groupColumns, columns, noSubtotalFields])

  const baseVisualItems = useMemo(() => {
    const items: VisualItem<T>[] = []
    const gCol1 = groupColumns[0] !== null && groupColumns[0]! < columns.length ? columns[groupColumns[0]!] : null
    const gCol2 = groupColumns[1] !== null && groupColumns[1]! < columns.length ? columns[groupColumns[1]!] : null

    if (isEmbedded || (!gCol1 && !gCol2 && !groupBy)) {
      sorted.forEach((row) => {
        items.push({ type: 'row', row, index: internalRows.indexOf(row) })
      })
    } else if (gCol1 && gCol2 && groupColumns[0] !== groupColumns[1]) {
      const mapL1 = new Map<string, Map<string, T[]>>()
      sorted.forEach((row) => {
        const val1 = cellText(gCol1, row) || ''
        const val2 = cellText(gCol2, row) || ''
        if (!mapL1.has(val1)) mapL1.set(val1, new Map())
        const mapL2 = mapL1.get(val1)!
        mapL2.set(val2, [...(mapL2.get(val2) ?? []), row])
      })
      const sortedL1 = [...mapL1.entries()].sort(([a], [b]) => compareGroupKeys(a, b))
      sortedL1.forEach(([val1, mapL2]) => {
        const l1GroupRows: T[] = []
        mapL2.forEach((rList) => l1GroupRows.push(...rList))
        const keyL1 = val1
        const collapsedL1 = collapsedGroups.has(keyL1)
        // One second-level value across the whole parent: a single group, "Parent Child".
        if (groupMergeSingle && mapL2.size === 1) {
          const label = `${val1} ${[...mapL2.keys()][0]}`.trim()
          items.push({ type: 'group', label, count: l1GroupRows.length, collapsed: collapsedL1, groupRows: l1GroupRows, level: 0, groupKey: keyL1 })
          if (!collapsedL1) l1GroupRows.forEach((row) => items.push({ type: 'row', row, index: internalRows.indexOf(row) }))
          closeGroup(items, { label, count: l1GroupRows.length, collapsed: collapsedL1, groupRows: l1GroupRows, level: 0, groupKey: keyL1 })
          return
        }
        items.push({ type: 'group', label: val1, count: l1GroupRows.length, collapsed: collapsedL1, groupRows: l1GroupRows, level: 0, groupKey: keyL1 })

        if (!collapsedL1) {
          const sortedL2 = [...mapL2.entries()].sort(([a], [b]) => {
            const rankA = groupRank.get(`${val1} > ${a}`) ?? Infinity
            const rankB = groupRank.get(`${val1} > ${b}`) ?? Infinity
            return rankA === rankB ? compareGroupLabels(a, b) : rankA - rankB
          })
          sortedL2.forEach(([val2, l2GroupRows]) => {
            const keyL2 = `${val1} > ${val2}`
            const collapsedL2 = collapsedGroups.has(keyL2)
            items.push({ type: 'group', label: val2, count: l2GroupRows.length, collapsed: collapsedL2, groupRows: l2GroupRows, level: 1, groupKey: keyL2 })
            if (!collapsedL2) {
              l2GroupRows.forEach((row) => {
                items.push({ type: 'row', row, index: internalRows.indexOf(row) })
              })
            }
            closeGroup(items, { label: val2, count: l2GroupRows.length, collapsed: collapsedL2, groupRows: l2GroupRows, level: 1, groupKey: keyL2 })
          })
        }
        closeGroup(items, { label: val1, count: l1GroupRows.length, collapsed: collapsedL1, groupRows: l1GroupRows, level: 0, groupKey: keyL1 })
      })
    } else {
      const gCol = gCol1 || gCol2
      const map = new Map<string, T[]>()
      sorted.forEach((row) => {
        const label = gCol
          ? (cellText(gCol, row) || '')
          : String((row as Record<string, unknown>)[groupBy!] ?? '')
        map.set(label, [...(map.get(label) ?? []), row])
      })
      const sortedGroups = [...map.entries()].sort(([a], [b]) => compareGroupKeys(a, b))
      sortedGroups.forEach(([label, groupRows]) => {
        const collapsed = collapsedGroups.has(label)
        items.push({ type: 'group', label, count: groupRows.length, collapsed, groupRows, level: 0, groupKey: label })
        if (!collapsed) {
          groupRows.forEach((row) => {
            items.push({ type: 'row', row, index: internalRows.indexOf(row) })
          })
        }
        closeGroup(items, { label, count: groupRows.length, collapsed, groupRows, level: 0, groupKey: label })
      })
    }
    return items
  }, [isEmbedded, sorted, groupBy, groupColumns, columns, collapsedGroups, internalRows, closeGroup, groupMergeSingle, compareGroupKeys, groupRank])

  /**
   * Outline: an open group loses its header band; its value is written once, in its
   * grouping column on its first row, beside a −/+ button. A closed group keeps a band,
   * since it has no row to carry the value.
   */
  const { visualItems: allVisualItems, outlineMarks } = useMemo(() => {
    const marks = new Map<T, { col: number; key: string; label: string }[]>()
    const headersOnly = (items: VisualItem<T>[]) => (movingGroup ? items.filter((item) => item.type === 'group') : items)
    if (!groupOutline) return { visualItems: headersOnly(baseVisualItems), outlineMarks: marks }
    const colOf = (level: number) => (level === 0 ? groupColumns[0] ?? groupColumns[1] : groupColumns[1])
    const out: VisualItem<T>[] = []
    let pending: Extract<VisualItem<T>, { type: 'group' | 'subtotal' }>[] = []
    for (const item of baseVisualItems) {
      if (item.type === 'group' && !item.collapsed && colOf(item.level ?? 0) !== null) { pending.push(item); continue }
      if (item.type === 'row' && pending.length > 0) {
        marks.set(item.row, pending.map((group) => ({ col: colOf(group.level ?? 0)!, key: group.groupKey ?? group.label, label: group.label })))
        pending = []
      } else if (item.type !== 'row' && pending.length > 0) {
        out.push(...pending)
        pending = []
      }
      out.push(item)
    }
    out.push(...pending)
    return { visualItems: headersOnly(out), outlineMarks: marks }
  }, [baseVisualItems, groupOutline, groupColumns, movingGroup])
  const outlineColumns = useMemo(() => new Set(groupOutline ? groupColumns.filter((c): c is number => c !== null) : []), [groupOutline, groupColumns])
  // Top-level groups closed by a totals row of their own, where a group dropped "after" lands below.
  const groupsWithFooter = useMemo(() => new Set(allVisualItems.flatMap((item) =>
    item.type === 'subtotal' && (item.level ?? 0) === 0 ? [item.groupKey ?? item.label] : [])), [allVisualItems])
  // The innermost group each row sits in: dragging within it reorders, across groups re-files.
  const rowGroupKey = useMemo(() => {
    const out = new Map<T, string>()
    let current = ''
    for (const item of baseVisualItems) {
      if (item.type === 'group') current = item.groupKey ?? item.label
      else if (item.type === 'row') out.set(item.row, current)
    }
    return out
  }, [baseVisualItems])

  const groupInfo = useMemo(() => {
    const gCol1 = groupColumns[0] !== null && groupColumns[0]! < columns.length ? columns[groupColumns[0]!] : null
    const gCol2 = groupColumns[1] !== null && groupColumns[1]! < columns.length ? columns[groupColumns[1]!] : null

    let l1Name = ''
    let l2Name = ''
    const l1Keys = new Set<string>()
    const l2Keys = new Set<string>()
    const l2Pairs = new Map<string, [string, string]>()

    if (gCol1 && gCol2 && groupColumns[0] !== groupColumns[1]) {
      l1Name = headerOf(groupColumns[0]!)
      l2Name = headerOf(groupColumns[1]!)
      sorted.forEach((row) => {
        const val1 = cellText(gCol1, row) || ''
        const val2 = cellText(gCol2, row) || ''
        l1Keys.add(val1)
        l2Keys.add(`${val1} > ${val2}`)
        l2Pairs.set(`${val1} > ${val2}`, [val1, val2])
      })
    } else {
      const gCol = gCol1 || gCol2
      if (gCol) {
        const colIdx = groupColumns[0] !== null ? groupColumns[0]! : groupColumns[1]!
        l1Name = headerOf(colIdx)
        sorted.forEach((row) => {
          const val1 = cellText(gCol, row) || ''
          l1Keys.add(val1)
        })
      } else if (groupBy) {
        const colIdx = columns.findIndex((c) => c.field === groupBy)
        l1Name = colIdx !== -1 ? headerOf(colIdx) : String(groupBy)
        sorted.forEach((row) => {
          const val1 = String((row as Record<string, unknown>)[groupBy] ?? '')
          l1Keys.add(val1)
        })
      }
    }

    const level1Keys = Array.from(l1Keys)
    const level2Keys = Array.from(l2Keys)

    return {
      hasGrouping: l1Name !== '' || level1Keys.length > 0,
      hasLevel2: l2Name !== '',
      level1Keys,
      level2Keys,
      level2Pairs: l2Pairs,
      level1Name: l1Name,
      level2Name: l2Name,
    }
  }, [groupColumns, columns, groupBy, sorted, headerOf])

  const isL1AllCollapsed = useMemo(() => {
    if (groupInfo.level1Keys.length === 0) return false
    return groupInfo.level1Keys.every((k) => collapsedGroups.has(k))
  }, [groupInfo.level1Keys, collapsedGroups])

  const isL2AllCollapsed = useMemo(() => {
    if (groupInfo.level2Keys.length === 0) return false
    return groupInfo.level2Keys.every((k) => collapsedGroups.has(k))
  }, [groupInfo.level2Keys, collapsedGroups])

  const toggleL1Collapse = useCallback(() => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (isL1AllCollapsed) {
        groupInfo.level1Keys.forEach((k) => next.delete(k))
      } else {
        groupInfo.level1Keys.forEach((k) => next.add(k))
      }
      return next
    })
  }, [isL1AllCollapsed, groupInfo.level1Keys])

  const toggleL2Collapse = useCallback(() => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (isL2AllCollapsed) {
        groupInfo.level2Keys.forEach((k) => next.delete(k))
      } else {
        groupInfo.level2Keys.forEach((k) => next.add(k))
      }
      return next
    })
  }, [isL2AllCollapsed, groupInfo.level2Keys])

  const keyOf = useCallback((r: T) => getRowKey(r, internalRows.indexOf(r)), [getRowKey, internalRows])

  const visualItemKey = useCallback((item: VisualItem<T>) => item.type === 'row'
    ? keyOf(item.row)
    : `${item.type}-${item.groupKey ?? item.label}`, [keyOf])

  const renderedRows = useMemo(() => {
    return allVisualItems
      .filter((item): item is { type: 'row'; row: T; index: number } => item.type === 'row')
      .map((item) => item.row)
  }, [allVisualItems])

  /** Points for the chart panel from the rows on screen; rows sharing a category add up. */
  const buildChartPoints = useCallback((categoryIndex: number, seriesIndexes: number[]): ChartPoint[] => {
    const categoryColumn = columns[categoryIndex]
    if (!categoryColumn) return []
    const byCategory = new Map<string, ChartPoint>()
    const firstRow = new Map<string, T>()
    for (const row of renderedRows) {
      const category = cellText(categoryColumn, row)
      if (category === '') continue
      let point = byCategory.get(category)
      if (!point) {
        point = { category, values: seriesIndexes.map(() => 0) }
        byCategory.set(category, point)
        firstRow.set(category, row)
      }
      seriesIndexes.forEach((index, s) => {
        const column = columns[index]
        const value = column ? readValue(column, row) : undefined
        const number = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN
        if (Number.isFinite(number)) point!.values[s] = (point!.values[s] ?? 0) + number
      })
    }
    // Labels read like the source column: its own formatter over the summed value.
    for (const point of byCategory.values()) {
      const row = firstRow.get(point.category)!
      point.labels = seriesIndexes.map((index, s) => {
        const formatter = columns[index]?.valueFormatter
        return formatter ? formatter({ value: point.values[s], data: row }) : undefined
      })
    }
    const points = [...byCategory.values()]
    // Date categories carry their day, for the chart's Start / End / Frequency filters.
    if (columnType.get(categoryIndex) === 'date' || categoryColumn.dataFormat?.type === 'date') {
      for (const point of points) {
        const day = chartIsoDay(readValue(categoryColumn, firstRow.get(point.category)!))
        if (day) point.date = day
      }
    }
    // A personal view (the Numtable library) lists its newest day first, but a
    // line over dates reads left to right: order by the raw ISO date instead.
    if (personalView && columnType.get(categoryIndex) === 'date') {
      const raw = (point: ChartPoint) => String(readValue(categoryColumn, firstRow.get(point.category)!) ?? '')
      points.sort((left, right) => raw(left).localeCompare(raw(right)))
    }
    return points
  }, [columns, columnType, personalView, renderedRows])

  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => {
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight
      setFabBottom(Math.max(8, viewportHeight - el.getBoundingClientRect().bottom + 12))
      // An on-screen keyboard shrinks the visual viewport well below the layout one.
      setKeyboardOpen(viewportHeight < window.innerHeight * 0.8)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    observer.observe(document.body)
    window.addEventListener('resize', measure)
    window.visualViewport?.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
      window.visualViewport?.removeEventListener('resize', measure)
    }
  }, [loading])
  // Like Google Sheets: dragging a row or column near the grid's edge scrolls it, so it can be
  // dropped somewhere not currently visible. Rows scroll vertically, columns horizontally.
  useEffect(() => {
    const EDGE = 48
    let point: { x: number; y: number } | null = null
    let frame = 0
    const speed = (distance: number) => (distance >= EDGE ? 0 : Math.ceil(((EDGE - Math.max(0, distance)) / EDGE) * 20))
    const tick = () => {
      frame = 0
      const el = containerRef.current
      const rows = rowDrag.current !== null
      const cols = columnDrag.current !== null
      if (!el || !point || (!rows && !cols)) { point = null; return }
      const rect = el.getBoundingClientRect()
      if (rows) {
        const top = rect.top + (el.querySelector('thead')?.getBoundingClientRect().height ?? 0)
        el.scrollTop += speed(rect.bottom - point.y) - speed(point.y - top)
      }
      if (cols) el.scrollLeft += speed(rect.right - point.x) - speed(point.x - rect.left)
      frame = requestAnimationFrame(tick)
    }
    const track = (e: DragEvent | PointerEvent) => {
      if (rowDrag.current === null && columnDrag.current === null) return
      point = { x: e.clientX, y: e.clientY }
      if (!frame) frame = requestAnimationFrame(tick)
    }
    window.addEventListener('dragover', track)
    window.addEventListener('pointermove', track)
    return () => {
      window.removeEventListener('dragover', track)
      window.removeEventListener('pointermove', track)
      cancelAnimationFrame(frame)
    }
  }, [])
  usePinchZoom(containerRef, zoom, isEmbedded ? undefined : setZoom)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(600)

  const [measuredHeights, setMeasuredHeights] = useState<Record<string, number>>({})
  const rowHeightsRef = useRef<Map<string, number>>(new Map())
  const pendingHeightUpdateRef = useRef(false)

  const rowRefCallback = useCallback((key: string, el: HTMLTableRowElement | null) => {
    if (!el) return
    const h = el.offsetHeight
    const prev = rowHeightsRef.current.get(key)
    if (h > 0 && (!prev || Math.abs(prev - h) >= 2)) {
      rowHeightsRef.current.set(key, h)
      if (!pendingHeightUpdateRef.current) {
        pendingHeightUpdateRef.current = true
        requestAnimationFrame(() => {
          pendingHeightUpdateRef.current = false
          setMeasuredHeights((existing) => {
            const next = { ...existing }
            let changed = false
            for (const [k, v] of rowHeightsRef.current.entries()) {
              if (!next[k] || Math.abs(next[k] - v) >= 2) {
                next[k] = v
                changed = true
              }
            }
            return changed ? next : existing
          })
        })
      }
    }
  }, [])

  // Viewport memory: the first visible row and column, per gridKey, restored on the next visit.
  const viewportGeometryRef = useRef<() => { rowKeys: string[]; rowSizes: number[]; colKeys: string[]; colSizes: number[]; scale: number }>(() => ({ rowKeys: [], rowSizes: [], colKeys: [], colSizes: [], scale: 1 }))
  viewportGeometryRef.current = () => {
    const rowKeys: string[] = []
    const rowSizes: number[] = []
    for (const item of allVisualItems) {
      const k = visualItemKey(item)
      rowKeys.push(k)
      rowSizes.push(rowHeightsDraft[k] ?? measuredHeights[k] ?? rowHeightsRef.current.get(k) ?? 36)
    }
    return {
      rowKeys,
      rowSizes,
      colKeys: visibleColumns.map((index) => columnKey(columns[index]?.field, index)),
      colSizes: visibleColumns.map((index) => widthOf(index) ?? 140),
      scale: zoom / 100 / textScale,
    }
  }
  const viewportSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const viewportRestoredFor = useRef<string | null>(null)
  const viewportRestoring = useRef(false)
  const saveViewportSoon = useCallback(() => {
    if (isEmbedded || viewportRestoring.current || viewportRestoredFor.current !== gridKey) return
    if (viewportSaveTimer.current) clearTimeout(viewportSaveTimer.current)
    viewportSaveTimer.current = setTimeout(() => {
      const el = containerRef.current
      if (!el) return
      const g = viewportGeometryRef.current()
      if (g.rowKeys.length === 0) return
      const rowIndex = firstVisibleIndex(g.rowSizes, el.scrollTop / g.scale)
      const colIndex = el.scrollLeft > 0 ? firstVisibleIndex(g.colSizes, el.scrollLeft / g.scale) : 0
      const atBottom = el.scrollTop > 0 && el.scrollTop + el.clientHeight >= el.scrollHeight - 2
      saveGridViewport(gridKey, { rowKey: g.rowKeys[rowIndex] ?? null, rowIndex, colKey: g.colKeys[colIndex] ?? null, colIndex, atBottom })
    }, 250)
  }, [gridKey, isEmbedded])
  useEffect(() => () => { if (viewportSaveTimer.current) clearTimeout(viewportSaveTimer.current) }, [])

  const handleGridScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop)
    setSelectionToolbar(false)
    saveViewportSoon()
  }, [saveViewportSoon])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const updateSize = () => { setViewportHeight(el.clientHeight); setViewportWidth(el.clientWidth) }
    updateSize()
    const ro = new ResizeObserver(updateSize)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth)
    if (el.scrollLeft > maxScroll) el.scrollLeft = maxScroll
  }, [columns.length])

  // Keyed by the sort's value, not its identity: `sort` is rebuilt whenever the columns are,
  // and that must not throw the user back to the top while they scroll.
  const sortKey = sort ? `${sort.index}:${sort.dir}` : ''
  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTop = 0
    setScrollTop(0)
  }, [search, filters, sortKey])

  // Back to where the user was, once this grid's rows are on screen. A row or column that has
  // since been deleted or trashed resolves to its nearest surviving neighbour.
  const visualItemCount = allVisualItems.length
  const [viewportWaitTick, setViewportWaitTick] = useState(0)
  const viewportWaitStart = useRef<{ gridKey: string; at: number } | null>(null)
  useEffect(() => {
    if (isEmbedded || loading || visualItemCount === 0 || viewportRestoredFor.current === gridKey) return
    const el = containerRef.current
    const saved = readGridViewport(gridKey)
    if (!el || !saved) {
      viewportRestoredFor.current = gridKey
      return
    }
    const g = viewportGeometryRef.current()
    // Rows can arrive in stages (a placeholder first, the full list later). While the anchor
    // row is missing and the grid is still shorter than where the user was, wait for more rows;
    // only after a few seconds is the row taken as deleted and its nearest neighbour used.
    const anchorPresent = saved.rowKey !== null && g.rowKeys.includes(saved.rowKey)
    if (!anchorPresent && g.rowKeys.length <= saved.rowIndex) {
      if (viewportWaitStart.current?.gridKey !== gridKey) viewportWaitStart.current = { gridKey, at: Date.now() }
      const remaining = 4000 - (Date.now() - viewportWaitStart.current.at)
      if (remaining > 0) {
        const timer = setTimeout(() => setViewportWaitTick((tick) => tick + 1), remaining + 20)
        return () => clearTimeout(timer)
      }
    }
    viewportRestoredFor.current = gridKey
    const rowIndex = resolveAnchorIndex(g.rowKeys, saved.rowKey, saved.rowIndex)
    const colIndex = resolveAnchorIndex(g.colKeys, saved.colKey, saved.colIndex)
    const top = rowIndex > 0 ? offsetOf(g.rowSizes, rowIndex) * g.scale : 0
    const left = colIndex > 0 ? offsetOf(g.colSizes, colIndex) * g.scale : 0
    // The table keeps growing for a few frames (row measurement, column widths, late filter
    // prefs resetting scroll), so the position is re-applied until it has held for a while.
    let frame = 0
    let raf = 0
    const apply = () => {
      // Rows off screen have estimated heights until they render, so the anchor's offset is
      // recomputed from the latest measurements every frame until it stops moving.
      const latest = viewportGeometryRef.current()
      const rowTop = rowIndex > 0 ? offsetOf(latest.rowSizes, rowIndex) * latest.scale : top
      // Someone who was looking at the last rows sees the last rows, whatever the window height.
      const target = saved.atBottom ? Math.max(rowTop, el.scrollHeight - el.clientHeight) : rowTop
      if (Math.abs(el.scrollTop - target) > 1) el.scrollTop = target
      if (Math.abs(el.scrollLeft - left) > 1) el.scrollLeft = left
      setScrollTop(el.scrollTop)
      if (++frame < 40) raf = requestAnimationFrame(apply)
      else viewportRestoring.current = false
    }
    const stop = () => { frame = 40; viewportRestoring.current = false }
    viewportRestoring.current = true
    el.addEventListener('wheel', stop, { passive: true, once: true })
    el.addEventListener('touchstart', stop, { passive: true, once: true })
    el.addEventListener('keydown', stop, { once: true })
    raf = requestAnimationFrame(apply)
    // Torn down before it finished (StrictMode remount, rows reloading): let the next run restore.
    return () => { if (frame < 40) viewportRestoredFor.current = null; cancelAnimationFrame(raf); viewportRestoring.current = false; el.removeEventListener('wheel', stop); el.removeEventListener('touchstart', stop); el.removeEventListener('keydown', stop) }
  }, [gridKey, visualItemCount, viewportWaitTick, isEmbedded, loading])

  const { topSpacerHeight, bottomSpacerHeight, visibleItems } = useMemo(() => {
    if (isEmbedded || allVisualItems.length <= 120) {
      return {
        topSpacerHeight: 0,
        bottomSpacerHeight: 0,
        visibleItems: allVisualItems.map((item, virtualIndex) => ({ item, virtualIndex })),
      }
    }
    const ROW_HEIGHT = 36
    const scale = zoom / 100 / textScale
    const overscan = 25

    let cumulative = 0
    const itemTops: number[] = new Array(allVisualItems.length)
    const itemHeights: number[] = new Array(allVisualItems.length)

    for (let i = 0; i < allVisualItems.length; i++) {
      const item = allVisualItems[i]
      const k = visualItemKey(item!)
      const h = rowHeightsDraft[k] ?? measuredHeights[k] ?? rowHeightsRef.current.get(k) ?? ROW_HEIGHT
      itemTops[i] = cumulative
      itemHeights[i] = h
      cumulative += h
    }

    const unscaledScrollTop = scrollTop / scale
    const unscaledViewportHeight = viewportHeight / scale

    let start = 0
    if (unscaledScrollTop > 60) {
      while (start < allVisualItems.length - 1 && itemTops[start]! + itemHeights[start]! < unscaledScrollTop) {
        start++
      }
      start = Math.max(0, start - overscan)
    }

    let end = start
    const targetBottom = unscaledScrollTop + unscaledViewportHeight
    if (targetBottom >= cumulative - 120) {
      end = allVisualItems.length
    } else {
      while (end < allVisualItems.length && itemTops[end]! < targetBottom) {
        end++
      }
      end = Math.min(allVisualItems.length, end + overscan)
      if (end >= allVisualItems.length - 10) {
        end = allVisualItems.length
      }
    }

    const topSpacer = start > 0 ? Math.round(itemTops[start]!) : 0
    const bottomSpacer = end < allVisualItems.length ? Math.round(cumulative - itemTops[end]!) : 0

    const visible = allVisualItems.slice(start, end).map((item, idx) => ({
      item,
      virtualIndex: start + idx,
    }))

    return {
      topSpacerHeight: topSpacer,
      bottomSpacerHeight: bottomSpacer,
      visibleItems: visible,
    }
  }, [isEmbedded, allVisualItems, rowHeightsDraft, measuredHeights, scrollTop, viewportHeight, zoom, textScale, keyOf])

  const darkBand = useMemo(() => {
    const m = new Map<string, boolean>()
    renderedRows.forEach((r, i) => m.set(keyOf(r), i % 2 === 1))
    return m
  }, [renderedRows, keyOf])

  const rowPosition = useMemo(() => {
    const m = new Map<string, number>()
    renderedRows.forEach((r, i) => m.set(keyOf(r), i))
    return m
  }, [renderedRows, keyOf])
  const columnPosition = useMemo(() => {
    const m = new Map<number, number>()
    m.set(CHECKBOX_COL, 0)
    visibleColumns.forEach((originalIndex, pos) => m.set(originalIndex, pos + 1))
    return m
  }, [visibleColumns])

  const navColumns = useMemo(() => isEmbedded ? visibleColumns : [CHECKBOX_COL, ...visibleColumns], [isEmbedded, visibleColumns])

  const toRect = useCallback((a: { rowKey: string; originalCol: number } | null, b: { rowKey: string; originalCol: number } | null) => {
    if (!a || !b) return null
    const rowA = rowPosition.get(a.rowKey)
    const rowB = rowPosition.get(b.rowKey)
    const colA = columnPosition.get(a.originalCol)
    const colB = columnPosition.get(b.originalCol)
    if (rowA === undefined || rowB === undefined || colA === undefined || colB === undefined) return null
    return {
      rowMin: Math.min(rowA, rowB), rowMax: Math.max(rowA, rowB),
      colMin: Math.min(colA, colB), colMax: Math.max(colA, colB),
    }
  }, [rowPosition, columnPosition])
  const range = useMemo(() => toRect(anchor, end), [toRect, anchor, end])

  const subtotalBands = useMemo(() => (showGroupSubtotals
    ? allVisualItems.flatMap((item) => (item.type === (subtotalsAtBottom ? 'subtotal' : 'group'))
      ? [{ groupKey: item.groupKey ?? item.label, rows: item.groupRows as Record<string, unknown>[] }]
      : [])
    : []).concat(summaryCardsContext?.totalsRowVisible
    ? [{ groupKey: TOTALS_BAND_KEY, rows: internalRows as Record<string, unknown>[] }]
    : []), [allVisualItems, showGroupSubtotals, subtotalsAtBottom, summaryCardsContext?.totalsRowVisible, internalRows])
  const subtotalPosition = useMemo(() => new Map(subtotalBands.map((band, i) => [band.groupKey, i])), [subtotalBands])
  const subtotalRange = useMemo(() => {
    if (!subtotalAnchor || !subtotalEnd) return null
    const rowA = subtotalPosition.get(subtotalAnchor.groupKey)
    const rowB = subtotalPosition.get(subtotalEnd.groupKey)
    const colA = visibleColumns.indexOf(subtotalAnchor.originalCol)
    const colB = visibleColumns.indexOf(subtotalEnd.originalCol)
    if (rowA === undefined || rowB === undefined || colA < 0 || colB < 0) return null
    return { rowMin: Math.min(rowA, rowB), rowMax: Math.max(rowA, rowB), colMin: Math.min(colA, colB), colMax: Math.max(colA, colB) }
  }, [subtotalAnchor, subtotalEnd, subtotalPosition, visibleColumns])
  const formatSelectionFields = useMemo(() => {
    const activeRange = subtotalRange ?? range
    const indexes = selectedColumns.size > 0 ? [...selectedColumns] : activeRange ? visibleColumns.slice(Math.max(0, activeRange.colMin - (subtotalRange ? 0 : 1)), activeRange.colMax + (subtotalRange ? 1 : 0)) : []
    const fields = indexes.flatMap((index) => columns[index]?.field ? [columns[index]!.field!] : [])
    return fields.length > 1 ? fields : formatTarget ? [formatTarget] : []
  }, [selectedColumns, subtotalRange, range, visibleColumns, columns, formatTarget])
  formatSelectionRef.current = formatSelectionFields
  useEffect(() => {
    if (anchor || end || selectedColumns.size > 0) { setSubtotalAnchor(null); setSubtotalEnd(null) }
  }, [anchor, end, selectedColumns])
  const subtotalCellAt = useCallback((rows: readonly Record<string, unknown>[], originalCol: number, isTotals = false) => {
    const col = columns[originalCol]
    if (!col || (col.field && (isTotals ? hiddenTotalFields : hiddenSubtotalFields).has(col.field))) return { text: '', value: null }
    const card = configuredSummaryCards.find((c) => c.columnId === col.field)
    const cellType = columnType.get(originalCol) ?? 'text'
    const isNum = cellType === 'number' || col.cellType === 'number' || col.dataFormat?.type === 'number'
    const aggRows = aggregationRowsOf(rows)
    return {
      text: aggregateCellText(col, card, isNum, headerOf(originalCol), aggRows, formats),
      value: aggregateCellNumber(col, card, isNum, aggRows, formats),
    }
  }, [columns, hiddenSubtotalFields, hiddenTotalFields, configuredSummaryCards, columnType, aggregationRowsOf, headerOf, formats])
  /** How far an `overflow` cell spills: across the empty, unfrozen cells to its right. */
  const overflowSpill = useCallback((texts: readonly string[], colPos: number) => {
    let spill = 0
    for (let k = colPos + 1; k < visibleColumns.length; k++) {
      const originalCol = visibleColumns[k]!
      const width = widthOf(originalCol)
      if (texts[k] !== '' || width === undefined || stickyLeft.has(originalCol) || stickyRight.has(originalCol)) break
      spill += width
    }
    return spill
  }, [visibleColumns, widthOf, stickyLeft, stickyRight])
  const subtotalSelectionCells = useCallback(() => {
    if (!subtotalRange) return []
    return subtotalBands.slice(subtotalRange.rowMin, subtotalRange.rowMax + 1).map((band) =>
      visibleColumns.slice(subtotalRange.colMin, subtotalRange.colMax + 1).map((originalCol) => subtotalCellAt(band.rows, originalCol, band.groupKey === TOTALS_BAND_KEY)))
  }, [subtotalRange, subtotalBands, visibleColumns, subtotalCellAt])
  const subtotalStats = useMemo(() => {
    const cells = subtotalSelectionCells()
    if (cells.length === 0) return null
    const flat = cells.flat()
    return computeSelectionStats(cells.length, cells[0]!.length, flat.flatMap((c) => c.value === null ? [] : [c.value]), flat.map((c) => c.text))
  }, [subtotalSelectionCells])

  const cellColorTargets = useMemo<CellColorTarget[]>(() => {
    if (isEmbedded) return []
    // A subtotal's fill is stored like a cell's, under its band's key in place of a row's.
    if (subtotalRange) return cellColorTargetsForRange(
      { ...subtotalRange, colMin: subtotalRange.colMin + 1, colMax: subtotalRange.colMax + 1 },
      subtotalBands.map((band) => subtotalRowKey(band.groupKey)),
      visibleColumns,
      columns.map((column) => column.field),
    )
    if (!range) return []
    return cellColorTargetsForRange(
      range,
      renderedRows.map((row) => keyOf(row)),
      visibleColumns,
      columns.map((column) => column.field),
    )
  }, [columns, isEmbedded, keyOf, range, renderedRows, visibleColumns, subtotalRange, subtotalBands])

  const fillColumnFields = useMemo(() => {
    if (selectedColumns.size > 0) return [...new Set([...selectedColumns].flatMap((index) => columns[index]?.field ? [columns[index]!.field!] : []))]
    if (selectedFullColumn !== null && columns[selectedFullColumn]?.field) return [columns[selectedFullColumn]!.field!]
    return [...new Set(cellColorTargets.map((target) => target.field))]
  }, [selectedColumns, selectedFullColumn, columns, cellColorTargets])
  const fillsColumns = fillScope === 'column' || selectedColumns.size > 0 || selectedFullColumn !== null
  const selectedFillColor = useMemo(() => {
    if (!fillsColumns && fillScope !== 'header') {
      const colors = cellColorTargets.map((target) => cellColors.get(cellColorKey(target.field, target.rowKey)) ?? columnColors[target.field] ?? null)
      return colors.length > 0 && colors.every((color) => color === colors[0]) ? colors[0]! : null
    }
    const colors = fillScope === 'header' ? headerColors : columnColors
    const first = colors[fillColumnFields[0] ?? ''] ?? null
    return fillColumnFields.every((field) => (colors[field] ?? null) === first) ? first : null
  }, [fillScope, fillsColumns, cellColorTargets, cellColors, columnColors, headerColors, fillColumnFields])

  // By-cell columns: the selected cells of such columns, and their shared format (null when mixed).
  const columnFormatOf = useCallback((field: string): GridDataFormat | undefined =>
    dataFormatsConfig[gridKey]?.[field] ?? columns.find((column) => column.field === field)?.dataFormat,
  [dataFormatsConfig, gridKey, columns])
  const byCellTargets = useMemo(() => subtotalRange
    ? []
    : cellColorTargets.filter((target) => columnFormatOf(target.field)?.type === 'auto'),
  [cellColorTargets, columnFormatOf, subtotalRange])
  const selectedCellFormat = useMemo<GridCellFormat | null>(() => {
    if (byCellTargets.length === 0) return null
    const formatOf = (target: CellColorTarget) => columnFormatOf(target.field)?.cells?.[target.rowKey] ?? { type: 'auto' as const }
    const first = JSON.stringify(formatOf(byCellTargets[0]!))
    return byCellTargets.every((target) => JSON.stringify(formatOf(target)) === first) ? formatOf(byCellTargets[0]!) : null
  }, [byCellTargets, columnFormatOf])

  /** Gives every selected cell of a by-cell column the format `update` makes of its own. */
  const saveCellFormats = useCallback(async (update: (cell: GridCellFormat) => GridCellFormat) => {
    if (!gridConfig.canEditFormats || byCellTargets.length === 0) return
    const byField = new Map<string, Map<string, GridCellFormat | null>>()
    for (const target of byCellTargets) {
      const current = columnFormatOf(target.field)?.cells?.[target.rowKey] ?? { type: 'auto' as const }
      const changes = byField.get(target.field) ?? new Map<string, GridCellFormat | null>()
      changes.set(target.rowKey, update(current))
      byField.set(target.field, changes)
    }
    let next = dataFormatsConfig
    for (const [field, changes] of byField) {
      next = withGridDataFormat(next, gridKey, field, withGridCellFormats(columnFormatOf(field) ?? { type: 'auto' }, changes))
    }
    setFormatBusy(true)
    setFormatSaveError(null)
    markSaving()
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.dataFormats, next)
    if (error) {
      markFailed()
      setFormatSaveError(`Format could not be saved: ${error.message}`)
    } else {
      await reloadConfig()
      markSaved()
    }
    setFormatBusy(false)
  }, [gridConfig, byCellTargets, columnFormatOf, dataFormatsConfig, gridKey, markSaving, markSaved, markFailed, reloadConfig])

  const targetBold = Boolean(subtotalRange
    ? dataFormatsConfig[gridKey]?.[formatTarget]?.subtotalBold ?? true
    : dataFormatsConfig[gridKey]?.[formatTarget]?.bold)

  const usedCellColors = useMemo(
    () => [...new Set([...cellColors.values(), ...Object.values(columnColors), ...Object.values(headerColors)])],
    [cellColors, columnColors, headerColors],
  )

  const applyStoredCellColors = useCallback(async (expected: readonly CellColorChange[], changes: readonly CellColorChange[]) => {
    const current = cellColorsRef.current
    for (const change of expected) {
      const actual = current.get(cellColorKey(change.field, change.rowKey)) ?? null
      if (actual !== change.color) {
        setHistoryError(t('grid.historyConflict'))
        return false
      }
    }

    const previous = new Map(current)
    const next = new Map(current)
    for (const change of changes) {
      const key = cellColorKey(change.field, change.rowKey)
      if (change.color) next.set(key, change.color)
      else next.delete(key)
    }
    setCellColors(next)
    setCellColorBusy(true)
    setCellColorError(null)
    markSaving()
    const error = await saveCellColors(gridConfig.orgId ?? '', gridKey, changes, gridConfig.documentId)
    if (error) {
      markFailed()
      setCellColors(previous)
      setCellColorError(t('grid.cellColorSaveError'))
      setCellColorBusy(false)
      return false
    }
    setCellColorBusy(false)
    setHistoryError(null)
    markSaved()
    return true
  }, [gridConfig.documentId, gridConfig.orgId, gridKey, markSaving, markSaved, markFailed, t])

  const applyCellColor = useCallback(async (requestedColor: string | null) => {
    if (!canEditFormats || !gridConfig.orgId || cellColorTargets.length === 0) return

    const color = normalizeCellColor(requestedColor)
    const previous: CellColorChange[] = cellColorTargets.map((target) => ({
      rowKey: target.rowKey,
      field: target.field,
      color: cellColorsRef.current.get(cellColorKey(target.field, target.rowKey)) ?? null,
    }))
    const next: CellColorChange[] = cellColorTargets.map((target) => ({ ...target, color }))
    if (previous.every((change, index) => change.color === next[index]?.color)) return
    const applied = await applyStoredCellColors(previous, next)
    if (!applied) return
    addHistoryTaskRef.current({
      label: t('grid.historyFillColor', { count: next.length }),
      undo: () => applyStoredCellColors(next, previous),
      redo: () => applyStoredCellColors(previous, next),
    })
  }, [applyStoredCellColors, cellColorTargets, gridConfig.orgId, canEditFormats, t])

  const applyFillColor = useCallback(async (requestedColor: string | null) => {
    if (!fillsColumns && fillScope !== 'header') return applyCellColor(requestedColor)
    if (!canEditFormats || fillColumnFields.length === 0 || cellColorBusy) return
    setCellColorBusy(true)
    setCellColorError(null)
    markSaving()
    const key = fillScope === 'header' ? GRID_SETTING_KEYS.headerColors : GRID_SETTING_KEYS.columnColors
    const latest = normalizeColumnColors(gridConfigRef.current.read?.(key))
    const next = withColumnColors(latest, gridKey, fillColumnFields, requestedColor)
    const error = await saveSettingRef.current(key, next)
    if (error) { markFailed(); setCellColorError(t('grid.cellColorSaveError')) }
    else { await reloadConfig(); markSaved() }
    setCellColorBusy(false)
  }, [fillScope, fillsColumns, applyCellColor, canEditFormats, fillColumnFields, cellColorBusy, gridKey, markSaving, markSaved, markFailed, reloadConfig, t])

  const resetColumnCellColors = useCallback(async () => {
    if (!canEditFormats || !gridConfig.orgId || fillColumnFields.length === 0 || cellColorBusy) return
    setCellColorBusy(true)
    setCellColorError(null)
    try {
      // Load all overrides, including filtered/offscreen rows, before building an undoable reset.
      const fresh = await loadCellColors(gridConfig.orgId, gridKey, gridConfig.documentId)
      const next = columnCellColorChanges(fresh, fillColumnFields)
      cellColorsRef.current = fresh
      setCellColors(fresh)
      if (next.length === 0) { setCellColorBusy(false); return }
      const previous = next.map((change) => ({ ...change, color: fresh.get(cellColorKey(change.field, change.rowKey)) ?? null }))
      const applied = await applyStoredCellColors(previous, next)
      if (applied) addHistoryTaskRef.current({ label: t('grid.resetAllColumn'), undo: () => applyStoredCellColors(next, previous), redo: () => applyStoredCellColors(previous, next) })
    } catch {
      setCellColorError(t('grid.cellColorLoadError'))
      setCellColorBusy(false)
    }
  }, [canEditFormats, gridConfig.orgId, gridConfig.documentId, fillColumnFields, cellColorBusy, gridKey, applyStoredCellColors, t])
  const gridFillControls = useMemo(() => ({ scope: fillScope, onScopeChange: setFillScope, onResetColumn: () => { void resetColumnCellColors() }, resetColumnDisabled: !canEditFormats || cellColorBusy || fillColumnFields.length === 0 }), [fillScope, resetColumnCellColors, canEditFormats, cellColorBusy, fillColumnFields.length])

  // Paint format, as in Sheets/Excel: the button takes the selection's format, the next
  // selection receives it. A column header on either end carries the column's format;
  // between plain cells only the cell's own format (its fill) travels.
  const [formatPainter, setFormatPainter] = useState<{
    signature: string
    header: boolean
    format: GridDataFormat
    color: string | null
  } | null>(null)
  const [painterReleaseTick, setPainterReleaseTick] = useState(0)
  const selectionSignature = JSON.stringify([anchor, end, [...selectedColumns], subtotalAnchor, subtotalEnd])

  const armFormatPainter = useCallback(() => {
    if (formatPainter) { setFormatPainter(null); return }
    const header = selectedColumns.size > 0
    const sourceField = header
      ? columns[[...selectedColumns][0]!]?.field
      : cellColorTargets[0]?.field
    if (!sourceField) return
    const source = cellColorTargets[0]
    setFormatPainter({
      signature: selectionSignature,
      header,
      format: dataFormatsConfig[gridKey]?.[sourceField] ?? { type: 'text' },
      color: source ? cellColorsRef.current.get(cellColorKey(source.field, source.rowKey)) ?? null : null,
    })
  }, [formatPainter, selectedColumns, columns, cellColorTargets, selectionSignature, dataFormatsConfig, gridKey])

  // The parts paint format carries, each behind the permission that edits it in the toolbar.
  // Both column parts sit under `canEditFormats` today; they are kept apart so a finer
  // permission only needs to change here.
  const canPaintStyle = canEditFormats
  const canPaintDataType = canEditFormats
  const canPaintFill = canEditFormats && Boolean(gridConfig.orgId)

  const pasteColumnFormat = useCallback(async (fields: readonly string[], source: GridDataFormat) => {
    if (fields.length === 0 || (!canPaintStyle && !canPaintDataType)) return
    const styleOf = ({ bold, align, wrapMode, subtotalBold, subtotalWrapMode, borderLeft, borderRight }: GridDataFormat) => ({
      ...(bold !== undefined ? { bold } : {}),
      ...(borderLeft !== undefined ? { borderLeft } : {}),
      ...(borderRight !== undefined ? { borderRight } : {}),
      ...(align !== undefined ? { align } : {}),
      ...(wrapMode !== undefined ? { wrapMode } : {}),
      ...(subtotalBold !== undefined ? { subtotalBold } : {}),
      ...(subtotalWrapMode !== undefined ? { subtotalWrapMode } : {}),
    })
    const dataTypeOf = ({
      bold: _bold, align: _align, wrapMode: _wrapMode, borderLeft: _borderLeft, borderRight: _borderRight,
      subtotalBold: _subtotalBold, subtotalWrapMode: _subtotalWrapMode, ...rest
    }: GridDataFormat) => rest
    const formatFor = (field: string): GridDataFormat => {
      const current = dataFormatsConfig[gridKey]?.[field] ?? { type: 'text' }
      return {
        ...dataTypeOf(canPaintDataType ? source : current),
        ...styleOf(canPaintStyle ? source : current),
      }
    }
    setFormatBusy(true)
    setFormatSaveError(null)
    markSaving()
    // Each painted column keeps its own record of a borrowed list, as the format bar does.
    const link = source.optionsLink
    const documentId = formulaWorkbook?.documentId ?? gridConfig.documentId
    if (canPaintDataType && documentId && gridKey.startsWith('sheet:')) {
      const tabId = gridKey.slice('sheet:'.length)
      for (const field of fields) {
        const registerError = link
          ? await registerLinkedList(documentId, tabId, field, {
            documentId: link.documentId ?? documentId,
            tabId: link.tabId,
            field: link.field,
          })
          : await unregisterLinkedList(documentId, tabId, field)
        if (registerError && link) {
          markFailed()
          setFormatSaveError(registerError)
          setFormatBusy(false)
          return
        }
      }
    }
    const next = fields.reduce((all, field) => withGridDataFormat(all, gridKey, field, formatFor(field)), dataFormatsConfig)
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.dataFormats, next)
    if (error) {
      markFailed()
      setFormatSaveError(`Format could not be saved: ${error.message}`)
    } else {
      await reloadConfig()
      markSaved()
    }
    setFormatBusy(false)
  }, [canPaintStyle, canPaintDataType, dataFormatsConfig, formulaWorkbook, gridConfig, gridKey, markSaving, markSaved, markFailed, reloadConfig])

  useEffect(() => {
    if (!formatPainter || formatPainter.signature === selectionSignature) return
    if (draggingSelection.current || draggingColumnSelection.current) return
    const targetHeader = selectedColumns.size > 0
    if (!targetHeader && cellColorTargets.length === 0) return
    setFormatPainter(null)
    // Bold, alignment and the data type with its options are one column-level format;
    // the fill belongs to the cell. Each part is pasted only where the user may edit it.
    const fields = targetHeader
      ? [...selectedColumns].flatMap((col) => columns[col]?.field ? [columns[col]!.field!] : [])
      : [...new Set(cellColorTargets.map((target) => target.field))]
    void (async () => {
      await pasteColumnFormat(fields, formatPainter.format)
      // A header carries no single fill of its own, so painting from one leaves fills alone.
      if (!formatPainter.header && !targetHeader && canPaintFill) await applyCellColor(formatPainter.color)
    })()
  }, [formatPainter, selectionSignature, painterReleaseTick, selectedColumns, cellColorTargets, columns, pasteColumnFormat, applyCellColor, canPaintFill])

  useEffect(() => {
    if (!formatPainter) return
    // A drag settles on release, which changes no selection state by itself.
    const release = () => requestAnimationFrame(() => setPainterReleaseTick((tick) => tick + 1))
    const cancel = (e: KeyboardEvent) => { if (e.key === 'Escape') setFormatPainter(null) }
    window.addEventListener('pointerup', release)
    window.addEventListener('keydown', cancel)
    return () => {
      window.removeEventListener('pointerup', release)
      window.removeEventListener('keydown', cancel)
    }
  }, [formatPainter])

  const rangeEditable = useMemo(() => {
    if (!range || !editable) return false
    for (let c = range.colMin; c <= range.colMax; c++) {
      const originalCol = visibleColumns[c - 1]
      if (originalCol === undefined) continue
      const column = columns[originalCol]
      if (column!.field && renderedRows.some((row) => isColumnEditable(column!, row))) return true
    }
    return false
  }, [range, editable, visibleColumns, columns, renderedRows])

  const copiedRange = useMemo(() => copied ? toRect(copied.anchor, copied.end) : null, [toRect, copied])

  const selectionStats = useMemo(() => {
    if (!range && selectedColumns.size === 0) return null
    const isMultiCol = selectedColumns.size > 0
    const colMinData = range ? Math.max(range.colMin, 1) : 1
    const colMaxData = range ? range.colMax : 0
    const colsToInclude: number[] = isMultiCol
      ? Array.from(selectedColumns)
      : (colMaxData >= colMinData ? visibleColumns.slice(colMinData - 1, colMaxData) : [])
    const columnsCount = colsToInclude.length
    if (columnsCount === 0) return null
    const isFullColumnSelection = isMultiCol || (columnsCount === 1 && range !== null && range.rowMin === 0 && range.rowMax === renderedRows.length - 1)
    const sourceRows = isFullColumnSelection ? sorted : (range ? renderedRows.slice(range.rowMin, range.rowMax + 1) : [])
    const rowsCount = sourceRows.length
    const numbers: number[] = []
    const texts: string[] = []
    if (columnsCount > 0) {
      for (const row of sourceRows) {
        if (!row) continue
        for (const origCol of colsToInclude) {
          const column = columns[origCol]
          if (!column) continue
          const value = readValue(column, row)
          if (typeof value === 'number' && Number.isFinite(value)) numbers.push(value)
          texts.push(cellText(column, row))
        }
      }
    }
    return computeSelectionStats(rowsCount, columnsCount, numbers, texts)
  }, [range, selectedColumns, sorted, renderedRows, visibleColumns, columns])


  useEffect(() => {
    if (!anchor || !end) {
      if (selectedFullColumn !== null) setSelectedFullColumn(null)
      if (selectedColumns.size > 0) setSelectedColumns(new Set())
      return
    }
    if (range && renderedRows.length > 0) {
      const isFull = range.colMin >= 1 && range.rowMin === 0 && range.rowMax === renderedRows.length - 1
      if (isFull) {
        const fullCols = visibleColumns.slice(range.colMin - 1, range.colMax)
        if (fullCols.length === 1) {
          const colIndex = fullCols[0]!
          if (selectedFullColumn !== colIndex) setSelectedFullColumn(colIndex)
        } else if (selectedFullColumn !== null) {
          setSelectedFullColumn(null)
        }
        setSelectedColumns((prev) => {
          if (prev.size > 1 && Array.from(prev).some((c) => !fullCols.includes(c))) return prev
          if (prev.size === fullCols.length && fullCols.every((c) => prev.has(c))) return prev
          return new Set(fullCols)
        })
      } else {
        if (selectedFullColumn !== null) setSelectedFullColumn(null)
        if (selectedColumns.size > 0) setSelectedColumns((prev) => (prev.size === 0 ? prev : new Set()))
      }
    }
  }, [anchor, end, range, renderedRows.length, visibleColumns, selectedFullColumn, selectedColumns.size])

  const countedRows = useMemo(() => {
    if (!resolvedAutoRows) return sorted.length
    const isEmptyFn = resolvedAutoRows.isEmpty ?? resolvedAutoRows.estaVacia ?? (() => false)
    return sorted.filter((row) => !isEmptyFn(row)).length
  }, [sorted, resolvedAutoRows])

  const itemPositionByRowKey = useMemo(() => {
    const m = new Map<string, number>()
    allVisualItems.forEach((item, index) => {
      if (item.type === 'row') {
        m.set(keyOf(item.row), index)
      }
    })
    return m
  }, [allVisualItems, keyOf])

  const scrolledToCellRef = useRef<string | null>(null)
  // The cell the grid last scrolled toward. A cell that never mounts (its column hidden, as in a
  // split panel) never reaches scrolledToCellRef, and re-scrolling to it on every render would
  // pull the user back each time they scroll.
  const scrollAttemptedCellRef = useRef<string | null>(null)

  useEffect(() => {
    const isFullCol = selectedColumns.size > 0 || selectedFullColumn !== null
    const activeCell = isFullCol ? (anchor ?? end) : (end ?? anchor)
    if (!activeCell) {
      scrolledToCellRef.current = null
      scrollAttemptedCellRef.current = null
      return
    }
    const activeTag = document.activeElement?.tagName
    // The formula editor is a contenteditable: stealing focus from it commits the half-typed formula.
    if (activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT' || (document.activeElement as HTMLElement | null)?.isContentEditable) return
    const container = containerRef.current
    const cellKey = `${activeCell.rowKey}|${activeCell.originalCol}`

    if (scrolledToCellRef.current === cellKey) {
      const el = cellRefs.current.get(cellKey)
      const focused = document.activeElement
      const canSteal = !focused || focused === document.body || (container?.contains(focused) ?? false)
      if (el && focused !== el && canSteal) el.focus({ preventScroll: true })
      return
    }

    if (container && !isFullCol && scrollAttemptedCellRef.current !== cellKey) {
      scrollAttemptedCellRef.current = cellKey
      const targetIndex = itemPositionByRowKey.get(activeCell.rowKey)
      if (targetIndex !== undefined) {
        const headerHeight = container.querySelector('thead')?.getBoundingClientRect().height ?? 0
        const scale = zoom / 100 / textScale

        let targetTop = 0
        for (let i = 0; i < targetIndex; i++) {
          const item = allVisualItems[i]
          if (!item) break
          const k = visualItemKey(item)
          targetTop += rowHeightsRef.current.get(k) ?? 36
        }

        const scaledTargetTop = targetTop * scale
        const targetRowHeight = (rowHeightsRef.current.get(activeCell.rowKey) ?? 36) * scale
        const currentScrollTop = container.scrollTop
        const containerHeight = container.clientHeight

        if (scaledTargetTop < currentScrollTop + headerHeight) {
          container.scrollTop = Math.max(0, scaledTargetTop - headerHeight)
        } else if (scaledTargetTop + targetRowHeight > currentScrollTop + containerHeight) {
          container.scrollTop = scaledTargetTop + targetRowHeight - containerHeight
        }
      }
    }

    const focusCell = () => {
      const el = cellRefs.current.get(cellKey)
      if (!el) return false
      el.focus({ preventScroll: true })
      scrolledToCellRef.current = cellKey
      if (container && !isFullCol) {
        const headerHeight = container.querySelector('thead')?.getBoundingClientRect().height ?? 0
        const cellRect = el.getBoundingClientRect()
        const contRect = container.getBoundingClientRect()
        if (cellRect.top < contRect.top + headerHeight) container.scrollTop -= contRect.top + headerHeight - cellRect.top
        else if (cellRect.bottom > contRect.bottom) container.scrollTop += cellRect.bottom - contRect.bottom
        // Frozen columns sit over the scrolled ones, so the visible band starts after the last
        // pinned cell on the left and ends before the first pinned cell on the right.
        let visibleLeft = contRect.left
        let visibleRight = contRect.right
        if (el.style.position !== 'sticky') {
          for (const sibling of Array.from(el.parentElement?.children ?? [])) {
            const s = (sibling as HTMLElement).style
            if (sibling === el || s.position !== 'sticky') continue
            const r = sibling.getBoundingClientRect()
            if (s.left !== '') visibleLeft = Math.max(visibleLeft, r.right)
            else if (s.right !== '') visibleRight = Math.min(visibleRight, r.left)
          }
        }
        if (cellRect.left < visibleLeft) container.scrollLeft -= visibleLeft - cellRect.left
        else if (cellRect.right > visibleRight) container.scrollLeft += cellRect.right - visibleRight
      }
      return true
    }

    if (!focusCell()) {
      const raf = requestAnimationFrame(() => {
        focusCell()
      })
      return () => cancelAnimationFrame(raf)
    }
  }, [anchor, end, itemPositionByRowKey, zoom, textScale, allVisualItems, keyOf, visibleItems, editing, selectedColumns.size, selectedFullColumn])

  const autoScrollRef = useRef<{ animId: number | null; mouseX: number; mouseY: number }>({
    animId: null,
    mouseX: 0,
    mouseY: 0,
  })

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRef.current.animId !== null) {
      cancelAnimationFrame(autoScrollRef.current.animId)
      autoScrollRef.current.animId = null
    }
  }, [])

  const updateDragSelectionFromMousePosition = useCallback((mouseX: number, mouseY: number) => {
    const container = containerRef.current
    if (!container) return
    const contRect = container.getBoundingClientRect()
    const headerHeight = container.querySelector('thead')?.getBoundingClientRect().height ?? 0
    const topEdge = contRect.top + headerHeight
    const bottomEdge = contRect.bottom

    const clampedX = Math.max(contRect.left + 5, Math.min(contRect.right - 5, mouseX))
    const clampedY = Math.max(topEdge + 5, Math.min(bottomEdge - 5, mouseY))

    const el = document.elementFromPoint(clampedX, clampedY)
    const td = el?.closest('td') as HTMLTableCellElement | null
    if (td) {
      for (const [key, cellEl] of cellRefs.current.entries()) {
        if (cellEl === td) {
          const [rowKeyStr, colStr] = key.split('|')
          const colNum = Number(colStr)
          if (rowKeyStr && !isNaN(colNum)) {
            setEnd({ rowKey: rowKeyStr, originalCol: colNum })
          }
          break
        }
      }
    }
  }, [])

  const startAutoScrollIfNeeded = useCallback((mouseX: number, mouseY: number) => {
    autoScrollRef.current.mouseX = mouseX
    autoScrollRef.current.mouseY = mouseY

    if (autoScrollRef.current.animId !== null) return

    const loop = () => {
      if (!draggingSelection.current) {
        stopAutoScroll()
        return
      }

      const container = containerRef.current
      if (!container) {
        stopAutoScroll()
        return
      }

      const rect = container.getBoundingClientRect()
      const headerHeight = container.querySelector('thead')?.getBoundingClientRect().height ?? 0
      const topEdge = rect.top + headerHeight
      const bottomEdge = rect.bottom
      const leftEdge = rect.left
      const rightEdge = rect.right

      const currX = autoScrollRef.current.mouseX
      const currY = autoScrollRef.current.mouseY

      const MARGIN = 35
      let speedY = 0
      let speedX = 0

      if (currY < topEdge + MARGIN) {
        speedY = -Math.min(30, Math.max(5, Math.round((topEdge + MARGIN - currY) / 2)))
      } else if (currY > bottomEdge - MARGIN) {
        speedY = Math.min(30, Math.max(5, Math.round((currY - (bottomEdge - MARGIN)) / 2)))
      }

      if (currX < leftEdge + MARGIN) {
        speedX = -Math.min(30, Math.max(5, Math.round((leftEdge + MARGIN - currX) / 2)))
      } else if (currX > rightEdge - MARGIN) {
        speedX = Math.min(30, Math.max(5, Math.round((currX - (rightEdge - MARGIN)) / 2)))
      }

      if (speedY !== 0 || speedX !== 0) {
        if (speedY !== 0) container.scrollTop += speedY
        if (speedX !== 0) container.scrollLeft += speedX
        updateDragSelectionFromMousePosition(currX, currY)
        autoScrollRef.current.animId = requestAnimationFrame(loop)
      } else {
        autoScrollRef.current.animId = null
      }
    }

    autoScrollRef.current.animId = requestAnimationFrame(loop)
  }, [stopAutoScroll, updateDragSelectionFromMousePosition])

  const startDrag = useCallback((cellRowKey: string, originalCol: number, extend: boolean) => {
    draggingSelection.current = true
    if (!extend) {
      setSelectedColumns(new Set())
      setSelectedFullColumn(null)
    }
    const cell = { rowKey: cellRowKey, originalCol }
    setEnd(cell)
    if (!extend) setAnchor(cell)
  }, [])

  /**
   * Phones resize a selection by dragging the dots on its corners, like Sheets and Excel mobile:
   * the opposite corner stays put and the dragged one follows the finger, auto-scrolling at the edges.
   */
  const startSelectionHandleDrag = (corner: 'start' | 'end', e: React.PointerEvent) => {
    if (!anchor || !end) return
    e.preventDefault()
    e.stopPropagation()
    const rowOf = (c: { rowKey: string }) => rowPosition.get(c.rowKey) ?? 0
    const colOf = (c: { originalCol: number }) => columnPosition.get(c.originalCol) ?? 0
    const pickRow = (max: boolean) => ((rowOf(anchor) >= rowOf(end)) === max ? anchor : end).rowKey
    const pickCol = (max: boolean) => ((colOf(anchor) >= colOf(end)) === max ? anchor : end).originalCol
    // Dragging the top-left dot keeps the bottom-right corner fixed, and the other way round.
    const keepMax = corner === 'start'
    setAnchor({ rowKey: pickRow(keepMax), originalCol: pickCol(keepMax) })
    setEnd({ rowKey: pickRow(!keepMax), originalCol: pickCol(!keepMax) })
    setSelectedColumns(new Set())
    setSelectedFullColumn(null)
    draggingSelection.current = true
    setSelectionHandleDragging(true)
    const onMove = (ev: PointerEvent) => {
      ev.preventDefault()
      updateDragSelectionFromMousePosition(ev.clientX, ev.clientY)
      startAutoScrollIfNeeded(ev.clientX, ev.clientY)
    }
    const onUp = () => {
      draggingSelection.current = false
      stopAutoScroll()
      setSelectionHandleDragging(false)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
    window.addEventListener('pointermove', onMove, { passive: false })
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }

  const extendDrag = useCallback((cellRowKey: string, originalCol: number, e?: React.MouseEvent) => {
    if (!draggingSelection.current) return
    if (e && (e.buttons === undefined || (e.buttons & 1) === 0)) {
      draggingSelection.current = false
      stopAutoScroll()
      return
    }
    setEnd({ rowKey: cellRowKey, originalCol })
    if (e) {
      startAutoScrollIfNeeded(e.clientX, e.clientY)
    }
  }, [stopAutoScroll, startAutoScrollIfNeeded])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!draggingSelection.current) return
      if ((e.buttons & 1) === 0) {
        draggingSelection.current = false
        stopAutoScroll()
        return
      }
      startAutoScrollIfNeeded(e.clientX, e.clientY)
    }

    const release = () => {
      draggingSelection.current = false
      draggingSubtotalSelection.current = false
      draggingColumnSelection.current = null
      stopAutoScroll()
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', release)
    window.addEventListener('pointerup', release)
    window.addEventListener('blur', release)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', release)
      window.removeEventListener('pointerup', release)
      window.removeEventListener('blur', release)
      stopAutoScroll()
    }
  }, [startAutoScrollIfNeeded, stopAutoScroll])

  const move = useCallback((deltaRow: number, deltaCol: number, extend: boolean) => {
    const base = end ?? anchor
    if (!base || renderedRows.length === 0 || navColumns.length === 0) return
    const currentRowPos = rowPosition.get(base.rowKey)
    const currentColPos = columnPosition.get(base.originalCol)
    if (currentRowPos === undefined || currentColPos === undefined) return
    const newRowPos = Math.min(Math.max(currentRowPos + deltaRow, 0), renderedRows.length - 1)
    const newColPos = Math.min(Math.max(currentColPos + deltaCol, 0), navColumns.length - 1)
    const targetRow = renderedRows[newRowPos]
    const targetOriginalCol = navColumns[newColPos]
    if (!targetRow || targetOriginalCol === undefined) return
    const cell = { rowKey: keyOf(targetRow), originalCol: targetOriginalCol }
    setEnd(cell)
    if (!extend) setAnchor(cell)
  }, [anchor, end, renderedRows, navColumns, rowPosition, columnPosition, keyOf])

  // Formula point mode: arrow keys inside an open cell formula move this pointer and the editor
  // writes its `[Column]N` reference, as Excel and Sheets do.
  const [formulaPointer, setFormulaPointer] = useState<{ rowKey: string; originalCol: number } | null>(null)
  useEffect(() => { setFormulaPointer(null) }, [editing])
  const formulaPointerRef = useRef(formulaPointer)
  formulaPointerRef.current = formulaPointer
  // `cell` references pin a row (`[Column]N`); column formulas reference the same row (`[@Column]`).
  const formulaRefAt = useCallback((rowKey: string, originalCol: number, kind: 'cell' | 'column' = 'cell'): string | null => {
    const field = columns[originalCol]?.field
    if (!field) return null
    const name = (cellFormulaScope.nameOfField(field) ?? field).replace(/]/g, ']]')
    if (kind === 'column') {
      setFormulaPointer({ rowKey, originalCol })
      return `[@${name}]`
    }
    const rowNumber = cellFormulaScope.rowIndexOf?.(rowKey)
    if (rowNumber === undefined) return null
    setFormulaPointer({ rowKey, originalCol })
    return `[${name}]${rowNumber}`
  }, [columns, cellFormulaScope])
  const pointFrom = useCallback((origin: { rowKey: string; originalCol: number } | null, deltaRow: number, deltaCol: number, fresh: boolean, kind: 'cell' | 'column'): string | null => {
    const cols = navColumns.filter((c) => c !== CHECKBOX_COL)
    const firstRow = renderedRows[0]
    const fallback = firstRow && cols[0] !== undefined ? { rowKey: keyOf(firstRow), originalCol: cols[0] } : null
    const base = (fresh || !formulaPointerRef.current ? origin : formulaPointerRef.current) ?? fallback
    if (!base) return null
    const rowPos = rowPosition.get(base.rowKey)
    const colPos = cols.indexOf(base.originalCol)
    if (rowPos === undefined || colPos < 0 || renderedRows.length === 0) return null
    const row = renderedRows[Math.min(Math.max(rowPos + deltaRow, 0), renderedRows.length - 1)]
    const originalCol = cols[Math.min(Math.max(colPos + deltaCol, 0), cols.length - 1)]
    if (!row || originalCol === undefined) return null
    return formulaRefAt(keyOf(row), originalCol, kind)
  }, [navColumns, rowPosition, renderedRows, keyOf, formulaRefAt])
  const pointFormulaRef = useCallback((deltaRow: number, deltaCol: number, fresh: boolean): string | null => {
    if (!editing) return null
    return pointFrom({ rowKey: editing.rowKey, originalCol: columns.findIndex((c) => c.field === editing.field) }, deltaRow, deltaCol, fresh, 'cell')
  }, [editing, columns, pointFrom])
  // A click outside the grid commits an open cell formula (clicks inside it reference cells).
  const cellFormulaCommitRef = useRef<(() => void) | null>(null)
  useEffect(() => {
    if (!editing || !isEditingCellFormula) return
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (target && containerRef.current?.contains(target)) return
      cellFormulaCommitRef.current?.()
    }
    document.addEventListener('mousedown', onDown, true)
    return () => document.removeEventListener('mousedown', onDown, true)
  }, [editing, isEditingCellFormula])
  // A cell clicked while a cell formula is open: the editor inserts (or replaces) its reference.
  const [formulaPointRequest, setFormulaPointRequest] = useState<{ ref: string; nonce: number } | undefined>(undefined)
  const [formulaBarMode, setFormulaBarMode] = useState<FormulaBarMode>(() => readPref('formulaMode') === 'cell' ? 'cell' : 'column')
  useEffect(() => { savePref('formulaMode', formulaBarMode) }, [formulaBarMode, savePref])
  // The formula bar / sidebar editor gets its own request: it can be mounted beside a cell editor.
  const [barPointRequest, setBarPointRequest] = useState<{ ref: string; nonce: number } | undefined>(undefined)
  const pointBarRef = useCallback((deltaRow: number, deltaCol: number, fresh: boolean): string | null => {
    const active = end ?? anchor
    const origin = active && active.originalCol !== CHECKBOX_COL ? active : null
    return pointFrom(origin, deltaRow, deltaCol, fresh, formulaBarMode === 'cell' ? 'cell' : 'column')
  }, [end, anchor, pointFrom, formulaBarMode])
  // Suggested first after `=` or an operator: the other cells of the row (cell formulas) or the
  // other columns of the sheet (column formulas).
  const operandRefsFor = useCallback((rowKey: string | null, excludeField: string | undefined, kind: 'cell' | 'column'): FormulaOperandRef[] => {
    const rowNumber = rowKey ? cellFormulaScope.rowIndexOf?.(rowKey) : undefined
    if (kind === 'cell' && rowNumber === undefined) return []
    return visibleColumns.flatMap((originalCol) => {
      const field = columns[originalCol]?.field
      if (!field || field === excludeField) return []
      const name = cellFormulaScope.nameOfField(field) ?? field
      const escaped = name.replace(/]/g, ']]')
      return [{ name, ref: kind === 'cell' ? `[${escaped}]${rowNumber}` : `[@${escaped}]` }]
    })
  }, [visibleColumns, columns, cellFormulaScope])
  const cellOperandRefs = useMemo(
    () => editing ? operandRefsFor(editing.rowKey, editing.field, 'cell') : [],
    [editing, operandRefsFor],
  )
  const barOperandRefs = useMemo(() => {
    if (formulaBarMode === 'cell') {
      const active = end ?? anchor
      return active ? operandRefsFor(active.rowKey, columns[active.originalCol]?.field, 'cell') : []
    }
    return operandRefsFor(null, formulaTarget, 'column')
  }, [formulaBarMode, end, anchor, columns, formulaTarget, operandRefsFor])
  // The pointer outline lasts only while a formula editor has focus.
  useEffect(() => {
    const onFocusIn = (event: FocusEvent) => {
      if ((event.target as HTMLElement | null)?.dataset?.formulaEditor !== '1') setFormulaPointer(null)
    }
    document.addEventListener('focusin', onFocusIn)
    return () => document.removeEventListener('focusin', onFocusIn)
  }, [])

  const moveDataEdge = useCallback((dirRow: number, dirCol: number, extend: boolean) => {
    const base = end ?? anchor
    if (!base || renderedRows.length === 0 || navColumns.length === 0) return
    const currentRowPos = rowPosition.get(base.rowKey)
    const currentColPos = columnPosition.get(base.originalCol)
    if (currentRowPos === undefined || currentColPos === undefined) return

    const getVal = (rIndex: number, cIndex: number): string => {
      const row = renderedRows[rIndex]
      const origCol = navColumns[cIndex]
      if (!row || origCol === undefined) return ''
      if (origCol === CHECKBOX_COL) return selected.has(keyOf(row)) ? 'checked' : ''
      return cellText(columns[origCol]!, row).trim()
    }

    const maxRow = renderedRows.length - 1
    const maxCol = navColumns.length - 1

    let targetRowPos = currentRowPos
    let targetColPos = currentColPos

    if (dirRow !== 0) {
      const startHasVal = Boolean(getVal(currentRowPos, currentColPos))
      let curr = currentRowPos + dirRow
      if (curr < 0) { targetRowPos = 0 }
      else if (curr > maxRow) { targetRowPos = maxRow }
      else {
        const nextHasVal = Boolean(getVal(curr, currentColPos))
        if (startHasVal && !nextHasVal) {
          while (curr >= 0 && curr <= maxRow && !getVal(curr, currentColPos)) {
            curr += dirRow
          }
          targetRowPos = curr < 0 ? 0 : curr > maxRow ? maxRow : curr
        } else if (startHasVal && nextHasVal) {
          while (curr + dirRow >= 0 && curr + dirRow <= maxRow && getVal(curr + dirRow, currentColPos)) {
            curr += dirRow
          }
          targetRowPos = curr
        } else {
          while (curr >= 0 && curr <= maxRow && !getVal(curr, currentColPos)) {
            curr += dirRow
          }
          targetRowPos = curr < 0 ? 0 : curr > maxRow ? maxRow : curr
        }
      }
    } else if (dirCol !== 0) {
      const startHasVal = Boolean(getVal(currentRowPos, currentColPos))
      let curr = currentColPos + dirCol
      if (curr < 0) { targetColPos = 0 }
      else if (curr > maxCol) { targetColPos = maxCol }
      else {
        const nextHasVal = Boolean(getVal(currentRowPos, curr))
        if (startHasVal && !nextHasVal) {
          while (curr >= 0 && curr <= maxCol && !getVal(currentRowPos, curr)) {
            curr += dirCol
          }
          targetColPos = curr < 0 ? 0 : curr > maxCol ? maxCol : curr
        } else if (startHasVal && nextHasVal) {
          while (curr + dirCol >= 0 && curr + dirCol <= maxCol && getVal(currentRowPos, curr)) {
            curr += dirCol
          }
          targetColPos = curr
        } else {
          while (curr >= 0 && curr <= maxCol && !getVal(currentRowPos, curr)) {
            curr += dirCol
          }
          targetColPos = curr < 0 ? 0 : curr > maxCol ? maxCol : curr
        }
      }
    }

    const targetRow = renderedRows[targetRowPos]
    const targetOriginalCol = navColumns[targetColPos]
    if (!targetRow || targetOriginalCol === undefined) return
    const cell = { rowKey: keyOf(targetRow), originalCol: targetOriginalCol }
    setEnd(cell)
    if (!extend) setAnchor(cell)
  }, [anchor, end, renderedRows, navColumns, rowPosition, columnPosition, keyOf, columns, selected])

  const moveHome = useCallback((extend: boolean) => {
    if (renderedRows.length === 0 || navColumns.length === 0) return
    const firstRow = renderedRows[0]
    const firstCol = navColumns[0]
    if (!firstRow || firstCol === undefined) return
    const cell = { rowKey: keyOf(firstRow), originalCol: firstCol }
    setEnd(cell)
    if (!extend) setAnchor(cell)
  }, [renderedRows, navColumns, keyOf])

  const moveEnd = useCallback((extend: boolean) => {
    if (renderedRows.length === 0 || navColumns.length === 0) return
    let lastRowPos = -1
    let lastColPos = -1
    for (let r = renderedRows.length - 1; r >= 0; r--) {
      const row = renderedRows[r]
      if (!row) continue
      for (let c = navColumns.length - 1; c >= 0; c--) {
        const origCol = navColumns[c]
        if (origCol === undefined) continue
        const hasVal = origCol === CHECKBOX_COL
          ? selected.has(keyOf(row))
          : Boolean(cellText(columns[origCol]!, row).trim())
        if (hasVal) {
          if (r > lastRowPos) lastRowPos = r
          if (c > lastColPos) lastColPos = c
        }
      }
    }
    const finalRowPos = lastRowPos >= 0 ? lastRowPos : renderedRows.length - 1
    const finalColPos = lastColPos >= 0 ? lastColPos : navColumns.length - 1
    const targetRow = renderedRows[finalRowPos]
    const targetCol = navColumns[finalColPos]
    if (!targetRow || targetCol === undefined) return
    const cell = { rowKey: keyOf(targetRow), originalCol: targetCol }
    setEnd(cell)
    if (!extend) setAnchor(cell)
  }, [renderedRows, navColumns, selected, keyOf, columns])

  const jumpPage = useCallback((element: HTMLElement, direction: 1 | -1, extend: boolean) => {
    const rowEl = element.closest('tr')
    const container = element.closest('.overflow-auto') as HTMLElement | null
    const rowHeight = rowEl?.getBoundingClientRect().height || 32
    const headerHeight = container?.querySelector('thead')?.getBoundingClientRect().height ?? 0
    const visibleHeight = container ? container.clientHeight - headerHeight : rowHeight
    const rowJump = Math.max(1, Math.floor(visibleHeight / rowHeight))
    move(direction * rowJump, 0, extend)
  }, [move])

  const selectColumn = useCallback(
    (originalCol: number, modifiers?: { shiftKey?: boolean; ctrlKey?: boolean }) => {
      const { shiftKey = false, ctrlKey = false } = modifiers ?? {}

      if (shiftKey) {
        const anchorCol =
          anchor && anchor.originalCol !== CHECKBOX_COL && visibleColumns.includes(anchor.originalCol)
            ? anchor.originalCol
            : activeHeaderIndex !== null && visibleColumns.includes(activeHeaderIndex)
              ? activeHeaderIndex
              : originalCol

        const posA = visibleColumns.indexOf(anchorCol)
        const posB = visibleColumns.indexOf(originalCol)
        if (posA !== -1 && posB !== -1) {
          const minPos = Math.min(posA, posB)
          const maxPos = Math.max(posA, posB)
          const rangeCols = visibleColumns.slice(minPos, maxPos + 1)
          setSelectedColumns(new Set(rangeCols))
          if (rangeCols.length === 1) {
            setSelectedFullColumn(rangeCols[0]!)
          } else {
            setSelectedFullColumn(null)
          }
          if (renderedRows.length > 0) {
            setAnchor({ rowKey: keyOf(renderedRows[0]!), originalCol: anchorCol })
            setEnd({ rowKey: keyOf(renderedRows[renderedRows.length - 1]!), originalCol })
          }
        }
      } else if (ctrlKey) {
        setSelectedColumns((prev) => {
          const next = new Set(prev)
          if (next.has(originalCol)) {
            next.delete(originalCol)
          } else {
            next.add(originalCol)
          }
          if (next.size === 1) {
            setSelectedFullColumn(Array.from(next)[0]!)
          } else {
            setSelectedFullColumn(null)
          }
          return next
        })
        if (renderedRows.length > 0) {
          setAnchor({ rowKey: keyOf(renderedRows[0]!), originalCol })
          setEnd({ rowKey: keyOf(renderedRows[renderedRows.length - 1]!), originalCol })
        }
      } else {
        setSelectedFullColumn(originalCol)
        setSelectedColumns(new Set([originalCol]))
        if (renderedRows.length === 0) return
        setAnchor({ rowKey: keyOf(renderedRows[0]!), originalCol })
        setEnd({ rowKey: keyOf(renderedRows[renderedRows.length - 1]!), originalCol })
      }
    },
    [anchor, activeHeaderIndex, visibleColumns, renderedRows, keyOf],
  )

  const selectRow = useCallback((cellRowKey: string) => {
    setSelectedFullColumn(null)
    setSelectedColumns(new Set())
    if (visibleColumns.length === 0) { setAnchor({ rowKey: cellRowKey, originalCol: CHECKBOX_COL }); setEnd({ rowKey: cellRowKey, originalCol: CHECKBOX_COL }); return }
    setAnchor({ rowKey: cellRowKey, originalCol: CHECKBOX_COL })
    setEnd({ rowKey: cellRowKey, originalCol: visibleColumns[visibleColumns.length - 1]! })
  }, [visibleColumns])

  const formatHistoryValue = useCallback((value: unknown): string => {
    if (value === null || value === undefined || value === '') return t('grid.historyEmptyValue')
    if (typeof value === 'boolean') return t(value ? 'grid.historyTrue' : 'grid.historyFalse')
    if (typeof value === 'number') return new Intl.NumberFormat(locale).format(value)
    return String(value)
  }, [locale, t])

  /** A field as the user sees it in the header, for a history label. */
  const historyColumnLabel = useCallback((field: string) => {
    const columnIndex = columns.findIndex((column) => column.field === field)
    return columnIndex >= 0 ? headerOf(columnIndex) : field
  }, [columns, headerOf])

  const historyCellLabel = useCallback((field: string, previous: unknown, next: unknown) => {
    return t('grid.historyCellChange', {
      column: historyColumnLabel(field),
      previous: formatHistoryValue(previous),
      next: formatHistoryValue(next),
    })
  }, [formatHistoryValue, historyColumnLabel, t])

  // Hands changed rows to the host: one call for a multi-row change when it can
  // take them together, otherwise one per row. Returns the pending saves.
  const saveChangedRows = useCallback((entries: ReadonlyArray<readonly [T, ReadonlySet<string> | undefined]>): Promise<void>[] => {
    const pending: Promise<void>[] = []
    const track = (res: void | Promise<void>) => {
      if (res && typeof (res as Promise<void>).then === 'function') pending.push(res as Promise<void>)
    }
    if (onCellsChange && entries.length > 1) {
      track(onCellsChange(entries.map(([row]) => row)))
      return pending
    }
    if (!onCellChange) return pending
    for (const [row, fields] of entries) {
      track(onCellChange(row, fields?.size === 1 ? [...fields][0] : undefined))
    }
    return pending
  }, [onCellChange, onCellsChange])

  const applyHistoryChanges = useCallback((changes: readonly CellHistoryChange[], direction: 'undo' | 'redo'): boolean => {
    const currentRows = internalRowsRef.current
    const expectedKey = direction === 'undo' ? 'next' : 'previous'
    const targetKey: 'previous' | 'next' = direction === 'undo' ? 'previous' : 'next'
    const nextRows = [...currentRows]
    const updatedRows = new Map<number, T>()
    const changedFields = new Map<number, Set<string>>()

    for (const change of changes) {
      const index = currentRows.findIndex((row, rowIndex) => getRowKey(row, rowIndex) === change.rowKey)
      if (index < 0) {
        setHistoryError(t('grid.historyConflict'))
        return false
      }
      const row = updatedRows.get(index) ?? currentRows[index]!
      const actual = (row as Record<string, unknown>)[change.field]
      if (!historyValueMatches(actual, change[expectedKey])) {
        setHistoryError(t('grid.historyConflict'))
        return false
      }
      const updated = { ...row, [change.field]: change[targetKey] } as T
      nextRows[index] = updated
      updatedRows.set(index, updated)
      const fields = changedFields.get(index) ?? new Set<string>()
      fields.add(change.field)
      changedFields.set(index, fields)
    }

    internalRowsRef.current = nextRows
    setInternalRows(nextRows)
    markSaving()
    const historyPromises = saveChangedRows([...updatedRows].map(([index, updated]) => [updated, changedFields.get(index)]))
    if (historyPromises.length > 0) {
      // A goal seek's solved value, or a replayed one, may come back stored differently too.
      const unsettled = { changes: changes as CellHistoryChange[], side: targetKey, saved: false }
      unsettledEditsRef.current.add(unsettled)
      Promise.all(historyPromises).then(() => { unsettled.saved = true; markSaved() }).catch((err) => {
        unsettledEditsRef.current.delete(unsettled)
        markFailed()
        console.error('Error during history autosave:', err)
      })
    } else {
      markSaved()
    }
    setHistoryError(null)
    return true
  }, [getRowKey, markFailed, markSaved, markSaving, saveChangedRows, t])

  const applyCellChanges = useCallback((changes: readonly CellHistoryChange[], label: string) => {
    if (changes.length === 0) return
    const currentRows = internalRowsRef.current
    const nextRows = [...currentRows]
    const applied: CellHistoryChange[] = []
    const changedFields = new Map<number, Set<string>>()
    for (const change of changes) {
      const index = currentRows.findIndex((row, rowIndex) => getRowKey(row, rowIndex) === change.rowKey)
      if (index < 0) continue
      const currentRow = nextRows[index]!
      nextRows[index] = { ...currentRow, [change.field]: change.next } as T
      const fields = changedFields.get(index) ?? new Set<string>()
      fields.add(change.field)
      changedFields.set(index, fields)
      applied.push(change)
    }
    if (applied.length === 0) return

    // Sign rules ride along in the same change: typing 5 on a Sell row, or
    // turning Buy into Sell, stores the signed value at once and one undo
    // reverts both. The database applies the same rule as a backstop.
    const signRules = signRulesRef.current
    if (Object.keys(signRules).length > 0) {
      for (const [index, fields] of changedFields) {
        const row = nextRows[index]! as Record<string, unknown>
        const signed = signChanges(signRules, row, fields)
        if (signed.size === 0) continue
        const rowKey = getRowKey(nextRows[index]!, index)
        const patched: Record<string, unknown> = { ...row }
        for (const [field, next] of signed) {
          // The typed cell keeps one history entry, ending at the signed value.
          const earlier = applied.findIndex((c) => c.rowKey === rowKey && c.field === field)
          if (earlier >= 0) applied[earlier] = { ...applied[earlier]!, next }
          else applied.push({ rowKey, field, previous: row[field], next })
          patched[field] = next
          fields.add(field)
        }
        nextRows[index] = patched as T
      }
    }

    // Exchange-rate columns fill from the row's date in the same change, so
    // one undo reverts the date and the rate it brought.
    const { rules: fxRules, history, autoOf, ready } = exchangeRateRef.current
    const autoRates: { rowKey: string; field: string; value: number }[] = []
    if (history && ready && Object.keys(fxRules).length > 0) {
      for (const [index, fields] of changedFields) {
        const row = nextRows[index]! as Record<string, unknown>
        const rowKey = getRowKey(nextRows[index]!, index)
        // A rate cell written in this same change is an override, not the rule's.
        const rates = exchangeRateChanges(fxRules, row, fields, history, autoOf(rowKey))
        if (rates.size === 0) continue
        const patched: Record<string, unknown> = { ...row }
        for (const [field, next] of rates) {
          if (patched[field] === next) continue
          const earlier = applied.findIndex((c) => c.rowKey === rowKey && c.field === field)
          if (earlier >= 0) applied[earlier] = { ...applied[earlier]!, next }
          else applied.push({ rowKey, field, previous: row[field], next })
          patched[field] = next
          fields.add(field)
          autoRates.push({ rowKey, field, value: next })
        }
        nextRows[index] = patched as T
      }
    }
    recordAutoRates(autoRates)

    internalRowsRef.current = nextRows
    setInternalRows(nextRows)
    markSaving()
    const cellPromises = saveChangedRows([...changedFields].map(([index, fields]) => [nextRows[index]!, fields]))
    if (cellPromises.length > 0) {
      const unsettled = { changes: applied, side: 'next' as const, saved: false }
      unsettledEditsRef.current.add(unsettled)
      Promise.all(cellPromises).then(() => { unsettled.saved = true; markSaved() }).catch((err) => {
        unsettledEditsRef.current.delete(unsettled)
        markFailed()
        console.error('Error during cell change autosave:', err)
      })
    } else {
      markSaved()
    }
    const task: Omit<HistoryTask, 'id'> = {
      label,
      undo: () => applyHistoryChanges(applied, 'undo'),
      redo: () => applyHistoryChanges(applied, 'redo'),
    }
    addHistoryTaskRef.current(task)
  }, [applyHistoryChanges, getRowKey, markFailed, markSaved, markSaving, recordAutoRates, saveChangedRows])

  // A spreadsheet tab refreshes on the server: one request for the whole tab,
  // and it works for readers too. Module grids have no server path and refresh here.
  const serverFxTab = gridKey.startsWith('sheet:') && gridConfig.documentId && !isEmbedded ? gridKey.slice('sheet:'.length) : null

  /** Linked cells whose rate moved, per the rules given. Checked locally, so a current sheet costs no request. */
  const staleRates = useCallback((rules: Readonly<Record<string, NonNullable<GridDataFormat['exchangeRate']>>>, history: FxHistory): CellHistoryChange[] => {
    const changes: CellHistoryChange[] = []
    const { autoOf } = exchangeRateRef.current
    internalRowsRef.current.forEach((row, index) => {
      const values = row as Record<string, unknown>
      const rowKey = getRowKey(row, index)
      for (const [field, next] of exchangeRateChanges(rules, values, 'all', history, autoOf(rowKey))) {
        changes.push({ rowKey, field, previous: values[field], next })
      }
    })
    return changes
  }, [getRowKey])

  /** Asks the server to rewrite the tab's stale rates and shows what it wrote, without saving again. */
  const refreshRatesOnServer = useCallback(async (tabId: string) => {
    const documentId = gridConfig.documentId
    if (!documentId) return
    let written: Awaited<ReturnType<typeof refreshSheetExchangeRates>>
    try { written = await refreshSheetExchangeRates(documentId, tabId) } catch (error) { console.error('Grid: exchange rates could not be refreshed', error); return }
    if (written.length === 0) return
    const byRow = new Map<string, Record<string, number>>()
    for (const cell of written) byRow.set(`id:${cell.rowId}`, { ...byRow.get(`id:${cell.rowId}`), [cell.field]: cell.value })
    const nextRows = internalRowsRef.current.map((row, index) => {
      const patch = byRow.get(getRowKey(row, index))
      return patch ? ({ ...(row as Record<string, unknown>), ...patch } as T) : row
    })
    internalRowsRef.current = nextRows
    setInternalRows(nextRows)
    setOverridesMap((previous) => {
      const next = new Map(previous)
      for (const cell of written) next.set(overrideKey(exchangeRateAutoField(cell.field), `id:${cell.rowId}`), { kind: 'value', value: cell.value })
      return next
    })
  }, [getRowKey, gridConfig.documentId])

  /**
   * Brings exchange-rate columns up to date: every linked cell gets the rate its
   * date (or today) has now, and overrides stay. Runs when a column is chosen or
   * reconfigured, when the sheet opens and from "Refresh FX rates". Here, one
   * history entry, so one undo takes it all back.
   */
  const refreshExchangeRates = useCallback(async (rules: Readonly<Record<string, NonNullable<GridDataFormat['exchangeRate']>>>, fresh = false) => {
    if (isEmbedded || Object.keys(rules).length === 0) return
    let history: FxHistory
    try { history = await loadFxHistory(fresh) } catch (error) { console.error('Grid: exchange-rate history unavailable', error); return }
    if (fresh) setFxHistory(history)
    const changes = staleRates(rules, history)
    if (changes.length === 0) return
    if (serverFxTab) { await refreshRatesOnServer(serverFxTab); return }
    if (!editable) return
    applyCellChanges(changes, t('grid.exchangeRate.type'))
    recordAutoRates(changes.map((change) => ({ rowKey: change.rowKey, field: change.field, value: change.next as number })))
  }, [applyCellChanges, editable, isEmbedded, recordAutoRates, refreshRatesOnServer, serverFxTab, staleRates, t])
  const fillExchangeRateColumn = useCallback((field: string, rule: NonNullable<GridDataFormat['exchangeRate']>) =>
    refreshExchangeRates({ [field]: rule }), [refreshExchangeRates])

  // Opening the sheet brings every exchange-rate column up to date (a "Today"
  // rate moves daily), for whoever opens it. Once per setup per visit, after the
  // markers that tell linked cells from overrides have loaded.
  const autoFilledRatesRef = useRef(new Set<string>())
  const rowCount = internalRows.length
  useEffect(() => {
    if (!fxHistory || !overridesLoaded || (!editable && !serverFxTab) || isEmbedded || loading || rowCount === 0) return
    const key = `${gridKey}:${JSON.stringify(exchangeRateRules)}`
    if (autoFilledRatesRef.current.has(key)) return
    autoFilledRatesRef.current.add(key)
    void refreshExchangeRates(exchangeRateRules)
  }, [editable, exchangeRateRules, refreshExchangeRates, fxHistory, gridKey, isEmbedded, loading, overridesLoaded, rowCount, serverFxTab])

  // Refresh: shown when something on the page can go stale - an exchange-rate
  // column, an FXRATE formula, or a module's own data (`onRefresh`).
  const usesFxRate = useMemo(() =>
    JSON.stringify(formulaConfig).includes('FXRATE')
    || [...overridesMap.values()].some((state) => state.kind === 'formula' && JSON.stringify(state.formula).includes('FXRATE')),
  [formulaConfig, overridesMap])
  const showRefresh = !isEmbedded && (hasExchangeRates || usesFxRate || onRefresh !== undefined)
  const [refreshing, setRefreshing] = useState(false)
  const refreshAll = useCallback(async () => {
    setRefreshing(true)
    try {
      await Promise.all([
        hasExchangeRates
          ? refreshExchangeRates(exchangeRateRules, true)
          : usesFxRate ? loadFxHistory(true).catch((error: unknown) => console.error('Grid: exchange-rate history unavailable', error)) : undefined,
        onRefresh?.().catch((error: unknown) => console.error('Grid: refresh failed', error)),
      ])
    } finally { setRefreshing(false) }
  }, [exchangeRateRules, hasExchangeRates, onRefresh, refreshExchangeRates, usesFxRate])
  // Phones show Refresh in the top bar, left of undo, instead of the toolbar.
  const phoneLayout = useIsMobile()
  const setTopBarRefresh = editor?.setTopBarRefresh
  // Phones keep Refresh in the toolbar, left of the search box.
  const refreshInTopBar: boolean = false
  const refreshAllRef = useRef(refreshAll)
  refreshAllRef.current = refreshAll
  const topBarRefreshTitle = refreshTitle ?? t('grid.refresh')
  useEffect(() => {
    if (!refreshInTopBar || !setTopBarRefresh) return
    setTopBarRefresh({ run: () => { void refreshAllRef.current() }, busy: refreshing, title: topBarRefreshTitle })
  }, [refreshInTopBar, refreshing, setTopBarRefresh, topBarRefreshTitle])
  useEffect(() => {
    if (!refreshInTopBar || !setTopBarRefresh) return
    return () => setTopBarRefresh(null)
  }, [refreshInTopBar, setTopBarRefresh])

  const [signDialogField, setSignDialogField] = useState<string | null>(null)
  /** The exchange-rate column whose quote rule is being edited. */
  const [quoteRuleField, setQuoteRuleField] = useState<string | null>(null)

  /** The existing rows a sign rule would rewrite, as history changes. */
  const signRewrites = useCallback((field: string, rule: GridSignRule | null): CellHistoryChange[] => {
    if (!rule) return []
    const out: CellHistoryChange[] = []
    internalRowsRef.current.forEach((row, index) => {
      const data = row as Record<string, unknown>
      const next = signedValue(rule, data[field], data[rule.field])
      if (next !== undefined) out.push({ rowKey: getRowKey(row, index), field, previous: data[field], next })
    })
    return out
  }, [getRowKey])

  /** Saves a column's sign rule (or removes it), then rewrites the rows it now
   * disagrees with as one undoable change. */
  const saveSignRule = useCallback(async (field: string, rule: GridSignRule | null) => {
    const current = dataFormatsConfig[gridKey]?.[field]
      ?? columns.find((c) => c.field === field)?.dataFormat
      ?? { type: 'number' as const }
    const { sign: _previous, ...rest } = current
    const saved = await saveDataFormat(rule ? { ...rest, sign: rule } : rest)
    if (!saved) return
    // The config reload has not rendered yet; the rewrite must not be judged
    // by the rule it replaces.
    const nextRules = { ...signRulesRef.current }
    if (rule) nextRules[field] = rule
    else delete nextRules[field]
    signRulesRef.current = nextRules
    applyCellChanges(signRewrites(field, rule), t('grid.signRule.button'))
    setSignDialogField(null)
  }, [applyCellChanges, columns, dataFormatsConfig, gridKey, saveDataFormat, signRewrites, t])

  /** A column's saved format, else its own default — what its sign rule reads. */
  const effectiveFormat = (field: string): GridDataFormat | undefined =>
    field ? formatBaseOf(field) : undefined

  /** The format bar's ± button: number columns someone types into. A formula
   * column already decides its own sign. */
  const renderSignRuleButton = () => {
    if (formatTarget === '') return null
    const selected = effectiveFormat(formatTarget)
    // A formula column, or one nobody types into (a module's calculated
    // column, like Investments's Value), already decides its own sign.
    const unavailable = isFormulaField(formatTarget, formulaConfig)
      ? t('grid.signRule.formulaColumn')
      : columns.find((c) => c.field === formatTarget)?.editable === false
        ? t('grid.signRule.readOnlyColumn')
        : null
    if (unavailable) {
      if (selected && selected.type !== 'number') return null
      // Shown but unavailable, so the reason is discoverable: aria-disabled
      // rather than `disabled`, which would swallow the hover tooltip.
      return <>
        <span aria-hidden="true" className="flex h-5 w-2.5 shrink-0 justify-center"><span className="h-full w-px bg-border" /></span>
        <button
          type="button"
          aria-disabled="true"
          title={unavailable}
          aria-label={`${t('grid.signRule.button')}: ${unavailable}`}
          data-testid="grid-sign-rule"
          className="flex h-7 w-7 shrink-0 cursor-not-allowed items-center justify-center rounded text-text opacity-40"
        >
          <Icon name="plusMinus" className="h-4 w-4" />
        </button>
      </>
    }
    if (selected?.type !== 'number') return null
    const active = Boolean(selected.sign)
    return <>
      <span aria-hidden="true" className="flex h-5 w-2.5 shrink-0 justify-center"><span className="h-full w-px bg-border" /></span>
      <button
        type="button"
        disabled={formatBusy}
        title={t('grid.signRule.button')}
        aria-label={t('grid.signRule.button')}
        aria-pressed={active}
        data-testid="grid-sign-rule"
        onClick={() => setSignDialogField(formatTarget)}
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors hover:bg-surface-hover disabled:opacity-50 ${active ? 'bg-surface-hover text-brand' : 'text-text'}`}
      >
        <Icon name="plusMinus" className="h-4 w-4" />
      </button>
    </>
  }

  const registerChange = useCallback((row: T, field: string, previous: unknown, next: unknown, originalIndex: number) => {
    applyCellChanges([{
      rowKey: getRowKey(row, originalIndex),
      field,
      previous,
      next,
    }], historyCellLabel(field, previous, next))
  }, [applyCellChanges, getRowKey, historyCellLabel])

  const applyStoredOverride = useCallback(async (rowKeyValue: string, field: string, state: CellOverrideState | undefined): Promise<boolean> => {
    if (!gridConfig.orgId) return false
    markSaving()
    const error = state?.kind === 'formula'
      ? await saveFormulaOverride(gridConfig.orgId, gridKey, rowKeyValue, field, state.formula, gridConfig.documentId)
      : state?.kind === 'value'
        ? await saveOverrideValue(gridConfig.orgId, gridKey, rowKeyValue, field, state.value, gridConfig.documentId)
        : state?.kind === 'blank'
          ? await blankOverride(gridConfig.orgId, gridKey, rowKeyValue, field, gridConfig.documentId)
          : await clearOverride(gridConfig.orgId, gridKey, rowKeyValue, field, gridConfig.documentId)
    if (error) {
      markFailed()
      setHistoryError(t('grid.historySaveError'))
      return false
    }
    await fetchOverrides()
    markSaved()
    return true
  }, [fetchOverrides, gridConfig, gridKey, markFailed, markSaved, markSaving, t])

  const persistCellFormulaWithHistory = useCallback(async (row: T, originalIndex: number, field: string, source: string): Promise<boolean> => {
    const rowKeyValue = getRowKey(row, originalIndex)
    const previous = overridesMap.get(overrideKey(field, rowKeyValue))
    const parsed = parseCellFormula(source.slice(1), cellFormulaScope, formulaLocale)
    if (!parsed.ok) return persistCellFormula(row, originalIndex, field, source)
    const next: CellOverrideState = { kind: 'formula', formula: toStored(parsed.ast) }
    const saved = await persistCellFormula(row, originalIndex, field, source)
    if (!saved) return false
    addHistoryTaskRef.current({
      label: t('grid.historyCellFormula'),
      undo: () => applyStoredOverride(rowKeyValue, field, previous),
      redo: () => applyStoredOverride(rowKeyValue, field, next),
    })
    return true
  }, [applyStoredOverride, cellFormulaScope, formulaLocale, getRowKey, overridesMap, persistCellFormula, t])

  const removeCellFormulaWithHistory = useCallback(async (rowKeyValue: string, field: string, previous: CellOverrideState): Promise<boolean> => {
    const removed = await applyStoredOverride(rowKeyValue, field, undefined)
    if (!removed) return false
    addHistoryTaskRef.current({
      label: t('grid.historyCellFormula'),
      undo: () => applyStoredOverride(rowKeyValue, field, previous),
      redo: () => applyStoredOverride(rowKeyValue, field, undefined),
    })
    return true
  }, [applyStoredOverride, t])

  const replaceStoredOverrideWithHistory = useCallback(async (rowKeyValue: string, field: string, previous: CellOverrideState, next: CellOverrideState): Promise<boolean> => {
    if (!await applyStoredOverride(rowKeyValue, field, next)) return false
    addHistoryTaskRef.current({
      label: t('grid.historyCellFormula'),
      undo: () => applyStoredOverride(rowKeyValue, field, previous),
      redo: () => applyStoredOverride(rowKeyValue, field, next),
    })
    return true
  }, [applyStoredOverride, t])

  const replaceCellFormulaWithLiteral = useCallback(async (
    row: T,
    originalIndex: number,
    field: string,
    previousOverride: CellOverrideState,
    previousValue: unknown,
    nextValue: unknown,
  ): Promise<boolean> => {
    const rowKeyValue = getRowKey(row, originalIndex)
    if (!await applyStoredOverride(rowKeyValue, field, undefined)) return false
    const change: CellHistoryChange = { rowKey: rowKeyValue, field, previous: previousValue, next: nextValue }
    if (previousValue !== nextValue && !await applyHistoryChanges([change], 'redo')) return false
    addHistoryTaskRef.current({
      label: t('grid.historyCellFormula'),
      undo: async () => {
        if (previousValue !== nextValue && !await applyHistoryChanges([change], 'undo')) return false
        return applyStoredOverride(rowKeyValue, field, previousOverride)
      },
      redo: async () => {
        if (!await applyStoredOverride(rowKeyValue, field, undefined)) return false
        return previousValue === nextValue || applyHistoryChanges([change], 'redo')
      },
    })
    return true
  }, [applyHistoryChanges, applyStoredOverride, getRowKey, t])

  /**
   * Goal seek: the typed value does not stay in the cell - it decides what ANOTHER column of
   * this row must hold for the formula to produce it, and that column is written instead
   * (`goalSeek.ts`). Returns `true` once it has taken responsibility for the edit, including
   * when it had to refuse: reporting that nothing could be solved and then silently writing a
   * manual override anyway would be the one outcome the user did not ask for.
   *
   * An empty edit is not a goal to seek - it is the request to blank the cell - so it declines
   * and the ordinary override path handles it.
   *
   * The decision is synchronous, because the caller has to know at once whether the ordinary
   * override path still owns this edit; the writes that follow are not, and they are wrapped in
   * ONE history task. Undoing has to put back both halves — the value written into the target
   * column AND any override that was cleared off the edited cell — since a half-undone goal
   * seek would leave the row saying something the user never typed.
   */
  const applyGoalSeek = useCallback((row: T, originalIndex: number, field: string, text: string | number): boolean => {
    const target = overrideTargetOf(formulaConfig, field)
    if (target === null) return false
    const normalized = typeof text === 'number' ? '' : normalizeText(text)
    if (typeof text !== 'number' && normalized === '') return false
    const formula = storedFormulaOf(field)
    if (formula === undefined) return false

    const rowKeyValue = getRowKey(row, originalIndex)
    if (rowKeyValue.startsWith('idx:')) {
      setOverrideError(t('grid.goalSeekStableRowRequired'))
      return true
    }
    if (!columns.some((column) => column.field === target)) {
      setOverrideError(t('grid.goalSeekTargetMissing'))
      return true
    }
    const typed = typeof text === 'number'
      ? text
      : parseGridNumber(normalized, formats, columns.find((column) => column.field === field)?.dataFormat)
    if (typed === null) {
      setOverrideError(t('grid.goalSeekNumberRequired'))
      return true
    }

    const solved = goalSeekFormula(formula, field, target, typed, (row as Record<string, unknown>)[target])
    if (!solved.ok) {
      // Unreachable while the dropdown only offers invertible columns; reachable if the formula
      // was rewritten after the choice was stored, which is why it reports instead of throwing.
      console.error('Grid: goal seek could not solve the formula', solved.reason)
      setOverrideError(t('grid.goalSeekUnsolvable'))
      return true
    }

    // Evaluated as a CANDIDATE at the target cell: the runtime resolves the rest of the row and
    // reports the cycle if this pair happens to feed each other.
    const evaluated = cellFormulaRuntime.validateCandidate(
      { sheetId: cellFormulaSheetId, rowId: rowKeyValue, field: target },
      solved.formula,
    )
    if (!evaluated.ok) {
      setOverrideError(t(evaluated.kind === 'cycle' ? 'grid.goalSeekCycle' : 'grid.goalSeekUnsolvable'))
      return true
    }
    if (evaluated.value.kind === 'error') {
      setOverrideError(localizeErrorCode(evaluated.value.code, locale))
      return true
    }
    if (evaluated.value.kind !== 'number' || !Number.isFinite(evaluated.value.n)) {
      setOverrideError(t('grid.goalSeekUnsolvable'))
      return true
    }

    setOverrideError(null)
    const previous = (row as Record<string, unknown>)[target]
    const next = evaluated.value.n
    // The edited cell goes back to showing its formula, so anything sitting on top of it has to
    // go - otherwise the value just computed would be hidden by the old manual one.
    const previousOverride = overridesMap.get(overrideKey(field, rowKeyValue))
    // The answer is what the row already holds and nothing was covering it: there is nothing to
    // write, and an undo entry for a change that never happened would be a lie.
    if (previous === next && previousOverride === undefined) return true

    const change: CellHistoryChange = { rowKey: rowKeyValue, field: target, previous, next }
    const label = t('grid.historyGoalSeek', {
      target: historyColumnLabel(target),
      column: historyColumnLabel(field),
      previous: formatHistoryValue(previous),
      next: formatHistoryValue(next),
    })
    const clearEditedCell = previousOverride !== undefined
    const writesTarget = previous !== next

    void (async () => {
      if (clearEditedCell && !await applyStoredOverride(rowKeyValue, field, undefined)) return
      if (writesTarget && !await applyHistoryChanges([change], 'redo')) return
      addHistoryTaskRef.current({
        label,
        undo: async () => {
          if (writesTarget && !await applyHistoryChanges([change], 'undo')) return false
          return !clearEditedCell || applyStoredOverride(rowKeyValue, field, previousOverride)
        },
        redo: async () => {
          if (clearEditedCell && !await applyStoredOverride(rowKeyValue, field, undefined)) return false
          return !writesTarget || applyHistoryChanges([change], 'redo')
        },
      })
    })()
    return true
  }, [applyHistoryChanges, applyStoredOverride, cellFormulaRuntime, cellFormulaSheetId, columns, formats, formulaConfig, formatHistoryValue, getRowKey, historyColumnLabel, locale, overridesMap, storedFormulaOf, t])

  // Formula cells of the last copy, each with its formula text and the value it shows.
  const copiedFormulaValuesRef = useRef<Array<[string, string]>>([])
  const lastCopyValuesRef = useRef<{ text: string; values: string } | null>(null)
  const [copiedHasFormulas, setCopiedHasFormulas] = useState(false)

  const clipboardValueText = useCallback((column: GridColumn<T>, row: T): string => {
    if (column.dataFormat?.type === 'date' || column.cellType === 'date') {
      const value = readValue(column, row)
      const dataFormat = column.dataFormat ?? { type: 'date', formatKey: 'date' }
      return clipboardGridData(value, dataFormat, formats)
    }
    if (!copyNumberFormat && (column.dataFormat?.type === 'number' || column.cellType === 'number')) {
      // A number column copies the value, not its format: USD 784 goes out as 784.
      const value = readValue(column, row)
      if (typeof value === 'number') return editableGridNumber(value, formats)
    }
    return cellText(column, row)
  }, [copyNumberFormat, formats])

  const clipboardCellText = useCallback((column: GridColumn<T>, row: T): string => {
    if (!column.field) return cellText(column, row)
    const rowIndex = internalRows.indexOf(row)
    const state = overridesMap.get(overrideKey(column.field, getRowKey(row, rowIndex)))
    if (state?.kind === 'formula' && copyFormulas) {
      // `fromStored` always yields a node — a `raw` one for anything unreadable — so the text a
      // broken formula shows is still the text the user wrote, ready to be repaired.
      const formulaText = `=${serializeCellFormula(fromStored(state.formula), cellFormulaScope, formulaLocale)}`
      // Paste values (Ctrl+Shift+V) swaps it back for what the cell shows.
      copiedFormulaValuesRef.current.push([formulaText, clipboardValueText(column, row)])
      return formulaText
    }
    return clipboardValueText(column, row)
  }, [cellFormulaScope, copyFormulas, formulaLocale, getRowKey, internalRows, overridesMap])

  /** Keeps the copied text's values-only twin, read by paste values. */
  const rememberCopy = useCallback((text: string, asMarkdown: boolean) => {
    const formulas = copiedFormulaValuesRef.current
    copiedFormulaValuesRef.current = []
    setCopiedHasFormulas(!asMarkdown && formulas.length > 0)
    if (asMarkdown) { lastCopyValuesRef.current = null; return }
    let next = 0
    const values = text.split('\n').map((line) => line.split('\t').map((cell) => {
      const pair = formulas[next]
      if (pair && cell === pair[0]) { next += 1; return pair[1] }
      return cell
    }).join('\t')).join('\n')
    lastCopyValuesRef.current = { text, values }
  }, [])

  const copySelection = useCallback(async (asMarkdown = false) => {
    copiedFormulaValuesRef.current = []
    const subtotalCells = subtotalSelectionCells()
    if (subtotalCells.length > 0) {
      const text = subtotalCells.map((line) => asMarkdown
        ? `| ${line.map((c) => c.text.replace(/\|/g, '\\|')).join(' | ')} |`
        : line.map((c) => c.text).join('\t')).join('\n')
      try {
        await navigator.clipboard.writeText(text)
rememberCopy(text, asMarkdown)
        setClipboardHasText(true)
      } catch { /* clipboard not available */ }
      return
    }
    if (selected.size > 1) {
      const rowsToCopy = sorted.filter((rowObj) => selected.has(keyOf(rowObj)))
      if (rowsToCopy.length === 0) return
      const headers = visibleColumns.map((originalCol) => headerOf(originalCol))
      const rowValues = rowsToCopy.map((rowObj) => visibleColumns.map((originalCol) => clipboardCellText(columns[originalCol]!, rowObj)))
      let text = ''
      if (asMarkdown) {
        const headerLine = `| ${headers.join(' | ')} |`
        const sepLine = `| ${headers.map(() => '---').join(' | ')} |`
        const dataLines = rowValues.map((r) => `| ${r.map((c) => c.replace(/\|/g, '\\|')).join(' | ')} |`)
        text = [headerLine, sepLine, ...dataLines].join('\n')
      } else {
        const dataLines = rowValues.map((r) => r.join('\t'))
        text = (copyHeaders ? [headers.join('\t'), ...dataLines] : dataLines).join('\n')
      }
      try {
        await navigator.clipboard.writeText(text)
rememberCopy(text, asMarkdown)
        if (anchor && end) setCopied({ anchor, end })
        else setCopied(null)
        setClipboardHasText(true)
      } catch { /* clipboard not available */ }
      return
    }
    if (selectedColumns.size > 0) {
      const targetCols = visibleColumns.filter((colIdx) => selectedColumns.has(colIdx))
      if (targetCols.length > 0) {
        const rowsToIterate = renderedRows.length > 0 ? renderedRows : sorted
        const headers = targetCols.map((colIdx) => headerOf(colIdx))
        const rowValues = rowsToIterate.map((rowObj) =>
          targetCols.map((colIdx) => clipboardCellText(columns[colIdx]!, rowObj))
        )
        let text = ''
        if (asMarkdown) {
          const headerLine = `| ${headers.join(' | ')} |`
          const sepLine = `| ${headers.map(() => '---').join(' | ')} |`
          const dataLines = rowValues.map((r) => `| ${r.map((c) => c.replace(/\|/g, '\\|')).join(' | ')} |`)
          text = [headerLine, sepLine, ...dataLines].join('\n')
        } else {
          const dataLines = rowValues.map((r) => r.join('\t'))
          text = (copyHeaders ? [headers.join('\t'), ...dataLines] : dataLines).join('\n')
        }
        try {
          await navigator.clipboard.writeText(text)
rememberCopy(text, asMarkdown)
          setCopied(null)
          setCopiedColumns(new Set(selectedColumns))
          setClipboardHasText(true)
        } catch { /* clipboard not available */ }
        return
      }
    }
    setCopiedColumns(null)
    if (!range || !anchor || !end) return
    const isFullColumnSelection = range.colMin === range.colMax && range.colMin >= 1 && range.rowMin === 0 && range.rowMax === renderedRows.length - 1
    if (isFullColumnSelection) {
      const originalCol = visibleColumns[range.colMin - 1]
      if (originalCol === undefined) return
      const column = columns[originalCol]
      const header = headerOf(originalCol)
      const dataValues = sorted.map((rowObj) => clipboardCellText(column!, rowObj))
      let text = ''
      if (asMarkdown) {
        const headerLine = `| ${header} |`
        const sepLine = '| --- |'
        const dataLines = dataValues.map((val) => `| ${val.replace(/\|/g, '\\|')} |`)
        text = [headerLine, sepLine, ...dataLines].join('\n')
      } else {
        text = (copyHeaders ? [header, ...dataValues] : dataValues).join('\n')
      }
      try {
        await navigator.clipboard.writeText(text)
rememberCopy(text, asMarkdown)
        setCopied({ anchor, end })
        setClipboardHasText(true)
      } catch { /* clipboard not available */ }
      return
    }
    const colIndices: number[] = []
    const headerCells: string[] = []
    for (let c = range.colMin; c <= range.colMax; c++) {
      const originalCol = visibleColumns[c - 1]
      if (originalCol === undefined) continue
      colIndices.push(originalCol)
      headerCells.push(headerOf(originalCol))
    }
    const rowsValues: string[][] = []
    for (let f = range.rowMin; f <= range.rowMax; f++) {
      const rowObj = renderedRows[f]
      if (!rowObj) continue
      const cells: string[] = []
      for (const originalCol of colIndices) {
        cells.push(clipboardCellText(columns[originalCol]!, rowObj))
      }
      rowsValues.push(cells)
    }
    let text = ''
    if (asMarkdown) {
      const headerLine = `| ${headerCells.join(' | ')} |`
      const sepLine = `| ${headerCells.map(() => '---').join(' | ')} |`
      const dataLines = rowsValues.map((cells) => `| ${cells.map((c) => c.replace(/\|/g, '\\|')).join(' | ')} |`)
      text = [headerLine, sepLine, ...dataLines].join('\n')
    } else {
      const dataLines = rowsValues.map((cells) => cells.join('\t'))
      text = (copyHeaders && headerCells.length > 0 ? [headerCells.join('\t'), ...dataLines] : dataLines).join('\n')
    }
    try {
      await navigator.clipboard.writeText(text)
rememberCopy(text, asMarkdown)
      setCopied({ anchor, end })
      setClipboardHasText(true)
    } catch { /* clipboard not available */ }
  }, [selected, sorted, keyOf, visibleColumns, clipboardCellText, rememberCopy, columns, copyHeaders, headerOf, selectedColumns, anchor, end, range, renderedRows, subtotalSelectionCells])

  const copyAll = useCallback(async (asMarkdown = false) => {
    copiedFormulaValuesRef.current = []
    const rowsToIterate = renderedRows.length > 0 ? renderedRows : sorted
    const headers = visibleColumns.map((colIdx) => headerOf(colIdx))
    const rowValues = rowsToIterate.map((rowObj) =>
      visibleColumns.map((colIdx) => clipboardCellText(columns[colIdx]!, rowObj))
    )
    let text = ''
    if (asMarkdown) {
      const headerLine = `| ${headers.join(' | ')} |`
      const sepLine = `| ${headers.map(() => '---').join(' | ')} |`
      const dataLines = rowValues.map((r) => `| ${r.map((c) => c.replace(/\|/g, '\\|')).join(' | ')} |`)
      text = [headerLine, sepLine, ...dataLines].join('\n')
    } else {
      const dataLines = rowValues.map((r) => r.join('\t'))
      text = (copyHeaders ? [headers.join('\t'), ...dataLines] : dataLines).join('\n')
    }
    try {
      await navigator.clipboard.writeText(text)
rememberCopy(text, asMarkdown)
      if (renderedRows.length > 0 && visibleColumns.length > 0) {
        const firstRowKey = keyOf(renderedRows[0]!)
        const lastRowKey = keyOf(renderedRows[renderedRows.length - 1]!)
        const firstCol = visibleColumns[0]!
        const lastCol = visibleColumns[visibleColumns.length - 1]!
        setCopied({ anchor: { rowKey: firstRowKey, originalCol: firstCol }, end: { rowKey: lastRowKey, originalCol: lastCol } })
      }
      setClipboardHasText(true)
    } catch { /* clipboard not available */ }
  }, [renderedRows, sorted, visibleColumns, headerOf, clipboardCellText, columns, copyHeaders, keyOf, rememberCopy])

  const pasteSelection = useCallback(async (options?: { transpose?: boolean; valuesOnly?: boolean }) => {
    if (!range || !editable) return
    let text: string
    try { text = await navigator.clipboard.readText() } catch { return }
    if (!text) return
    // Values only: copied formulas land as the values they showed, as in Excel and Sheets.
    const valuesOnly = options?.valuesOnly === true
    if (valuesOnly && lastCopyValuesRef.current?.text === text) text = lastCopyValuesRef.current.values
    const textRows = text.replace(/\r/g, '').split('\n')
    if (textRows.length > 1 && textRows[textRows.length - 1] === '') textRows.pop()
    const invalidValues = new Set<string>()
    let typeErrors = 0
    let rejectedValues = 0
    const changes: CellHistoryChange[] = []
    const formulaWrites: Promise<boolean>[] = []
    const overrideWrites: Promise<boolean>[] = []

    // One copied cell over a selection fills all of it, as in Excel and Sheets.
    const copied = textRows.map((line) => line.split('\t'))
    // Transposed: the copied rows land as columns and its columns as rows.
    const block = options?.transpose
      ? Array.from({ length: copied.reduce((widest, line) => Math.max(widest, line.length), 1) }, (_, c) => copied.map((line) => line[c] ?? ''))
      : copied
    const blockRows = block.length
    const blockCols = block.reduce((widest, line) => Math.max(widest, line.length), 1)
    const { rows: targetRows, cols: targetCols } = pasteExtent(
      { rows: blockRows, cols: blockCols },
      { rows: range.rowMax - range.rowMin + 1, cols: range.colMax - range.colMin + 1 },
    )

    for (let deltaRow = 0; deltaRow < targetRows; deltaRow++) {
      const rowObj = renderedRows[range.rowMin + deltaRow]
      if (!rowObj) continue
      const originalIndex = internalRows.indexOf(rowObj)
      if (originalIndex === -1) continue
      const line = block[deltaRow % blockRows] ?? []
      for (let deltaCol = 0; deltaCol < targetCols; deltaCol++) {
        const textValue = line[deltaCol % blockCols] ?? ''
        const originalCol = visibleColumns[range.colMin + deltaCol - 1]
        if (originalCol === undefined) continue
        const column = columns[originalCol]
        if (!column!.field || !isColumnEditable(column!, rowObj)) continue
        if (!valuesOnly && textValue.trim().startsWith('=') && textValue.trim().length > 1) {
          formulaWrites.push(persistCellFormulaWithHistory(rowObj, originalIndex, column!.field, textValue.trim()))
          continue
        }
        const previous = (rowObj as Record<string, unknown>)[column!.field]
        let next: unknown = textValue
        const isBoolCol = column!.cellType === 'checkbox' || typeof previous === 'boolean'
        const isDateCol = column!.dataFormat?.type === 'date' || column!.cellType === 'date' || columnType.get(originalCol) === 'date'
        const isNumCol = column!.dataFormat?.type === 'number' || column!.cellType === 'number' || typeof previous === 'number'

        if (isBoolCol) {
          const clean = textValue.trim().toLowerCase()
          next = clean === 'true' || clean === '1' || clean === 'yes' || clean === 'si' || clean === 'sí' || clean === 'v' || clean === 'x'
        } else if (column!.cellEditorOptions) {
          const clean = textValue.trim()
          if (clean !== '' && !column!.cellEditorOptions.includes(clean)) { invalidValues.add(clean); continue }
          next = clean
        } else if (column!.dataFormat?.type === 'auto') {
          next = parseGridCellInput(textValue, gridCellDataFormat(column!.dataFormat, getRowKey(rowObj, originalIndex)), formats)
        } else if (isDateCol) {
          const parsedDate = parseGridDate(textValue)
          next = parsedDate ? toISODate(parsedDate) : (textValue.trim() === '' ? null : textValue)
        } else if (isNumCol) {
          // Text that is not a number is kept as pasted and flagged, never stripped to digits.
          next = parseGridNumberCell(textValue, formats, column!.dataFormat)
        } else if (typeof previous === 'string' || typeof next === 'string') {
          next = normalizeText(textValue)
        }
        const rowKeyValue = getRowKey(rowObj, originalIndex)
        const previousOverride = overridesMap.get(overrideKey(column!.field, rowKeyValue))
        if (isFormulaField(column!.field, formulaConfig)) {
          const state: CellOverrideState = next === null || next === ''
            ? { kind: 'blank' }
            : { kind: 'value', value: typeof next === 'number' || typeof next === 'string' ? next : String(next) }
          overrideWrites.push(previousOverride?.kind === 'formula'
            ? replaceStoredOverrideWithHistory(rowKeyValue, column!.field, previousOverride, state)
            : applyStoredOverride(rowKeyValue, column!.field, state))
          continue
        }
        if (previousOverride?.kind === 'formula') {
          overrideWrites.push(removeCellFormulaWithHistory(rowKeyValue, column!.field, previousOverride))
        }
        if (cellError(column!, { ...rowObj, [column!.field]: next } as T)) {
          // A reject column keeps its value; the notice says how many were left out.
          if (column!.dataFormat?.invalid === 'reject') { rejectedValues += 1; continue }
          typeErrors += 1
        }
        if (previous === next) continue
        changes.push({ rowKey: rowKeyValue, field: column!.field, previous, next })
      }
    }
    if (formulaWrites.length > 0 || overrideWrites.length > 0) await Promise.all([...formulaWrites, ...overrideWrites])
    applyCellChanges(changes, t('grid.historyPaste', { count: changes.length + formulaWrites.length + overrideWrites.length }))
    if (invalidValues.size > 0) onPasteInvalid?.([...invalidValues])
    setTypeNotice(typeErrors > 0 || rejectedValues > 0 ? { count: typeErrors, rejected: rejectedValues, typed: false } : null)
  }, [applyCellChanges, applyStoredOverride, getRowKey, columns, columnType, editable, formats, formulaConfig, cellError, internalRows, onPasteInvalid, overridesMap, persistCellFormulaWithHistory, range, removeCellFormulaWithHistory, renderedRows, replaceStoredOverrideWithHistory, t, visibleColumns])

  const toggleSelectionInRange = useCallback(() => {
    if (!range) return
    const keys: string[] = []
    for (let f = range.rowMin; f <= range.rowMax; f++) {
      const rowObj = renderedRows[f]
      if (!rowObj) continue
      keys.push(keyOf(rowObj))
    }
    if (keys.length === 0) return
    setSelected((current) => {
      const allChecked = keys.every((k) => current.has(k))
      const copy = new Set(current)
      keys.forEach((k) => allChecked ? copy.delete(k) : copy.add(k))
      return copy
    })
  }, [range, renderedRows, keyOf])

  const toggleCheckboxesInRange = useCallback(() => {
    if (!range || !editable) return
    const targets: { row: T; originalIndex: number; column: GridColumn<T> }[] = []
    for (let f = range.rowMin; f <= range.rowMax; f++) {
      const rowObj = renderedRows[f]
      if (!rowObj) continue
      const originalIndex = internalRows.indexOf(rowObj)
      if (originalIndex === -1) continue
      for (let c = range.colMin; c <= range.colMax; c++) {
        const originalCol = visibleColumns[c - 1]
        if (originalCol === undefined) continue
        const column = columns[originalCol]
        if (!column!.field || !isColumnEditable(column!, rowObj)) continue
        if (typeof readValue(column!, rowObj) !== 'boolean') continue
        targets.push({ row: rowObj, originalIndex, column: column! })
      }
    }
    if (targets.length === 0) return
    const allChecked = targets.every(({ row, column }) => Boolean((row as Record<string, unknown>)[column.field!]))
    const newValue = !allChecked
    const changes: CellHistoryChange[] = []
    targets.forEach(({ row, originalIndex, column }) => {
      const previous = (row as Record<string, unknown>)[column.field!]
      if (previous !== newValue) changes.push({ rowKey: getRowKey(row, originalIndex), field: column.field!, previous, next: newValue })
    })
    applyCellChanges(changes, t('grid.historyCheckboxes', { count: changes.length }))
  }, [applyCellChanges, getRowKey, columns, editable, internalRows, range, renderedRows, t, visibleColumns])

  // Empties every editable cell in the current selection as ONE history task, so a single
  // Ctrl+Z restores the whole block. Both the Delete/Backspace key and the context menu
  // route through here, which is what makes them behave identically on a multi-cell range.
  const clearSelection = useCallback(() => {
    if (!range || !editable) return
    const changes: CellHistoryChange[] = []
    const overrideRemovals: Promise<unknown>[] = []
    for (let f = range.rowMin; f <= range.rowMax; f++) {
      const rowObj = renderedRows[f]
      if (!rowObj) continue
      const originalIndex = internalRows.indexOf(rowObj)
      if (originalIndex === -1) continue
      const rowKeyValue = getRowKey(rowObj, originalIndex)
      for (let c = range.colMin; c <= range.colMax; c++) {
        const originalCol = visibleColumns[c - 1]
        if (originalCol === undefined) continue
        const column = columns[originalCol]
        if (!column?.field) continue
        const field = column.field
        const previousOverride = overridesMap.get(overrideKey(field, rowKeyValue))
        if (previousOverride) {
          overrideRemovals.push(removeOverride(rowKeyValue, field))
        }
        if (!isColumnEditable(column, rowObj)) continue
        const previous = (rowObj as Record<string, unknown>)[field]
        // Checkboxes clear to `false`; everything else clears to an empty string, matching
        // what `confirmEdit` wrote for a single cell before this existed.
        const next = typeof readValue(column, rowObj) === 'boolean' ? false : ''
        if (previous === next) continue
        changes.push({ rowKey: rowKeyValue, field, previous, next })
      }
    }
    if (overrideRemovals.length > 0) {
      void Promise.all(overrideRemovals)
    }
    if (changes.length === 0) return
    applyCellChanges(changes, t('grid.historyClear', { count: changes.length }))
  }, [applyCellChanges, getRowKey, columns, editable, internalRows, range, renderedRows, t, visibleColumns, overridesMap, removeOverride])

  const handleReplaceAll = useCallback(() => {
    if (!editable || !search) return
    const targetCols: number[] = selectedColumns.size > 0
      ? Array.from(selectedColumns)
      : selectedFullColumn !== null && columns[selectedFullColumn]
        ? [selectedFullColumn]
        : visibleColumns.filter((colIdx) => colIdx !== undefined)

    const changes: CellHistoryChange[] = []
    const query = search

    internalRows.forEach((rowObj, originalIndex) => {
      targetCols.forEach((colIdx) => {
        const column = columns[colIdx]
        if (!column?.field || !isColumnEditable(column, rowObj)) return
        if (isFormulaField(column.field, formulaConfig)) return

        const previousVal = (rowObj as Record<string, unknown>)[column.field]
        if (typeof previousVal === 'boolean') return
        const rawText = previousVal === null || previousVal === undefined ? '' : String(previousVal)

        // Case- and accent-preserving replacement or case-insensitive search replacement
        const normalizedQuery = normalizeGridSearchText(query).trim()
        if (!normalizedQuery) return
        const normalizedRaw = normalizeGridSearchText(rawText)
        if (!normalizedRaw.includes(normalizedQuery)) return

        // Perform token/substring replacement
        // For regex search, escape special characters to replace safely
        const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const regex = new RegExp(escaped, 'gi')
        const replacedString = rawText.replace(regex, replaceText)

        if (replacedString === rawText) return

        let next: unknown = replacedString
        const isDateCol = column.dataFormat?.type === 'date' || column.cellType === 'date' || columnType.get(colIdx) === 'date'
        const isNumCol = column.dataFormat?.type === 'number' || column.cellType === 'number' || typeof previousVal === 'number'

        if (isNumCol) {
          const parsed = parseGridNumber(replacedString, formats)
          if (parsed !== null) next = parsed
        } else if (isDateCol) {
          const parsed = parseGridDate(replacedString)
          if (parsed !== null) next = parsed
        }

        changes.push({
          rowKey: getRowKey(rowObj, originalIndex),
          field: column.field,
          previous: previousVal,
          next,
        })
      })
    })

    if (changes.length === 0) return
    applyCellChanges(changes, t('grid.historyReplace', { count: changes.length }))
    // Update search term to the replacement so the grid continues to show the replaced rows
    if (replaceText) setSearch(replaceText)
  }, [applyCellChanges, columnType, columns, editable, formats, formulaConfig, getRowKey, internalRows, replaceText, search, selectedFullColumn, selectedColumns, t, visibleColumns])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      const isCheckbox = target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox'

      // Ctrl+F — focus search input (intercept before browser native find)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F') && !e.shiftKey && !e.altKey) {
        const card = cardRef.current
        const active = document.activeElement
        // Only intercept when focus is inside the grid card or on the window/body
        if (card && (card.contains(active) || active === document.body || active === null)) {
          e.preventDefault()
          // On narrow viewports the search is in the collapsed row
          if (collapsedToolbarKeys.includes('search')) {
            setSearchRowOpen(true)
          }
          // Use rAF so the collapsed row has time to render before we focus
          requestAnimationFrame(() => searchInputRef.current?.focus())
          return
        }
      }

      // Ctrl+H — open replace textbox and focus it (editable grids only)
      if (editable && (e.ctrlKey || e.metaKey) && (e.key === 'h' || e.key === 'H') && !e.shiftKey && !e.altKey) {
        const card = cardRef.current
        const active = document.activeElement
        if (card && (card.contains(active) || active === document.body || active === null)) {
          e.preventDefault()
          if (collapsedToolbarKeys.includes('search')) {
            setSearchRowOpen(true)
          }
          setReplaceVisible(true)
          requestAnimationFrame(() => replaceInputRef.current?.focus())
          return
        }
      }

      // The formula editor is a contenteditable: leave its clipboard shortcuts to the browser
      if (!isCheckbox && (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable)) return
      if (e.key === 'Escape') { setCopied(null); setCopiedColumns(null); return }
      if (!(e.ctrlKey || e.metaKey)) return
      if (e.key === 'c' || e.key === 'C') { e.preventDefault(); void copySelection() }
      else if (e.key === 'v' || e.key === 'V') { e.preventDefault(); void pasteSelection({ valuesOnly: e.shiftKey }) }
    }
    const onCopy = () => {
      const target = document.activeElement as HTMLElement | null
      if (target && (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable)) return
      if (range || subtotalRange || selected.size > 1 || selectedColumns.size > 0) {
        void copySelection()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('copy', onCopy)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('copy', onCopy)
    }
  }, [collapsedToolbarKeys, copySelection, editable, pasteSelection, range, subtotalRange, selected.size, selectedColumns.size])

  useEffect(() => {
    if (!editable || isEmbedded) return
    const checkClipboard = () => {
      navigator.clipboard.readText().then((text) => setClipboardHasText(text.length > 0)).catch(() => { })
    }
    checkClipboard()
    window.addEventListener('focus', checkClipboard)
    return () => window.removeEventListener('focus', checkClipboard)
  }, [editable, isEmbedded])

  /** Choose files, upload them to R2 and append them to an attachment cell. */
  const pickAttachments = (cellKey: string, row: T, originalIndex: number, column: GridColumn<T>, value: unknown) => {
    const documentId = gridConfig.documentId
    if (!documentId) return
    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = true
    input.accept = ACCEPT_ATTRIBUTE
    input.onchange = () => {
      const files = Array.from(input.files ?? [])
      if (files.length === 0) return
      setUploadingCellKey(cellKey)
      void (async () => {
        const added = []
        try {
          for (const file of files) added.push(await uploadAttachment(documentId, file))
        } catch (err) {
          const failure = err instanceof UploadError ? err.failure : { reason: 'failed' as const }
          setOverrideError(failure.reason === 'plan_limit'
            ? failure.key === 'types' ? t('attachments.limit.types')
              : failure.key === 'max_file_bytes' ? t('attachments.fileTooLarge', { max: String(Math.round(Number(failure.limit) / 1048576)) })
              : t('attachments.limit.bytes')
            : failure.reason === 'type' ? t('attachments.typeNotAllowed')
              : failure.reason === 'forbidden' ? t('attachments.limit.forbidden')
              : failure.reason === 'email_unverified' ? t('attachments.limit.emailUnverified')
              : failure.reason === 'rate_limited' ? t('attachments.limit.rateLimited')
                : t('attachments.uploadFailed'))
        } finally {
          setUploadingCellKey((current) => current === cellKey ? null : current)
        }
        if (added.length > 0) confirmEdit(row, originalIndex, column, JSON.stringify([...normalizeAttachments(value), ...added]))
      })()
    }
    input.click()
  }

  const confirmEdit = (row: T, originalIndex: number, column: GridColumn<T>, newText: string) => {
    if (!column.field) return
    const field = column.field
    const currentRowKey = getRowKey(row, originalIndex)
    const currentOverride = overridesMap.get(overrideKey(field, currentRowKey))
    // `-` opens a formula as `=-`; a bare number after it is just a negative value, not a formula.
    if (/^=-\s*\d[\d.,]*\s*%?$/.test(newText.trim())) newText = `-${newText.trim().slice(2).trim()}`
    const typed = newText.trim()
    if (typed.startsWith('=') && typed.length > 1) {
      // A formula typed into a goal-seek column is a way of writing the goal (`-782,79-3,03`):
      // its value is what gets back-solved, rather than the formula sitting on the cell.
      if (
        isFormulaField(field, formulaConfig)
        && !formulaEngine.cycleGroups.has(field)
        && cellOverridesAllowed(formulaConfig, field)
        && overrideTargetOf(formulaConfig, field) !== null
      ) {
        const parsed = parseCellFormula(typed.slice(1), cellFormulaScope, formulaLocale)
        const evaluated = parsed.ok
          ? cellFormulaRuntime.validateCandidate(
            { sheetId: cellFormulaSheetId, rowId: currentRowKey, field },
            toStored(parsed.ast),
          )
          : null
        if (evaluated?.ok && evaluated.value.kind === 'number' && Number.isFinite(evaluated.value.n)) {
          applyGoalSeek(row, originalIndex, field, evaluated.value.n)
          setEditing(null)
          return
        }
      }
      void persistCellFormulaWithHistory(row, originalIndex, field, typed)
      setEditing(null)
      return
    }

    if (currentOverride?.kind === 'formula' && typed === '') {
      void removeCellFormulaWithHistory(currentRowKey, field, currentOverride)
      setEditing(null)
      return
    }

    // A member of a cycle of typeable columns is typed like any stored cell; see below.
    const inCycle = formulaEngine.cycleGroups.has(field)
    if (isFormulaField(field, formulaConfig) && !inCycle) {
      if (cellOverridesAllowed(formulaConfig, field)) {
        if (applyGoalSeek(row, originalIndex, field, newText)) {
          setEditing(null)
          return
        }
        if (currentOverride?.kind === 'formula') {
          const normalized = normalizeText(newText)
          const parsedNumber = parseGridNumber(normalized, formats, column.dataFormat)
          const nextOverride: CellOverrideState = normalized === ''
            ? { kind: 'blank' }
            : { kind: 'value', value: parsedNumber ?? normalized }
          void replaceStoredOverrideWithHistory(currentRowKey, field, currentOverride, nextOverride)
        } else {
          void confirmOverride(row, field, newText)
        }
      }
      setEditing(null)
      return
    }

    const previous = (row as Record<string, unknown>)[field]

    let next: unknown = newText
    const colIndex = columns.indexOf(column)
    const isDateCol = column.dataFormat?.type === 'date' || column.cellType === 'date' || (colIndex !== -1 && columnType.get(colIndex) === 'date')
    const isNumCol = column.dataFormat?.type === 'number' || column.cellType === 'number' || typeof previous === 'number'

    if (column.dataFormat?.type === 'auto') {
      next = parseGridCellInput(newText, gridCellDataFormat(column.dataFormat, currentRowKey), formats)
    } else if (isDateCol) {
      const parsedDate = parseGridDate(newText)
      next = parsedDate ? toISODate(parsedDate) : (newText.trim() === '' ? null : newText)
    } else if (isNumCol) {
      next = parseGridNumberCell(newText, formats, column.dataFormat)
    } else if (typeof previous === 'string') {
      next = normalizeText(newText)
    }
    let keptInvalid = false
    if (cellError(column, { ...row, [field]: next } as T)) {
      if (column.dataFormat?.invalid === 'reject') {
        // Like Excel: nothing is saved, and Retry reopens this cell with what was typed.
        cancelingEdit.current = true
        setEditing(null)
        setRejection({ rowKey: currentRowKey, field, text: newText, anchor, end })
        return
      }
      keptInvalid = true
    }
    setTypeNotice(keptInvalid ? { count: 1, typed: true } : null)
    if (currentOverride?.kind === 'formula') {
      void replaceCellFormulaWithLiteral(row, originalIndex, field, currentOverride, previous, next)
      setEditing(null)
      return
    }
    if (inCycle) {
      // Typing makes this cell the row's input for its cycle, so the stamp moves with the value
      // and one undo puts both back. Retyping the same value still claims the input.
      const rowKey = getRowKey(row, originalIndex)
      const stamp = editedAtField(field)
      applyCellChanges([
        ...(previous === next ? [] : [{ rowKey, field, previous, next }]),
        { rowKey, field: stamp, previous: (row as Record<string, unknown>)[stamp], next: Date.now() },
      ], historyCellLabel(field, previous, next))
      setEditing(null)
      return
    }
    if (previous === next) { setEditing(null); return }
    registerChange(row, field, previous, next, originalIndex)
    setEditing(null)
  }

  const toggleBoolean = (row: T, originalIndex: number, column: GridColumn<T>) => {
    if (!column.field) return
    const previous = normalizeGridCellValue((row as Record<string, unknown>)[column.field], 'checkbox') === true
    registerChange(row, column.field, previous, !previous, originalIndex)
  }

  const activeCell = end ?? anchor
  const activeCellRow = useMemo(() => {
    if (!activeCell) return null
    return renderedRows.find((r) => keyOf(r) === activeCell.rowKey) ?? null
  }, [activeCell, renderedRows, keyOf])
  const confirmEditRef = useRef(confirmEdit)
  confirmEditRef.current = confirmEdit
  const registerChangeRef = useRef(registerChange)
  registerChangeRef.current = registerChange
  const activeCellCol = useMemo(() => {
    if (!activeCell || activeCell.originalCol === CHECKBOX_COL) return null
    return columns[activeCell.originalCol] ?? null
  }, [activeCell, columns])
  const activeCellIsEditable = Boolean(editable && activeCellRow && activeCellCol && activeCellCol.field && isColumnEditable(activeCellCol, activeCellRow))

  // Formula bar in Cell mode: it reads and writes the active cell's own formula (an override).
  const activeCellRowKey = activeCellRow ? keyOf(activeCellRow) : null
  const activeCellField = activeCellCol?.field ?? null
  const activeCellOverride = activeCellRowKey !== null && activeCellField
    ? overridesMap.get(overrideKey(activeCellField, activeCellRowKey))
    : undefined
  const activeCellFormula = activeCellOverride?.kind === 'formula' ? activeCellOverride.formula : null
  const acceptActiveCellFormula = (formula: StoredFormula) => {
    if (!activeCellRow || !activeCellField) return
    const originalIndex = internalRows.indexOf(activeCellRow)
    if (originalIndex === -1) return
    void persistCellFormulaWithHistory(activeCellRow, originalIndex, activeCellField,
      `=${serializeCellFormula(fromStored(formula), cellFormulaScope, formulaLocale)}`)
  }
  const deleteActiveCellFormula = () => {
    if (activeCellRowKey === null || !activeCellField || activeCellOverride?.kind !== 'formula') return
    void removeCellFormulaWithHistory(activeCellRowKey, activeCellField, activeCellOverride)
  }

  const openLinkDialog = useCallback(() => {
    if (!activeCell || !activeCellRow || !activeCellCol || !activeCellCol.field) return
    const cellKey = `${activeCell.rowKey}|${activeCell.originalCol}`
    const td = cellRefs.current.get(cellKey)
    const url = getCellUrl(activeCellCol, activeCellRow, td)
    setLinkDialogValue(url)
    setLinkDialogCopied(false)
    setLinkDialogOpen(true)
  }, [activeCell, activeCellRow, activeCellCol])

  const saveActiveCellLink = useCallback((newUrl: string) => {
    if (!activeCell || !activeCellRow || !activeCellCol || !activeCellCol.field) return
    const originalIndex = internalRows.indexOf(activeCellRow)
    if (originalIndex === -1) return

    const trimmedUrl = newUrl.trim()
    const field = activeCellCol.field
    const rowRec = activeCellRow as Record<string, unknown>
    const urlFieldCandidates = [`${field}Url`, `${field}_url`, `${field}Link`, `${field}_link`]
    const matchingUrlField = urlFieldCandidates.find((cand) => cand in rowRec)

    if (matchingUrlField) {
      const previous = rowRec[matchingUrlField]
      registerChange(activeCellRow, matchingUrlField, previous, trimmedUrl || null, originalIndex)
    } else {
      confirmEdit(activeCellRow, originalIndex, activeCellCol, trimmedUrl)
    }
    setLinkDialogOpen(false)
  }, [activeCell, activeCellRow, activeCellCol, internalRows, confirmEdit, registerChange])

  // ---- Cell context menu (right-click on desktop, long-press on touch) ----

  /**
   * `scope` says what the user pointed at, which decides which items make sense:
   * a header has no single row, and the checkbox gutter is not a real column.
   */
  type MenuScope = 'cell' | 'header' | 'rowGutter'
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; touch: boolean; scope: MenuScope; column?: number } | null>(null)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  /** Moves the selection to the clicked cell UNLESS it already falls inside the current range,
   *  so right-clicking within a multi-cell block keeps that block (standard spreadsheet behavior). */
  const selectForContextMenu = useCallback((rowKey: string, originalCol: number) => {
    const rowPos = rowPosition.get(rowKey)
    const colPos = columnPosition.get(originalCol)
    const inside = Boolean(
      range && rowPos !== undefined && colPos !== undefined &&
      rowPos >= range.rowMin && rowPos <= range.rowMax &&
      colPos >= range.colMin && colPos <= range.colMax,
    )
    if (inside) return
    setAnchor({ rowKey, originalCol })
    setEnd({ rowKey, originalCol })
  }, [columnPosition, range, rowPosition])

  const openContextMenu = useCallback((
    e: React.MouseEvent | { clientX: number; clientY: number; preventDefault: () => void },
    rowKey: string,
    originalCol: number,
  ) => {
    if (isEmbedded || originalCol === CHECKBOX_COL) return
    e.preventDefault()
    selectForContextMenu(rowKey, originalCol)
    setContextMenu({ x: e.clientX, y: e.clientY, touch: false, scope: 'cell' })
  }, [isEmbedded, selectForContextMenu])

  /** Right-click on a column header: selects the whole column (or keeps multi-column selection) and drops the row items. */
  const openHeaderContextMenu = useCallback((e: React.MouseEvent, originalCol: number) => {
    if (isEmbedded) return
    e.preventDefault()
    e.stopPropagation()
    if (!selectedColumns.has(originalCol)) {
      selectColumn(originalCol)
    }
    setContextMenu({ x: e.clientX, y: e.clientY, touch: false, scope: 'header', column: originalCol })
  }, [isEmbedded, selectColumn, selectedColumns])

  /** Right-click on the checkbox gutter: selects the whole row and drops the column items. */
  const openGutterContextMenu = useCallback((e: React.MouseEvent, rowKey: string) => {
    if (isEmbedded) return
    e.preventDefault()
    e.stopPropagation()
    selectRow(rowKey)
    setContextMenu({ x: e.clientX, y: e.clientY, touch: false, scope: 'rowGutter' })
  }, [isEmbedded, selectRow])

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  /** Touch has no right-click, so a ~500ms press on the cell opens the same menu. */
  const startLongPress = useCallback((e: React.PointerEvent, rowKey: string, originalCol: number) => {
    cancelLongPress()
    if (e.pointerType === 'mouse' || isEmbedded || originalCol === CHECKBOX_COL) return
    const { clientX, clientY } = e
    longPressTimer.current = setTimeout(() => {
      longPressTimer.current = null
      selectForContextMenu(rowKey, originalCol)
      setContextMenu({ x: clientX, y: clientY, touch: true, scope: 'cell' })
    }, 500)
  }, [cancelLongPress, isEmbedded, selectForContextMenu])

  useEffect(() => cancelLongPress, [cancelLongPress])

  /** The URL currently on the active cell, resolved the same way `openLinkDialog` resolves it. */
  const activeCellUrl = useMemo(() => {
    if (!contextMenu || !activeCell || !activeCellRow || !activeCellCol?.field) return ''
    const td = cellRefs.current.get(`${activeCell.rowKey}|${activeCell.originalCol}`)
    return getCellUrl(activeCellCol, activeCellRow, td)
  }, [contextMenu, activeCell, activeCellRow, activeCellCol])

  /**
   * Runs a structural edit owned by the page (insert/delete row or column) and, when the
   * page hands back a way to reverse it, puts it on the grid's undo stack so Ctrl+Z walks
   * back through structural changes and cell edits in the one order they happened.
   */
  // A column the saved order has never seen is placed after its neighbour in the column
  // list, so deleting that neighbour would move it. Saving the order on screen first keeps
  // every other column where it is; the deleted one's key stays so undo puts it back.
  const pinColumnOrder = useCallback(async () => {
    if (isEmbedded || !canEditFormats) return
    const savedOrder = columnOrder
      .map((index) => columnKeys[index])
      .filter((key): key is string => key !== undefined)
    const saved = (columnOrderConfig[gridKey] ?? []).map(String)
    if (saved.length === savedOrder.length && saved.every((key, i) => key === savedOrder[i])) return
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.columnOrder, { ...columnOrderConfig, [gridKey]: savedOrder }, false)
    if (!error) await reloadConfig()
  }, [canEditFormats, columnKeys, columnOrder, columnOrderConfig, gridConfig, gridKey, isEmbedded, reloadConfig])

  // The row menu is memoized; this always reaches the latest row order and grouping.
  const pinGroupRowOrderRef = useRef<() => Promise<void>>(async () => {})
  const runStructural = useCallback(async (work: () => void | Promise<void | GridReversible>) => {
    const reversible = await work()
    if (reversible) addHistoryTaskRef.current(reversible)
  }, [])

  // Saves several widths in one write, so a multi-column resize doesn't race itself.
  const shareWidths = useCallback(async (entries: ReadonlyArray<readonly [number, number]>) => {
    const pairs = entries.flatMap(([index, width]) => {
      const key = columnKeys[index]
      return key === undefined ? [] : [[key, width] as const]
    })
    if (pairs.length === 0) return
    const keys = pairs.map(([key]) => key)
    const clearDrafts = () => setWidthsDraft((current) => { const copy = { ...current }; for (const key of keys) delete copy[key]; return copy })
    const nextWidths = { ...widthsConfig, [columnsKey]: { ...(widthsConfig[columnsKey] ?? {}), ...Object.fromEntries(pairs) } }
    markSaving()
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.widths, nextWidths)
    if (error) {
      markFailed()
      clearDrafts()
      setConfigError(`Column width could not be saved: ${error.message}`)
      return
    }
    setConfigError(null)
    clearDrafts()
    await reloadConfig()
    markSaved()
  }, [columnKeys, widthsConfig, columnsKey, gridConfig, markSaving, markSaved, markFailed, reloadConfig])
  const applyHidden = useCallback(async (nextHidden: Set<number>) => {
    const keys = [...nextHidden]
      .map((i) => columnKeys[i])
      .filter((key): key is string => key !== undefined)
    if (canToggleColumns) {
      markSaving()
      const nextHiddenCols = { ...hiddenColumnsConfig }
      if (keys.length === 0) delete nextHiddenCols[columnsKey]
      else nextHiddenCols[columnsKey] = keys
      const error = await saveSettingRef.current(GRID_SETTING_KEYS.hiddenColumns, nextHiddenCols)
      if (error) {
        markFailed()
        console.error('Grid: hidden columns could not be saved', error)
        setConfigError(t('grid.hiddenColumnsSaveError'))
        return
      }
      setConfigError(null)
      await reloadConfig()
      markSaved()
    }
  }, [columnKeys, canToggleColumns, hiddenColumnsConfig, columnsKey, gridConfig, markSaving, markSaved, markFailed, reloadConfig, t])

  const saveColumnGroups = useCallback(async (next: ColumnGroup[]) => {
    if (!canEditFormats) return
    const all = { ...(gridConfig.columnGroups ?? {}) }
    if (next.length === 0) delete all[columnsKey]
    else all[columnsKey] = next
    markSaving()
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.columnGroups, all)
    if (error) {
      markFailed()
      console.error('Grid: column groups could not be saved', error)
      return
    }
    await reloadConfig()
    markSaved()
  }, [canEditFormats, gridConfig, columnsKey, markSaving, markFailed, markSaved, reloadConfig])

  /** The selected columns as positions in the full column order, or null with nothing selected. */
  const selectedOrderSpan = useCallback((fallback: number | null): { start: number; end: number } | null => {
    const indices = selectedColumns.size > 0
      ? [...selectedColumns]
      : range
        ? visibleColumns.filter((i) => { const pos = columnPosition.get(i); return pos !== undefined && pos >= range.colMin && pos <= range.colMax })
        : fallback !== null ? [fallback] : []
    const positions = indices.map((i) => columnOrder.indexOf(i)).filter((p) => p !== -1)
    return positions.length === 0 ? null : { start: Math.min(...positions), end: Math.max(...positions) }
  }, [selectedColumns, range, visibleColumns, columnPosition, columnOrder])
  const groupSelectedColumns = useCallback((fallback: number | null) => {
    const span = selectedOrderSpan(fallback)
    if (span) void saveColumnGroups(addColumnGroup(columnGroups, orderKeys, span.start, span.end))
  }, [selectedOrderSpan, saveColumnGroups, columnGroups, orderKeys])
  const ungroupSelectedColumns = useCallback((fallback: number | null) => {
    const span = selectedOrderSpan(fallback)
    if (span) void saveColumnGroups(removeColumnGroup(columnGroups, orderKeys, span.start, span.end))
  }, [selectedOrderSpan, saveColumnGroups, columnGroups, orderKeys])

  // Alt+Shift+→ groups the selected columns and Alt+Shift+← ungroups them, as in Google Sheets.
  useEffect(() => {
    if (!canEditFormats || isEmbedded) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.altKey || !e.shiftKey || e.ctrlKey || e.metaKey || (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft')) return
      const target = e.target as HTMLElement | null
      if (target && (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable)) return
      const card = cardRef.current
      if (!card || !(card.contains(document.activeElement) || document.activeElement === document.body)) return
      if (selectedColumns.size === 0 && !range) return
      e.preventDefault()
      if (e.key === 'ArrowRight') groupSelectedColumns(null)
      else ungroupSelectedColumns(null)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [canEditFormats, isEmbedded, selectedColumns.size, range, groupSelectedColumns, ungroupSelectedColumns])

  const [groupButtonMenu, setGroupButtonMenu] = useState<{ x: number; y: number; id: number } | null>(null)
  const groupButtonMenuItems = useMemo<GridMenuItem[]>(() => {
    const group = groupButtonMenu ? columnGroups[groupButtonMenu.id] : undefined
    if (!groupButtonMenu || !group) return []
    const id = groupButtonMenu.id
    const side = group.side ?? 'left'
    const setAll = (collapsed: boolean) => columnGroups.map((g) => ({ ...g, collapsed }))
    return [
      group.collapsed
        ? { kind: 'action', key: 'expand', icon: MENU_ICONS.expand, label: t('grid.menu.expandColumnGroup'), onSelect: () => { void saveColumnGroups(updateColumnGroup(columnGroups, id, { collapsed: false })) } }
        : { kind: 'action', key: 'collapse', icon: MENU_ICONS.collapse, label: t('grid.menu.collapseColumnGroup'), onSelect: () => { void saveColumnGroups(updateColumnGroup(columnGroups, id, { collapsed: true })) } },
      { kind: 'action', key: 'remove', icon: MENU_ICONS.trash, label: t('grid.menu.removeColumnGroup'), onSelect: () => { void saveColumnGroups(columnGroups.filter((_, i) => i !== id)) } },
      { kind: 'action', key: 'expand-all', icon: MENU_ICONS.expand, label: t('grid.menu.expandAllColumnGroups'), onSelect: () => { void saveColumnGroups(setAll(false)) } },
      { kind: 'action', key: 'collapse-all', icon: MENU_ICONS.collapse, label: t('grid.menu.collapseAllColumnGroups'), onSelect: () => { void saveColumnGroups(setAll(true)) } },
      side === 'left'
        ? { kind: 'action', key: 'side', icon: MENU_ICONS.arrowRight, label: t('grid.menu.moveGroupButtonRight'), onSelect: () => { void saveColumnGroups(updateColumnGroup(columnGroups, id, { side: 'right' })) } }
        : { kind: 'action', key: 'side', icon: MENU_ICONS.arrowLeft, label: t('grid.menu.moveGroupButtonLeft'), onSelect: () => { void saveColumnGroups(updateColumnGroup(columnGroups, id, { side: 'left' })) } },
    ]
  }, [groupButtonMenu, columnGroups, saveColumnGroups, t])

  const contextMenuItems = useMemo<GridMenuItem[]>(() => {
    if (!contextMenu) return []
    const items: GridMenuItem[] = []
    const scope = contextMenu.scope
    // The gutter is not a column, so nothing column-shaped may key off the cell that
    // selecting the row happened to leave active.
    const onGutter = scope === 'rowGutter'
    // A header knows its column even when the grid has no rows to put an active cell in.
    const activeColIndex = scope === 'header' && contextMenu.column !== undefined
      ? contextMenu.column
      : !onGutter && activeCell && activeCell.originalCol !== CHECKBOX_COL ? activeCell.originalCol : null
    const activeColField = activeColIndex !== null ? (columns[activeColIndex]?.field ?? null) : null
    const canInsertColumn = activeColField !== null && visibleColumns.includes(activeColIndex!)

    items.push({ kind: 'action', key: 'copy', icon: MENU_ICONS.copy, label: t('grid.copySelection'), onSelect: () => { void copySelection() } })
    if (editable) {
      if (copiedHasFormulas) {
        items.push({ kind: 'action', key: 'paste', icon: MENU_ICONS.paste, label: t('grid.pasteFormulas'), onSelect: () => { void pasteSelection() } })
        items.push({ kind: 'action', key: 'paste-values', icon: MENU_ICONS.paste, label: t('grid.pasteValues'), onSelect: () => { void pasteSelection({ valuesOnly: true }) } })
      } else {
        items.push({ kind: 'action', key: 'paste', icon: MENU_ICONS.paste, label: t('grid.paste'), onSelect: () => { void pasteSelection() } })
      }
      items.push({ kind: 'action', key: 'clear', icon: MENU_ICONS.clear, label: t('grid.menu.clearCell'), disabled: !rangeEditable, onSelect: clearSelection })
    }

    if (!isEmbedded && !personalView && (cellColorTargets.length > 0 || fillColumnFields.length > 0)) {
      items.push({ kind: 'separator', key: 'sep-color' })
      items.push({
        kind: 'node',
        key: 'fill-color',
        node: (
          <ColorPicker
            valor={selectedFillColor}
            onChange={(color) => { void applyFillColor(color) }}
            gridFill={gridFillControls}
            coloresUsados={usedCellColors}
            disabled={!canEditFormats || cellColorBusy || cellColorLoading}
            ariaLabel={t('grid.fillColor')}
            title={t('grid.fillColor')}
            triggerClassName="w-full"
            trigger={
              <span className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm sm:py-1.5 sm:text-xs">
                <span
                  aria-hidden="true"
                  className="h-3.5 w-3.5 shrink-0 rounded-full border border-border"
                  style={{ background: selectedFillColor ?? 'transparent' }}
                />
                <span className="min-w-0 flex-1 truncate">{t('grid.fillColor')}</span>
              </span>
            }
          />
        ),
      })
    }

    const rowItems: GridMenuItem[] = []
    if (editable && onInsertRow && activeCellRow && scope !== 'header') {
      const row = activeCellRow
      rowItems.push({
        kind: 'submenu', key: 'insert-row', icon: MENU_ICONS.insertRow, label: t('grid.menu.insertRow'), items: [
          { kind: 'action', key: 'insert-row-above', icon: MENU_ICONS.arrowUp, label: t('grid.menu.insertRowAbove'), onSelect: () => { void runStructural(() => onInsertRow(row, 'above')) } },
          { kind: 'action', key: 'insert-row-below', icon: MENU_ICONS.arrowDown, label: t('grid.menu.insertRowBelow'), onSelect: () => { void runStructural(() => onInsertRow(row, 'below')) } },
        ],
      })
    }
    // Inserting a column is a column action, so it lives on the header menu only.
    if (editable && onInsertColumn && scope === 'header' && canInsertColumn && activeColField !== null) {
      const field = activeColField
      const anchor = (where: 'left' | 'right') =>
        columnInsertAnchor(visibleColumns, columns.map((column, index) => columnKey(column.field, index)), field, where)
      rowItems.push({
        kind: 'submenu', key: 'insert-col', icon: MENU_ICONS.insertColumn, label: t('grid.menu.insertColumn'), items: [
          { kind: 'action', key: 'insert-col-left', icon: MENU_ICONS.arrowLeft, label: t('grid.menu.insertColumnLeft'), onSelect: () => { void runStructural(() => onInsertColumn(anchor('left'))) } },
          { kind: 'action', key: 'insert-col-right', icon: MENU_ICONS.arrowRight, label: t('grid.menu.insertColumnRight'), onSelect: () => { void runStructural(() => onInsertColumn(anchor('right'))) } },
        ],
      })
    }
    if (editable && onDeleteRow && activeCellRow && scope !== 'header') {
      const row = activeCellRow
      rowItems.push({ kind: 'action', key: 'delete-row', icon: MENU_ICONS.trash, label: onBulkRestore ? t('grid.menu.deleteRow') : t('grid.moveToTrash'), onSelect: () => { void (async () => { await pinGroupRowOrderRef.current(); await runStructural(() => onDeleteRow(row)) })() } })
    }
    // Column-wide actions belong to the column, so they only show on its header.
    if (editable && onDeleteColumn && scope === 'header' && (selectedColumns.size > 0 || (activeColField !== null && activeColIndex !== null))) {
      const colsToDelete = selectedColumns.size > 0
        ? Array.from(selectedColumns)
            .map((idx) => ({ field: columns[idx]?.field, index: idx, header: headerOf(idx) }))
            .filter((c): c is { field: string; index: number; header: string } => Boolean(c.field))
        : activeColField !== null && activeColIndex !== null
          ? [{ field: activeColField, index: activeColIndex, header: headerOf(activeColIndex) }]
          : []
      if (colsToDelete.length === 1) {
        const { field } = colsToDelete[0]!
        rowItems.push({
          kind: 'action',
          key: 'delete-col', icon: MENU_ICONS.trash,
          label: t('grid.menu.deleteColumn'),
          onSelect: () => {
            if (!onDeleteColumn) return
            void (async () => {
              await pinColumnOrder()
              await runStructural(() => onDeleteColumn(field))
            })()
          },
        })
      } else if (colsToDelete.length > 1) {
        rowItems.push({
          kind: 'action',
          key: 'delete-col', icon: MENU_ICONS.trash,
          label: t('grid.menu.deleteColumns', { count: colsToDelete.length }),
          onSelect: () => {
            if (!onDeleteColumn) return
            void (async () => {
              await pinColumnOrder()
              for (const item of colsToDelete) {
                await runStructural(() => onDeleteColumn(item.field))
              }
              setSelectedColumns(new Set())
            })()
          },
        })
      }
    }
    if (canEditFormats && scope === 'header' && (selectedColumns.size > 0 || activeColIndex !== null)) {
      const colsToHide = selectedColumns.size > 0 ? Array.from(selectedColumns) : (activeColIndex !== null ? [activeColIndex] : [])
      const isMulti = colsToHide.length > 1
      rowItems.push({
        kind: 'action',
        key: 'hide-col', icon: MENU_ICONS.hide,
        label: isMulti ? t('grid.menu.hideColumns', { count: colsToHide.length }) : t('grid.menu.hideColumn'),
        onSelect: () => {
          const next = new Set(hidden)
          colsToHide.forEach((c) => next.add(c))
          void applyHidden(next)
        },
      })
    }
    if (canEditFormats && scope === 'header' && activeColIndex !== null) {
      const fallback = activeColIndex
      rowItems.push({ kind: 'action', key: 'group-cols', icon: MENU_ICONS.group, label: t('grid.menu.groupColumns'), shortcut: 'Alt+Shift+→', onSelect: () => groupSelectedColumns(fallback) })
      const span = selectedOrderSpan(fallback)
      if (span && columnGroupSpanList.some((g) => g.start <= span.end && g.end >= span.start)) {
        rowItems.push({ kind: 'action', key: 'ungroup-cols', icon: MENU_ICONS.group, label: t('grid.menu.ungroupColumns'), shortcut: 'Alt+Shift+←', onSelect: () => ungroupSelectedColumns(fallback) })
      }
    }
    if (canEditFormats && scope === 'header' && activeColIndex !== null && activeColField !== null) {
      rowItems.push({
        kind: 'action',
        key: 'rename-col', icon: MENU_ICONS.rename,
        label: t('grid.menu.renameColumn'),
        onSelect: () => setEditingHeader(activeColIndex),
      })
      const note = headerNotes[activeColField]
      rowItems.push({
        kind: 'action',
        key: 'note-col', icon: MENU_ICONS.note,
        label: note ? t('grid.menu.editNote') : t('grid.menu.addNote'),
        onSelect: () => setNoteEditor({ field: activeColField, text: note ?? columns[activeColIndex]?.description ?? '' }),
      })
      if (note) {
        rowItems.push({
          kind: 'action',
          key: 'delete-note-col', icon: MENU_ICONS.trash,
          label: t('grid.menu.deleteNote'),
          onSelect: () => { void saveHeaderNote(activeColField, '') },
        })
      }
      const sourceField = changeTimeSource(activeColField) ?? activeColField
      const shown = changeTimeFields.includes(sourceField)
      if (tracksChanges) {
        rowItems.push({
          kind: 'action',
          key: 'change-time-col', icon: MENU_ICONS.clock,
          label: shown ? t('grid.menu.hideChangeTimeColumn') : t('grid.menu.showChangeTimeColumn'),
          onSelect: () => {
            const nextFields = shown ? changeTimeFields.filter((f) => f !== sourceField) : [...changeTimeFields, sourceField]
            void (async () => {
              markSaving()
              const error = await saveSettingRef.current(GRID_SETTING_KEYS.changeTimeColumns, { ...changeTimeColumnsConfig, [gridKey]: nextFields })
              if (error) { markFailed(); return }
              await reloadConfig()
              markSaved()
            })()
          },
        })
      }
      rowItems.push({
        kind: 'action',
        key: 'rollup-col', icon: MENU_ICONS.rollup,
        label: 'Configure Rollup / Lookup…',
        onSelect: () => {
          setFormatTarget(activeColField)
          setRollupDialog(true)
        },
      })
    }
    if (rowItems.length > 0) {
      items.push({ kind: 'separator', key: 'sep-structure' })
      items.push(...rowItems)
    }

    if (activeCellIsEditable) {
      items.push({ kind: 'separator', key: 'sep-link' })
      if (activeCellUrl) {
        items.push({ kind: 'action', key: 'edit-link', icon: MENU_ICONS.link, label: t('grid.menu.editLink'), onSelect: openLinkDialog })
        items.push({ kind: 'action', key: 'remove-link', icon: MENU_ICONS.unlink, label: t('grid.menu.removeLink'), onSelect: () => saveActiveCellLink('') })
      } else {
        items.push({ kind: 'action', key: 'insert-link', icon: MENU_ICONS.link, label: t('grid.menu.insertLink'), onSelect: openLinkDialog })
      }
    }

    // Actions the column offers on its own cells, such as looking a value up.
    const cellActions = scope === 'cell' && editable && activeCellRow && activeCellCol?.cellActions ? activeCellCol.cellActions(activeCellRow) : []
    if (cellActions.length > 0 && activeCellRow && activeCellCol) {
      const row = activeCellRow
      const column = activeCellCol
      items.push({ kind: 'separator', key: 'sep-cell-actions' })
      for (const action of cellActions) {
        items.push({
          kind: 'action', key: `cell-action-${action.key}`, icon: action.icon, label: action.label, disabled: Boolean(action.disabled) || !isColumnEditable(column, row),
          onSelect: () => {
            setCellActionStatus({ state: 'running', label: action.label })
            void action.run().then((value) => {
              const originalIndex = internalRows.indexOf(row)
              setCellActionStatus(value === null || originalIndex === -1 ? null : { state: 'done', label: action.label })
              if (value === null || originalIndex === -1) return
              // A number is stored as is: written out as text, the grid would parse it with the
              // organization's decimal separator and read 0.99 as 99 where that is a comma.
              if (typeof value === 'number') registerChangeRef.current(row, column.field!, (row as Record<string, unknown>)[column.field!], value, originalIndex)
              else confirmEditRef.current(row, originalIndex, column, value)
            }, (error: unknown) => setCellActionStatus({ state: 'error', label: action.label, message: error instanceof Error ? error.message : String(error) }))
          },
        })
      }
    }

    if (canUseFormulas && canEditSharedConfig && activeColIndex !== null) {
      items.push({ kind: 'separator', key: 'sep-formula' })
      items.push({
        kind: 'action', key: 'edit-formula', icon: MENU_ICONS.formula, label: t('grid.menu.editFormula'),
        // The effect that syncs `formulaTarget` to the active cell's column does the
        // targeting; here we only need to make sure the bar is the one occupying the slot.
        onSelect: () => setInternalFormulaBar(true),
      })
    }

    const extra = scope === 'cell' && extraCellMenuItems ? extraCellMenuItems({ row: activeCellRow, field: activeCellCol?.field ?? null }) : []
    if (extra.length > 0) {
      items.push({ kind: 'separator', key: 'sep-extra' })
      items.push(...extra)
    }

    return items
  }, [extraCellMenuItems, 
    activeCell, activeCellCol, activeCellIsEditable, activeCellRow, activeCellUrl, internalRows, applyCellColor, applyHidden, canEditFormats, canEditSharedConfig,
    canUseFormulas, cellColorBusy, cellColorTargets.length, clearSelection, contextMenu, copySelection,
    columns, editable, headerOf, hidden, isEmbedded, onDeleteColumn, pinColumnOrder, changeTimeFields, headerNotes, saveHeaderNote, changeTimeColumnsConfig, gridConfig, gridKey, markFailed, markSaved, markSaving, reloadConfig, tracksChanges, onDeleteRow, onInsertColumn, onInsertRow, openLinkDialog, runStructural, visibleColumns,
    groupSelectedColumns, ungroupSelectedColumns, selectedOrderSpan, columnGroupSpanList,
    copiedHasFormulas, pasteSelection, personalView, rangeEditable, saveActiveCellLink, selectedFillColor, applyFillColor, gridFillControls, fillColumnFields.length, cellColorLoading, selectedColumns, t, usedCellColors,
  ])

  const runHistory = useCallback(async (direction: 'undo' | 'redo', count: number) => {
    for (let i = 0; i < count; i += 1) {
      const current = historyRef.current
      const source = direction === 'undo' ? current.undo : current.redo
      const task = source[source.length - 1]
      if (!task) return
      const applied = direction === 'undo' ? await task.undo() : await task.redo()
      if (!applied) return
      const next: HistoryState = direction === 'undo'
        ? { undo: current.undo.slice(0, -1), redo: [...current.redo, task] }
        : { undo: [...current.undo, task], redo: current.redo.slice(0, -1) }
      historyRef.current = next
      setHistory(next)
    }
  }, [])
  const undo = useCallback(() => { void runHistory('undo', 1) }, [runHistory])
  const redo = useCallback(() => { void runHistory('redo', 1) }, [runHistory])
  const undoMany = useCallback((count: number) => { void runHistory('undo', count) }, [runHistory])
  const redoMany = useCallback((count: number) => { void runHistory('redo', count) }, [runHistory])

  useEffect(() => {
    if (!editorSetHistory) return
    editorSetHistory({
      canUndo: history.undo.length > 0,
      canRedo: history.redo.length > 0,
      undo,
      redo,
      undoMany,
      redoMany,
      undoTasks: history.undo,
      redoTasks: history.redo,
    })
  }, [editorSetHistory, history.redo, history.undo, redo, redoMany, undo, undoMany])

  /** Freeze through column `index` (null: unfreeze) on the devices of `scope`. */
  const setFrozenOn = useCallback(async (index: number | null, scope: DeviceScope) => {
    if (!canEditFormats) return
    const key = index === null ? null : columnKeys[index]
    if (key === undefined) return
    const nextFrozen = { ...frozenColumnsConfig }
    const entry = withFrozenOn(nextFrozen[gridKey], key, scope)
    if (entry === undefined) delete nextFrozen[gridKey]
    else nextFrozen[gridKey] = entry
    markSaving()
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.frozenColumns, nextFrozen)
    if (error) {
      markFailed()
      console.error('Grid: frozen column could not be saved', error)
      setConfigError(t('grid.frozenColumnSaveError'))
      return
    }
    setConfigError(null)
    await reloadConfig()
    markSaved()
  }, [columnKeys, frozenColumnsConfig, gridConfig, gridKey, markSaving, markSaved, markFailed, canEditFormats, reloadConfig])

  /**
   * Show or hide columns on the devices of `scope`, leaving the other device as
   * it was. A column shown on both is plainly visible, on neither is hidden, and
   * on one keeps that device in `grid_column_devices`.
   */
  const setColumnsVisibility = useCallback(async (indices: readonly number[], show: boolean, scope: DeviceScope) => {
    if (!canToggleColumns) return
    const nextHidden = new Set(hidden)
    const nextForGrid: Record<string, 'desktop' | 'mobile'> = { ...(columnDevicesConfig[columnsKey] ?? {}) }
    for (const index of indices) {
      const key = columnKeys[index]
      if (key === undefined) continue
      const only = nextForGrid[key]
      const shownOn = (d: 'desktop' | 'mobile') => scopeIncludes(scope, d) ? show : !hidden.has(index) && scopeIncludes(only, d)
      const desktop = shownOn('desktop')
      const mobile = shownOn('mobile')
      if (desktop || mobile) nextHidden.delete(index)
      else nextHidden.add(index)
      if (desktop !== mobile) nextForGrid[key] = desktop ? 'desktop' : 'mobile'
      else delete nextForGrid[key]
    }
    const nextDevices = { ...columnDevicesConfig }
    if (Object.keys(nextForGrid).length === 0) delete nextDevices[columnsKey]
    else nextDevices[columnsKey] = nextForGrid
    markSaving()
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.columnDevices, nextDevices)
    if (error) {
      markFailed()
      console.error('Grid: column devices could not be saved', error)
      setConfigError(t('grid.hiddenColumnsSaveError'))
      return
    }
    await applyHidden(nextHidden)
  }, [canToggleColumns, hidden, columnKeys, columnDevicesConfig, columnsKey, gridConfig, markSaving, markFailed, applyHidden, t])

  const moveColumn = useCallback(async (source: number, target: number, band?: readonly number[], after = false) => {
    if (isEmbedded || !canEditFormats || source === target) return
    const sourcePos = columnOrder.indexOf(source)
    const targetPos = columnOrder.indexOf(target)
    if (sourcePos === -1 || targetPos === -1) return
    if (band?.includes(target)) return
    const copy = band ? moveColumnBand(columnOrder, band, target, after) : [...columnOrder]
    if (!band) {
      const [moved] = copy.splice(sourcePos, 1)
      copy.splice(targetPos, 0, moved!)
    }
    const savedOrder = copy
      .map((index) => columnKeys[index])
      .filter((key): key is string => key !== undefined)
    markSaving()
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.columnOrder, { ...columnOrderConfig, [gridKey]: savedOrder })
    if (error) {
      markFailed()
      setConfigError(`Column order could not be saved: ${error.message}`)
      return
    }
    setConfigError(null)
    await reloadConfig()
    markSaved()
  }, [canEditFormats, columnKeys, columnOrder, columnOrderConfig, gridConfig, gridKey, isEmbedded, markFailed, markSaved, markSaving, reloadConfig])

  const handleAddColumn = useCallback(async () => {
    if (onAddColumn) {
      await onAddColumn()
    } else if (calculatedColumnsOnly) {
      setFormulaTarget(NEW_COLUMN)
      setInternalFormulaBar(true)
    } else {
      const nextIndex = columns.length + 1
      const nextField = `col_${Date.now().toString(36)}_${nextIndex}`
      setDynamicColumns((prev) => [
        ...prev,
        {
          field: nextField,
          headerName: t('sheet.newColumn', { n: nextIndex }),
          editable: true,
        },
      ])
    }
  }, [onAddColumn, calculatedColumnsOnly, columns.length, t])

  const fitWidthOf = useCallback((index: number): number | null => {
    const column = columns[index]
    if (!column) return null

    // 1. Measure header text (padding in th is p-1: 8px horizontal + 4px breathing room)
    const headerText = headerOf(index)
    let maxNeeded = measureText(headerText, CELL_FONT_SIZE, true) + 12
    if (sort?.index === index) maxNeeded += 12

    // 2. Measure all rows in this column
    const hasDropdown = Boolean(column.dropdown)
    const isChip = column.cellType === 'chip' || column.cellType === 'smartchip'
    const extraBadge = hasDropdown ? 16 : isChip ? 10 : 0

    for (let i = 0; i < internalRows.length; i++) {
      const row = internalRows[i]
      if (!row) continue
      const text = cellText(column, row)
      if (!text) continue
      const lines = text.split('\n')
      for (const line of lines) {
        const w = measureText(line, CELL_FONT_SIZE) + MEASURE_MARGIN + (2 * CELL_PAD) + extraBadge
        if (w > maxNeeded) maxNeeded = w
      }
    }

    const minFloor = Math.max(40, numberAdjustments.get(index)?.minWidth ?? 0)
    return Math.max(minFloor, Math.ceil(maxNeeded))
  }, [columns, headerOf, sort, internalRows, numberAdjustments])

  // Like Excel: resizing a column that is part of a column selection resizes the whole selection.
  const resizeTargets = (index: number): number[] =>
    selectedColumns.size > 1 && selectedColumns.has(index) ? [...selectedColumns] : [index]

  // Each column fits its own content.
  const autoFitColumn = useCallback(async (index: number) => {
    const targets = selectedColumns.size > 1 && selectedColumns.has(index) ? [...selectedColumns] : [index]
    const entries = targets.flatMap((i) => {
      const width = fitWidthOf(i)
      return width === null || columnKeys[i] === undefined ? [] : [[i, width] as const]
    })
    if (entries.length === 0) return
    setWidthsDraft((a) => ({ ...a, ...Object.fromEntries(entries.map(([i, w]) => [columnKeys[i]!, w])) }))
    if (canEditFormats) {
      await shareWidths(entries)
    }
  }, [selectedColumns, fitWidthOf, columnKeys, canEditFormats, shareWidths])

  const startResize = (index: number, event: React.MouseEvent | React.PointerEvent) => {
    if (!canEditFormats) return
    event.preventDefault(); event.stopPropagation()
    if (event.detail === 2) {
      resizeDrag.current = null
      void autoFitColumn(index)
      return
    }
    const targets = resizeTargets(index)
    const measured = event.currentTarget.closest('th')?.getBoundingClientRect().width
    const width0 = widthOf(index) ?? (measured ? measured / (zoom / 100 / textScale) : columns[index]!.minWidth ?? 140)
    resizeDrag.current = { index, x0: event.clientX, width0 }
    const move = (e: MouseEvent | PointerEvent) => {
      if (!resizeDrag.current) return
      const { index: i, x0, width0: startWidth } = resizeDrag.current
      const next = Math.max(minWidthOf(i), startWidth + (e.clientX - x0))
      resizeDrag.current.width = next
      setWidthsDraft((a) => {
        const copy = { ...a }
        for (const t of targets) { const key = columnKeys[t]; if (key !== undefined) copy[key] = next }
        return copy
      })
    }
    const release = () => {
      const drag = resizeDrag.current
      resizeDrag.current = null
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', release)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', release)
      window.removeEventListener('pointercancel', release)
      const width = drag?.width
      if (canEditFormats && width !== undefined) void shareWidths(targets.map((t) => [t, width] as const))
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', release)
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', release)
    window.addEventListener('pointercancel', release)
  }

  const dropColumn = async (target: number, after = false) => {
    const source = columnDrag.current
    const band = columnBandDrag.current
    columnDrag.current = null
    columnBandDrag.current = null
    if (source !== null) {
      // A single column moves as a band of one: "after" means after the target as shown,
      // not the next column index, which is elsewhere once the order has been changed.
      await moveColumn(source, target, band ?? [source], after)
    }
  }

  // Hand-ordering only means something while nothing else decides the order. When a
  // sort or a grouping does, the handle stays - it moves a row between bands instead.
  const canReorderRows = !isEmbedded && !personalView && !sort && !groupBy && groupColumns[0] === null && groupColumns[1] === null
  const canMoveBetweenGroups = !isEmbedded && (onMoveRowsToGroup
    ? groupColumns[0] !== null || groupColumns[1] !== null || Boolean(groupBy)
    : editable && groupField !== null && Boolean(onCellChange))
  // Grouped and unsorted, rows keep their hand order inside each group: dragging within one reorders it.
  const canReorderInGroups = !isEmbedded && !personalView && !sort && !canReorderRows
    && (groupColumns[0] !== null || groupColumns[1] !== null || Boolean(groupBy))
  // Sorted, rows tied on the sort column keep their hand order (the sort is stable): dragging among them reorders it.
  const canReorderSortTies = !isEmbedded && !personalView && Boolean(sort)
  /** Whether the dragged rows and the target share the sort column's value, so a move among them shows. */
  const isSortTie = (sourceKey: string, targetKey: string) => {
    if (!sort || !canReorderSortTies) return false
    const column = columns[sort.index]
    if (!column) return false
    const byKey = new Map(orderedKeyed.map((item) => [item.key, item.row]))
    const target = byKey.get(targetKey)
    if (!target) return false
    const keys = selected.has(sourceKey) && selected.size > 1 ? [...selected] : [sourceKey]
    const targetValue = readValue(column, target)
    return keys.every((key) => {
      const row = byKey.get(key)
      return row !== undefined && compareValues(readValue(column, row), targetValue) === 0
        && rowGroupKey.get(row) === rowGroupKey.get(target)
    })
  }

  /**
   * Re-files the dragged rows into another band by writing the grouped field, which
   * regroups them where they were dropped. It goes through the same call an inline
   * edit makes, so the page saves it the way it saves any other cell - and it lands
   * on the undo stack beside them.
   */
  const moveRowsToGroup = (targetValue: unknown, targetRow?: T) => {
    const source = rowDrag.current
    rowDrag.current = null
    setRowDropIndicator(null)
    if (source === null) return
    const keys = selected.has(source) && selected.size > 1
      ? orderedKeyed.map((item) => item.key).filter((key) => selected.has(key))
      : [source]
    if (onMoveRowsToGroup) {
      if (!targetRow) return
      const byKey = new Map(orderedKeyed.map((item) => [item.key, item.row]))
      const moving = keys.map((key) => byKey.get(key)).filter((row): row is T => row !== undefined)
      const groupFields = groupBy && groupColumns[0] === null && groupColumns[1] === null
        ? [groupBy]
        : groupColumns.flatMap((index) => (index !== null && columns[index]?.field ? [columns[index]!.field!] : []))
      if (moving.length > 0) void onMoveRowsToGroup(moving, targetRow, [...new Set(groupFields)])
      return
    }
    if (!groupField) return
    const changes: CellHistoryChange[] = []
    for (const key of keys) {
      const index = internalRowsRef.current.findIndex((row, position) => getRowKey(row, position) === key)
      const row = internalRowsRef.current[index]
      if (!row) continue
      const previous = (row as Record<string, unknown>)[groupField]
      if (String(previous ?? '') === String(targetValue ?? '')) continue
      changes.push({ rowKey: key, field: groupField, previous, next: targetValue })
    }
    applyCellChanges(changes, t('grid.historyMoveGroup'))
  }

  /** The value a band stands for, read from a row in it rather than from its label. */
  // Filters and the group panel key an empty cell as '(vacío)'; only what is shown is translated.
  const displayFilterValue = (value: string) => value === '(vacío)' ? t('grid.emptyValue') : value
  const groupValueOf = (rows: readonly Record<string, unknown>[]): unknown =>
    groupField ? rows[0]?.[groupField] : undefined

  const dropRow = async (targetKey: string) => {
    const source = rowDrag.current
    if (source === null || source === targetKey) { rowDrag.current = null; return }
    // Grouped: dropping onto a row of another group means joining that group; within
    // its own group, the row only changes place.
    if (sort && isSortTie(source, targetKey)) {
      // Falls through to the hand-order save below.
    } else if (!canReorderRows) {
      const byKey = new Map(orderedKeyed.map((item) => [item.key, item.row]))
      const target = byKey.get(targetKey)
      const sourceRow = byKey.get(source)
      const sameGroup = target !== undefined && sourceRow !== undefined && rowGroupKey.get(target) === rowGroupKey.get(sourceRow)
      if (!sameGroup) {
        if (target && canMoveBetweenGroups) moveRowsToGroup(groupField ? (target as Record<string, unknown>)[groupField] : undefined, target)
        else { rowDrag.current = null; setRowDropIndicator(null) }
        return
      }
      if (!canReorderInGroups) { rowDrag.current = null; setRowDropIndicator(null); return }
    }
    rowDrag.current = null
    const currentOrder = orderedKeyed.map((k) => k.key)
    const isMultiSelected = selected.has(source) && selected.size > 1
    const movingKeys = isMultiSelected
      ? currentOrder.filter((k) => selected.has(k))
      : [source]

    const targetPos = currentOrder.indexOf(targetKey)
    if (targetPos === -1) return

    // Position 'before' or 'after' target
    const isAfter = rowDropIndicator?.position === 'after'
    setRowDropIndicator(null)

    const remaining = currentOrder.filter((k) => !movingKeys.includes(k))
    let insertIndex = remaining.indexOf(targetKey)
    if (insertIndex === -1) {
      insertIndex = Math.min(targetPos, remaining.length)
    } else if (isAfter) {
      insertIndex += 1
    }

    const copy = [...remaining]
    copy.splice(insertIndex, 0, ...movingKeys)
    await saveRowOrder(copy)
  }

  const isGrouped = groupColumns[0] !== null || groupColumns[1] !== null || Boolean(groupBy)
  // Any grouped grid: the group order is its own setting, so a sort or a personal view doesn't get in the way.
  const canReorderGroups = !isEmbedded && isGrouped
  // Without a placed group order, groups line up by their first rows - which deleting a row can change.
  const canPinGroupRows = canReorderGroups && !personalView && !sort && groupRank.size === 0

  const startGroupDrag = (e: React.DragEvent, key: string) => {
    e.stopPropagation()
    groupDrag.current = { key, collapsedBefore: new Set(collapsedGroups) }
    // Folding can unmount the handle (an outline group's label sits in its first row), and
    // React no longer hears a detached node's dragend; the node itself still does.
    e.currentTarget.addEventListener('dragend', () => endGroupDrag(), { once: true })
    e.dataTransfer.effectAllowed = 'move'
    const band = (e.currentTarget as HTMLElement).closest('tr')
    if (band) e.dataTransfer.setDragImage(band, 16, band.offsetHeight / 2)
    // Every group folds while one moves, so only the headers are in the way. After this
    // tick: changing the DOM inside dragstart can cancel the drag.
    const topKeys: string[] = []
    for (const item of baseVisualItems) {
      if (item.type === 'group' && (item.level ?? 0) === 0) topKeys.push(item.groupKey ?? item.label)
    }
    setTimeout(() => {
      setCollapsedGroups((current) => new Set([...current, ...topKeys]))
      setMovingGroup(true)
    }, 0)
  }

  const endGroupDrag = () => {
    const drag = groupDrag.current
    groupDrag.current = null
    setRowDropIndicator(null)
    setMovingGroup(false)
    if (drag) setCollapsedGroups(drag.collapsedBefore)
  }

  const dropGroup = async (targetKey: string, position: 'before' | 'after') => {
    const drag = groupDrag.current
    endGroupDrag()
    if (!drag || drag.key === targetKey) return
    // Every top-level group as shown now, so the saved order pins all of them, not just the moved one.
    const shown: string[] = []
    for (const item of baseVisualItems) {
      if (item.type === 'group' && (item.level ?? 0) === 0) shown.push(item.groupKey ?? item.label)
    }
    const remaining = shown.filter((key) => key !== drag.key)
    const targetIndex = remaining.indexOf(targetKey)
    if (targetIndex === -1) return
    remaining.splice(position === 'before' ? targetIndex : targetIndex + 1, 0, drag.key)
    // Groups not on screen (filtered out) keep their place after the ones shown.
    const previous = isEmbedded ? [] : (localGroupOrder ?? rowOrderConfig[groupOrderKey] ?? [])
    await saveGroupOrder([...remaining, ...previous.filter((key) => !remaining.includes(key))])
  }

  /** Puts one level's groups in label order, A to Z or Z to A; second-level groups sort within each parent. */
  const groupLabelOrder = (level: 0 | 1, dir: 1 | -1): string[] => {
    const byLabel = (a: string, b: string) => compareGroupLabels(a, b) || dir * compareValues(a, b)
    if (level === 0) return [...groupInfo.level1Keys].sort(byLabel)
    const pairs = [...groupInfo.level2Pairs.entries()]
    return pairs.sort(([, [p1, a]], [, [p2, b]]) => (p1 === p2 ? byLabel(a, b) : 0)).map(([key]) => key)
  }
  const groupSortState = (level: 0 | 1): 1 | -1 | null => {
    const keys = level === 0 ? groupInfo.level1Keys : [...groupInfo.level2Pairs.keys()]
    if (keys.length < 2) return null
    const shown = level === 0
      ? [...keys].sort(compareGroupKeys)
      : [...keys].sort((a, b) => (groupRank.get(a) ?? Infinity) - (groupRank.get(b) ?? Infinity))
    for (const dir of [1, -1] as const) {
      const wanted = groupLabelOrder(level, dir)
      // Second level: only the order within each parent matters.
      const same = level === 0
        ? wanted.every((key, i) => key === shown[i])
        : groupInfo.level1Keys.every((parent) => {
          const inParent = (list: string[]) => list.filter((key) => groupInfo.level2Pairs.get(key)?.[0] === parent)
          const a = inParent(wanted), b = inParent(shown)
          return a.every((key, i) => key === b[i])
        })
      if (same) return dir
    }
    return null
  }
  const sortGroups = async (level: 0 | 1, dir: 1 | -1) => {
    const ordered = groupLabelOrder(level, dir)
    const previous = isEmbedded ? [] : (localGroupOrder ?? rowOrderConfig[groupOrderKey] ?? [])
    const placed = new Set(ordered)
    await saveGroupOrder(level === 0
      ? [...ordered, ...previous.filter((key) => !placed.has(key))]
      : [...previous.filter((key) => !placed.has(key)), ...ordered])
  }

  const saveGroupOrder = async (order: string[]) => {
    if (canEditSharedConfig && !personalView) {
      markSaving()
      const error = await saveSettingRef.current(GRID_SETTING_KEYS.rowOrder, { ...rowOrderConfig, [groupOrderKey]: order })
      if (error) {
        markFailed()
        setConfigError(`Group order could not be saved: ${error.message}`)
        return
      }
      setConfigError(null)
      setLocalGroupOrder(null)
      await reloadConfig()
      markSaved()
    } else {
      setLocalGroupOrder(order)
    }
  }

  // Groups line up by their first row, so deleting that row would send the group to
  // wherever its next row happens to sit. Saving the order on screen first - each group's
  // rows together - keeps every group where it is.
  const pinGroupRowOrder = async () => {
    if (!canPinGroupRows) return
    const gCol1 = groupColumns[0] !== null ? columns[groupColumns[0]!] ?? null : null
    const gCol2 = groupColumns[1] !== null && groupColumns[1] !== groupColumns[0] ? columns[groupColumns[1]!] ?? null : null
    const labelOf = (row: T) => (gCol1 || gCol2
      ? cellText((gCol1 ?? gCol2)!, row) || ''
      : String((row as Record<string, unknown>)[groupBy!] ?? ''))
    const firstSeen = new Map<string, number>()
    const keyed = orderedKeyed.map((entry, position) => {
      const l1 = labelOf(entry.row)
      const l2 = gCol1 && gCol2 ? `${l1} > ${cellText(gCol2, entry.row) || ''}` : l1
      if (!firstSeen.has(l1)) firstSeen.set(l1, position)
      if (!firstSeen.has(l2)) firstSeen.set(l2, position)
      return { key: entry.key, l1, l2, position }
    })
    const copy = [...keyed]
      .sort((a, b) => firstSeen.get(a.l1)! - firstSeen.get(b.l1)! || firstSeen.get(a.l2)! - firstSeen.get(b.l2)! || a.position - b.position)
      .map((entry) => entry.key)
    if (copy.every((key, i) => key === keyed[i]!.key)) return
    await saveRowOrder(copy, false)
  }

  pinGroupRowOrderRef.current = pinGroupRowOrder

  const saveRowOrder = async (copy: string[], record = true) => {
    if (canEditSharedConfig) {
      markSaving()
      const error = await saveSettingRef.current(GRID_SETTING_KEYS.rowOrder, { ...rowOrderConfig, [gridKey]: copy }, record)
      if (error) {
        markFailed()
        setConfigError(`Row order could not be saved: ${error.message}`)
        return
      }
      setConfigError(null)
      setLocalRowOrder(null)
      await reloadConfig()
      markSaved()
    } else {
      setLocalRowOrder(copy)
    }
  }

  const selectGroupColumn = useCallback(async (levelIndex: 0 | 1, index: number | null) => {
    if (!canChooseGrouping) return
    const currentGroupCols = groupColumns
    const nextGroupCols: [number | null, number | null] = [...currentGroupCols] as [number | null, number | null]
    nextGroupCols[levelIndex] = index

    if (nextGroupCols[0] === null && nextGroupCols[1] !== null) {
      nextGroupCols[0] = nextGroupCols[1]
      nextGroupCols[1] = null
    }
    if (nextGroupCols[0] !== null && nextGroupCols[0] === nextGroupCols[1]) {
      nextGroupCols[1] = null
    }

    const buildRef = (i: number | null) => (i !== null && columns[i] ? { field: columns[i].field ?? null, header: headerOf(i), index: i } : null)
    const ref1 = buildRef(nextGroupCols[0])
    const ref2 = buildRef(nextGroupCols[1])

    let ref: GridGroupRef = null
    if (ref1 && ref2) ref = [ref1, ref2]
    else if (ref1) ref = ref1
    else if (ref2) ref = ref2

    const nextGrouping = { ...groupingConfig }
    // With a default grouping, "none" must be stored, or the default would come back.
    if (ref) nextGrouping[gridKey] = ref; else if (defaultGroupFields?.length) nextGrouping[gridKey] = null; else delete nextGrouping[gridKey]
    markSaving()
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.grouping, nextGrouping)
    if (error) {
      markFailed()
      setConfigError(`Shared grouping could not be saved: ${error.message}`)
      return
    }
    setConfigError(null)
    await reloadConfig()
    markSaved()
  }, [canChooseGrouping, groupColumns, columns, headerOf, groupingConfig, gridKey, gridConfig, markSaving, markSaved, markFailed, reloadConfig, defaultGroupFields])

  const selectedRows = useMemo(
    () => internalRows.filter((r, i) => selected.has(getRowKey(r, i))),
    [internalRows, getRowKey, selected],
  )
  const allSelected = sorted.length > 0 && sorted.every((r) => selected.has(getRowKey(r, internalRows.indexOf(r))))
  const someSelected = sorted.some((r) => selected.has(getRowKey(r, internalRows.indexOf(r))))
  const selectAllCheckboxRef = useCallback((element: HTMLInputElement | null) => {
    if (element) element.indeterminate = someSelected && !allSelected
  }, [someSelected, allSelected])

  const toggleSelectAll = () => {
    setSelected((current) => {
      const copy = new Set(current)
      if (allSelected) sorted.forEach((r) => copy.delete(getRowKey(r, internalRows.indexOf(r))))
      else sorted.forEach((r) => copy.add(getRowKey(r, internalRows.indexOf(r))))
      return copy
    })
  }
  const toggleRowSelection = (key: string) => {
    setSelected((current) => {
      const copy = new Set(current)
      if (copy.has(key)) copy.delete(key); else copy.add(key)
      return copy
    })
  }

  const runActivate = async () => {
    if (!onBulkActivate) return
    setProcessingBulk(true)
    try {
      await runWithSaveState(() => onBulkActivate(selectedRows))
      setSelected(new Set())
    } finally {
      setProcessingBulk(false)
    }
  }
  const runDeactivate = async () => {
    if (!onBulkDeactivate) return
    setProcessingBulk(true)
    try {
      await runWithSaveState(() => onBulkDeactivate(selectedRows))
      setSelected(new Set())
    } finally {
      setProcessingBulk(false)
    }
  }
  const runRestore = async () => {
    if (!onBulkRestore) return
    setProcessingBulk(true)
    try {
      await runWithSaveState(() => onBulkRestore(selectedRows))
      setSelected(new Set())
    } finally {
      setProcessingBulk(false)
    }
  }
  const runDelete = async () => {
    if (!onBulkDelete) return
    setProcessingBulk(true)
    try {
      await pinGroupRowOrder()
      await runWithSaveState(() => onBulkDelete(selectedRows))
      setSelected(new Set())
      setConfirmingDelete(false)
    } finally {
      setProcessingBulk(false)
    }
  }

  const [moveSheetMenuOpen, setMoveSheetMenuOpen] = useState(false)
  const moveSheetMenuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!moveSheetMenuOpen) return
    const handleClickOutside = (e: PointerEvent) => {
      if (moveSheetMenuRef.current && !moveSheetMenuRef.current.contains(e.target as Node)) {
        setMoveSheetMenuOpen(false)
      }
    }
    document.addEventListener('pointerdown', handleClickOutside)
    return () => document.removeEventListener('pointerdown', handleClickOutside)
  }, [moveSheetMenuOpen])

  const currentSheetId = gridKey.startsWith('sheet:') ? gridKey.slice(6) : gridKey

  const activeHeaderNames = useMemo(() => {
    return columns
      .map((_, i) => headerOf(i).trim().toLowerCase())
      .filter(Boolean)
      .sort()
  }, [columns, headerOf])

  const eligibleMoveSheets = useMemo(() => {
    if (!availableSheets || availableSheets.length === 0 || activeHeaderNames.length === 0) return []
    return availableSheets.filter((sheet) => {
      if (sheet.id === currentSheetId) return false
      const targetOverrides = gridHeadersConfig[`sheet:${sheet.id}`] ?? {}
      const targetHeaderNames = sheet.columns
        .map((c) => (targetOverrides[c.id ?? ''] ?? c.name ?? '').trim().toLowerCase())
        .filter(Boolean)
        .sort()
      if (targetHeaderNames.length !== activeHeaderNames.length) return false
      return targetHeaderNames.every((h, idx) => h === activeHeaderNames[idx])
    })
  }, [availableSheets, currentSheetId, activeHeaderNames, gridHeadersConfig])

  const runMoveRows = async (targetSheetId: string) => {
    if (!onBulkMoveToSheet || selectedRows.length === 0) return
    setProcessingBulk(true)
    try {
      await runWithSaveState(() => onBulkMoveToSheet(selectedRows, targetSheetId))
      setSelected(new Set())
    } finally {
      setProcessingBulk(false)
    }
  }

  const renderRow = (row: T, originalIndex: number) => {
    const currentRowKey = getRowKey(row, originalIndex)
    const dark = darkBand.get(currentRowKey) ?? (originalIndex % 2 === 1)
    const bandSuffix = dark ? 'deep' : 'pale'
    const checkedHighlight = selected.size > 1 && selected.has(currentRowKey)
    const checkboxRowPos = rowPosition.get(currentRowKey)
    const inCheckboxRange = Boolean(range && checkboxRowPos !== undefined
      && checkboxRowPos >= range.rowMin && checkboxRowPos <= range.rowMax
      && range.colMin <= 0 && range.colMax >= 0)
    const checkboxCopiedOverlayStyle = checkboxRowPos !== undefined ? getCopiedBorderStyles(checkboxRowPos, 0, copiedRange) : null
    const hasCheckboxCopiedTop = Boolean(checkboxCopiedOverlayStyle?.borderTop)
    const hasCheckboxCopiedBottom = Boolean(checkboxCopiedOverlayStyle?.borderBottom)
    const hasCheckboxCopiedLeft = Boolean(checkboxCopiedOverlayStyle?.borderLeft)
    const hasCheckboxCopiedRight = Boolean(checkboxCopiedOverlayStyle?.borderRight)
    const checkboxRangeBorders = inCheckboxRange && range ? [
      checkboxRowPos === range.rowMin && !hasCheckboxCopiedTop ? `inset 0 1px 0 0 ${SELECTION_COLOR}` : '',
      checkboxRowPos === range.rowMax && !hasCheckboxCopiedBottom ? `inset 0 -1px 0 0 ${SELECTION_COLOR}` : '',
      range.colMin === 0 && !hasCheckboxCopiedLeft ? `inset 1px 0 0 0 ${SELECTION_COLOR}` : '',
      range.colMax === 0 && !hasCheckboxCopiedRight ? `inset -1px 0 0 0 ${SELECTION_COLOR}` : '',
    ].filter(Boolean) : []
    const checkboxCheckedBorders = checkedHighlight ? [
      `inset 0 1px 0 0 ${SELECTION_COLOR}`,
      `inset 0 -1px 0 0 ${SELECTION_COLOR}`,
      `inset 1px 0 0 0 ${SELECTION_COLOR}`,
    ] : []
    const checkboxCellKey = `${currentRowKey}|${CHECKBOX_COL}`

    const customRowHeight = rowHeightsDraft[currentRowKey]
    // A resized row shows as many whole lines as fit inside its padding (8px top and bottom), at least one.
    const fittedLines = customRowHeight
      ? Math.max(1, Math.floor((customRowHeight - 16) / (renderedCellFontSize() * LINE_HEIGHT) + 0.01))
      : undefined
    const isRowDropTarget = rowDropIndicator?.key === currentRowKey
    const rowDropPos = isRowDropTarget ? rowDropIndicator?.position : null
    const rowDropShadow = rowDropPos === 'before'
      ? 'inset 0 2px 0 0 var(--brand)'
      : rowDropPos === 'after'
        ? 'inset 0 -2px 0 0 var(--brand)'
        : ''

    return (
      <tr key={currentRowKey} ref={(el) => rowRefCallback(currentRowKey, el)} onClick={() => onRowSelected?.(row)}
        onDragOver={isEmbedded ? undefined : (e) => {
          if (rowDrag.current !== null) {
            // Sorted and ungrouped, only a row tied on the sort value takes the drop.
            if (sort && !canMoveBetweenGroups && !isSortTie(rowDrag.current, currentRowKey)) {
              const source = rowDrag.current
              const message = t('grid.dragBlockedSorted', { column: headerOf(sort.index) })
              setRowDropIndicator(null)
              setRowDropRefusal((prev) => (prev && prev.x === e.clientX && prev.y === e.clientY ? prev : { source, x: e.clientX, y: e.clientY, message }))
              return
            }
            if (rowDropRefusal) setRowDropRefusal(null)
            e.preventDefault()
            e.dataTransfer.dropEffect = 'move'
            const rect = e.currentTarget.getBoundingClientRect()
            const pos = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after'
            setRowDropIndicator((prev) => (prev?.key === currentRowKey && prev?.position === pos ? prev : { key: currentRowKey, position: pos }))
          }
        }}
        onDragLeave={isEmbedded ? undefined : () => { setRowDropIndicator(null); setRowDropRefusal(null) }}
        onDrop={isEmbedded ? undefined : (e) => {
          if (rowDrag.current !== null) {
            e.preventDefault()
            void dropRow(currentRowKey)
          }
        }}
        style={{
          background: `var(--cell-edit-${bandSuffix})`,
          ...(customRowHeight ? { height: customRowHeight } : {}),
          ...(rowDropShadow ? { boxShadow: rowDropShadow } : {}),
        }}
        className={`nt-grid-row group border-b border-grid-line${onRowSelected ? ' cursor-pointer' : ''}`}>
        {!isEmbedded && <td
          ref={(el) => { if (el) cellRefs.current.set(checkboxCellKey, el); else cellRefs.current.delete(checkboxCellKey) }}
          tabIndex={0}
          style={{
            width: 38,
            minWidth: 38,
            outline: 'none',
            ...(stickyLeft.size > 0 ? { position: 'sticky' as const, left: 0, zIndex: 2, background: `var(--cell-edit-${bandSuffix})` } : {}),
            ...(inCheckboxRange || checkedHighlight ? { filter: 'brightness(0.95)' } : {}),
            ...([...checkboxRangeBorders, ...checkboxCheckedBorders, rowDropShadow].filter(Boolean).length > 0 ? { boxShadow: [...checkboxRangeBorders, ...checkboxCheckedBorders, rowDropShadow].filter(Boolean).join(', ') } : {}),
          }}
          className="relative cursor-pointer p-2 text-center outline-none"
          onMouseDown={(e) => { e.stopPropagation(); if (e.button !== 0) return; startDrag(currentRowKey, CHECKBOX_COL, e.shiftKey) }}
          onContextMenu={(e) => openGutterContextMenu(e, currentRowKey)}
          onMouseEnter={(e) => extendDrag(currentRowKey, CHECKBOX_COL, e)}
          onClick={(e) => { e.stopPropagation(); setSelectedColumns(new Set()); setSelectedFullColumn(null); setAnchor({ rowKey: currentRowKey, originalCol: CHECKBOX_COL }); setEnd({ rowKey: currentRowKey, originalCol: CHECKBOX_COL }) }}
          onKeyDown={(e) => {
            if (e.ctrlKey || e.metaKey) {
              if (e.key === 'Home') {
                e.preventDefault()
                if (renderedRows.length > 0 && visibleColumns.length > 0) {
                  const cell = { rowKey: keyOf(renderedRows[0]!), originalCol: visibleColumns[0]! }
                  setEnd(cell)
                  if (!e.shiftKey) setAnchor(cell)
                }
                return
              }
              if (e.key === 'End') {
                e.preventDefault()
                if (renderedRows.length > 0 && visibleColumns.length > 0) {
                  const cell = { rowKey: keyOf(renderedRows[renderedRows.length - 1]!), originalCol: visibleColumns[visibleColumns.length - 1]! }
                  setEnd(cell)
                  if (!e.shiftKey) setAnchor(cell)
                }
                return
              }
              if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                e.preventDefault()
                const dirRow = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0
                const dirCol = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0
                moveDataEdge(dirRow, dirCol, e.shiftKey)
                return
              }
            }
            if (e.key === 'ArrowUp') { e.preventDefault(); move(-1, 0, e.shiftKey) }
            else if (e.key === 'ArrowDown') { e.preventDefault(); move(1, 0, e.shiftKey) }
            else if (e.key === 'ArrowLeft') { e.preventDefault(); move(0, -1, e.shiftKey) }
            else if (e.key === 'ArrowRight') { e.preventDefault(); move(0, 1, e.shiftKey) }
            else if (e.key === 'Tab') { e.preventDefault(); move(0, e.shiftKey ? -1 : 1, false) }
            else if (e.key === 'Enter') { e.preventDefault(); move(e.shiftKey ? -1 : 1, 0, false) }
            else if (e.key === 'PageDown') { e.preventDefault(); jumpPage(e.currentTarget, 1, e.shiftKey) }
            else if (e.key === 'PageUp') { e.preventDefault(); jumpPage(e.currentTarget, -1, e.shiftKey) }
            else if (e.key === 'Home') {
              e.preventDefault()
              if (visibleColumns.length > 0) {
                const base = end ?? anchor
                if (base) {
                  const cell = { rowKey: base.rowKey, originalCol: visibleColumns[0]! }
                  setEnd(cell)
                  if (!e.shiftKey) setAnchor(cell)
                }
              }
            } else if (e.key === 'End') {
              e.preventDefault()
              if (visibleColumns.length > 0) {
                const base = end ?? anchor
                if (base) {
                  const cell = { rowKey: base.rowKey, originalCol: visibleColumns[visibleColumns.length - 1]! }
                  setEnd(cell)
                  if (!e.shiftKey) setAnchor(cell)
                }
              }
            }
            else if (e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); toggleSelectionInRange() }
          }}
        >
          {checkboxCopiedOverlayStyle && (
            <div
              className="pointer-events-none absolute inset-0 z-10"
              style={checkboxCopiedOverlayStyle}
            />
          )}
          {rowDropRefusal?.source === currentRowKey && createPortal(
            <div role="tooltip" className="pointer-events-none fixed z-[1000] max-w-xs rounded bg-fg px-2 py-1 text-xs text-bg shadow-lg"
              style={{ left: rowDropRefusal.x + 18, top: rowDropRefusal.y + 4 }}>
              {rowDropRefusal.message}
            </div>,
            document.body,
          )}
          {!isEditingCellFormula && (canReorderRows || canMoveBetweenGroups || canReorderInGroups || canReorderSortTies) && <span
            draggable
            onDragStart={(e) => { e.stopPropagation(); rowDrag.current = currentRowKey; e.dataTransfer.effectAllowed = 'move' }}
            onDragEnd={() => { rowDrag.current = null; setRowDropRefusal(null) }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            title={canReorderRows || !canMoveBetweenGroups ? t('grid.dragReorder') : t('grid.dragToGroup')}
            className="absolute left-0 top-0 flex h-full w-3 cursor-grab items-center justify-center text-[11px] leading-none text-text-muted opacity-0 group-hover:opacity-50 hover:!opacity-100 active:cursor-grabbing"
          >⠿</span>}
          {isEditingCellFormula ? (
            <span className="text-xs text-text-muted tabular-nums select-none font-medium">
              {formulaRowIndexMap.get(currentRowKey) ?? (originalIndex + 1)}
            </span>
          ) : (
            <input type="checkbox" aria-label={`${t('common.row')} ${originalIndex + 1}`} checked={selected.has(currentRowKey)} onChange={() => { toggleRowSelection(currentRowKey); selectRow(currentRowKey) }} onClick={(e) => e.stopPropagation()} tabIndex={-1} className="rounded-full" />
          )}
          {canEditFormats && (
            <span
              onMouseDown={(e) => startRowResize(currentRowKey, e)}
              onPointerDown={(e) => startRowResize(currentRowKey, e)}
              {...doubleTapProps((e) => {
                e.stopPropagation()
                e.preventDefault()
                resetRowHeight(currentRowKey)
              })}
              className="absolute left-0 bottom-0 w-full h-1.5 cursor-row-resize hover:bg-brand active:bg-brand touch-none select-none z-10"
            />
          )}
        </td>}
        {visibleColumns.map((index, columnPosArr) => {
          const columnPos = columnPosArr + 1
          const column = columns[index]
          const spill = column!.wrapMode === 'overflow' && editing?.rowKey !== currentRowKey
            ? overflowSpill(visibleColumns.map((i) => cellText(columns[i]!, row)), columnPosArr)
            : 0
          const value = readValue(column!, row)
          const canEdit = editable && isColumnEditable(column!, row) && Boolean(column!.field)
          const isBoolean = typeof value === 'boolean' || column!.cellType === 'checkbox'
            || (column!.dataFormat?.cells !== undefined && gridCellDataFormat(column!.dataFormat, currentRowKey).type === 'checkbox')
          const ownControl = Boolean(column!.ownControl) || Boolean(column!.dropdown)
          const canStartEdit = (canEdit || (editable && ownControl && Boolean(column!.field))) && !isBoolean
          // A by-cell column aligns each cell by what it holds, as the settings say for that kind.
          const cellType = column!.dataFormat?.type === 'auto'
            ? byCellKind(value, gridCellDataFormat(column!.dataFormat, currentRowKey))
            : columnType.get(index) ?? 'text'
          const cellRule = alignmentConfig[cellType]
          const content = contentStyle(cellRule, column!.multiline, column!.wrapMode, fittedLines)
          const numberAdj = numberAdjustments.get(index)
          const currentKey = currentRowKey
          const isEditing = editing?.rowKey === currentKey && editing.field === column!.field
          const overrideState = column!.field
            ? overridesMap.get(overrideKey(column!.field, currentKey))
            : undefined
          // An override only means something against a formula: once the column has none, its
          // leftover overrides are no longer shown.
          const hasOverride = (overrideState?.kind === 'value' || overrideState?.kind === 'blank') && isFormulaField(column!.field!, formulaConfig)
          const hasCellFormula = overrideState?.kind === 'formula'
          const numericColor = cellType === 'number' && typeof value === 'number' && !isEditing
            ? numberColor(value, cellText(column!, row), formats)
            : undefined
          const isReadOnly = editable && !canEdit && !column!.ownControl && !column!.dropdown
          const rowPos = rowPosition.get(currentKey)
          const inRange = !isEditing && range && rowPos !== undefined
            && rowPos >= range.rowMin && rowPos <= range.rowMax
            && columnPos >= range.colMin && columnPos <= range.colMax
            && (selectedColumns.size === 0 || selectedColumns.has(index))
          const isColSelected = !isEditing && (selectedColumns.size > 0 ? selectedColumns.has(index) : Boolean(inRange))
          const cellHighlight = isColSelected || (!isEditing && checkedHighlight)
          const copiedOverlayStyle = rowPos !== undefined
            ? (getCopiedBorderStyles(rowPos, columnPos, copiedRange) ?? getCopiedColumnBorderStyles(rowPos, columnPos, index, copiedColumns, renderedRows.length, visibleColumns))
            : null
          const hasCopiedTop = Boolean(copiedOverlayStyle?.borderTop)
          const hasCopiedBottom = Boolean(copiedOverlayStyle?.borderBottom)
          const hasCopiedLeft = Boolean(copiedOverlayStyle?.borderLeft)
          const hasCopiedRight = Boolean(copiedOverlayStyle?.borderRight)
          const rangeBorders = inRange && range && selectedColumns.size <= 1 ? [
            rowPos === range.rowMin && !hasCopiedTop ? `inset 0 1px 0 0 ${SELECTION_COLOR}` : '',
            rowPos === range.rowMax && !hasCopiedBottom ? `inset 0 -1px 0 0 ${SELECTION_COLOR}` : '',
            columnPos === range.colMin && !hasCopiedLeft ? `inset 1px 0 0 0 ${SELECTION_COLOR}` : '',
            columnPos === range.colMax && !hasCopiedRight ? `inset -1px 0 0 0 ${SELECTION_COLOR}` : '',
          ].filter(Boolean) : []
          const showSelectionHandles = isPhone && inRange && range && selectedColumns.size === 0 && selectedFullColumn === null && !editing
          const startHandle = showSelectionHandles && rowPos === range.rowMin && columnPos === range.colMin
          const endHandle = showSelectionHandles && rowPos === range.rowMax && columnPos === range.colMax
          const colSelectedBorders = !isEditing && selectedColumns.has(index) && (selectedColumns.size > 1 || !inRange) ? [
            rowPos === 0 && !hasCopiedTop ? `inset 0 1px 0 0 ${SELECTION_COLOR}` : '',
            rowPos === renderedRows.length - 1 && !hasCopiedBottom ? `inset 0 -1px 0 0 ${SELECTION_COLOR}` : '',
            (columnPos === 1 || !selectedColumns.has(visibleColumns[columnPos - 2] ?? -1)) && !hasCopiedLeft ? `inset 1px 0 0 0 ${SELECTION_COLOR}` : '',
            (columnPos === visibleColumns.length || !selectedColumns.has(visibleColumns[columnPos] ?? -1)) && !hasCopiedRight ? `inset -1px 0 0 0 ${SELECTION_COLOR}` : '',
          ].filter(Boolean) : []
          const checkedCellBorders = !isEditing && checkedHighlight ? [
            `inset 0 1px 0 0 ${SELECTION_COLOR}`,
            `inset 0 -1px 0 0 ${SELECTION_COLOR}`,
            columnPos === 1 ? `inset 1px 0 0 0 ${SELECTION_COLOR}` : '',
            columnPos === visibleColumns.length ? `inset -1px 0 0 0 ${SELECTION_COLOR}` : '',
          ].filter(Boolean) : []
          const isColDropTarget = columnDropIndicator?.index === index
          const colDropPos = isColDropTarget ? columnDropIndicator?.position : null
          const colDropShadow = colDropPos === 'before'
            ? 'inset 2px 0 0 0 var(--brand)'
            : colDropPos === 'after'
              ? 'inset -2px 0 0 0 var(--brand)'
              : ''
          const isBoldCol = Boolean(column!.dataFormat?.bold || (column!.field && dataFormatsConfig[gridKey]?.[column!.field]?.bold))
          const cellKey = `${currentKey}|${index}`
          const cellShadows = [...rangeBorders, ...colSelectedBorders, ...checkedCellBorders, rowDropShadow, colDropShadow, ...bandEdgeShadows(index), ...columnBorderShadows(column!.field ? dataFormatsConfig[gridKey]?.[column!.field] : undefined)].filter(Boolean)
          const cellFillColor = column!.field
            ? cellColors.get(cellColorKey(column!.field, currentKey))
            : undefined
          const cellBackground = effectiveFillColor(cellFillColor, columnColors[column!.field ?? ''], `var(--cell-${isReadOnly ? 'ro' : 'edit'}-${bandSuffix})`)

          return <td key={index}
            ref={(el) => { if (el) cellRefs.current.set(cellKey, el); else cellRefs.current.delete(cellKey) }}
            style={{
              width: widthOf(index),
              minWidth: widthOf(index),
              outline: 'none',
              borderRadius: 0,
              background: cellBackground,
              ...(stickyLeft.has(index) ? {
                position: 'sticky' as const,
                left: stickyLeft.get(index),
                zIndex: 2,
              } : {}),
              ...(stickyRight.has(index) ? {
                position: 'sticky' as const,
                right: stickyRight.get(index),
                zIndex: 1,
              } : {}),
              ...(cellHighlight ? { filter: 'brightness(0.95)' } : {}),
              ...(spill > 0 && !stickyLeft.has(index) && !stickyRight.has(index) ? { zIndex: 1 } : {}),
              // A corner dot spills past its cell, so that cell paints over its neighbours.
              ...(startHandle || endHandle ? { zIndex: 3, overflow: 'visible' } : {}),
              ...(numberAdj ? {
                ...(numberAdj.padding !== CELL_PAD ? { padding: numberAdj.padding } : {}),
                ...(numberAdj.reduced ? { fontSize: numberAdj.fontSize } : {}),
              } : {}),
              ...(cellShadows.length > 0 ? { boxShadow: cellShadows.join(', ') } : {}),
            }}
            tabIndex={0}
            // Only the primary button starts a range drag: a right-click must not collapse
            // an existing selection before `onContextMenu` gets a chance to read it.
            onMouseDown={(e) => {
              e.stopPropagation()
              // The emulated mousedown that trails a touch double-tap would move focus to the cell,
              // blurring the editor the double-tap just opened and closing the phone keyboard.
              if (followsDoubleTap(e)) { e.preventDefault(); return }
              // A tap inside the selection keeps it and toggles the action bar instead of collapsing it.
              if (touchTapInSelectionRef.current) {
                touchTapInSelectionRef.current = false
                setSelectionToolbarMenu(null)
                setSelectionToolbar((open) => !open)
                return
              }
              if (e.button !== 0) return
              if (editing && isEditingCellFormula && !isEditing) {
                // Keep focus in the formula editor and reference this cell instead of selecting it.
                e.preventDefault()
                const ref = formulaRefAt(currentKey, index)
                if (ref) setFormulaPointRequest({ ref, nonce: Date.now() })
                return
              }
              const focused = document.activeElement
              if (focused instanceof HTMLElement && focused.dataset.formulaEditor === '1' && !focused.closest('td')) {
                // The formula bar or sidebar is being edited: reference this cell there.
                e.preventDefault()
                const ref = formulaRefAt(currentKey, index, formulaBarMode === 'cell' ? 'cell' : 'column')
                if (ref) setBarPointRequest({ ref, nonce: Date.now() })
                return
              }
              startDrag(currentKey, index, e.shiftKey)
            }}
            onContextMenu={(e) => openContextMenu(e, currentKey, index)}
            onPointerDown={(e) => {
              touchTapInSelectionRef.current = isPhone && e.pointerType === 'touch' && Boolean(inRange) && !editing
              startLongPress(e, currentKey, index)
            }}
            onPointerMove={cancelLongPress}
            onPointerUp={(e) => {
              cancelLongPress()
              if (!isDoubleTap(e, `${currentKey}|${index}`)) return
              if (editing && isEditingCellFormula && !isEditing) return
              if (canStartEdit) { e.stopPropagation(); setEditing({ rowKey: currentKey, field: column!.field! }) }
            }}
            onPointerCancel={cancelLongPress}
            onMouseEnter={(e) => extendDrag(currentKey, index, e)}
            onClick={(e) => { if (canStartEdit) e.stopPropagation() }}
            onDoubleClick={(e) => { if (followsDoubleTap(e)) return; if (editing && isEditingCellFormula && !isEditing) return; if (canStartEdit) { e.stopPropagation(); setEditing({ rowKey: currentKey, field: column!.field! }) } }}
            onKeyDown={(e) => {
              if (isEditing) return
              if (document.querySelector('[data-menu-open="1"], [data-dropdown-open="1"], [data-popover-open="1"], [role="menu"]')) return
              if (e.ctrlKey || e.metaKey) {
                // Ctrl+Space selects the active cell's whole column, as in Excel and Sheets.
                if (e.key === ' ' && index !== CHECKBOX_COL) {
                  e.preventDefault()
                  setActiveHeaderIndex(index)
                  selectColumn(index)
                  return
                }
                if (e.key === 'Home') {
                  e.preventDefault()
                  moveHome(e.shiftKey)
                  return
                }
                if (e.key === 'End') {
                  e.preventDefault()
                  moveEnd(e.shiftKey)
                  return
                }
                if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault()
                  const dirRow = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0
                  const dirCol = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0
                  moveDataEdge(dirRow, dirCol, e.shiftKey)
                  return
                }
              }
              if (e.key === 'Home') { e.preventDefault(); move(0, -99999, e.shiftKey) }
              else if (e.key === 'End') { e.preventDefault(); move(0, 99999, e.shiftKey) }
              else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1, 0, e.shiftKey) }
              else if (e.key === 'ArrowDown') { e.preventDefault(); move(1, 0, e.shiftKey) }
              else if (e.key === 'ArrowLeft') { e.preventDefault(); move(0, -1, e.shiftKey) }
              else if (e.key === 'ArrowRight') { e.preventDefault(); move(0, 1, e.shiftKey) }
              else if (e.key === 'Tab') { e.preventDefault(); move(0, e.shiftKey ? -1 : 1, false) }
              else if (e.key === 'Enter') { e.preventDefault(); move(e.shiftKey ? -1 : 1, 0, false) }
              else if (e.key === 'PageDown') { e.preventDefault(); jumpPage(e.currentTarget, 1, e.shiftKey) }
              else if (e.key === 'PageUp') { e.preventDefault(); jumpPage(e.currentTarget, -1, e.shiftKey) }
              else if (e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); toggleCheckboxesInRange() }
              else if (e.key === 'F2') { if (canStartEdit) { e.preventDefault(); setEditing({ rowKey: currentKey, field: column!.field! }) } }
              else if (!canStartEdit) return
              // A focused cell with no active range (reached by Tab) still clears on its own.
              else if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault()
                const currentOverride = column!.field ? overridesMap.get(overrideKey(column!.field, currentKey)) : undefined
                if (currentOverride) {
                  void removeOverride(currentKey, column!.field!)
                }
                if (range) clearSelection()
                else confirmEdit(row, originalIndex, column!, '')
              }
              else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault()
                // `+` and `-` also open a formula, as in Excel: they become `=+` / `=-`, so the next arrow or click points.
                setEditing({ rowKey: currentKey, field: column!.field!, initialValue: e.key === '+' || e.key === '-' ? `=${e.key}` : e.code === 'NumpadDecimal' ? getGridDecimalSeparator() : e.key })
              }
            }}
            className={`nt-grid-cell group/cell relative outline-none select-none cursor-cell ${numberAdj && numberAdj.padding !== CELL_PAD ? 'px-0 py-2' : 'p-2'} text-sm sm:text-xs ${textClass(cellRule, column!.align)} ${cellType === 'number' || cellType === 'date' ? 'tabular-nums' : ''} ${isBoldCol ? 'font-bold' : ''}`}>
            {formulaPointer && formulaPointer.rowKey === currentKey && formulaPointer.originalCol === index && (
              <div
                className="pointer-events-none absolute inset-0 z-10"
                style={{ border: `2px dashed ${SELECTION_COLOR}` }}
              />
            )}
            {copiedOverlayStyle && (
              <div
                className="pointer-events-none absolute inset-0 z-10"
                style={copiedOverlayStyle}
              />
            )}
            {(startHandle || endHandle) && (['start', 'end'] as const).filter((corner) => corner === 'start' ? startHandle : endHandle).map((corner) => (
              <span
                key={corner}
                aria-hidden="true"
                data-selection-handle={corner}
                onPointerDown={(e) => startSelectionHandleDrag(corner, e)}
                onMouseDown={(e) => { e.stopPropagation(); e.preventDefault() }}
                onClick={(e) => e.stopPropagation()}
                className={`absolute z-20 flex h-7 w-7 items-center justify-center ${corner === 'start' ? '-left-3.5 -top-3.5' : '-bottom-3.5 -right-3.5'} ${selectionHandleDragging ? 'pointer-events-none' : ''}`}
                style={{ touchAction: 'none' }}
              >
                <span className="h-3 w-3 rounded-full" style={{ background: SELECTION_COLOR, boxShadow: '0 0 0 2px var(--color-surface, #fff)' }} />
              </span>
            ))}
            <div className={numberAdj ? 'overflow-hidden whitespace-nowrap leading-snug' : content.className}
style={{ ...(numberAdj ? {} : content.style), ...(numericColor ? { color: numericColor } : null), ...(spill > 0 ? { width: `calc(100% + ${spill}px)` } : null) }}>
                {(() => {
                  const cellContent = isBoolean
                    ? <input type="checkbox" checked={normalizeGridCellValue(value, 'checkbox') === true} disabled={!canEdit} onClick={(e) => e.stopPropagation()} onChange={() => toggleBoolean(row, originalIndex, column!)} className="rounded-full" />
                    : isEditing && !ownControl
                      ? column!.cellEditorOptions
                            ? <select autoFocus defaultValue={String(value ?? '')}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => confirmEdit(row, originalIndex, column!, e.target.value)}
                              onBlur={(e) => { if (cancelingEdit.current) { cancelingEdit.current = false; return } confirmEdit(row, originalIndex, column!, e.target.value) }}
                              onKeyDown={(e) => { if (e.key === 'Escape') { cancelingEdit.current = true; setEditing(null) } }}
                              className="h-7 w-full rounded-none border border-transparent bg-bg px-1 text-sm sm:text-xs outline-none">
                              {column!.cellEditorOptions.map((op) => <option key={op} value={op}>{op}</option>)}
                            </select>
                            : phoneEditor(((column!.dataFormat?.type === 'date' || column!.cellType === 'date' || cellType === 'date') && !hasCellFormula
                              ? <DateCellEditor
                                  value={value}
                                  initialText={editing.initialValue}
                                  formats={formats}
                                  dataFormat={column!.dataFormat}
                                  cellRule={cellRule}
                                  align={column!.align}
                                  onConfirm={(v) => {
                                    cancelingEdit.current = true
                                    confirmEdit(row, originalIndex, column!, v)
                                  }}
                                  onCancel={() => {
                                    cancelingEdit.current = true
                                    setEditing(null)
                                  }}
                                  onNavigate={(rowDelta, colDelta) => {
                                    cancelingEdit.current = true
                                    setEditing(null)
                                    move(rowDelta, colDelta, false)
                                  }}
                                />
                              : column!.dataFormat?.type === 'smartchip' && !hasCellFormula && !(editing.initialValue ?? '').startsWith('=')
                                ? <ListChipEditor
                                  {...(editOptionsOf(column!) ? { onEditOptions: editOptionsOf(column!)! } : {})}                                  items={optionsByField.get(column!.field ?? '')?.items ?? []}
                                  initialText={editing.initialValue ?? (value === null || value === undefined ? '' : String(value))}
                                  onConfirm={(v) => {
                                    cancelingEdit.current = true
                                    confirmEdit(row, originalIndex, column!, v)
                                    setEditing(null)
                                  }}
                                  onCancel={() => {
                                    cancelingEdit.current = true
                                    setEditing(null)
                                  }}
                                />
                              : column!.dataFormat?.type === 'autofill' && !hasCellFormula && !(editing.initialValue ?? '').startsWith('=')
                                ? <AutofillCellEditor
                                  {...(editOptionsOf(column!) ? { onEditOptions: editOptionsOf(column!)! } : {})}                                  options={(optionsByField.get(column!.field ?? '')?.options ?? []).map((option) => option.value)}
                                  initialText={editing.initialValue ?? String(value ?? '')}
                                  className={`min-h-7 w-full rounded-none border border-transparent bg-bg px-1 text-sm sm:text-xs outline-none leading-snug ${textClass(cellRule, column!.align)}`}
                                  onConfirm={(v) => {
                                    cancelingEdit.current = true
                                    confirmEdit(row, originalIndex, column!, v)
                                    setEditing(null)
                                  }}
                                  onCancel={() => {
                                    cancelingEdit.current = true
                                    setEditing(null)
                                  }}
                                  onNavigate={(rowDelta, colDelta, v) => {
                                    cancelingEdit.current = true
                                    confirmEdit(row, originalIndex, column!, v)
                                    setEditing(null)
                                    move(rowDelta, colDelta, false)
                                  }}
                                />
                              : (isEditingCellFormula || (seededEditRef.current !== editing && (editing.initialValue ?? '').startsWith('=')))
                                ? <div
                                  // Floats over the neighbouring cells like Sheets: grows right with the text up to
                                  // the grid's visible edge, then wraps and grows down.
                                  className={isPhone ? 'w-full rounded-lg border border-border bg-bg p-2 text-left' : 'absolute left-0 top-0 z-30 min-w-full w-max min-h-full bg-bg p-2 text-left'}
                                  style={isPhone ? undefined : { boxShadow: `inset 0 0 0 2px ${SELECTION_COLOR}, 0 2px 6px rgba(0,0,0,0.15)` }}
                                  onClick={(e) => e.stopPropagation()}
                                  ref={(el) => {
                                    if (el) {
                                      const container = containerRef.current
                                      const cell = el.parentElement?.closest('td')?.getBoundingClientRect()
                                      if (container && cell) {
                                        // clientWidth leaves out the vertical scrollbar, so the editor never adds a horizontal one.
                                        const right = container.getBoundingClientRect().left + container.clientWidth
                                        el.style.maxWidth = `${Math.max(cell.width, right - cell.left - 8) / (zoom / 100 / textScale)}px`
                                      }
                                    }
                                    cellFormulaCommitRef.current = el ? () => {
                                      cancelingEdit.current = true
                                      confirmEdit(row, originalIndex, column!, cellFormulaDraftRef.current)
                                      setEditing(null)
                                    } : null
                                  }}>
                                    <FormulaEditor
                                      value={cellFormulaDraftRef.current || (editing.initialValue ?? (hasCellFormula
                                        ? `=${serializeCellFormula(fromStored(overrideState.formula), cellFormulaScope, formulaLocale)}`
                                        : '='))}
                                      onChange={(text) => {
                                        cellFormulaDraftRef.current = text
                                        if (!text.startsWith('=')) {
                                          cancelingEdit.current = true
                                          setIsEditingCellFormula(false)
                                        }
                                      }}
                                      onCommit={() => {
                                        cancelingEdit.current = true
                                        confirmEdit(row, originalIndex, column!, cellFormulaDraftRef.current)
                                        setEditing(null)
                                        move(1, 0, false)
                                      }}
                                      onCancel={() => {
                                        cancelingEdit.current = true
                                        setEditing(null)
                                      }}
                                      variant="cell"
                                      singleLine={true}
                                      columns={formulaReferenceTargets ?? formulaTargets}
                                      schema={formulaCompletionSchema}
                                      functionMetadata={formulaFunctionMetadata}
                                      locale={formulaLocale}
                                      insertRequest={formulaInsert}
                                      onPointRef={pointFormulaRef}
                                      pointRequest={formulaPointRequest}
                                      operandRefs={cellOperandRefs}
                                      autoFocus={true}
                                      placeholder={t('grid.formulaPlaceholder')}
                                      // Like Excel and Sheets, only Enter or Escape leave a cell formula; losing
                                      // focus (a click elsewhere) keeps it open.
                                    />
                                  </div>
                                : <textarea ref={(el) => {
                              if (!el) { editingElRef.current = null; return }
                              if (editingElRef.current === el) return
                              editingElRef.current = el
                              el.setSelectionRange(el.value.length, el.value.length)
                              el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'
                            }}
                              autoFocus
                              rows={1}
                              defaultValue={editing.initialValue ?? (hasCellFormula
                                ? `=${serializeCellFormula(fromStored(overrideState.formula), cellFormulaScope, formulaLocale)}`
                                : (
                                column!.dataFormat?.type === 'date' || column!.cellType === 'date'
                                  ? formatGridData(value, column!.dataFormat ?? { type: 'date', formatKey: 'date' }, formats)
                                  : typeof value === 'number'
                                    ? editableGridNumber(value, formats, column!.dataFormat)
                                    : String(value ?? '')
                                ))}
                              inputMode={typeof value === 'number' ? 'decimal' : undefined}
                              onClick={(e) => e.stopPropagation()}
                              onInput={(e) => {
                                const el = e.target as HTMLTextAreaElement
                                el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'
                                cellFormulaDraftRef.current = el.value
                                const startsWithEquals = el.value.startsWith('=')
                                if (startsWithEquals !== isEditingCellFormula) {
                                  cancelingEdit.current = true
                                  setIsEditingCellFormula(startsWithEquals)
                                }
                              }}
                              onBlur={(e) => { if (cancelingEdit.current) { cancelingEdit.current = false; return } confirmEdit(row, originalIndex, column!, (e.target as HTMLTextAreaElement).value) }}
                              onKeyDown={(e) => {
                                if ((e.key === 'Enter' && e.altKey) || (e.key === 'Enter' && e.shiftKey)) {
                                  e.preventDefault()
                                  const el = e.target as HTMLTextAreaElement
                                  const s = el.selectionStart, se = el.selectionEnd
                                  el.value = el.value.substring(0, s) + '\n' + el.value.substring(se)
                                  el.selectionStart = el.selectionEnd = s + 1
                                  el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'
                                } else if (e.key === 'Enter') {
                                  e.preventDefault()
                                  confirmEdit(row, originalIndex, column!, (e.target as HTMLTextAreaElement).value)
                                  cancelingEdit.current = true; setEditing(null); move(1, 0, false)
                                } else if (e.key === 'Tab') {
                                  e.preventDefault()
                                  confirmEdit(row, originalIndex, column!, (e.target as HTMLTextAreaElement).value)
                                  cancelingEdit.current = true; setEditing(null); move(0, e.shiftKey ? -1 : 1, false)
                                } else if (e.key === 'Escape') { cancelingEdit.current = true; setEditing(null) }
                              }}
                              className={`min-h-7 w-full rounded-none border border-transparent bg-bg px-1 text-sm sm:text-xs outline-none leading-snug ${textClass(cellRule, column!.align)}`}
                              style={{ resize: 'none', overflow: 'hidden' }} />), editing.initialValue ?? (typeof value === 'number' ? editableGridNumber(value, formats, column!.dataFormat) : String(value ?? '')))
                      : column!.dropdown
                        ? <DropdownCell
                          options={column!.dropdown.options}
                          value={value === null || value === undefined ? '' : String(value)}
                          placeholder={column!.dropdown.placeholder}
                          emptyLabel={column!.dropdown.emptyLabel}
                          onChange={(v) => { void runWithSaveState(() => column!.dropdown!.onChange(v, originalIndex, row)) }}
                          sizeKey={`tms-desplegable-${columnsKey}-${column!.field ?? headerOf(index)}`}
                          editing={isEditing}
                          initialText={isEditing ? editing?.initialValue : undefined}
                          onStop={() => stopEditingCell(currentKey, column!.field)}
                        />
                        : column!.cellRenderer ? column!.cellRenderer({
                          value, data: row, rowIndex: originalIndex,
                          formatted: column!.dataFormat ? formatGridData(value, column!.dataFormat, formats) : undefined,
                          parseFormatted: (text: string) => parseGridNumber(text, formats, column!.dataFormat),
                          editing: isEditing,
                          initialText: isEditing ? editing?.initialValue : undefined,
                          stopEditing: () => stopEditingCell(currentKey, column!.field),
                        }) : column!.valueFormatter ? column!.valueFormatter({ value, data: row, rowIndex: originalIndex })
                          : column!.dataFormat?.type === 'attachment'
                            ? <AttachmentCell
                              value={value}
                              editable={canEdit && Boolean(gridConfig.documentId)}
                              uploading={uploadingCellKey === currentKey}
                              onChange={(next) => confirmEdit(row, originalIndex, column!, next.length === 0 ? '' : JSON.stringify(next))}
                              onOpenUpload={() => pickAttachments(currentKey, row, originalIndex, column!, value)}
                            />
                          : column!.dataFormat?.type === 'smartchip'
                            ? isEditing
                              ? <ListChipEditor
                                {...(editOptionsOf(column!) ? { onEditOptions: editOptionsOf(column!)! } : {})}                                items={optionsByField.get(column!.field ?? '')?.items ?? []}
                                initialText={editing?.initialValue ?? (value === null || value === undefined ? '' : String(value))}
                                onConfirm={(v) => {
                                  confirmEdit(row, originalIndex, column!, v)
                                  cancelingEdit.current = true
                                  setEditing(null)
                                }}
                                onCancel={() => {
                                  cancelingEdit.current = true
                                  setEditing(null)
                                }}
                              />
                              : <ListChipCell
                                {...(editOptionsOf(column!) ? { onEditOptions: editOptionsOf(column!)! } : {})}                                items={optionsByField.get(column!.field ?? '')?.items ?? []}
                                value={value === null || value === undefined ? '' : String(value)}
                                editable={canEdit}
                                onChange={(v) => confirmEdit(row, originalIndex, column!, v)}
                                {...(column!.align === undefined ? {} : { align: column!.align })}
                                {...(column!.wrapMode === undefined ? {} : { wrapMode: column!.wrapMode })}
                              />
                            : String(value ?? '')

                  const mark = outlineColumns.has(index) ? outlineMarks.get(row)?.find((m) => m.col === index) : undefined
                  // Off a group's first row the value is left out, unless that column repeats it.
                  if (outlineColumns.has(index) && !mark && !(column!.field && repeatGroupLabelFields?.includes(column!.field))) return null
                  if (mark) {
                    const markLevel0 = mark.col === (groupColumns[0] ?? groupColumns[1])
                    return (
                      <span className="relative flex min-w-0 items-center gap-1.5 font-bold">
                        {canReorderGroups && markLevel0 && <span
                          draggable
                          onDragStart={(e) => startGroupDrag(e, mark.key)}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          title={t('grid.dragGroup')}
                          className="-mr-1 flex w-3 shrink-0 cursor-grab items-center justify-center self-stretch text-[11px] leading-none text-text-muted opacity-0 group-hover:opacity-50 hover:!opacity-100 active:cursor-grabbing"
                        >⠿</span>}
                        <button
                          type="button"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); setCollapsedGroups((s) => { const c = new Set(s); if (c.has(mark.key)) c.delete(mark.key); else c.add(mark.key); return c }) }}
                          aria-label={t('grid.collapseGroup')}
                          className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border-strong bg-bg text-[10px] leading-none hover:bg-surface-3"
                        >−</button>
                        <span className="min-w-0 truncate">{mark.label}</span>
                      </span>
                    )
                  }
                  const cellUrl = !isEditing ? getCellUrl(column!, row) : ''
                  if (!cellUrl) return cellContent

                  const isWrap = column!.wrapMode === 'wrap' || (column!.wrapMode !== 'clip' && column!.wrapMode !== 'shrink' && column!.wrapMode !== 'expand' && column!.wrapMode !== 'overflow' && cellRule.wrap)
                  const cellKey = `${currentKey}|${index}`
                  const isCopied = copiedCellKey === cellKey
                  return (
                    <div className="flex w-full min-w-0 items-center justify-between gap-1.5">
                      <div className={`min-w-0 flex-1 ${isWrap ? 'break-words whitespace-normal' : 'overflow-hidden truncate'}`}>
                        {cellContent}
                      </div>
                      <div className="ml-auto inline-flex shrink-0 items-center gap-1 opacity-70 hover:opacity-100 group-hover/cell:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            window.open(cellUrl, '_blank', 'noreferrer,noopener')
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          title={t('grid.openLink')}
                          aria-label={t('grid.openLink')}
                          className="inline-flex h-5 w-5 items-center justify-center rounded p-0.5 text-text-muted hover:bg-surface-3 hover:text-brand transition-colors"
                        >
                          <Icon name="globe" className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            void (async () => {
                              try {
                                await navigator.clipboard.writeText(cellUrl)
                                setCopiedCellKey(cellKey)
                                setTimeout(() => setCopiedCellKey((prev) => (prev === cellKey ? null : prev)), 2000)
                              } catch { /* clipboard not available */ }
                            })()
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          title={isCopied ? t('grid.urlCopied') : t('grid.copyUrl')}
                          aria-label={isCopied ? t('grid.urlCopied') : t('grid.copyUrl')}
                          className="inline-flex h-5 w-5 items-center justify-center rounded p-0.5 text-text-muted hover:bg-surface-3 hover:text-brand transition-colors"
                        >
                          {isCopied ? (
                            <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-brand">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <Icon name="link" className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  )
                })()}
            </div>
            {hasCellFormula && !isEditing && <span
              role="img"
              aria-label={t('grid.cellFormulaBadge')}
              title={t('grid.cellFormulaBadge')}
              className="pointer-events-none absolute right-0.5 top-0.5 z-[1] rounded-sm bg-brand/10 px-0.5 text-[9px] font-bold leading-3 text-brand"
            >fx</span>}
            {!isEditing && !hasCellFormula && column!.dataFormat?.type !== 'autofill' && column!.dataFormat?.type !== 'smartchip' && cellError(column!, row) && <span
              role="img"
              aria-label={t('grid.typeMismatch')}
              title={t('grid.typeMismatch')}
              data-type-error="1"
              className="absolute right-0 top-0 z-[1] h-0 w-0 border-l-[7px] border-t-[7px] border-l-transparent border-t-red-600"
            />}
            {!isEditing && !hasCellFormula && flagErrors && (() => {
              const listType = column!.dataFormat?.type
              if (listType !== 'autofill' && listType !== 'smartchip') return null
              const text = value === null || value === undefined ? '' : String(value).trim()
              const options = optionsByField.get(column!.field ?? '')?.options ?? []
              // An empty list is still loading, or not set up: nothing to check against.
              if (text === '' || options.length === 0 || options.some((option) => option.value === text)) return null
              return <span
                role="img"
                aria-label={t('grid.notInList')}
                title={t('grid.notInList')}
                data-type-error="1"
                className="absolute right-0 top-0 z-[1] h-0 w-0 border-l-[7px] border-t-[7px] border-l-transparent border-t-red-600"
              />
            })()}
            {hasOverride && !isEditing && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  const r = e.currentTarget.getBoundingClientRect()
                  setOverrideMenu((m) => (m?.rowKey === currentKey && m.field === column!.field
                    ? null
                    : { rowKey: currentKey, field: column!.field!, top: r.bottom, left: r.right }))
                }}
                onMouseDown={(e) => e.stopPropagation()}
                data-override-menu="1"
                title={overrideState?.kind === 'blank' ? 'Manually cleared' : 'Manual value'}
                className="absolute right-0 top-0 h-0 w-0 border-b-[8px] border-l-[8px] border-b-transparent border-l-transparent"
                style={{ borderTopWidth: 8, borderTopColor: overrideState?.kind === 'blank' ? 'var(--text-faint)' : 'var(--warning)', borderTopStyle: 'solid' }}
              />
            )}
          </td>
        })}
      </tr>
    )
  }

  const gridItemName = gridConfig.itemNames[gridKey]?.trim()
  const addItemLabel = gridItemName ? t('grid.addItem', { item: gridItemName }) : t('grid.addRow')

  /**
   * Whether group headers count their items, and the label they use. Both are
   * the grid's, not the reader's: the setting is stored per grid alongside the
   * formats, so everyone who opens this sheet sees the same headers. Changing
   * it takes format authority, which is what `grid_settings` already enforces.
   */
  /**
   * The chart is the sheet's: saved per grid, seen by everyone who can read it
   * and changed by anyone who can edit it (RLS lets `grid_chart` through at data level).
   */
  const canEditChart = (gridConfig.canEditData || personalView) && !isEmbedded
  const saveChartSettings = useCallback(async (next: GridChartSettings, record = true) => {
    if (!canEditChart) return
    markSaving()
    // Merge: the panel reports only its own fields, and must not drop open/splits.
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.chart, { ...gridConfig.chart, [gridKey]: { ...gridConfig.chart[gridKey], ...next } }, record)
    if (error) { markFailed(); return }
    await reloadConfig()
    markSaved()
  }, [canEditChart, gridConfig, gridKey, markFailed, markSaved, markSaving, reloadConfig])

  /**
   * The chart panel and the split view open for everyone who opens the sheet:
   * their state rides in the sheet's shared chart settings. Only designers and
   * developers (format rights) open, close or pick them; the toolbar hides the
   * controls from everyone else.
   */
  const sharedChart = gridConfig.chart[gridKey]
  const sharedChartOpen = sharedChart?.open ?? false
  const sharedSplits = (sharedChart?.splits ?? (sharedChart?.split ? [sharedChart.split] : [])).slice(0, MAX_SPLIT_SHEETS).join('\n')
  // Each panel shows only on the devices its scope names; on the others the grid stays alone.
  const chartOpenHere = sharedChartOpen && scopeIncludes(sharedChart?.openOn, device)
  const splitsHere = scopeIncludes(sharedChart?.splitsOn, device) ? sharedSplits : ''
  useEffect(() => {
    if (bare || isEmbedded) return
    setChartPanel(chartOpenHere)
    setSplitSheetIds(splitsHere ? splitsHere.split('\n') : [])
  }, [bare, isEmbedded, chartOpenHere, splitsHere])
  const persistPanels = async (patch: Pick<GridChartSettings, 'open' | 'splits' | 'splitHidden' | 'openOn' | 'splitsOn'>) => {
    if ((!canEditFormats && !personalView) || bare) return
    const { split: _legacy, ...current } = gridConfig.chart[gridKey] ?? {}
    // Opening or splitting a panel is where you look, not a change to undo.
    await saveChartSettings({ ...current, ...patch }, false)
  }

  const showGroupCounts = gridConfig.groupCounts[gridKey] ?? true

  const toggleGroupCounts = useCallback(async () => {
    if (!canEditFormats) return
    const next = { ...gridConfig.groupCounts, [gridKey]: !showGroupCounts }
    markSaving()
    const error = await saveSettingRef.current(GRID_SETTING_KEYS.groupCounts, next)
    if (error) { markFailed(); return }
    await reloadConfig()
    markSaved()
  }, [canEditFormats, gridConfig, gridKey, markFailed, markSaved, markSaving, reloadConfig, showGroupCounts])

  /**
   * What a group header counts: "4 lodgings" rather than a bare "4". The noun is
   * the grid's own item name — the same one the Add button uses — pluralised by
   * adding an `s`, which is the rule the names in use follow. Without one, the
   * generic row/rows carries the language's real plural rules.
   */
  const groupCountLabel = (count: number) => (
    gridItemName
      ? `${formatNumber(count)} ${count === 1 ? gridItemName : `${gridItemName}s`}`
      : t('grid.groupCount', { count })
  )
  const searchCollapsed = collapsedToolbarKeys.includes('search')

  /**
   * "Add row" split button. It renders either as the first toolbar control (28px,
   * matching the rest of the bar) or floating over the grid, the placement the user
   * picks from its own dropdown; the choice is stored per grid in localStorage.
   */
  const renderAddRowButton = (placement: 'toolbar' | 'grid') => {
    const inGrid = placement === 'grid'
    return (
      <div
        ref={fabMenuRef}
        style={inGrid ? { bottom: fabBottom } : undefined}
        className={inGrid
          ? 'fixed right-2.5 sm:right-7 z-30 flex h-11 items-center rounded-full bg-accent text-accent-fg shadow-lg select-none hover:bg-accent-hover'
          : 'relative flex h-7 shrink-0 items-center rounded-full bg-accent text-accent-fg shadow-sm select-none hover:bg-accent-hover'}
      >
        <button
          type="button"
          onClick={() => {
            setLocalSort('none')
            pendingActivateNewRow.current = true
            if (onAddRow) {
              void runWithSaveState(() => onAddRow())
            } else if (onAddRows) {
              void runWithSaveState(() => onAddRows(1))
            }
          }}
          aria-label={addItemLabel}
          title={addItemLabel}
          className={`flex h-full items-center rounded-l-full font-semibold text-accent-fg transition-transform active:scale-95 focus-visible:outline-none ${inGrid ? 'gap-1.5 pl-4 pr-2 text-xs' : 'gap-1 pl-2.5 pr-1 text-sm sm:text-xs'}`}
        >
          <Icon name="plus" className={`shrink-0 ${inGrid ? 'h-4 w-4' : 'h-3.5 w-3.5'}`} />
          <span className="whitespace-nowrap">{addItemLabel}</span>
        </button>

        <div className={`w-[1px] bg-accent-fg/20 ${inGrid ? 'h-4' : 'h-3.5'}`} />

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setFabMenuOpen((v) => !v)
          }}
          aria-label={t('grid.addButtonOptions')}
          title={t('grid.addButtonOptions')}
          aria-haspopup="menu"
          aria-expanded={fabMenuOpen}
          className={`flex h-full items-center justify-center rounded-r-full text-accent-fg transition-transform active:scale-95 focus-visible:outline-none ${inGrid ? 'pl-2 pr-3.5' : 'pl-1 pr-2'}`}
        >
          <Caret className={`transition-transform duration-150 ${fabMenuOpen ? 'rotate-180' : ''}`} />
        </button>

        {fabMenuOpen && (
          <div
            role="menu"
            aria-label={t('grid.addButtonOptions')}
            className={`absolute z-50 flex min-w-44 flex-col rounded-xl border border-border bg-surface py-1 text-sm sm:text-xs shadow-card text-text ${inGrid ? 'bottom-[calc(100%+0.5rem)] right-0' : 'left-0 top-[calc(100%+0.35rem)]'}`}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setRenameValue(gridConfig.itemNames[gridKey]?.trim() ?? '')
                setFabMenuOpen(false)
                setRenameDialogOpen(true)
              }}
              className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-3 transition-colors"
            >
              <Icon name="pencil" className="h-3.5 w-3.5 shrink-0 text-text-muted" />
              <span>{t('grid.renameItemName')}</span>
            </button>
            <div className="flex items-center gap-2 pr-2 hover:bg-surface-3 transition-colors">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setFabMenuOpen(false)
                  moveAddButton(!inGrid)
                }}
                className="flex min-w-0 flex-1 items-center gap-2 py-2 pl-3 text-left"
              >
                <Icon name="move" className="h-3.5 w-3.5 shrink-0 text-text-muted" />
                <span>{t(inGrid ? 'grid.moveAddButtonToToolbar' : 'grid.moveAddButtonToGrid')}</span>
              </button>
              <div role="radiogroup" aria-label={t('grid.addButtonMoveScope')} className="flex shrink-0 items-center gap-0.5 rounded-full border border-border bg-surface p-0.5">
                {([['all', 'devices', 'grid.addButtonScopeAll'], ['desktop', 'monitor', 'grid.addButtonScopeDesktop'], ['mobile', 'smartphone', 'grid.addButtonScopeMobile']] as const).map(([scope, icon, label]) => (
                  <button
                    key={scope}
                    type="button"
                    role="radio"
                    aria-checked={addButtonMoveScope === scope}
                    aria-label={t(label)}
                    title={t(label)}
                    onClick={(e) => { e.stopPropagation(); setAddButtonMoveScope(scope) }}
                    className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors ${addButtonMoveScope === scope ? 'bg-accent-soft text-accent' : 'text-text-muted hover:text-text'}`}
                  >
                    <Icon name={icon} className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>
            </div>

          </div>
        )}
      </div>
    )
  }

  // The outline bar over the headers: one row per nesting level, a line over each
  // open group and its +/− button over the column beside it, on the group's chosen side.
  const outlineLevels = columnGroupSpanList.length === 0 ? 0 : Math.max(...columnGroupSpanList.map((g) => g.level)) + 1
  const renderColumnOutlineRow = (level: number) => {
    const orderPos = (index: number) => columnOrder.indexOf(index)
    const spans = columnGroupSpanList.filter((g) => g.level === level)
    // The visible column that carries a group's button: just outside the group on its side,
    // else just outside on the other side (a group at the sheet's edge), else its own edge column.
    // `toRight` says where the group lies from there, which is where the arrow points.
    const anchorOf = (g: ColumnGroupSpan): { at: number; toRight: boolean } | undefined => {
      const before = [...visibleColumns].reverse().find((i) => orderPos(i) < g.start)
      const after = visibleColumns.find((i) => orderPos(i) > g.end)
      const preferred = g.side === 'left' ? [before, after] : [after, before]
      const outside = preferred.find((i) => i !== undefined)
      if (outside !== undefined) return { at: outside, toRight: outside === before }
      const inside = visibleColumns.filter((i) => orderPos(i) >= g.start && orderPos(i) <= g.end)
      const edge = g.side === 'left' ? inside[0] : inside[inside.length - 1]
      return edge === undefined ? undefined : { at: edge, toRight: g.side === 'left' }
    }
    const buttons = new Map<number, (ColumnGroupSpan & { toRight: boolean })[]>()
    for (const g of spans) {
      const anchor = anchorOf(g)
      if (anchor) buttons.set(anchor.at, [...(buttons.get(anchor.at) ?? []), { ...g, toRight: anchor.toRight }])
    }
    const button = (g: ColumnGroupSpan & { toRight: boolean }) => {
      const label = g.collapsed ? t('grid.expandColumnGroup') : t('grid.collapseColumnGroup')
      return <button key={g.id} type="button" aria-label={label} title={label} disabled={!canEditFormats}
        onClick={(e) => { e.stopPropagation(); void saveColumnGroups(updateColumnGroup(columnGroups, g.id, { collapsed: !g.collapsed })) }}
        onContextMenu={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (canEditFormats && !isEmbedded) setGroupButtonMenu({ x: e.clientX, y: e.clientY, id: g.id })
        }}
        className="flex items-center gap-0.5 leading-none text-text-muted hover:text-text disabled:cursor-default">
        {g.collapsed && !g.toRight && <span className="text-[9px]">◀</span>}
        <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-[3px] border text-[11px] font-bold ${g.collapsed ? 'border-text bg-text text-surface' : 'border-current bg-grid-head'}`}>{g.collapsed ? '+' : '−'}</span>
        {g.collapsed && g.toRight && <span className="text-[9px]">▶</span>}
      </button>
    }
    return <tr key={`outline-${level}`}>
      {!isEmbedded && <th className="bg-grid-head p-0" style={{ height: 20, ...(stickyLeft.size > 0 ? { position: 'sticky', left: 0, zIndex: 12 } : {}) }} />}
      {visibleColumns.map((index) => {
        const pos = orderPos(index)
        const open = spans.find((g) => !g.collapsed && pos >= g.start && pos <= g.end)
        const here = buttons.get(index) ?? []
        const lefts = here.filter((g) => !g.toRight)
        const rights = here.filter((g) => g.toRight)
        const sticky = stickyLeft.get(index)
        let line: React.ReactNode = null
        if (open) {
          const members = visibleColumns.filter((i) => orderPos(i) >= open.start && orderPos(i) <= open.end)
          // The far end from the button gets a tick down, as in Sheets.
          const tickEnd = open.side === 'left' ? members[members.length - 1] === index : members[0] === index
          line = <span className="pointer-events-none absolute left-0 right-0 top-1/2 border-t border-text-muted" style={tickEnd ? (open.side === 'left' ? { right: 4 } : { left: 4 }) : undefined}>
            {tickEnd && <span className={`absolute top-0 h-1.5 border-l border-text-muted ${open.side === 'left' ? 'right-0' : 'left-0'}`} />}
          </span>
        }
        return <th key={index} className="relative bg-grid-head p-0" style={{ height: 20, ...(sticky !== undefined ? { position: 'sticky', left: sticky, zIndex: 11 } : {}) }}>
          {line}
          {lefts.length > 0 && <span className="absolute inset-y-0 left-0.5 flex items-center gap-1">{lefts.map(button)}</span>}
          {rights.length > 0 && <span className="absolute inset-y-0 right-0.5 flex items-center gap-1">{rights.map(button)}</span>}
        </th>
      })}
    </tr>
  }

  const renderHeaderCell = (index: number, columnPos: number, rowSpan?: number) => {
    const column = columns[index]
    const dir = sort?.index === index ? sort.dir : 0
    const filterActive = filters[index] !== undefined
    const isColSelected = selectedColumns.size > 0
      ? selectedColumns.has(index)
      : Boolean(range && columnPos >= range.colMin && columnPos <= range.colMax)
    const isDropTarget = columnDropIndicator?.index === index
    const dropPosition = isDropTarget ? columnDropIndicator?.position : null
    const dropShadow = dropPosition === 'before'
      ? 'inset 2px 0 0 0 var(--brand)'
      : dropPosition === 'after'
        ? 'inset -2px 0 0 0 var(--brand)'
        : ''

    const canReorderCol = !isEmbedded && canEditFormats && editingHeader !== index && visibleColumns.length > 1
    const headingRule = resolveHeaderRule(column!.headerFormat ?? column!.dataFormat, alignmentConfig, columnType.get(index) ?? 'text', column!.align, column!.wrapMode)
    const headingWrap = ruleWrapMode(headingRule, 'text')
    const headingText = `${headerOf(index)}${dir === 1 ? ' ▲' : dir === -1 ? ' ▼' : ''}`
    const headingWidth = Math.max(1, (widthOf(index) ?? 140) - 8)
    const headingFontSize = headingWrap === 'shrink' ? `${Math.max(0.6, Math.min(1, headingWidth / Math.max(1, measureText(headingText, renderedCellFontSize(), true))))}em` : undefined
    let headingSpill = 0
    if (headingWrap === 'overflow') for (let next = columnPos + 1; next < visibleColumns.length; next++) {
      const nextIndex = visibleColumns[next]!
      if (headerOf(nextIndex).trim() !== '' || stickyLeft.has(nextIndex) || stickyRight.has(nextIndex)) break
      headingSpill += widthOf(nextIndex) ?? 140
    }

    return <th key={index} data-col-index={index} onPointerUp={(e) => setTouchedHeader(e.pointerType === "touch" ? index : null)} rowSpan={rowSpan} ref={openFilter === index ? filterRef : undefined} onContextMenu={(e) => openHeaderContextMenu(e, index)}
      onMouseDown={(e) => {
        if (insertRefFromHeader(column!.field, e)) return
        if (e.button === 0 && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
          draggingColumnSelection.current = { anchorCol: index }
        }
      }}
      onClick={(e) => {
        if (headerRefInsertedRef.current) { headerRefInsertedRef.current = false; return }
        setActiveHeaderIndex(index)
        selectColumn(index, { shiftKey: e.shiftKey, ctrlKey: e.ctrlKey || e.metaKey })
      }}
      onDragOver={(e) => {
        if (columnDrag.current === null) return
        if (canEditFormats) {
          e.preventDefault()
          const rect = e.currentTarget.getBoundingClientRect()
          const pos = e.clientX < rect.left + rect.width / 2 ? 'before' : 'after'
          setColumnDropIndicator((prev) => (prev?.index === index && prev?.position === pos ? prev : { index, position: pos }))
        }
      }}
      onDragLeave={() => setColumnDropIndicator(null)}
      onDrop={(e) => {
        if (columnDrag.current === null) return
        // The side comes from where the column is let go, not from the last indicator drawn,
        // which can still belong to the neighbour the pointer just crossed.
        const rect = e.currentTarget.getBoundingClientRect()
        const after = e.clientX >= rect.left + rect.width / 2
        setColumnDropIndicator(null)
        void dropColumn(index, after)
      }}
      onMouseEnter={() => {
        if (draggingColumnSelection.current && renderedRows.length > 0) {
          const anchorCol = draggingColumnSelection.current.anchorCol
          const posA = visibleColumns.indexOf(anchorCol)
          const posB = visibleColumns.indexOf(index)
          if (posA !== -1 && posB !== -1) {
            const minPos = Math.min(posA, posB)
            const maxPos = Math.max(posA, posB)
            const rangeCols = visibleColumns.slice(minPos, maxPos + 1)
            setSelectedColumns(new Set(rangeCols))
            if (rangeCols.length === 1) setSelectedFullColumn(rangeCols[0]!)
            else setSelectedFullColumn(null)
            setAnchor({ rowKey: keyOf(renderedRows[0]!), originalCol: anchorCol })
            setEnd({ rowKey: keyOf(renderedRows[renderedRows.length - 1]!), originalCol: index })
          }
        }
      }}
      style={{
        width: widthOf(index),
        minWidth: widthOf(index),
        backgroundColor: headerColors[column!.field ?? ''],
        boxShadow: ['inset 0 -1px 0 0 var(--grid-line-strong)', 'inset -1px 0 0 0 var(--grid-line-strong)', dropShadow, ...bandEdgeShadows(index), ...columnBorderShadows(column!.field ? dataFormatsConfig[gridKey]?.[column!.field] : undefined)].filter(Boolean).join(', '),
        ...(isColSelected ? { filter: 'brightness(0.9)' } : {}),
        ...(headingSpill > 0 ? { zIndex: 2 } : {}),
        ...(stickyLeft.has(index) ? { position: 'sticky' as const, left: stickyLeft.get(index), zIndex: 12 } : {}),
        ...(stickyRight.has(index) ? { position: 'sticky' as const, right: stickyRight.get(index), zIndex: 11 } : {}),
      }}
      className="nt-grid-head group/th relative select-none bg-grid-head p-1 text-left text-sm sm:text-xs font-bold">
      {column!.field && headerNotes[column!.field] && <span aria-hidden className="pointer-events-none absolute right-0 top-0 h-0 w-0 border-l-[6px] border-t-[6px] border-l-transparent border-t-danger" />}
      {canReorderCol && (
        <span
          draggable
          onDragStart={(e) => {
            if (e.shiftKey || e.ctrlKey || e.metaKey) {
              e.preventDefault()
              columnDrag.current = null
              return
            }
            e.stopPropagation()
            columnDrag.current = index
            columnBandDrag.current = null
            e.dataTransfer.effectAllowed = 'move'
          }}
          onDragEnd={() => { columnDrag.current = null; columnBandDrag.current = null; setColumnDropIndicator(null) }}
          onPointerDown={(e) => {
            // Touch has no HTML5 drag: the handle follows the finger and drops on the header under it.
            if (e.pointerType !== 'touch') return
            e.stopPropagation()
            e.preventDefault()
            columnDrag.current = index
            columnBandDrag.current = null
            const handle = e.currentTarget
            try { handle.setPointerCapture(e.pointerId) } catch { /* pointer already gone */ }
            const targetAt = (x: number, y: number) => {
              const th = document.elementFromPoint(x, y)?.closest<HTMLElement>('th[data-col-index]')
              if (!th) return null
              const rect = th.getBoundingClientRect()
              return { index: Number(th.dataset.colIndex), after: x >= rect.left + rect.width / 2 }
            }
            const move = (ev: PointerEvent) => {
              const target = targetAt(ev.clientX, ev.clientY)
              const pos = target?.after ? 'after' : 'before'
              setColumnDropIndicator((prev) => (!target ? null : prev?.index === target.index && prev?.position === pos ? prev : { index: target.index, position: pos }))
            }
            const end = (ev: PointerEvent) => {
              handle.removeEventListener('pointermove', move)
              handle.removeEventListener('pointerup', end)
              handle.removeEventListener('pointercancel', end)
              setColumnDropIndicator(null)
              const target = ev.type === 'pointerup' ? targetAt(ev.clientX, ev.clientY) : null
              if (target && target.index !== index) void dropColumn(target.index, target.after)
              else { columnDrag.current = null; columnBandDrag.current = null }
            }
            handle.addEventListener('pointermove', move)
            handle.addEventListener('pointerup', end)
            handle.addEventListener('pointercancel', end)
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          title={t('grid.dragReorderColumn')}
          className={`absolute left-1/2 top-0.5 flex h-3 w-5 -translate-x-1/2 rotate-90 cursor-grab touch-none items-center justify-center text-[11px] leading-none text-text-muted ${touchedHeader === index ? 'opacity-60' : 'opacity-0'} group-hover/th:opacity-60 hover:!opacity-100 active:cursor-grabbing`}
        >⠿</span>
      )}
      <div className={`flex items-center gap-1 ${canReorderCol ? 'pt-2' : ''}`}>
        {editingHeader === index
          ? phoneEditor(<textarea autoFocus rows={1} defaultValue={headerOf(index)}
            ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px' } }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onInput={(e) => { const el = e.target as HTMLTextAreaElement; el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px' }}
            onBlur={(e) => void confirmHeaderRename(index, e.target.value)}
            onKeyDown={(e) => {
              const el = e.target as HTMLTextAreaElement
              if (e.key === 'Enter' && (e.altKey || e.shiftKey)) {
                e.preventDefault()
                const s = el.selectionStart, se = el.selectionEnd
                el.value = el.value.substring(0, s) + '\n' + el.value.substring(se)
                el.selectionStart = el.selectionEnd = s + 1
                el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'
              } else if (e.key === 'Enter') { e.preventDefault(); el.blur() }
              else if (e.key === 'Escape') { el.value = headerOf(index); setEditingHeader(null) }
            }}
            className="min-h-6 min-w-0 flex-1 rounded-none border border-transparent bg-bg px-1 text-sm sm:text-xs font-bold leading-snug outline-none"
            style={{ resize: 'none', overflow: 'hidden' }} />, headerOf(index))
          : <button type="button" className={`min-w-0 flex-1 ${textClass(headingRule)}`}
            ref={(el) => { if (el) headerButtonRefs.current.set(index, el); else headerButtonRefs.current.delete(index) }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                const pos = visibleColumns.indexOf(index)
                if (pos === -1) return
                const nextPos = e.key === 'ArrowRight' ? pos + 1 : pos - 1
                if (nextPos >= 0 && nextPos < visibleColumns.length) {
                  e.preventDefault()
                  const targetCol = visibleColumns[nextPos]!
                  setActiveHeaderIndex(targetCol)
                  if (e.shiftKey) {
                    selectColumn(targetCol, { shiftKey: true })
                  } else if (!e.ctrlKey && !e.metaKey) {
                    selectColumn(targetCol)
                  }
                  headerButtonRefs.current.get(targetCol)?.focus()
                }
              }
            }}
            {...doubleTapProps(canEditFormats && column!.field ? (e) => { e.stopPropagation(); setEditingHeader(index) } : undefined)}
            title={(column!.field ? headerNotes[column!.field] : undefined) ?? column!.description ?? t('grid.selectColumn')}>
            <span className={`block ${headingWrap === 'wrap' ? 'overflow-hidden whitespace-pre-line break-words' : headingWrap === 'clip' ? 'truncate' : 'overflow-hidden whitespace-nowrap'}`} style={{ ...(headingWrap === 'wrap' ? { display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: headingRule.lines } : {}), ...(headingSpill > 0 ? { position: 'relative', width: `calc(100% + ${headingSpill}px)` } : {}), ...(headingFontSize ? { fontSize: headingFontSize } : {}) }}>{headingText}</span>
          </button>}
        {!column!.noFilter && !isEmbedded && <button type="button" aria-label={`${t('grid.filterColumn')}: ${headerOf(index)}`} style={{ backgroundColor: headerColors[column!.field ?? ''] }} onClick={() => { setActiveHeaderIndex(index); setOpenFilter(openFilter === index ? null : index) }} className={`absolute bottom-0.5 right-1 flex h-5 w-5 items-center justify-center rounded bg-grid-head ${filterControlsVisible || activeHeaderIndex === index || filterActive || openFilter === index ? '' : 'pointer-events-none opacity-0 group-hover/th:pointer-events-auto group-hover/th:opacity-100 group-focus-within/th:pointer-events-auto group-focus-within/th:opacity-100'} transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus hover:bg-surface-3 ${filterActive ? 'text-accent' : ''}`}>
          {filterActive ? (
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
              <rect x="0.5" y="0.5" width="19" height="19" rx="2" fill="#146c2e" stroke="#10552a" />
              <path d="M4.5 5h11l-4.25 5v5l-2.5-1.25V10L4.5 5Z" fill="#fff" />
            </svg>
          ) : (
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="11" height="7" viewBox="0 0 22 14" fill="#105525">
              <rect x="0" y="0" width="22" height="3" />
              <rect x="5" y="6" width="13" height="2" />
              <rect x="9" y="11" width="4" height="3" />
            </svg>
          )}
        </button>}
      </div>
      {canEditFormats && (
        <span
          onMouseDown={(e) => startResize(index, e)}
          onPointerDown={(e) => startResize(index, e)}
          {...doubleTapProps((e) => {
            e.stopPropagation()
            e.preventDefault()
            void autoFitColumn(index)
          })}
          className="absolute right-0 top-0 h-full w-2 sm:w-1 cursor-col-resize hover:bg-brand active:bg-brand touch-none select-none"
        />
      )}
    </th>
  }

  const groupButtonLabel = useMemo(() => {
    if (groupInfo.hasGrouping && groupInfo.hasLevel2 && groupInfo.level1Name && groupInfo.level2Name) {
      return `${groupInfo.level1Name} & ${groupInfo.level2Name}`
    }
    if (groupInfo.hasGrouping && groupInfo.level1Name) {
      return groupInfo.level1Name
    }
    return t('grid.group')
  }, [groupInfo, t])

  /** Row-selection actions; they take the status bar's place while rows are selected. */
  const rowSelectionActions = selected.size > 0 && (onBulkActivate || onBulkDeactivate || onBulkRestore || onBulkDelete || (onBulkMoveToSheet && eligibleMoveSheets.length > 0)) ? <>
    <span className="shrink-0 px-1 font-bold">{t('grid.selectedCount', { count: selected.size })}</span>
    {onBulkActivate && <button type="button" disabled={processingBulk} onClick={() => void runActivate()} className="h-7 sm:h-6 shrink-0 rounded-full px-2 font-bold hover:bg-header-fg/15 disabled:opacity-40">{t('grid.activate')}</button>}
    {onBulkDeactivate && <button type="button" disabled={processingBulk} onClick={() => void runDeactivate()} className="h-7 sm:h-6 shrink-0 rounded-full px-2 font-bold hover:bg-header-fg/15 disabled:opacity-40">{t('grid.deactivate')}</button>}
    {onBulkRestore && <button type="button" disabled={processingBulk} onClick={() => void runRestore()} className="h-7 sm:h-6 shrink-0 rounded-full px-2 font-bold hover:bg-header-fg/15 disabled:opacity-40">{t('grid.restore')}</button>}
    {onBulkDelete && <button type="button" disabled={processingBulk} onClick={() => setConfirmingDelete(true)} className="flex h-7 sm:h-6 shrink-0 items-center gap-1 rounded-full bg-danger px-2.5 font-bold text-white hover:opacity-90 disabled:opacity-40 [&_svg]:h-3.5 [&_svg]:w-3.5">
      {MENU_ICONS.trash}
      <span>{deleteAction.label}</span>
    </button>}
    {onBulkMoveToSheet && eligibleMoveSheets.length === 1 && eligibleMoveSheets[0] && (
      <button
        type="button"
        disabled={processingBulk}
        onClick={() => void runMoveRows(eligibleMoveSheets[0]!.id)}
        className="flex h-7 sm:h-6 shrink-0 items-center gap-1 rounded-full px-2 font-bold hover:bg-header-fg/15 disabled:opacity-40"
      >
        <Icon name="sheets" className="h-3.5 w-3.5" />
        <span>{t('grid.moveToSheetName', { name: eligibleMoveSheets[0]!.name }) ?? `Move to ${eligibleMoveSheets[0]!.name}`}</span>
      </button>
    )}
    {onBulkMoveToSheet && eligibleMoveSheets.length > 1 && (
      <div className="relative" ref={moveSheetMenuRef}>
        <button
          type="button"
          disabled={processingBulk}
          onClick={() => setMoveSheetMenuOpen((prev) => !prev)}
          className="flex h-7 sm:h-6 shrink-0 items-center gap-1 rounded-full px-2 font-bold hover:bg-header-fg/15 disabled:opacity-40"
        >
          <Icon name="sheets" className="h-3.5 w-3.5" />
          <span>{t('grid.moveToSheet')}</span>
          <Caret />
        </button>
        {moveSheetMenuOpen && (
          <div className="absolute left-0 bottom-full z-50 mb-1 min-w-44 max-h-60 overflow-y-auto rounded-lg border border-border bg-surface py-1 shadow-lg text-fg">
            {eligibleMoveSheets.map((sheet) => (
              <button
                key={sheet.id}
                type="button"
                onClick={() => {
                  setMoveSheetMenuOpen(false)
                  void runMoveRows(sheet.id)
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm sm:text-xs font-medium hover:bg-surface-2 transition-colors"
              >
                <Icon name="sheets" className="h-3.5 w-3.5 opacity-60 shrink-0" />
                <span className="truncate">{sheet.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    )}
    <button type="button" onClick={() => setSelected(new Set())} className="flex h-7 sm:h-6 shrink-0 items-center gap-1 rounded-full px-2 font-bold hover:bg-header-fg/15">
      <Icon name="close" className="h-3.5 w-3.5" />
      <span>{t('grid.clearSelection')}</span>
    </button>
  </> : null

  const toolbarSpacing = `shrink-0 ${fullScreen ? 'px-3 pt-1' : ''} ${(canUseFormulas && formulaBar && !formulaPanel) || (canEditFormats && formatBar) ? 'mb-0.5 border-b border-border pb-0.5' : 'mb-1'}`
  // Phones keep only adding and searching in the bar; everything else is in the ⋮ menu.
  const resizeFiltersPanel = (width: number) => { setFiltersPanelWidth(width); savePref('filtros-panel-ancho', String(width)) }
  /** Shows up to three of a filter's chosen values, then how many more there are. */
  const describeFilter = (selection: Set<string>) => {
    const values = [...selection].sort(compareFilterMenuValues)
    if (values.length === 0) return t('grid.filtersPanel.none')
    const shown = values.slice(0, 3).map((value) => value === '' ? t('grid.filtersPanel.blank') : value).join(', ')
    return values.length > 3 ? t('grid.filtersPanel.more', { values: shown, count: values.length - 3 }) : shown
  }
  /** Sort & filter: shows the header filter buttons and, on desktop, the filters sidebar with them. */
  const toggleFilterControls = () => {
    const next = !filterControlsVisible
    setFilterControlsVisible(next)
    if (!phoneLayout) setFiltersPanel(next)
    if (!next) setOpenFilter(null)
  }
  const closeFiltersPanel = () => {
    setFiltersPanel(false)
    if (!phoneLayout) { setFilterControlsVisible(false); setOpenFilter(null) }
  }
  const unfilteredColumns = columns.flatMap((column, index) => !column.noFilter && filters[index] === undefined ? [index] : [])
  const filtersPanelBody = (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-12 shrink-0 items-center justify-between border-b border-border pl-4 pr-2">
        <span className="truncate text-base font-bold">{t('grid.filtersPanel.title')}</span>
        <button type="button" onClick={closeFiltersPanel} aria-label={t('common.close')} title={t('common.close')}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-text-muted hover:bg-surface-3 hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">
          <Icon name="close" className="h-5 w-5" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3 space-y-2">
        {Object.keys(filters).length === 0 && <p className="px-1 text-sm text-text-muted">{t('grid.filtersPanel.empty')}</p>}
        {Object.entries(filters).map(([indexStr, selection]) => {
          const index = Number(indexStr)
          if (!columns[index]) return null
          return (
            <div key={index} className="relative rounded-lg border border-border bg-bg p-3 pr-9">
              <div className="truncate text-sm font-bold">{headerOf(index)}</div>
              <button type="button" onClick={() => { setActiveHeaderIndex(index); setOpenFilter(index) }}
                title={t('grid.filterColumn')}
                className="mt-1 block w-full truncate text-left text-sm text-brand hover:underline">
                {describeFilter(selection)}
              </button>
              <button type="button" aria-label={`${t('grid.filtersPanel.clear')}: ${headerOf(index)}`} title={t('grid.filtersPanel.clear')}
                onClick={() => filterWithHistory((f) => { const c = { ...f }; delete c[index]; return c })}
                className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full text-text-muted hover:bg-surface-3 hover:text-text">
                <Icon name="close" className="h-4 w-4" />
              </button>
            </div>
          )
        })}
        {unfilteredColumns.length > 0 && <div>
          <button type="button" aria-expanded={addFilterOpen} onClick={() => setAddFilterOpen((open) => !open)}
            className="w-full rounded-lg border border-dashed border-border py-2 text-sm font-bold text-brand hover:bg-surface-3">
            + {t('grid.filtersPanel.add')}
          </button>
          {addFilterOpen && <div className="mt-1 max-h-72 overflow-y-auto rounded-lg border border-border bg-surface py-1">
            {unfilteredColumns.map((index) => (
              <button key={index} type="button" onClick={() => { setAddFilterOpen(false); setActiveHeaderIndex(index); setOpenFilter(index) }}
                className="block w-full truncate px-3 py-2 text-left text-sm hover:bg-surface-3">
                {headerOf(index)}
              </button>
            ))}
          </div>}
        </div>}
      </div>
    </div>
  )

  const toolbar = <ToolbarOverflow
      layer="z-40"
      hoistOnMobile={!splitPane && !isEmbedded}
      mobileOnly={!splitPane && !isEmbedded ? MOBILE_TOOLBAR_KEYS : undefined}
      // In a sidebar the page's toolbar slot already carries the main grid's spacing.
      className={splitPane ? '' : toolbarSpacing}
      menuLabel={t('grid.moreOptions')}
      onCollapsedChange={setCollapsedToolbarKeys}
      items={[
        ...(groupable && (groupInfo.hasGrouping || canEditFormats) ? [{
          key: 'group', menuOnMobile: true, menuTitle: groupButtonLabel, menu: [
            {
              icon: MENU_ICONS.group,
              label: groupButtonLabel,
              active: groupPanel,
              onSelect: () => setGroupPanel((open) => {
                const next = !open
                if (next) setColumnsPanel(false)
                savePref('agrupar', next ? '1' : '0')
                return next
              }),
            },
            ...(canChooseGrouping ? [{
              icon: MENU_ICONS.columns,
              label: t('grid.chooseGroupingColumns'),
              onSelect: () => setGroupingDialogOpen(true),
            }] : []),
            ...(canReorderGroups && groupInfo.hasGrouping ? ([[0, groupInfo.level1Name], ...(groupInfo.hasLevel2 ? [[1, groupInfo.level2Name]] : [])] as [0 | 1, string][])
              .flatMap(([level, name]) => ([1, -1] as const).map((dir) => ({
                icon: MENU_ICONS.group,
                label: t(dir === 1 ? 'grid.sortGroupsAsc' : 'grid.sortGroupsDesc', { group: name }),
                active: groupSortState(level) === dir,
                onSelect: () => { void sortGroups(level, dir) },
              }))) : []),
            ...(groupInfo.hasGrouping ? [{
              icon: isL1AllCollapsed ? MENU_ICONS.expand : MENU_ICONS.collapse,
              label: t(isL1AllCollapsed ? 'grid.showAllGroups' : 'grid.collapseAllGroups', { group: groupInfo.level1Name }),
              onSelect: () => toggleL1Collapse(),
            }] : []),
            ...(groupInfo.hasGrouping && groupInfo.hasLevel2 ? [{
              icon: isL2AllCollapsed ? MENU_ICONS.expand : MENU_ICONS.collapse,
              label: t(isL2AllCollapsed ? 'grid.showAllGroups' : 'grid.collapseAllGroups', { group: groupInfo.level2Name }),
              onSelect: () => toggleL2Collapse(),
            }] : []),
            ...(canEditFormats && groupInfo.hasGrouping ? [{
              icon: MENU_ICONS.group,
              label: t('grid.showGroupCounts'),
              active: showGroupCounts,
              onSelect: () => { void toggleGroupCounts() },
            }] : []),
            ...(groupInfo.hasGrouping ? [{
              icon: MENU_ICONS.group,
              label: t('grid.showGroupSubtotals'),
              active: showGroupSubtotals,
              onSelect: () => toggleGroupSubtotals(),
            }] : []),
            ...(groupInfo.hasGrouping && showGroupSubtotals ? [{
              icon: MENU_ICONS.group,
              label: t('grid.subtotalsBelow'),
              active: groupSubtotalsPosition === 'bottom',
              onSelect: () => setGroupSubtotalsPosition(groupSubtotalsPosition === 'bottom' ? 'top' : 'bottom'),
            }] : []),
            ...(groupInfo.hasGrouping && groupInfo.hasLevel2 ? [{
              icon: MENU_ICONS.group,
              label: t('grid.groupMergeSingle'),
              active: groupMergeSingle,
              onSelect: () => toggleGroupMergeSingle(),
            }] : []),
            ...(groupInfo.hasGrouping ? [{
              icon: MENU_ICONS.group,
              label: t('grid.groupBlankRows'),
              active: groupBlankRows,
              onSelect: () => toggleGroupBlankRows(),
            }] : []),
            ...(groupInfo.hasGrouping ? [{
              icon: MENU_ICONS.group,
              label: t('grid.groupOutline'),
              active: groupOutline,
              onSelect: () => toggleGroupOutline(),
            }] : []),
          ], node: (
            <div className="relative -ml-2 inline-flex shrink-0 items-center rounded">
              <button type="button" title={groupButtonLabel}
                onClick={() => setGroupPanel((open) => {
                  const next = !open
                  if (next) setColumnsPanel(false)
                  savePref('agrupar', next ? '1' : '0')
                  return next
                })}
                className={`h-7 pl-2.5 text-sm sm:text-xs transition-colors ${(canChooseGrouping || groupInfo.hasGrouping) ? 'rounded-l pr-0.5' : 'rounded pr-2.5'} ${groupPanel ? 'bg-brand/5 text-brand' : 'hover:bg-surface-hover'}`}>
                <span className="sm:hidden">G</span>
                <span className="hidden sm:inline">{groupButtonLabel}</span>
              </button>
              {(canChooseGrouping || groupInfo.hasGrouping) && (
                <button type="button" title={t('grid.collapseOptions')}
                  onClick={() => setGroupDropdownOpen((o) => !o)}
                  className={`flex h-7 w-4 items-center justify-start rounded-r text-sm sm:text-xs transition-colors ${groupPanel ? 'bg-brand/5 text-brand hover:bg-brand/10' : 'hover:bg-surface-hover'}`}>
                  <Caret />
                </button>
              )}
              {groupDropdownOpen && (canChooseGrouping || groupInfo.hasGrouping) && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setGroupDropdownOpen(false)} />
                  <div
                    className="absolute left-0 top-full mt-1 z-50 flex flex-col min-w-[210px] w-max rounded-lg border border-border bg-surface py-1 shadow-lg text-sm sm:text-xs">
                    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                      {canChooseGrouping && (
                        <button
                          type="button"
                          onClick={() => {
                            setGroupingDialogOpen(true)
                            setGroupDropdownOpen(false)
                          }}
                          className="text-left px-3 py-2 hover:bg-surface-hover font-medium transition-colors"
                        >
                          {t('grid.chooseGroupingColumns')}
                        </button>
                      )}
                      {canReorderGroups && groupInfo.hasGrouping && ([[0, groupInfo.level1Name], ...(groupInfo.hasLevel2 ? [[1, groupInfo.level2Name]] : [])] as [0 | 1, string][]).map(([level, name]) => {
                        const state = groupSortState(level)
                        return (
                          <div key={level} className="flex items-center hover:bg-surface-hover transition-colors">
                            <span className="min-w-0 flex-1 px-3 py-2 font-medium">{t('grid.sortGroups', { group: name })}</span>
                            <div className="mr-2 flex shrink-0 overflow-hidden rounded border border-border" role="radiogroup" aria-label={t('grid.sortGroups', { group: name })}>
                              {([1, -1] as const).map((dir) => (
                                <button
                                  key={dir}
                                  type="button"
                                  role="radio"
                                  aria-checked={state === dir}
                                  title={t(dir === 1 ? 'grid.sortGroupsAsc' : 'grid.sortGroupsDesc', { group: name })}
                                  aria-label={t(dir === 1 ? 'grid.sortGroupsAsc' : 'grid.sortGroupsDesc', { group: name })}
                                  onClick={() => { void sortGroups(level, dir) }}
                                  className={`flex h-6 items-center justify-center px-1.5 text-[11px] font-semibold transition-colors ${state === dir ? 'bg-brand/10 text-brand' : 'text-text-muted hover:bg-surface'}`}
                                >
                                  {dir === 1 ? 'A→Z' : 'Z→A'}
                                </button>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                      {groupInfo.hasGrouping && (
                        <button
                          type="button"
                          onClick={() => {
                            toggleL1Collapse()
                            setGroupDropdownOpen(false)
                          }}
                          className="text-left px-3 py-2 hover:bg-surface-hover font-medium transition-colors"
                        >
                          {t(isL1AllCollapsed ? 'grid.showAllGroups' : 'grid.collapseAllGroups', { group: groupInfo.level1Name })}
                        </button>
                      )}
                      {groupInfo.hasGrouping && groupInfo.hasLevel2 && (
                        <button
                          type="button"
                          onClick={() => {
                            toggleL2Collapse()
                            setGroupDropdownOpen(false)
                          }}
                          className="text-left px-3 py-2 hover:bg-surface-hover font-medium transition-colors"
                        >
                          {t(isL2AllCollapsed ? 'grid.showAllGroups' : 'grid.collapseAllGroups', { group: groupInfo.level2Name })}
                        </button>
                      )}
                      {canEditFormats && groupInfo.hasGrouping && (
                        <button
                          type="button"
                          role="menuitemcheckbox"
                          aria-checked={showGroupCounts}
                          onClick={() => { void toggleGroupCounts() }}
                          className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-hover font-medium transition-colors"
                        >
                          <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${showGroupCounts ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                            {showGroupCounts && <Icon name="check" className="h-2.5 w-2.5" />}
                          </span>
                          <span>{t('grid.showGroupCounts')}</span>
                        </button>
                      )}
                      {groupInfo.hasGrouping && (
                        <div className="flex items-center hover:bg-surface-hover transition-colors">
                          <button
                            type="button"
                            role="menuitemcheckbox"
                            aria-checked={showGroupSubtotals}
                            onClick={toggleGroupSubtotals}
                            className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left font-medium"
                          >
                            <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${showGroupSubtotals ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                              {showGroupSubtotals && <Icon name="check" className="h-2.5 w-2.5" />}
                            </span>
                            <span>{t('grid.showGroupSubtotals')}</span>
                          </button>
                          {showGroupSubtotals && (
                            <div className="mr-2 flex shrink-0 overflow-hidden rounded border border-border" role="radiogroup" aria-label={t('grid.subtotalsPosition')}>
                              {(['top', 'bottom'] as const).map((position) => (
                                <button
                                  key={position}
                                  type="button"
                                  role="radio"
                                  aria-checked={groupSubtotalsPosition === position}
                                  title={t(position === 'top' ? 'grid.subtotalsAbove' : 'grid.subtotalsBelow')}
                                  aria-label={t(position === 'top' ? 'grid.subtotalsAbove' : 'grid.subtotalsBelow')}
                                  onClick={() => setGroupSubtotalsPosition(position)}
                                  className={`flex h-6 w-6 items-center justify-center transition-colors ${groupSubtotalsPosition === position ? 'bg-brand/10 text-brand' : 'text-text-muted hover:bg-surface'}`}
                                >
                                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    {position === 'top' ? <path d="M12 19V5M5 12l7-7 7 7" /> : <path d="M12 5v14M19 12l-7 7-7-7" />}
                                  </svg>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {groupInfo.hasGrouping && groupInfo.hasLevel2 && (
                        <button
                          type="button"
                          role="menuitemcheckbox"
                          aria-checked={groupMergeSingle}
                          onClick={toggleGroupMergeSingle}
                          className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-hover font-medium transition-colors"
                        >
                          <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${groupMergeSingle ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                            {groupMergeSingle && <Icon name="check" className="h-2.5 w-2.5" />}
                          </span>
                          <span>{t('grid.groupMergeSingle')}</span>
                        </button>
                      )}
                      {groupInfo.hasGrouping && (
                        <button
                          type="button"
                          role="menuitemcheckbox"
                          aria-checked={groupBlankRows}
                          onClick={toggleGroupBlankRows}
                          className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-hover font-medium transition-colors"
                        >
                          <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${groupBlankRows ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                            {groupBlankRows && <Icon name="check" className="h-2.5 w-2.5" />}
                          </span>
                          <span>{t('grid.groupBlankRows')}</span>
                        </button>
                      )}
                      {groupInfo.hasGrouping && (
                        <button
                          type="button"
                          role="menuitemcheckbox"
                          aria-checked={groupOutline}
                          onClick={toggleGroupOutline}
                          className="flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-hover font-medium transition-colors"
                        >
                          <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${groupOutline ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                            {groupOutline && <Icon name="check" className="h-2.5 w-2.5" />}
                          </span>
                          <span>{t('grid.groupOutline')}</span>
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )
        }] : []),
        ...(editable && (onAddRow || onAddRows) && !loading && !addButtonInGrid
          ? [{ key: 'add-rows', separatorBefore: true, node: renderAddRowButton('toolbar') }]
          : []),
        {
          key: 'copy',
          menuOnMobile: true,
          menuTitle: t('grid.copyFormulasLabel'),
          menu: [
            ...((Boolean(range || selected.size > 1 || selectedColumns.size > 0)) ? [{
              icon: MENU_ICONS.copy,
              label: t('grid.copySelection'),
              onSelect: () => void copySelection(false),
            }] : []),
            {
              icon: MENU_ICONS.copy,
              label: t('grid.copyAll'),
              onSelect: () => void copyAll(false),
            },
            {
              icon: MENU_ICONS.copy,
              label: t('grid.copyAllMd'),
              onSelect: () => void copyAll(true),
            },
          ],
          node: (
            <CopySplitButton
              onCopy={() => {
                if (range || selected.size > 1 || selectedColumns.size > 0) void copySelection(false)
                else void copyAll(false)
              }}
              hasSelection={Boolean(range || selected.size > 1 || selectedColumns.size > 0)}
              onCopySelection={(asMd) => void copySelection(asMd)}
              onCopyAll={(asMd) => void copyAll(asMd)}
              copyHeaders={copyHeaders}
              onToggleCopyHeaders={setCopyHeaders}
              copyNumberFormat={copyNumberFormat}
              onToggleCopyNumberFormat={setCopyNumberFormat}
              copyFormulas={copyFormulas}
              onToggleCopyFormulas={setCopyFormulas}
            />
          ),
        },
        ...(rangeEditable && clipboardHasText ? [{
          key: 'paste',
          menuTitle: t('grid.paste'),
          menu: copiedHasFormulas
            ? [
              { icon: MENU_ICONS.paste, label: t('grid.pasteFormulas'), onSelect: () => void pasteSelection() },
              { icon: MENU_ICONS.paste, label: t('grid.pasteValues'), onSelect: () => void pasteSelection({ valuesOnly: true }) },
            ]
            : [{ icon: MENU_ICONS.paste, label: t('grid.paste'), onSelect: () => void pasteSelection() }],
          node: (
            <PasteSplitButton
              onPaste={() => void pasteSelection()}
              onPasteValues={() => void pasteSelection({ valuesOnly: true })}
              hasFormulas={copiedHasFormulas}
            />
          )
        }] : []),
        ...(showRefresh && !refreshInTopBar ? [{
          key: 'refresh', separatorBefore: true, node: (
            <button
              type="button"
              disabled={refreshing}
              onClick={() => { void refreshAll() }}
              aria-busy={refreshing}
              aria-label={t('grid.refresh')}
              title={refreshTitle ?? t('grid.refresh')}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors hover:bg-surface-hover disabled:opacity-50"
            >
              <svg aria-hidden="true" className={refreshing ? 'animate-spin' : ''} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 0 1-15.5 6.2L3 16" />
                <path d="M3 12a9 9 0 0 1 15.5-6.2L21 8" />
                <path d="M21 3v5h-5" />
                <path d="M3 21v-5h5" />
              </svg>
            </button>
          ),
        }] : []),
        {
          key: 'search', separatorBefore: true, grow: true, minWidth: replaceVisible ? SEARCH_MIN_WIDTH * 2 + 6 : SEARCH_MIN_WIDTH, node: (
            <div className="flex flex-1 min-w-0 items-center gap-1.5">
              <div className="relative flex-1 min-w-[9rem]">
                <Icon name="search" className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted" />
                <input
                  ref={searchInputRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={selectedFullColumn !== null && columns[selectedFullColumn]
                    ? t('grid.searchInColumn', { column: headerOf(selectedFullColumn) })
                    : t('grid.searchGrid')}
                  aria-label={selectedFullColumn !== null && columns[selectedFullColumn]
                    ? t('grid.searchInColumn', { column: headerOf(selectedFullColumn) })
                    : t('grid.searchGrid')}
                  className={`h-7 w-full rounded-full border border-border bg-bg text-sm sm:text-xs outline-none focus:border-focus ${search ? 'pl-7 pr-6' : 'pl-7 pr-2.5'}`}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text text-xs font-bold"
                    aria-label={t('grid.clearSelection')}
                    title={t('grid.clearSelection')}
                  >
                    ✕
                  </button>
                )}
              </div>
              {replaceVisible && (
                <div className="relative flex-1 min-w-[9rem]">
                  <input
                    ref={replaceInputRef}
                    value={replaceText}
                    onChange={(e) => setReplaceText(e.target.value)}
                    placeholder={t('grid.replaceWith')}
                    aria-label={t('grid.replaceWith')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleReplaceAll()
                      } else if (e.key === 'Escape') {
                        setReplaceVisible(false)
                      }
                    }}
                    className="h-7 w-full rounded-full border border-border bg-bg pl-2.5 pr-6 text-sm sm:text-xs outline-none focus:border-focus"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setReplaceVisible(false)
                      setReplaceText('')
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text text-xs font-bold"
                    aria-label={t('grid.cancel')}
                    title={t('grid.cancel')}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          ), collapsedNode: (
            <button
              type="button"
              aria-label={t('grid.search')}
              title={t('grid.searchGrid')}
              aria-expanded={searchRowOpen}
              onClick={() => setSearchRowOpen((open) => !open)}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors ${searchRowOpen || search ? 'bg-brand/5 text-brand' : 'hover:bg-surface-hover'}`}
            >
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </button>
          )
        },
        // Desktop opens the filters sidebar from the Sort & filter button instead.
        ...(!isEmbedded && phoneLayout ? [{
          key: 'filtersPanel', node: (
            <button
              type="button"
              aria-pressed={filtersPanel}
              aria-label={t('grid.filtersPanel.title')}
              title={t('grid.filtersPanel.title')}
              onClick={() => setFiltersPanel((open) => !open)}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors hover:bg-surface-hover ${filtersPanel ? 'bg-brand/5' : ''}`}
            >
              {Object.keys(filters).length > 0 ? (
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 20 20">
                  <rect x="0.5" y="0.5" width="19" height="19" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M4.5 5h11l-4.25 5v5l-2.5-1.25V10L4.5 5Z" fill="currentColor" />
                </svg>
              ) : (
                <svg aria-hidden="true" width="16" height="10" viewBox="0 0 22 14" fill="currentColor">
                  <rect x="0" y="0" width="22" height="3" />
                  <rect x="5" y="6" width="13" height="2" />
                  <rect x="9" y="11" width="4" height="3" />
                </svg>
              )}
            </button>
          ),
        }] : []),
        ...(editable ? [{
          key: 'replace', menuOnMobile: true, menu: [{
            icon: MENU_ICONS.replace,
            label: selectedFullColumn !== null && columns[selectedFullColumn]
              ? t('grid.replaceInColumn', { column: headerOf(selectedFullColumn) })
              : t('grid.replaceGrid'),
            active: replaceVisible,
            onSelect: () => {
              if (replaceVisible) {
                handleReplaceAll()
              } else {
                setReplaceVisible(true)
                if (searchCollapsed) setSearchRowOpen(true)
              }
            },
          }], node: (
            <button
              type="button"
              aria-pressed={replaceVisible}
              aria-label={selectedFullColumn !== null && columns[selectedFullColumn]
                ? t('grid.replaceInColumn', { column: headerOf(selectedFullColumn) })
                : t('grid.replaceGrid')}
              title={selectedFullColumn !== null && columns[selectedFullColumn]
                ? t('grid.replaceInColumn', { column: headerOf(selectedFullColumn) })
                : t('grid.replaceGrid')}
              onClick={() => {
                if (replaceVisible) {
                  handleReplaceAll()
                } else {
                  setReplaceVisible(true)
                  if (searchCollapsed) setSearchRowOpen(true)
                }
              }}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded text-sm sm:text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${replaceVisible ? 'bg-brand/5 text-brand font-medium' : 'hover:bg-surface-hover'}`}
            >
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 11V9a2 2 0 0 0-2-2H4" /><path d="m7 4-3 3 3 3" /><path d="M5 13v2a2 2 0 0 0 2 2h13" /><path d="m17 14 3 3-3 3" />
              </svg>
            </button>
          )
        }] : []),
        {
          key: 'filters', menuOnMobile: true, menu: [{
            icon: MENU_ICONS.filters,
            label: filterControlsVisible ? t('grid.hideFilters') : t('grid.showFilters'),
            active: filterControlsVisible,
            onSelect: () => toggleFilterControls(),
          }], node: (
            <button
              type="button"
              aria-pressed={filterControlsVisible}
              aria-label={filterControlsVisible ? t('grid.hideFilters') : t('grid.showFilters')}
              title={filterControlsVisible ? t('grid.hideFilters') : t('grid.showFilters')}
              onClick={() => toggleFilterControls()}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded text-sm sm:text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${filterControlsVisible ? 'bg-brand/5 text-brand' : 'hover:bg-surface-hover'}`}
            >
              <svg aria-hidden="true" width="11" height="7" viewBox="0 0 22 14" fill="currentColor">
                <rect x="0" y="0" width="22" height="3" />
                <rect x="5" y="6" width="13" height="2" />
                <rect x="9" y="11" width="4" height="3" />
              </svg>
            </button>
          )
        },
        ...(canEditFormats && cellColorTargets.length > 0 ? [{
          key: 'bold', separatorBefore: true, menu: [{
            icon: <span className="text-xs font-bold leading-none">B</span>,
            label: t('grid.bold'),
            active: targetBold,
            onSelect: () => { if (!formatBusy && formatTarget !== '') toggleTargetBold() },
          }], node: (
            <button
              type="button"
              disabled={formatBusy || formatTarget === ''}
              title={t('grid.bold')}
              aria-label={t('grid.bold')}
              aria-pressed={targetBold}
              onClick={toggleTargetBold}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors disabled:opacity-50 ${targetBold ? 'bg-brand/10 text-brand' : 'text-text hover:bg-surface-hover'}`}
            >
              <span className="text-xs font-bold leading-none">B</span>
            </button>
          )
        }] : []),
        ...(canEditFormats && (cellColorTargets.length > 0 || fillColumnFields.length > 0) ? [{
          key: 'cell-color', menuLabel: t('grid.fillColor'), node: (
            <div className="flex shrink-0 items-center">
              <button
                type="button"
                aria-label={t('grid.fillColor')}
                title={t('grid.fillColor')}
                onClick={() => {
                  if (!cellColorLoading && !cellColorBusy && selectedFillColor) void applyFillColor(selectedFillColor)
                }}
                className="flex h-7 items-center justify-center pl-1.5 pr-0.5 rounded-l transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              >
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m19 11-8-8-6 6 8 8 6-6Z" />
                  <path d="m5 15-2 2v4h4l2-2" />
                  <path d="m13 5 6 6" />
                  <path d="M16 21h5" />
                  <path d="M3 22h18" stroke={selectedFillColor ?? 'currentColor'} strokeWidth="2.5" />
                </svg>
              </button>
              <ColorPicker
                valor={selectedFillColor}
                onChange={(color) => void applyFillColor(color)}
                gridFill={gridFillControls}
                coloresUsados={usedCellColors}
                disabled={cellColorLoading || cellColorBusy}
                ariaLabel={t('grid.changeColors')}
                title={t('grid.changeColors')}
                triggerClassName="flex h-7 w-4 shrink-0 items-center justify-start rounded-r transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50 hover:bg-surface-hover"
                trigger={<Caret />}
              />
            </div>
          )
        }] : []),
        ...(canEditFormats && cellColorTargets.length > 0 ? [{
          key: 'borders', node: (
            <ToolbarPopover label={t('grid.borders')} disabled={formatBusy || formatTarget === ''} icon={
              <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="3" y="3" width="18" height="18" rx="1" />
                <path d="M12 3v18M3 12h18" />
              </svg>
            }>
              {(close) => <div className="flex flex-col gap-2 p-1" style={{ minWidth: 200 }}>
                {(['borderLeft', 'borderRight'] as const).map((side) => {
                  const current = dataFormatsConfig[gridKey]?.[formatTarget] ?? { type: 'text' as const }
                  const border = current[side]
                  const save = (next: typeof border) => {
                    const { [side]: _drop, ...rest } = current
                    void saveDataFormat(next ? { ...rest, [side]: next } : rest, false, [side])
                  }
                  return (
                    <div key={side} className="flex flex-col gap-1">
                      <button type="button" disabled={formatBusy}
                        onClick={() => { if (border) { save(undefined); close() } else save(lastBorderStyle()) }}
                        className="rounded px-2 py-1 text-left text-sm hover:bg-surface-hover disabled:opacity-50">
                        {t(side === 'borderLeft'
                          ? (border ? 'grid.clearLeftBorder' : 'grid.addLeftBorder')
                          : (border ? 'grid.clearRightBorder' : 'grid.addRightBorder'))}
                      </button>
                      {border && (
                        <div className="flex items-center gap-1 pl-2">
                          {GRID_BORDER_WEIGHTS.map((weight) => (
                            <button key={weight} type="button" disabled={formatBusy}
                              title={t(`grid.border_${weight}`)} aria-label={t(`grid.border_${weight}`)}
                              aria-pressed={border.weight === weight}
                              onClick={() => { rememberBorderStyle({ ...border, weight }); save({ ...border, weight }) }}
                              className={`flex h-7 w-9 items-center justify-center rounded transition-colors disabled:opacity-50 ${border.weight === weight ? 'bg-brand/10 text-brand' : 'hover:bg-surface-hover'}`}>
                              <span className="block w-6 bg-current" style={{ height: GRID_BORDER_WIDTHS[weight] }} />
                            </button>
                          ))}
                          <ColorPicker
                            valor={border.color}
                            onChange={(color) => { const next = { ...border, color: color ?? '#000000' }; rememberBorderStyle(next); save(next) }}
                            coloresUsados={usedCellColors}
                            disabled={formatBusy}
                            ariaLabel={t('grid.borderColor')}
                            title={t('grid.borderColor')}
                            triggerClassName="flex h-7 w-7 items-center justify-center rounded hover:bg-surface-hover disabled:opacity-50"
                            trigger={<span className="block h-4 w-4 rounded-sm border border-border" style={{ background: border.color }} />}
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>}
            </ToolbarPopover>
          )
        }] : []),
        ...(canEditFormats && cellColorTargets.length > 0 ? [{
          key: 'format-align', node: (
            <ToolbarPopover label={t('grid.formatAlignBar')} disabled={formatBusy || formatTarget === ''} icon={<>
              <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M3 5h18M3 10h12M3 15h18M3 20h12" />
              </svg>
            </>}>
              {[
        { key: 'align', node: (<>
  
        {/* Align icon buttons: mobile dropdown, desktop button group */}
        <div className="flex shrink-0 items-center rounded p-[1px]" role="group" aria-label={t('grid.alignLabel')}>
          {([
            { key: 'auto', icon: 'alignAuto', label: t('grid.alignAuto') },
            { key: 'left', icon: 'alignLeft', label: t('grid.alignLeft') },
            { key: 'center', icon: 'alignCenter', label: t('grid.alignCenter') },
            { key: 'right', icon: 'alignRight', label: t('grid.alignRight') },
          ] as const).map(({ key, icon, label }) => {
            const currentAlign = (formatTarget !== '' ? formatBaseOf(formatTarget)?.align : undefined) ?? 'auto'
            const isSelected = currentAlign === key
            return (
              <button
                key={key}
                type="button"
                disabled={formatBusy || formatTarget === ''}
                title={label}
                aria-label={label}
                onClick={() => {
                  const current = formatBaseOf(formatTarget) ?? { type: 'text' }
                  void saveDataFormat({ ...current, align: key }, false, ['align'])
                }}
                className={`flex h-6 w-6 items-center justify-center rounded transition-colors disabled:opacity-50 ${isSelected ? 'bg-brand/10 text-brand font-bold' : 'text-text hover:bg-surface-hover'}`}
              >
                <Icon name={icon} className="h-3.5 w-3.5" />
              </button>
            )
          })}
        </div>
        </>) },
        { key: 'wrap', node: (<>
  
        {/* Text wrapping / overflow: mobile dropdown, desktop button group */}
        <div className="flex shrink-0 items-center rounded p-[1px]" role="group" aria-label={t('grid.wrapLabel')}>
          {([
            { key: 'wrap', icon: 'wrapText', label: t('grid.wrapText') },
            { key: 'clip', icon: 'clipText', label: t('grid.clipText') },
            { key: 'shrink', icon: 'shrinkText', label: t('grid.shrinkText') },
              { key: 'expand', icon: 'expandText', label: t('grid.expandText') },
              { key: 'overflow', icon: 'overflowText', label: t('grid.overflowText') },
          ] as const).map(({ key, icon, label }) => {
            const currentWrap = (() => {
              const configuredWrap = formatTarget !== '' ? formatBaseOf(formatTarget)?.wrapMode : undefined
              if (configuredWrap) return configuredWrap
              const targetCol = columns.find((c) => c.field === formatTarget)
              const targetDataFormat = formatTarget !== '' ? formatBaseOf(formatTarget) : undefined
              const targetType: CellType = targetDataFormat?.type === 'rollup'
                ? (['SUM', 'AVG', 'COUNT'].includes(targetDataFormat.rollup?.agg ?? '') ? 'number' : 'text')
                : targetDataFormat?.type === 'lookup' || targetDataFormat?.type === 'auto'
                  ? 'text'
                  : (targetDataFormat?.type ?? targetCol?.cellType ?? 'text')
              return ruleWrapMode(alignmentConfig[targetType], targetType)
            })()
            const isSelected = currentWrap === key
            return (
              <button
                key={key}
                type="button"
                disabled={formatBusy || formatTarget === ''}
                title={label}
                aria-label={label}
                onClick={() => {
                  const current = formatBaseOf(formatTarget) ?? { type: 'text' }
                  void saveDataFormat({ ...current, wrapMode: key }, false, ['wrapMode'])
                }}
                className={`flex h-6 w-6 items-center justify-center rounded transition-colors disabled:opacity-50 ${isSelected ? 'bg-brand/10 text-brand font-bold' : 'text-text hover:bg-surface-hover'}`}
              >
                <Icon name={icon} className="h-3.5 w-3.5" />
              </button>
            )
          })}
        </div>
        </>) },
        ...(showGroupSubtotals && groupColumns[0] !== null ? [{ key: 'subtotal-align', node: (<>
        {(() => {
          const current = formatBaseOf(formatTarget) ?? { type: 'text' as const }
          const custom = current.subtotalWrapMode !== undefined
          return <>
            <span className="shrink-0 text-sm sm:text-xs text-text-muted">{t('grid.subtotalsLabel')}</span>
            <select
              aria-label={t('grid.subtotalAlignLabel')}
              title={t('grid.subtotalAlignLabel')}
              value={custom ? 'custom' : 'same'}
              disabled={formatBusy || formatTarget === ''}
              onChange={(event) => {
                const { subtotalWrapMode: _wrap, ...rest } = current
                void saveDataFormat(event.target.value === 'custom'
                  ? { ...rest, subtotalWrapMode: current.wrapMode ?? 'clip' }
                  : rest, false, ['subtotalWrapMode'])
              }}
              className="h-7 rounded bg-transparent px-1 hover:bg-surface-hover cursor-pointer text-sm sm:text-xs text-fg outline-none focus:border-focus disabled:opacity-50"
            >
              <option value="same">{t('grid.subtotalAlignSame')}</option>
              <option value="custom">{t('grid.subtotalAlignCustom')}</option>
            </select>
            {custom && <div className="flex shrink-0 items-center rounded p-[1px]" role="group" aria-label={t('grid.subtotalAlignLabel')}>
              {([
                { key: 'wrap', icon: 'wrapText', label: t('grid.wrapText') },
                { key: 'clip', icon: 'clipText', label: t('grid.clipText') },
                { key: 'shrink', icon: 'shrinkText', label: t('grid.shrinkText') },
                { key: 'expand', icon: 'expandText', label: t('grid.expandText') },
                { key: 'overflow', icon: 'overflowText', label: t('grid.overflowText') },
              ] as const).map(({ key, icon, label }) => (
                <button
                  key={key}
                  type="button"
                  disabled={formatBusy || formatTarget === ''}
                  title={label}
                  aria-label={label}
                  onClick={() => void saveDataFormat({ ...current, subtotalWrapMode: key }, false, ['subtotalWrapMode'])}
                  className={`flex h-6 w-6 items-center justify-center rounded transition-colors disabled:opacity-50 ${(current.subtotalWrapMode ?? 'clip') === key ? 'bg-brand/10 text-brand font-bold' : 'text-text hover:bg-surface-hover'}`}
                >
                  <Icon name={icon} className="h-3.5 w-3.5" />
                </button>
              ))}
            </div>}
          </>
        })()}
        </>) }] : []),
        { key: 'header-align', node: <HeaderAlignmentControls current={effectiveFormat(formatTarget) ?? { type: 'text', headerOnly: true }} defaultRule={alignmentConfig.header} disabled={formatBusy || formatTarget === ''} onChange={(next, keys) => { void saveDataFormat(next, true, keys) }} /> },
              ].map((item, index) => <Fragment key={item.key}>
                {index > 0 && <span aria-hidden="true" className="flex h-5 w-2.5 shrink-0 justify-center"><span className="h-full w-px bg-border" /></span>}
                {item.node}
              </Fragment>)}
            </ToolbarPopover>
          )
        }] : []),
        ...(canEditFormats && cellColorTargets.length > 0 ? [{
          key: 'paint-format', menu: [{
            icon: (
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="2" width="14" height="6" rx="1" />
                <path d="M18 5h2v5h-8v3" />
                <rect x="10" y="13" width="4" height="9" rx="1" />
              </svg>
            ),
            label: t('grid.paintFormat'),
            active: formatPainter !== null,
            onSelect: () => { if (!formatBusy && !cellColorBusy) armFormatPainter() },
          }], node: (
            <button
              type="button"
              disabled={formatBusy || cellColorBusy}
              aria-label={t('grid.paintFormat')}
              title={t('grid.paintFormat')}
              aria-pressed={formatPainter !== null}
              onClick={armFormatPainter}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${formatPainter ? 'bg-brand/10 text-brand' : 'text-text hover:bg-surface-hover'}`}
            >
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="2" width="14" height="6" rx="1" />
                <path d="M18 5h2v5h-8v3" />
                <rect x="10" y="13" width="4" height="9" rx="1" />
              </svg>
            </button>
          )
        }] : []),
        ...(canEditFormats ? [{
          key: 'format-data', separatorBefore: true, menuOnMobile: true, menu: [{
            icon: MENU_ICONS.format,
            label: t('grid.formatDataBar'),
            active: formatBar,
            onSelect: () => setInternalFormatBar(!formatBar),
          }], node: (
            <button type="button" aria-label={t('grid.formatDataBar')} title={t('grid.formatDataBar')}
              onClick={() => setInternalFormatBar(!formatBar)}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors ${formatBar ? 'bg-brand/5 text-brand' : 'hover:bg-surface-hover'}`}>
              {/* Two typed entries, one accepted and one rejected: data types and validation */}
              <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 3H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h5" />
                <path d="m13 6 3 3 6-6.5" />
                <path d="M10 13H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h5" />
                <path d="m15 14 6 6M21 14l-6 6" />
              </svg>
            </button>
          )
        }] : []),
        ...(canEditSharedConfig ? [{
          key: 'formula-bar', menuOnMobile: true, menu: [{
            icon: MENU_ICONS.formula,
            label: t('grid.formulaBar'),
            active: formulaBar,
            onSelect: () => setInternalFormulaBar((open) => {
              const next = !open
              return next
            }),
          }], node: (
            <button type="button" title={t('grid.formulaBar')}
              onClick={() => setInternalFormulaBar((open) => {
                const next = !open
                return next
              })}
              className={`h-7 shrink-0 rounded px-2.5 text-sm sm:text-xs italic transition-colors ${formulaBar ? 'bg-brand/5 text-brand' : 'hover:bg-surface-hover'}`}>fx</button>
          )
        }] : []),
        ...(summaryCardsContext && canEditFormats ? [{
          key: 'summary-cards', menuOnMobile: true, menuTitle: t('grid.summaryCards'), menu: [
            {
              icon: MENU_ICONS.summary,
              label: t('grid.summaryCards'),
              active: summaryCardsContext.summaryCardsVisible,
              onSelect: () => summaryCardsContext.toggleSummaryCards(),
            },
            {
              icon: MENU_ICONS.summary,
              label: t('grid.totalsRow'),
              active: summaryCardsContext.totalsRowVisible,
              onSelect: () => summaryCardsContext.toggleTotalsRow(),
            },
            ...(groupInfo.hasGrouping ? [{
              icon: MENU_ICONS.summary,
              label: t('grid.showGroupSubtotals'),
              active: showGroupSubtotals,
              onSelect: () => toggleGroupSubtotals(),
            }] : []),
          ], node: (
            <div className="relative inline-flex shrink-0 items-center rounded">
              <button
                type="button"
                aria-label={t('grid.summaryCards')}
                title={t('grid.summaryCards')}
                onClick={() => summaryCardsContext.toggleSummaryCards()}
                className={`flex h-7 items-center justify-center pl-1.5 pr-0.5 rounded-l transition-colors ${summaryCardsContext.summaryCardsVisible ? 'bg-brand/5 text-brand' : 'hover:bg-surface-hover'}`}
              >
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 4H6l7 8-7 8h12" />
                </svg>
              </button>
              <button
                type="button"
                aria-label={t('grid.summaryOptions')}
                title={t('grid.summaryOptions')}
                onClick={() => setSummaryDropdownOpen((o) => !o)}
                className={`flex h-7 w-4 items-center justify-start rounded-r transition-colors hover:bg-surface-hover ${summaryDropdownOpen ? 'bg-brand/10' : ''}`}
              >
                <Caret />
              </button>
              {summaryDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setSummaryDropdownOpen(false)} />
                  <div
                    ref={summaryMenuRef}
                    className="absolute right-0 top-full mt-1 z-50 flex flex-col min-w-[200px] w-max rounded-lg border border-border bg-surface py-1 shadow-lg text-sm sm:text-xs"
                  >
                    <div className="flex items-center justify-between gap-3 px-3 pb-1.5 pt-1">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-text-muted">{t('device.showOn')}</span>
                      <DeviceSwitch value={summaryCardsContext.summaryCardsDevice} onChange={summaryCardsContext.setSummaryCardsDevice}
                        label={t('device.showSummaryCardsOn')} />
                    </div>
                    <div className="px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-text-muted">
                      {t('grid.summaryCardsPlacement')}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        summaryCardsContext.setSummaryCardsPlacement('top')
                        setSummaryDropdownOpen(false)
                      }}
                      className={`flex items-center justify-between px-3 py-1.5 text-left hover:bg-surface-hover transition-colors ${summaryCardsContext.summaryCardsPlacement === 'top' && summaryCardsContext.summaryCardsVisible ? 'text-brand font-medium' : ''}`}
                    >
                      <span>{t('grid.summaryCardsTop')}</span>
                      {summaryCardsContext.summaryCardsPlacement === 'top' && summaryCardsContext.summaryCardsVisible && (
                        <Icon name="check" className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        summaryCardsContext.setSummaryCardsPlacement('topBelow')
                        setSummaryDropdownOpen(false)
                      }}
                      className={`flex items-center justify-between px-3 py-1.5 text-left hover:bg-surface-hover transition-colors ${summaryCardsContext.summaryCardsPlacement === 'topBelow' && summaryCardsContext.summaryCardsVisible ? 'text-brand font-medium' : ''}`}
                    >
                      <span>{t('grid.summaryCardsTopBelow')}</span>
                      {summaryCardsContext.summaryCardsPlacement === 'topBelow' && summaryCardsContext.summaryCardsVisible && (
                        <Icon name="check" className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        summaryCardsContext.setSummaryCardsPlacement('bottom')
                        setSummaryDropdownOpen(false)
                      }}
                      className={`flex items-center justify-between px-3 py-1.5 text-left hover:bg-surface-hover transition-colors ${summaryCardsContext.summaryCardsPlacement === 'bottom' && summaryCardsContext.summaryCardsVisible ? 'text-brand font-medium' : ''}`}
                    >
                      <span>{t('grid.summaryCardsBottom')}</span>
                      {summaryCardsContext.summaryCardsPlacement === 'bottom' && summaryCardsContext.summaryCardsVisible && (
                        <Icon name="check" className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        summaryCardsContext.setSummaryCardsPlacement('sidebar')
                        setSummaryDropdownOpen(false)
                      }}
                      className={`flex items-center justify-between px-3 py-1.5 text-left hover:bg-surface-hover transition-colors ${summaryCardsContext.summaryCardsPlacement === 'sidebar' && summaryCardsContext.summaryCardsVisible ? 'text-brand font-medium' : ''}`}
                    >
                      <span>{t('grid.summaryCardsSidebar')}</span>
                      {summaryCardsContext.summaryCardsPlacement === 'sidebar' && summaryCardsContext.summaryCardsVisible && (
                        <Icon name="check" className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        summaryCardsContext.toggleAddCardButton()
                        setSummaryDropdownOpen(false)
                      }}
                      className={`flex items-center justify-between px-3 py-1.5 text-left hover:bg-surface-hover transition-colors ${summaryCardsContext.addCardButtonVisible ? 'text-brand font-medium' : ''}`}
                    >
                      <span>{t('grid.addCardButton')}</span>
                      {summaryCardsContext.addCardButtonVisible && (
                        <Icon name="check" className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        summaryCardsContext.toggleTotalsRow()
                        setSummaryDropdownOpen(false)
                      }}
                      className={`flex items-center justify-between px-3 py-1.5 text-left hover:bg-surface-hover transition-colors ${summaryCardsContext.totalsRowVisible ? 'text-brand font-medium' : ''}`}
                    >
                      <span>{t('grid.totalsRow')}</span>
                      {summaryCardsContext.totalsRowVisible && (
                        <Icon name="check" className="h-3.5 w-3.5" />
                      )}
                    </button>
                    {groupInfo.hasGrouping && (
                      <button
                        type="button"
                        onClick={() => {
                          toggleGroupSubtotals()
                          setSummaryDropdownOpen(false)
                        }}
                        className={`flex items-center justify-between px-3 py-1.5 text-left hover:bg-surface-hover transition-colors ${showGroupSubtotals ? 'text-brand font-medium' : ''}`}
                      >
                        <span>{t('grid.showGroupSubtotals')}</span>
                        {showGroupSubtotals && (
                          <Icon name="check" className="h-3.5 w-3.5" />
                        )}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )
        }] : []),
        ...(activeCellIsEditable ? [{
          key: 'cell-link', menuOnMobile: true, menu: [{
            icon: MENU_ICONS.link,
            label: t('grid.editLink'),
            onSelect: openLinkDialog,
          }], node: (
            <button
              type="button"
              aria-label={t('grid.editLink')}
              title={t('grid.editLink')}
              onClick={openLinkDialog}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors hover:bg-surface-hover"
            >
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1 1" />
                <path d="M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1-1" />
              </svg>
            </button>
          )
        }] : []),
        ...((localRowOrder !== null || localSort !== null || Object.keys(widthsDraft).length > 0) ? [{
          key: 'reset-view', menu: [{
            icon: MENU_ICONS.resetView,
            label: t('grid.resetView'),
            onSelect: () => { setLocalRowOrder(null); setLocalSort(null); setWidthsDraft({}) },
          }], node: (
            <button type="button" onClick={() => { setLocalRowOrder(null); setLocalSort(null); setWidthsDraft({}) }} title={t('grid.resetViewTitle')} className="h-7 shrink-0 rounded px-2.5 text-sm sm:text-xs">{t('grid.resetView')}</button>
          )
        }] : []),
        ...(toolbarAppend ? [{ key: 'toolbar-append', node: toolbarAppend, ...(toolbarAppendMenu ? { menu: toolbarAppendMenu } : {}) }] : []),
        ...(canEditFormats || personalView ? [{
          key: 'chart', separatorBefore: true, menuOnMobile: true, menu: [{
            icon: MENU_ICONS.chart,
            label: t('grid.chart'),
            active: chartPanel,
            onSelect: toggleChartPanel,
          }], node: (
            <button type="button" title={t('grid.toggleChart')}
              aria-expanded={chartPanel}
              aria-controls="grid-chart-panel"
              onClick={toggleChartPanel}
              aria-label={t('grid.toggleChart')}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors ${chartPanel ? 'bg-brand/5 text-brand' : 'hover:bg-surface-hover'}`}>
              {MENU_ICONS.chart}
            </button>
          )
        }] : []),
        ...(canEditFormats && splitView && splitView.sheets.length > 0 ? [{
          key: 'split-view', menuOnMobile: true, menuTitle: t('grid.splitView'), menu: splitView.sheets
            .filter((sheet) => splitSheetIds.includes(sheet.id) || splitSheetIds.length < MAX_SPLIT_SHEETS)
            .map((sheet) => ({
              icon: MENU_ICONS.splitView,
              label: `${t('grid.splitView')}: ${sheet.name}`,
              active: splitSheetIds.includes(sheet.id),
              onSelect: () => toggleSplitSheet(sheet.id),
            })), node: (
            <button type="button" title={t('grid.splitView')}
              aria-label={t('grid.splitView')}
              aria-haspopup="menu"
              aria-expanded={splitMenu !== null}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect()
                setSplitMenu((open) => open ? null : { left: Math.max(8, Math.min(rect.left, window.innerWidth - 248)), top: rect.bottom + 4 })
              }}
              className={`flex h-7 shrink-0 items-center justify-center gap-0.5 rounded px-1 transition-colors ${splitSheets.length > 0 ? 'bg-brand/5 text-brand' : 'hover:bg-surface-hover'}`}>
              {MENU_ICONS.splitView}
              <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </button>
          )
        }] : []),
        ...(canToggleColumns ? [{
          key: 'columns', menuOnMobile: true, menu: [{
            icon: MENU_ICONS.columns,
            label: t('grid.columns'),
            active: columnsPanel,
            onSelect: () => setColumnsPanel((open) => {
              const next = !open
              if (next) { setGroupPanel(false); setChartPanel(false); setSplitSheetIds([]) }
              savePref('columnas', next ? '1' : '0')
              return next
            }),
          }], node: (
            <button type="button" title={t('grid.toggleColumns')}
              aria-expanded={columnsPanel}
              aria-controls="grid-columns-panel"
              onClick={() => setColumnsPanel((open) => {
                const next = !open
                if (next) { setGroupPanel(false); setChartPanel(false); setSplitSheetIds([]) }
                savePref('columnas', next ? '1' : '0')
                return next
              })}
              aria-label={t('grid.toggleColumns')}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors ${columnsPanel ? 'bg-brand/5 text-brand' : 'hover:bg-surface-hover'}`}>
              {MENU_ICONS.columns}
            </button>
          )
        }] : []),
      ]}
    />

  // A declaration, so the main toolbar built above can use it too.
  function toggleTargetBold() {
    const current = dataFormatsConfig[gridKey]?.[formatTarget] ?? { type: 'text' }
    void saveDataFormat(subtotalRange ? { ...current, subtotalBold: !targetBold } : { ...current, bold: !current.bold })
  }

  useEffect(() => { setSelectionToolbar(false) }, [anchor, end, editing, selectedColumns, selectedFullColumn])

  const renderSelectionToolbar = () => {
    if (!range) return null
    const topLeft = renderedRows[range.rowMin]
    const firstCol = visibleColumns[range.colMin - 1]
    if (!topLeft || firstCol === undefined) return null
    const topCell = cellRefs.current.get(`${keyOf(topLeft)}|${firstCol}`)?.getBoundingClientRect()
    if (!topCell) return null
    const BAR_H = 44
    // Always above the selection, like Sheets; at the top of the screen it overlaps the selection instead.
    const top = Math.max(topCell.top - BAR_H - 6, (window.visualViewport?.offsetTop ?? 0) + 4)
    const row = activeCellRow
    const close = () => { setSelectionToolbar(false); setSelectionToolbarMenu(null) }
    const run = (work: () => unknown) => () => { close(); void work() }
    const button = 'flex h-10 w-9 shrink-0 items-center justify-center rounded-lg text-text transition-colors hover:bg-surface-hover disabled:opacity-40'
    const caret = 'flex h-10 w-4 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-hover'
    const menuItem = 'flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-text hover:bg-surface-3 disabled:opacity-40'
    const canFormat = canEditFormats && cellColorTargets.length > 0
    return createPortal(
      <div
        role="toolbar"
        aria-label={t('grid.selectionActions')}
        data-selection-toolbar
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        className="fixed left-2 right-2 z-[60] mx-auto flex w-max max-w-[calc(100vw-1rem)] items-center overflow-x-auto rounded-xl border border-border bg-surface px-0.5 shadow-xl nt-hidden-scrollbar"
        style={{ top, height: BAR_H }}
      >
        <div className="flex shrink-0 items-center">
          <button type="button" className={button} title={t('grid.copySelection')} aria-label={t('grid.copySelection')} onClick={run(() => copySelection(false))}><span className="[&>svg]:h-5 [&>svg]:w-5">{MENU_ICONS.copy}</span></button>
          <CopySplitButton
            onCopy={run(() => copySelection(false))}
            hasSelection
            onCopySelection={(asMd) => { close(); void copySelection(asMd) }}
            onCopyAll={(asMd) => { close(); void copyAll(asMd) }}
            copyHeaders={copyHeaders}
            onToggleCopyHeaders={setCopyHeaders}
            copyNumberFormat={copyNumberFormat}
            onToggleCopyNumberFormat={setCopyNumberFormat}
            copyFormulas={copyFormulas}
            onToggleCopyFormulas={setCopyFormulas}
            caretOnly
          />
        </div>
        {rangeEditable && (
          <div className="flex shrink-0 items-center">
            <button type="button" className={button} title={t('grid.paste')} aria-label={t('grid.paste')} onClick={run(() => pasteSelection())}><span className="[&>svg]:h-5 [&>svg]:w-5">{MENU_ICONS.paste}</span></button>
            <button type="button" className={caret} aria-label={t('grid.pasteOptions')} aria-expanded={selectionToolbarMenu === 'paste'} onClick={() => setSelectionToolbarMenu((m) => m === 'paste' ? null : 'paste')}><Caret /></button>
          </div>
        )}
        {rangeEditable && (
          <button type="button" className={button} title={t('grid.menu.clearCell')} aria-label={t('grid.menu.clearCell')} onClick={run(clearSelection)}><span className="[&>svg]:h-5 [&>svg]:w-5">{MENU_ICONS.clear}</span></button>
        )}
        {canFormat && (
          <button
            type="button"
            className={`${button} ${targetBold ? 'bg-brand/10 text-brand' : ''}`}
            disabled={formatBusy || formatTarget === ''}
            title={t('grid.bold')}
            aria-label={t('grid.bold')}
            aria-pressed={targetBold}
            onClick={toggleTargetBold}
          >
            <span className="text-base font-bold leading-none">B</span>
          </button>
        )}
        {canFormat && (
          <ColorPicker
            valor={selectedFillColor}
            onChange={(color) => void applyFillColor(color)}
            gridFill={gridFillControls}
            coloresUsados={usedCellColors}
            disabled={cellColorLoading || cellColorBusy}
            ariaLabel={t('grid.fillColor')}
            title={t('grid.fillColor')}
            triggerClassName={button}
            trigger={
              <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="m19 11-8-8-6 6 8 8 6-6Z" />
                <path d="m5 15-2 2v4h4l2-2" />
                <path d="m13 5 6 6" />
                <path d="M3 22h18" stroke={selectedFillColor ?? 'currentColor'} strokeWidth="2.5" />
              </svg>
            }
          />
        )}
        {canFormat && (
          <button
            type="button"
            className={`${button} ${formatPainter ? 'bg-brand/10 text-brand' : ''}`}
            disabled={formatBusy || cellColorBusy}
            title={t('grid.paintFormat')}
            aria-label={t('grid.paintFormat')}
            aria-pressed={formatPainter !== null}
            onClick={() => { close(); armFormatPainter() }}
          >
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="2" width="14" height="6" rx="1" />
              <path d="M18 5h2v5h-8v3" />
              <rect x="10" y="13" width="4" height="9" rx="1" />
            </svg>
          </button>
        )}
        {editable && row && (onInsertRow || onDeleteRow) && (
          <button type="button" className={button} aria-label={t('grid.moreOptions')} aria-expanded={selectionToolbarMenu === 'more'} onClick={() => setSelectionToolbarMenu((m) => m === 'more' ? null : 'more')}>
            <svg aria-hidden="true" viewBox="0 0 4 16" className="h-4 w-1 fill-current"><circle cx="2" cy="2.5" r="1.6" /><circle cx="2" cy="8" r="1.6" /><circle cx="2" cy="13.5" r="1.6" /></svg>
          </button>
        )}
        {selectionToolbarMenu && (
          <div
            role="menu"
            className="fixed right-2 z-[61] w-56 rounded-xl border border-border bg-surface py-1 shadow-xl"
            style={top + BAR_H + 4 + 160 < window.innerHeight ? { top: top + BAR_H + 4 } : { bottom: window.innerHeight - top + 4 }}
          >
            {selectionToolbarMenu === 'paste' ? <>
              <button type="button" role="menuitem" className={menuItem} onClick={run(() => pasteSelection())}>{MENU_ICONS.paste}{t(copiedHasFormulas ? 'grid.pasteFormulas' : 'grid.paste')}</button>
              {copiedHasFormulas && <button type="button" role="menuitem" className={menuItem} onClick={run(() => pasteSelection({ valuesOnly: true }))}>{MENU_ICONS.paste}{t('grid.pasteValues')}</button>}
              <button type="button" role="menuitem" className={menuItem} onClick={run(() => pasteSelection({ transpose: true }))}>{MENU_ICONS.paste}{t('grid.pasteTransposed')}</button>
            </> : row && <>
              {onInsertRow && <button type="button" role="menuitem" className={menuItem} onClick={run(() => runStructural(() => onInsertRow(row, 'above')))}>{MENU_ICONS.arrowUp}{t('grid.insertRowAbove')}</button>}
              {onInsertRow && <button type="button" role="menuitem" className={menuItem} onClick={run(() => runStructural(() => onInsertRow(row, 'below')))}>{MENU_ICONS.arrowDown}{t('grid.insertRowBelow')}</button>}
              {onDeleteRow && <button type="button" role="menuitem" className={menuItem} onClick={run(async () => { await pinGroupRowOrderRef.current(); await runStructural(() => onDeleteRow(row)) })}>{MENU_ICONS.trash}{onBulkRestore ? t('grid.menu.deleteRow') : t('grid.moveToTrash')}</button>}
            </>}
          </div>
        )}
      </div>,
      document.body,
    )
  }

  return <div ref={cardRef} onPointerDownCapture={splitSheets.length > 0 ? followPane : undefined} data-typing-zone style={{ height: height !== undefined ? height : undefined }} className={isEmbedded ? 'flex flex-col' : `relative border-border bg-surface shadow-card flex flex-col flex-1 min-h-0 ${fullScreen ? '' : 'rounded-full border p-3'}`}>
    {!isEmbedded && !bare && !splitPane && <div data-split-toolbar className={sidebarActive ? 'hidden' : 'contents'}>{toolbar}</div>}
    {!isEmbedded && !bare && !splitPane && splitSheets.length > 0 && <div data-split-toolbar ref={setSplitToolbarSlot} className={sidebarActive ? toolbarSpacing : 'hidden'} />}
    {!isEmbedded && splitPane?.active && splitPane.toolbarSlot && createPortal(toolbar, splitPane.toolbarSlot)}

    {!isEmbedded && !bare && searchCollapsed && searchRowOpen && <div className={`mb-2 flex flex-wrap items-center gap-1.5 shrink-0 ${fullScreen ? 'px-3' : ''}`}>
      <div className="relative flex-1 min-w-[12rem]">
        <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={selectedFullColumn !== null && columns[selectedFullColumn]
            ? t('grid.searchInColumn', { column: headerOf(selectedFullColumn) })
            : t('grid.searchGrid')}
          autoFocus
          className="h-8 w-full rounded-full border border-border bg-bg pl-9 pr-8 text-sm outline-none focus:border-focus"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text text-sm font-bold"
            aria-label={t('grid.clearSelection')}
          >
            ✕
          </button>
        )}
      </div>
      {replaceVisible && (
        <div className="relative flex-1 min-w-[12rem]">
          <input
            value={replaceText}
            onChange={(e) => setReplaceText(e.target.value)}
            placeholder={t('grid.replaceWith')}
            aria-label={t('grid.replaceWith')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleReplaceAll()
              } else if (e.key === 'Escape') {
                setReplaceVisible(false)
              }
            }}
            className="h-8 w-full rounded-full border border-border bg-bg pl-3 pr-8 text-sm outline-none focus:border-focus"
          />
          <button
            type="button"
            onClick={() => {
              setReplaceVisible(false)
              setReplaceText('')
            }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text text-sm font-bold"
            aria-label={t('grid.cancel')}
            title={t('grid.cancel')}
          >
            ✕
          </button>
        </div>
      )}
      {editable && (
        <button
          type="button"
          aria-pressed={replaceVisible}
          onClick={() => {
            if (replaceVisible) {
              handleReplaceAll()
            } else {
              setReplaceVisible(true)
            }
          }}
          className={`flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-border px-3 text-xs font-medium transition-colors ${replaceVisible ? 'bg-brand/5 text-brand' : 'hover:bg-surface-hover'}`}
        >
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 11V9a2 2 0 0 0-2-2H4" /><path d="m7 4-3 3 3 3" /><path d="M5 13v2a2 2 0 0 0 2 2h13" /><path d="m17 14 3 3-3 3" />
          </svg>
          <span>{t('grid.replace')}</span>
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          setSearchRowOpen(false)
          setReplaceVisible(false)
        }}
        className="h-8 px-2 text-xs font-medium text-text-muted hover:text-text"
      >
        {t('grid.cancel')}
      </button>
    </div>}

    {cellActionStatus && (
      <div
        role="status"
        aria-live="polite"
        className={`fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-lg border px-3 py-2 text-sm shadow-lg ${
          cellActionStatus.state === 'error' ? 'border-danger bg-danger-soft text-danger' : 'border-border bg-surface text-fg'
        }`}
      >
        {cellActionStatus.state === 'running' && <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-accent" />}
        {cellActionStatus.state === 'done' && <span aria-hidden="true" className="font-bold text-accent">✓</span>}
        <span>
          {cellActionStatus.state === 'running'
            ? t('grid.menu.cellActionRunning', { label: cellActionStatus.label })
            : cellActionStatus.state === 'done'
              ? t('grid.menu.cellActionDone', { label: cellActionStatus.label })
              : cellActionStatus.message}
        </span>
        {cellActionStatus.state !== 'running' && (
          <button type="button" aria-label={t('common.close')} onClick={() => setCellActionStatus(null)} className="shrink-0 font-bold">✕</button>
        )}
      </div>
    )}
    {configError && <div className={`mb-3 flex items-center justify-between gap-2 rounded-lg border border-danger bg-danger-soft p-2 text-sm sm:text-xs text-danger shrink-0 ${fullScreen ? 'mx-3' : ''}`}>
      <span>{configError}</span>
      <button type="button" onClick={() => setConfigError(null)} className="shrink-0 font-bold">✕</button>
    </div>}

    {overrideError && <div className={`mb-3 flex items-center justify-between gap-2 rounded-full border border-danger bg-danger-soft p-2 text-sm sm:text-xs text-danger shrink-0 ${fullScreen ? 'mx-3' : ''}`}>
      <span>{overrideError}</span>
      <button type="button" onClick={() => setOverrideError(null)} className="shrink-0 font-bold">✕</button>
    </div>}

    {confirmingDelete && <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--overlay)] p-4" onClick={() => setConfirmingDelete(false)}>
      <div className="w-full max-w-sm rounded-token-lg border border-border bg-surface p-5 shadow-card text-fg" onClick={(event) => event.stopPropagation()}>
        <h2 className="font-display text-lg font-bold">{deleteAction.title}</h2>
        <p className="mt-2 text-sm text-text-muted">{deleteAction.message(selected.size)}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={() => setConfirmingDelete(false)} disabled={processingBulk} className="h-9 rounded-full border border-border px-4 text-xs sm:text-sm font-bold disabled:opacity-40">{t('grid.cancel')}</button>
          <button type="button" onClick={() => void runDelete()} disabled={processingBulk} className="h-9 rounded-full bg-danger px-4 text-xs sm:text-sm font-bold text-white disabled:opacity-40">{deleteAction.label}</button>
        </div>
      </div>
    </div>}

    {cellColorError && <div className={`mb-3 flex items-center justify-between gap-2 rounded-lg border border-danger bg-danger-soft p-2 text-sm sm:text-xs text-danger shrink-0 ${fullScreen ? 'mx-3' : ''}`}>
      <span>{cellColorError}</span>
      <button type="button" onClick={() => setConfigError(null)} className="shrink-0 font-bold">✕</button>
    </div>}

    {historyError && <div className={`mb-3 flex items-center justify-between gap-2 rounded-lg border border-danger bg-danger-soft p-2 text-sm sm:text-xs text-danger shrink-0 ${fullScreen ? 'mx-3' : ''}`}>
      <span>{historyError}</span>
      <button type="button" onClick={() => setHistoryError(null)} className="shrink-0 font-bold">✕</button>
    </div>}

    {canEditFormats && formatBar && <div data-hide-while-typing className={`mb-0.5 flex min-w-0 max-w-full shrink-0 items-center gap-2 text-sm sm:text-xs ${fullScreen ? 'mx-3' : ''}`}>
      <ToolbarOverflow
        className="min-w-0 flex-1"
        menuLabel={t('grid.moreOptions')}
        measureKey={`${formatTarget}:${formatSelectionFields.length}:${JSON.stringify(formatBaseOf(formatTarget) ?? null)}`}
        items={[
        { key: 'name', pinned: true, node: (
          <NameBox
            targets={formulaTargets}
            selected={formatTarget}
            selectionCount={formatSelectionFields.length}
            onSelect={(field) => { setSelectedColumns(new Set()); setSelectedFullColumn(null); setAnchor(null); setEnd(null); setSubtotalAnchor(null); setSubtotalEnd(null); setFormatTarget(field) }}
            resizable
            allowNew={false}
            disabled={formatBusy || formulaTargets.length === 0}
          />
        ) },
        { key: 'type', node: (<>
        <select
          aria-label={t('grid.dataType')}
          value={formatTarget !== '' ? (formatBaseOf(formatTarget)?.type ?? columns.find((column) => column.field === formatTarget)?.dataFormat?.type ?? 'text') : 'text'}
          disabled={formatBusy || formatTarget === ''}
          onChange={(event) => {
            const nextType = event.target.value as 'auto' | 'text' | 'date' | 'number' | 'smartchip' | 'autofill' | 'rollup' | 'lookup' | 'attachment' | 'checkbox'
            if (nextType === 'rollup' || nextType === 'lookup') {
              const current = formatBaseOf(formatTarget)
              void saveDataFormat({ ...(current ?? { type: nextType }), type: nextType }, false, ['type'])
              setRollupDialog(true)
            } else {
              void saveDataFormat({ type: nextType }, false, ['type', 'formatKey', 'currency', 'cents', 'customPattern', 'options', 'optionsLink', 'rollup', 'attachmentConfig', 'cells', 'invalid'])
            }
          }}
          className="h-7 shrink-0 rounded bg-transparent px-2 hover:bg-surface-hover cursor-pointer text-sm sm:text-xs outline-none focus:border-focus disabled:opacity-50 [&>option]:bg-surface [&>option]:text-text"
        >
          <option value="auto">{t('grid.dataTypeByCell')}</option>
          <option value="text">{t('grid.dataTypeText')}</option>
          <option value="date">{t('grid.dataTypeDate')}</option>
          <option value="number">{t('grid.dataTypeNumber')}</option>
          <option value="smartchip">{t('grid.dataTypeSmartchip')}</option>
          <option value="autofill">{t('grid.dataTypeAutofill')}</option>
          <option value="attachment">{t('grid.dataTypeAttachment')}</option>
          <option value="checkbox">{t('grid.dataTypeCheckbox')}</option>
          <option value="rollup">Rollup</option>
          <option value="lookup">Lookup</option>
        </select>
        {formatTarget !== '' && columnFormatOf(formatTarget)?.type === 'auto' && byCellTargets.length > 0 && <>
          <span aria-hidden="true" className="flex h-5 w-2.5 shrink-0 justify-center"><span className="h-full w-px bg-border" /></span>
          <select
            aria-label={t('grid.cellDataType')}
            title={t('grid.cellDataType')}
            value={selectedCellFormat?.type ?? ''}
            disabled={formatBusy}
            onChange={(event) => {
              const type = event.target.value as GridCellFormat['type']
              void saveCellFormats(() => ({ type }))
            }}
            className="h-7 shrink-0 rounded bg-transparent px-2 hover:bg-surface-hover cursor-pointer text-sm sm:text-xs outline-none focus:border-focus disabled:opacity-50 [&>option]:bg-surface [&>option]:text-text"
          >
            {selectedCellFormat === null && <option value="" disabled>—</option>}
            {GRID_CELL_DATA_TYPES.map((type) => (
              <option key={type} value={type}>{t(CELL_TYPE_LABELS[type])}</option>
            ))}
          </select>
        </>}
        </>) },
        { key: 'lists', node: (<>
        {(() => {
          const selected = formatTarget === '' ? undefined : formatBaseOf(formatTarget)
          if (selected?.type !== 'smartchip' && selected?.type !== 'autofill') return null
          return <button
            type="button"
            disabled={formatBusy}
            title={t('grid.editOptions')}
            aria-label={t('grid.editOptions')}
            onClick={() => setOptionsDialog(true)}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-text transition-colors hover:bg-surface-hover disabled:opacity-50"
          >
            <Icon name="pencil" className="h-3.5 w-3.5" />
          </button>
        })()}
        {(() => {
          const selected = formatTarget === '' ? undefined : formatBaseOf(formatTarget)
          if (selected?.type !== 'rollup' && selected?.type !== 'lookup') return null
          return <button
            type="button"
            disabled={formatBusy}
            title="Configure rollup / lookup"
            aria-label="Configure rollup / lookup"
            onClick={() => setRollupDialog(true)}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-text transition-colors hover:bg-surface-hover disabled:opacity-50"
          >
            <Icon name="pencil" className="h-3.5 w-3.5" />
          </button>
        })()}
        {(() => {
          // A by-cell column's number/date format belongs to the selected cells, not the column.
          const cellMode = formatTarget !== '' && columnFormatOf(formatTarget)?.type === 'auto'
          const selected: GridDataFormat | undefined = cellMode
            ? (selectedCellFormat ?? undefined)
            : formatTarget === '' ? undefined : formatBaseOf(formatTarget)
          const saveFormat = (format: GridDataFormat): Promise<boolean> => cellMode
            ? saveCellFormats((cell) => {
              const { formatKey, currency, cents, customPattern } = format
              return {
                type: cell.type,
                ...(formatKey !== undefined ? { formatKey } : {}),
                ...(currency !== undefined ? { currency } : {}),
                ...(cents !== undefined ? { cents } : {}),
                ...(customPattern !== undefined ? { customPattern } : {}),
              }
            }).then(() => true)
            : saveDataFormat(format, false, ['formatKey', 'currency', 'cents', 'customPattern', 'exchangeRate'])
          const options = selected?.type === 'date' ? GRID_DATE_FORMATS : (selected?.type === 'number' || selected?.type === 'rollup') ? GRID_NUMBER_FORMATS : []
          if (options.length === 0) return null
          const fallback: GridFormatKey = selected?.type === 'date' ? 'date' : 'integer'
          return <>
          <span aria-hidden="true" className="flex h-5 w-2.5 shrink-0 justify-center"><span className="h-full w-px bg-border" /></span>
          <select
            aria-label={t('grid.formatVariant')}
            value={selected?.exchangeRate ? 'exchangeRate' : selected?.customPattern !== undefined ? 'custom' : (selected?.formatKey ?? fallback)}
            disabled={formatBusy}
            onChange={(event) => {
              const base = selected ?? { type: 'text' as const }
              const { customPattern: _previous, exchangeRate: _rate, ...rest } = base
              if (event.target.value === 'exchangeRate') {
                // Start from the first two enabled currencies the history has, and the first date column.
                const supported = FX_HISTORY_CURRENCIES.filter((currency) => gridConfig.currencies.includes(currency))
                const from = supported[0] ?? 'USD'
                const to = supported.find((currency) => currency !== from) ?? (from === 'USD' ? 'ARS' : 'USD')
                const dateField = columns.find((column) => column.field && column.dataFormat?.type === 'date')?.field
                if (!dateField) return
                const exchangeRate = { from, to, dateField }
                void saveFormat({ ...rest, type: 'number', formatKey: 'decimal', exchangeRate })
                  .then((saved) => { if (saved) void fillExchangeRateColumn(formatTarget, exchangeRate) })
              } else if (event.target.value === 'custom') {
                // Start from the pattern the column shows now, so switching changes nothing until edited.
                void saveFormat({ ...rest, customPattern: formats[base.formatKey ?? fallback] })
              } else {
                void saveFormat({ ...rest, formatKey: event.target.value as GridFormatKey })
              }
            }}
            className="h-7 shrink-0 rounded bg-transparent px-2 hover:bg-surface-hover cursor-pointer text-sm sm:text-xs outline-none focus:border-focus disabled:opacity-50"
          >
            {options.map((option) => <option key={option} value={option}>{option}</option>)}
            {selected?.type === 'number' && !cellMode && (
              <option value="exchangeRate" disabled={!columns.some((column) => column.field && column.dataFormat?.type === 'date')}>
                {t('grid.exchangeRate.type')}
              </option>
            )}
            <option value="custom">{t('grid.formatCustom')}</option>
          </select>
          {selected && selected.customPattern !== undefined && <GridCustomPatternInput
            key={`${formatTarget}:${selected.customPattern}`}
            pattern={selected.customPattern}
            disabled={formatBusy}
            label={t('grid.formatCustom')}
            onCommit={(customPattern) => void saveFormat({ ...selected, customPattern })}
          />}
          </>
        })()}
        {(() => {
          // Exchange rate: currency 1, currency 2, date column, ARS market, side, overwriting.
          if (formatTarget === '') return null
          const column = columns.find((c) => c.field === formatTarget)
          const base = column?.dataFormat
          const fx = base?.exchangeRate
          if (!base || !fx) return null
          // Every change refills the empty cells under the new setup.
          const commitFx = (exchangeRate: typeof fx) => {
            void saveDataFormat({ ...base, exchangeRate })
              .then((saved) => { if (saved) void fillExchangeRateColumn(formatTarget, exchangeRate) })
          }
          const save = (patch: Partial<typeof fx>) => commitFx({ ...fx, ...patch })
          const supported = FX_HISTORY_CURRENCIES.filter((currency) => gridConfig.currencies.includes(currency) || currency === fx.from || currency === fx.to)
          const dateColumns = columns.flatMap((c, index) => (c.field && c.dataFormat?.type === 'date' ? [{ field: c.field, label: headerOf(index) }] : []))
          const fixed = fx.fixed === true
          const selectClass = 'h-7 shrink-0 rounded bg-transparent px-2 hover:bg-surface-hover cursor-pointer text-sm sm:text-xs outline-none focus:border-focus disabled:cursor-not-allowed disabled:opacity-60'
          const divider = <span aria-hidden="true" className="flex h-5 w-2.5 shrink-0 justify-center"><span className="h-full w-px bg-border" /></span>
          return <>
          {divider}
          <select aria-label={t('grid.exchangeRate.from')} title={fixed ? t('grid.exchangeRate.fixed') : t('grid.exchangeRate.from')}
            value={fx.from} disabled={formatBusy || fixed} className={selectClass}
            onChange={(event) => {
              const from = event.target.value as typeof fx.from
              save({ from, ...(from === fx.to ? { to: fx.from } : {}) })
            }}>
            {supported.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
          </select>
          <span aria-hidden="true" className="text-xs text-muted">→</span>
          <select aria-label={t('grid.exchangeRate.to')} title={fixed ? t('grid.exchangeRate.fixed') : t('grid.exchangeRate.to')}
            value={fx.to} disabled={formatBusy || fixed} className={selectClass}
            onChange={(event) => save({ to: event.target.value as typeof fx.to })}>
            {supported.filter((currency) => currency !== fx.from).map((currency) => <option key={currency} value={currency}>{currency}</option>)}
          </select>
          <select aria-label={t('grid.exchangeRate.date')} title={fixed ? t('grid.exchangeRate.fixed') : t('grid.exchangeRate.date')}
            value={fx.dateField} disabled={formatBusy || fixed} className={selectClass}
            onChange={(event) => save({ dateField: event.target.value })}>
            {/* A date column saved as another type (or a module's preset) still names itself. */}
            <option value={EXCHANGE_RATE_TODAY}>{t('grid.exchangeRate.today')}</option>
            {fx.dateField !== EXCHANGE_RATE_TODAY && !dateColumns.some((c) => c.field === fx.dateField) && (() => {
              const index = columns.findIndex((c) => c.field === fx.dateField)
              return <option value={fx.dateField}>{index >= 0 ? headerOf(index) : fx.dateField}</option>
            })()}
            {dateColumns.map((c) => <option key={c.field} value={c.field}>{c.label}</option>)}
          </select>
          {(fx.from === 'ARS' || fx.to === 'ARS') && (
            <select aria-label={t('grid.exchangeRate.market')} title={t('grid.exchangeRate.market')}
              value={fx.market ?? 'oficial'} disabled={formatBusy} className={selectClass}
              onChange={(event) => save({ market: event.target.value as (typeof FX_MARKETS)[number] })}>
              {FX_MARKETS.map((market) => <option key={market} value={market}>{t(`grid.exchangeRate.markets.${market}`)}</option>)}
            </select>
          )}
          <select aria-label={t('grid.exchangeRate.side')} title={t('grid.exchangeRate.side')}
            value={fx.side ?? 'avg'} disabled={formatBusy} className={selectClass}
            onChange={(event) => {
              // A custom rule needs its column and values first: the dialog saves it.
              if (event.target.value === 'rule') { setQuoteRuleField(formatTarget); return }
              const { sideRule: _rule, ...rest } = fx
              commitFx({ ...rest, side: event.target.value as (typeof FX_SIDES)[number] })
            }}>
            {FX_SIDES.map((side) => <option key={side} value={side}>{t(`grid.exchangeRate.sides.${side}`)}</option>)}
            <option value="rule">{t('grid.exchangeRate.sides.rule')}</option>
          </select>
          {fx.side === 'rule' && (
            <button type="button" disabled={formatBusy} onClick={() => setQuoteRuleField(formatTarget)}
              title={t('grid.exchangeRate.rule.edit')} aria-label={t('grid.exchangeRate.rule.edit')}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-text transition-colors hover:bg-surface-hover disabled:opacity-50">
              <Icon name="pencil" className="h-3.5 w-3.5" />
            </button>
          )}
          {divider}
          <label className="flex h-7 shrink-0 items-center gap-1.5 text-sm sm:text-xs">
            <input type="checkbox" checked={fx.allowOverwrite !== false} disabled={formatBusy}
              onChange={(event) => {
                const { allowOverwrite: _previous, ...rest } = fx
                commitFx(event.target.checked ? rest : { ...rest, allowOverwrite: false })
              }}
              className="h-4 w-4 accent-brand" />
            {t('grid.formulaAllowCellOverrides')}
          </label>
          </>
        })()}
        {(() => {
          const selected = formatTarget === '' ? undefined : formatBaseOf(formatTarget)
          if (!selected || selected.formatKey !== 'currency') return null
          const available = selected.currency && !gridConfig.currencies.includes(selected.currency)
            ? [...gridConfig.currencies, selected.currency]
            : gridConfig.currencies
          return <>
          <span aria-hidden="true" className="flex h-5 w-2.5 shrink-0 justify-center"><span className="h-full w-px bg-border" /></span>
          <select
            aria-label={t('grid.formatCurrency')}
            value={selected.currency ?? ''}
            disabled={formatBusy}
            onChange={(event) => {
              const { currency: _previous, ...rest } = selected
              void saveDataFormat(event.target.value ? { ...rest, currency: event.target.value as Currency } : rest, false, ['currency'])
            }}
            className="h-7 shrink-0 rounded bg-transparent px-2 hover:bg-surface-hover cursor-pointer text-sm sm:text-xs outline-none focus:border-focus disabled:opacity-50"
          >
            <option value="">$</option>
            {available.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
          </select>
          {renderSignRuleButton()}
          <span aria-hidden="true" className="flex h-5 w-2.5 shrink-0 justify-center"><span className="h-full w-px bg-border" /></span>
          <label className="flex h-7 shrink-0 items-center gap-1.5 text-sm sm:text-xs">
            <input
              type="checkbox"
              checked={selected.cents ?? patternHasCents(formats.currency)}
              disabled={formatBusy}
              onChange={(event) => void saveDataFormat({ ...selected, cents: event.target.checked }, false, ['cents'])}
              className="h-4 w-4 accent-brand"
            />
            {t('grid.formatCents')}
          </label>
          </>
        })()}
  
        </>) },
        { key: 'validation', node: (<>
  
        {/* Sign rule; a currency column shows it after its currency instead. */}
        {formatBaseOf(formatTarget)?.formatKey !== 'currency' && renderSignRuleButton()}

        {/* Data validation: what a typed value the column rejects does */}
        {(() => {
          if (formatTarget === '') return null
          const stored = formatBaseOf(formatTarget)
          const column = columns.find((c) => c.field === formatTarget)
          const type = stored?.type ?? (column?.cellType === 'number' || column?.cellType === 'date' ? column.cellType : undefined)
          if (type !== 'number' && type !== 'date' && type !== 'smartchip' && type !== 'autofill') return null
          return <>
          <span aria-hidden="true" className="flex h-5 w-2.5 shrink-0 justify-center"><span className="h-full w-px bg-border" /></span>
          <label className="flex shrink-0 items-center gap-1.5 text-text-muted">
            <span className="hidden whitespace-nowrap lg:inline">{t('grid.invalidData')}</span>
            <select
              aria-label={t('grid.invalidData')}
              data-testid="grid-invalid-mode"
              value={stored?.invalid ?? 'warn'}
              disabled={formatBusy}
              onChange={(event) => {
                const invalid = event.target.value === 'reject' ? 'reject' as const : undefined
                const { invalid: _previous, ...rest } = stored ?? { type }
                void saveDataFormat(invalid ? { ...rest, invalid } : rest, false, ['invalid'])
              }}
              className="h-7 rounded bg-transparent px-2 hover:bg-surface-hover cursor-pointer text-sm sm:text-xs text-fg outline-none focus:border-focus disabled:opacity-50"
            >
              <option value="warn">{t('grid.invalidWarn')}</option>
              <option value="reject">{t('grid.invalidReject')}</option>
            </select>
          </label>
          </>
        })()}
        </>) },
        ]}
      />
      {formatSaveError && <span className="min-w-[220px] font-bold text-danger">{formatSaveError}</span>}
      <button type="button" aria-label={t('common.close')} title={t('common.close')} onClick={() => setInternalFormatBar(false)}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-text-muted hover:bg-surface-hover hover:text-fg">
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </div>}

    {/* Formula bar: placed below the format bar, above the headers */}
    {canUseFormulas && formulaBar && !formulaPanel && <div data-hide-while-typing className={`flex min-w-0 max-w-full shrink-0 items-start gap-1 ${fullScreen ? 'mx-3' : ''}`}>
      <div className="min-w-0 flex-1">
      <FormulaBar
        targets={formulaTargets}
        completionSchema={formulaCompletionSchema}
        referenceTargets={formulaReferenceTargets}
        selected={formulaTarget}
        onSelectTarget={setFormulaTarget}
        formula={formulaBarMode === 'cell' ? activeCellFormula : selectedFormula.formula}
        title={selectedFormula.title}
        scope={cellFormulaScope}
        locale={formulaLocale}
        onAccept={(formula, title) => formulaBarMode === 'cell' ? acceptActiveCellFormula(formula) : void saveFormula(formula, title)}
        onDelete={() => formulaBarMode === 'cell' ? deleteActiveCellFormula() : void deleteFormula()}
        mode={formulaBarMode}
        onModeChange={setFormulaBarMode}
        cellAvailable={activeCellIsEditable}
        insertRequest={formulaInsert}
        onPointRef={pointBarRef}
        pointRequest={barPointRequest}
        operandRefs={barOperandRefs}
        expanded={false}
        onToggleExpanded={() => setFormulaPanel(true)}
        saveError={formulaSaveError}
        busy={formulaBusy}
        showAllowCellOverrides={formulaTarget !== NEW_COLUMN && isFormulaField(formulaTarget, formulaConfig)}
        allowCellOverrides={formulaTarget === NEW_COLUMN || cellOverridesAllowed(formulaConfig, formulaTarget)}
        onAllowCellOverridesChange={(allowed) => void setFormulaCellOverridesAllowed(allowed)}
        overrideTargetOptions={overrideTargetOptions}
        overrideTarget={overrideTargetOf(formulaConfig, formulaTarget) ?? SAME_COLUMN}
        onOverrideTargetChange={(field) => void setFormulaOverrideTarget(field)}
      />
      </div>
      <button type="button" aria-label={t('common.close')} title={t('common.close')} onClick={() => setInternalFormulaBar(false)}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-text-muted hover:bg-surface-hover hover:text-fg">
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </div>}



    {rollupDialog && formatTarget !== '' && (() => {
      const selected = dataFormatsConfig[gridKey]?.[formatTarget]
      const currentCols = columns
        .filter((col): col is typeof col & { field: string } => typeof col.field === 'string' && col.field !== '')
        .map((col) => ({ field: col.field, name: col.headerName }))
      return <RollupColumnDialog
        {...(selected?.rollup ? { initialConfig: selected.rollup } : {})}
        columnField={formatTarget}
        currentColumns={currentCols}
        currentSheetId={optionsWorkbook?.currentSheetId ?? formulaWorkbook?.currentSheetId ?? gridKey}
        orgId={gridConfig.orgId}
        {...(optionsWorkbook ? { workbook: optionsWorkbook } : {})}
        renames={gridHeadersConfig}
        calculated={calculatedColumnsByTab}
        onClose={() => setRollupDialog(false)}
        onPersist={(config, fmtKey) => {
          void saveDataFormat({
            ...(selected ?? { type: config.type }),
            type: config.type,
            rollup: config,
            ...(fmtKey ? { formatKey: fmtKey } : {}),
          }, false, ['type', 'rollup', 'formatKey'])
        }}
      />
    })()}

    {rejection && createPortal(
      <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/30 p-4" onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setRejection(null) } }}>
        <div role="alertdialog" aria-modal="true" aria-labelledby={`grid-reject-title-${gridKey}`} data-testid="grid-reject-dialog" className="w-full max-w-sm rounded-xl border border-border bg-surface p-4 text-sm text-fg shadow-xl">
          <div id={`grid-reject-title-${gridKey}`} className="mb-1 font-bold">{t('grid.rejectTitle')}</div>
          <p className="mb-4 text-text-muted">{t('grid.rejectMessage', { value: rejection.text.trim() })}</p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setRejection(null)}
              className="h-8 rounded-full border border-border bg-bg px-4 hover:bg-surface-3"
            >{t('grid.rejectCancel')}</button>
            <button
              type="button"
              ref={rejectRetryRef}
              onClick={() => {
                const r = rejection
                setRejection(null)
                setAnchor(r.anchor)
                setEnd(r.end)
                setEditing({ rowKey: r.rowKey, field: r.field, initialValue: r.text })
              }}
              className="h-8 rounded-full bg-brand px-4 font-medium text-white hover:opacity-90"
            >{t('grid.rejectRetry')}</button>
          </div>
        </div>
      </div>,
      document.body,
    )}

    {onStaleRefresh && !isEmbedded && !bare && <div
      role="status"
      data-testid="stale-rows-notice"
      className={`flex shrink-0 items-center gap-2 border border-[var(--notice-border)] bg-[var(--notice-bg)] px-3 py-1.5 text-sm sm:text-xs text-fg ${fullScreen ? 'mx-3' : ''}`}
    >
      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-3.5 w-3.5 shrink-0 text-warning"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
      <span className="min-w-0">{t('grid.staleRows')}</span>
      <button
        type="button"
        onClick={onStaleRefresh}
        className="shrink-0 rounded border border-[var(--notice-border)] px-2 py-0.5 font-bold hover:bg-[var(--notice-border)]"
      >{t('grid.staleRowsRefresh')}</button>
    </div>}
    {typeNotice && !isEmbedded && <div
      role="status"
      data-testid="paste-type-errors"
      className={`flex shrink-0 items-start justify-between gap-2 border border-[var(--notice-border)] bg-[var(--notice-bg)] px-3 py-1.5 text-sm sm:text-xs text-fg ${fullScreen ? 'mx-3' : ''}`}
    >
      <span className="flex min-w-0 items-start gap-2">
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
        <span>
          {(typeNotice.rejected ?? 0) > 0 && <span className="font-bold">{t('grid.pasteRejected', { count: typeNotice.rejected ?? 0 })}</span>}
          {(typeNotice.rejected ?? 0) > 0 && typeNotice.count > 0 && ' '}
          {typeNotice.count > 0 && <>
            <span className="font-bold">{typeNotice.typed ? t('grid.typedTypeError') : t('grid.pasteTypeErrors', { count: typeNotice.count })}</span>{' '}
            {typeErrorsHint ?? t('grid.pasteTypeErrorsHint')}
          </>}
        </span>
      </span>
      <button
        type="button"
        onClick={() => setTypeNotice(null)}
        aria-label={t('common.close')}
        title={t('common.close')}
        className="shrink-0 font-bold text-text-muted hover:text-text"
      >✕</button>
    </div>}

    {/* The split view's sidebars run the full height, beside the grid and its status bar. */}
    <div className={isEmbedded ? 'flex min-w-0 flex-col' : 'flex flex-1 min-h-0 min-w-0'}>
    <div className={isEmbedded ? 'flex min-w-0 flex-col' : 'flex flex-1 min-h-0 min-w-0 flex-col'}>
    <div className={isEmbedded ? 'flex min-w-0' : 'flex flex-1 min-h-0'}>
      {groupPanel && groupable && !isEmbedded && !bare && <aside style={{ width: groupPanelWidth }} className={`relative z-20 flex shrink-0 flex-col border border-grid-line bg-surface max-sm:static max-sm:!w-full max-sm:max-h-[calc(100dvh-12rem)] max-sm:rounded-lg ${fullScreen ? 'border-r-0' : 'rounded-l-lg border-r-0'}`}>
        <div className="flex shrink-0 items-center justify-between border-b border-grid-line p-2 sm:hidden">
          <span className="text-xs font-bold">{groupButtonLabel}</span>
          <button
            type="button"
            onClick={() => {
              setGroupPanel(false)
              savePref('agrupar', '0')
            }}
            className="flex h-7 w-7 items-center justify-center rounded-full text-text-muted hover:bg-surface-3 hover:text-text"
            aria-label={t('common.close')}
          >
            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {groupColumns[0] === null && groupColumns[1] === null
            ? (
              <div className="p-1 space-y-2">
                <p className="text-sm sm:text-xs text-text-muted">{t('grid.pickColumnHint')}</p>
                {canChooseGrouping && (
                  <button
                    type="button"
                    onClick={() => setGroupingDialogOpen(true)}
                    className="tap-target rounded-full border border-border px-3 py-1 text-xs font-medium text-text hover:bg-surface-3 transition-colors"
                  >
                    {t('grid.chooseGroupingColumns')}
                  </button>
                )}
              </div>
            )
            : <>
              <input value={groupSearch} onChange={(e) => setGroupSearch(e.target.value)} placeholder={t('grid.search')}
                className="mb-2 h-7 w-full rounded-full border border-border bg-bg px-2 text-sm sm:text-xs outline-none focus:border-focus" />
              <div className="mb-1.5 flex flex-wrap justify-between gap-1 text-xs sm:text-[11px]">
                <button type="button" onClick={() => {
                  setHighlightedGroupKey(null)
                  filterWithHistory((f) => {
                    const c = { ...f }
                    if (groupColumns[0] !== null) delete c[groupColumns[0]]
                    if (groupColumns[1] !== null) delete c[groupColumns[1]]
                    return c
                  })
                  setCollapsedGroups(new Set())
                }} className="rounded-full px-1 py-0.5 font-bold text-brand transition-colors hover:bg-brand/10">{t('grid.showAllM')}</button>
                <button type="button" onClick={() => {
                  setHighlightedGroupKey(null)
                  filterWithHistory((f) => {
                    const c = { ...f }
                    if (groupColumns[0] !== null) delete c[groupColumns[0]]
                    if (groupColumns[1] !== null) delete c[groupColumns[1]]
                    return c
                  })
                  const keys = new Set<string>()
                  groupTree.forEach((n1) => {
                    keys.add(n1.key)
                    n1.children?.forEach((n2) => keys.add(n2.key))
                  })
                  setCollapsedGroups(keys)
                }} className="rounded-full px-1 py-0.5 font-bold text-brand transition-colors hover:bg-brand/10">{t('grid.collapseAll')}</button>
                <button type="button" onClick={() => {
                  setHighlightedGroupKey(null)
                  filterWithHistory((f) => {
                    const c = { ...f }
                    if (groupColumns[0] !== null) c[groupColumns[0]] = new Set<string>()
                    if (groupColumns[1] !== null) c[groupColumns[1]] = new Set<string>()
                    return c
                  })
                }} className="rounded-full px-1 py-0.5 font-bold text-brand transition-colors hover:bg-brand/10">{t('grid.hideAllM')}</button>
              </div>
              {(() => {
                const searchTerm = groupSearch.trim().toLowerCase()
                const renderTreeNode = (node: GroupTreeNode) => {
                  const targetColIndex = node.level === 0 ? groupColumns[0] : groupColumns[1]
                  const isAllowed = targetColIndex === null || !filters[targetColIndex] || filters[targetColIndex].has(node.label)
                  const isCollapsed = isAllowed && collapsedGroups.has(node.key)
                  const isShowing = isAllowed && !isCollapsed
                  const state = isShowing ? 'show' : isCollapsed ? 'collapse' : 'hide'
                  const isHighlighted = highlightedGroupKey === node.key

                  const handleCycle = () => {
                    if (targetColIndex === null) return
                    const allVals = node.level === 0
                        ? groupTree.map((n) => n.label)
                      : (groupTree.flatMap((n) => n.children ?? []).filter((c) => c.label === node.label).map((c) => c.label))

                    if (state === 'show') {
                      filterWithHistory((f) => {
                        const current = new Set(f[targetColIndex] ?? allVals)
                        current.add(node.label)
                        return { ...f, [targetColIndex]: current }
                      })
                      setCollapsedGroups((cg) => new Set(cg).add(node.key))
                    } else if (state === 'collapse') {
                      setCollapsedGroups((cg) => {
                        const next = new Set(cg)
                        next.delete(node.key)
                        return next
                      })
                      filterWithHistory((f) => {
                        const current = new Set(f[targetColIndex] ?? allVals)
                        current.delete(node.label)
                        return { ...f, [targetColIndex]: current }
                      })
                    } else {
                      setCollapsedGroups((cg) => {
                        const next = new Set(cg)
                        next.delete(node.key)
                        return next
                      })
                      filterWithHistory((f) => {
                        const current = new Set(f[targetColIndex] ?? allVals)
                        current.add(node.label)
                        return { ...f, [targetColIndex]: current }
                      })
                    }
                  }

                  const handleSelectExclusive = () => {
                    if (isHighlighted) {
                      setHighlightedGroupKey(null)
                      filterWithHistory((f) => {
                        const next = { ...f }
                        if (groupColumns[0] !== null) delete next[groupColumns[0]]
                        if (groupColumns[1] !== null) delete next[groupColumns[1]]
                        return next
                      })
                    } else {
                      setHighlightedGroupKey(node.key)
                      if (node.level === 0) {
                        filterWithHistory((f) => {
                          const next = { ...f }
                          if (groupColumns[0] !== null) next[groupColumns[0]] = new Set([node.label])
                          if (groupColumns[1] !== null) delete next[groupColumns[1]]
                          return next
                        })
                        setCollapsedGroups((cg) => {
                          const next = new Set(cg)
                          next.delete(node.key)
                          return next
                        })
                      } else {
                        const parentLabel = node.key.includes(' > ') ? node.key.split(' > ')[0] : null
                        filterWithHistory((f) => {
                          const next = { ...f }
                          if (groupColumns[0] !== null && parentLabel) next[groupColumns[0]] = new Set([parentLabel])
                          if (groupColumns[1] !== null) next[groupColumns[1]] = new Set([node.label])
                          return next
                        })
                        setCollapsedGroups((cg) => {
                          const next = new Set(cg)
                          if (parentLabel) next.delete(parentLabel)
                          next.delete(node.key)
                          return next
                        })
                      }
                    }
                  }

                  const matchesSearch = !searchTerm || node.label.toLowerCase().includes(searchTerm) || (node.children?.some((c) => c.label.toLowerCase().includes(searchTerm)) ?? false)
                  if (!matchesSearch) return null

                  return (
                    <div key={node.key} className="select-none">
                      <div className={`flex items-center gap-1.5 rounded-full p-1 text-sm transition-colors ${isHighlighted ? 'bg-brand/15 text-brand font-semibold ring-1 ring-brand/30' : 'hover:bg-surface-3'} ${node.level > 0 ? 'ml-3' : ''}`}>
                        <input type="checkbox" className="h-3.5 w-3.5 shrink-0 cursor-pointer rounded-full border-border"
                          checked={isAllowed}
                          ref={(el) => { if (el) el.indeterminate = isCollapsed }}
                          onChange={() => {
                            setHighlightedGroupKey(null)
                            handleCycle()
                          }} />
                        <button type="button" title={displayFilterValue(node.label)} onClick={handleSelectExclusive} className="min-w-0 flex-1 truncate text-left font-medium">{displayFilterValue(node.label)}</button>
                        <span className="shrink-0 tabular-nums text-text-muted text-xs">({formatNumber(node.count)})</span>
                      </div>
                      {node.children && isAllowed && !isCollapsed && (
                        <div className="border-l border-border/40 ml-2.5 pl-0.5">
                          {node.children.map(renderTreeNode)}
                        </div>
                      )}
                    </div>
                  )
                }

                return groupTree.map(renderTreeNode)
              })()}
              {groupTree.length === 0 && <p className="p-1 text-sm sm:text-xs text-text-muted">{t('grid.noValues')}</p>}
            </>}
        </div>
        <div onMouseDown={startGroupPanelResize}
          className="absolute top-0 right-0 bottom-0 w-2 cursor-col-resize hover:bg-brand/30 active:bg-brand/50 transition-colors z-10"
          title={t('grid.dragResize')} />
      </aside>}

      <div className={splitView && splitSheets.length > 0 && !chartPanel && !columnsPanel && !optionsDialog && !isEmbedded && !bare ? 'flex min-w-0 min-h-0 flex-1 flex-col' : 'contents'}>
      {splitView && splitSheets.length > 0 && !chartPanel && !columnsPanel && !optionsDialog && !isEmbedded && !bare && <div className={`flex h-9 shrink-0 items-center gap-2 border border-b-0 border-grid-line bg-surface pl-3 pr-2 text-sm sm:text-xs ${fullScreen ? '' : 'rounded-tl-lg'} ${groupPanel ? 'sm:rounded-tl-none' : ''}`}>
        <span className="min-w-0 truncate font-bold">{splitView.title}</span>
      </div>}
      <div ref={containerRef} onScroll={isEmbedded ? undefined : handleGridScroll} className={isEmbedded ? 'overflow-auto rounded-xl border border-grid-line min-w-0' : `overflow-auto border border-grid-line flex-1 min-w-0 min-h-0 ${fullScreen ? '' : 'rounded-lg'} ${groupPanel ? 'sm:rounded-l-none' : ''} ${columnsPanel || chartPanel || optionsDialog || splitSheets.length > 0 ? 'sm:rounded-r-none' : ''} ${splitView && splitSheets.length > 0 && !chartPanel && !columnsPanel && !optionsDialog && !bare ? 'rounded-t-none' : ''}`} style={{ height: height !== undefined ? height : undefined }}>
        <div className={isEmbedded ? '' : 'pb-20'} style={{ zoom: zoom / 100 / textScale }}>
          {loading ? <div className="flex h-full items-center justify-center text-sm text-text-muted">{t('grid.loading')}</div> : <table className="nt-grid border-collapse" style={{ tableLayout: 'fixed', width: naturalTableWidth }}>
            <colgroup>
              {!isEmbedded && <col style={{ width: 38 }} />}
              {visibleColumns.map((index) => <col key={index} style={{ width: widthOf(index) }} />)}
            </colgroup>
            <thead className="sticky top-0 z-10 bg-grid-head text-grid-head-text touch-pan-x touch-pan-y">
              {Array.from({ length: outlineLevels }, (_, level) => renderColumnOutlineRow(level))}
              <tr>
                {!isEmbedded && <th rowSpan={hasTwoHeaderRows ? 2 : undefined} className="bg-grid-head p-2 text-center" style={{ width: 38, minWidth: 38, boxShadow: 'inset 0 -1px 0 0 var(--grid-line-strong), inset -1px 0 0 0 var(--grid-line-strong)', ...(stickyLeft.size > 0 ? { position: 'sticky', left: 0, zIndex: 12 } : {}) }}>
                  <div className={`flex items-center justify-center ${canEditFormats ? 'pt-2' : ''}`}>
                    {isEditingCellFormula ? (
                      <span className="text-xs font-semibold text-text-muted select-none">#</span>
                    ) : (
                      <input ref={selectAllCheckboxRef} type="checkbox" aria-label={t('grid.selectAll')} checked={allSelected} onChange={toggleSelectAll} className="rounded-full" />
                    )}
                  </div>
                </th>}
                {!hasTwoHeaderRows
                  ? visibleColumns.map((index, colPosArr) => renderHeaderCell(index, colPosArr + 1))
                  : headerBands.map((band) => band.group === null
                    ? renderHeaderCell(band.cols[0]!, visibleColumns.indexOf(band.cols[0]!) + 1, 2)
                    : <th key={`band-${band.group}-${band.cols[0]}`} colSpan={band.cols.length}
                      onDragOver={(e) => {
                        if (columnDrag.current === null || !canEditFormats) return
                        e.preventDefault()
                        const rect = e.currentTarget.getBoundingClientRect()
                        const after = e.clientX >= rect.left + rect.width / 2
                        setColumnDropIndicator({ index: after ? band.cols[band.cols.length - 1]! : band.cols[0]!, position: after ? 'after' : 'before' })
                      }}
                      onDragLeave={() => setColumnDropIndicator(null)}
                      onDrop={(e) => {
                        e.preventDefault()
                        const rect = e.currentTarget.getBoundingClientRect()
                        const after = e.clientX >= rect.left + rect.width / 2
                        setColumnDropIndicator(null)
                        void dropColumn(after ? band.cols[band.cols.length - 1]! : band.cols[0]!, after)
                      }}
                      onClick={() => {
                        const first = band.cols[0]!
                        const last = band.cols[band.cols.length - 1]!
                        setActiveHeaderIndex(first)
                        setSelectedColumns(new Set(band.cols))
                        setSelectedFullColumn(band.cols.length === 1 ? first : null)
                        if (renderedRows.length === 0) return
                        setAnchor({ rowKey: keyOf(renderedRows[0]!), originalCol: first })
                        setEnd({ rowKey: keyOf(renderedRows[renderedRows.length - 1]!), originalCol: last })
                      }}
                      style={{ boxShadow: ['inset 0 -1px 0 0 var(--grid-line-strong)', 'inset -1px 0 0 0 var(--grid-line-strong)', 'inset 1px 0 0 0 var(--grid-line-strong)'].join(', '), ...(band.cols.every((index) => selectedColumns.has(index)) ? { filter: 'brightness(0.9)' } : {}) }}
                      className="group/band relative select-none bg-grid-head p-2 text-center text-sm sm:text-xs font-bold" title={band.group}>
                      {!isEmbedded && canEditFormats && visibleColumns.length > 1 && <span
                        draggable
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        onDragStart={(e) => {
                          e.stopPropagation()
                          columnDrag.current = band.cols[0]!
                          columnBandDrag.current = band.cols
                          e.dataTransfer.effectAllowed = 'move'
                        }}
                        onDragEnd={() => { columnDrag.current = null; columnBandDrag.current = null; setColumnDropIndicator(null) }}
                        title={t('grid.dragReorderColumn')}
                        className="absolute left-1/2 top-0.5 flex h-3 w-5 -translate-x-1/2 rotate-90 cursor-grab items-center justify-center text-[11px] leading-none text-text-muted opacity-0 group-hover/band:opacity-60 hover:!opacity-100 active:cursor-grabbing"
                      >⠿</span>}
                      <button type="button" className="w-full whitespace-normal break-words font-bold">{band.group}</button>
                    </th>)}
              </tr>
              {hasTwoHeaderRows && <tr>
                {headerBands.filter((band) => band.group !== null).flatMap((band) =>
                  band.cols.map((index) => renderHeaderCell(index, visibleColumns.indexOf(index) + 1)))}
              </tr>}
            </thead>
            <tbody>
              {topSpacerHeight! > 0 && (
                <tr style={{ height: topSpacerHeight }} className="p-0 border-0 m-0 leading-none pointer-events-none">
                  <td
                    colSpan={visibleColumns.length + (isEmbedded ? 0 : 1)}
                    style={{ height: topSpacerHeight, padding: 0, border: 0, margin: 0, lineHeight: 0, fontSize: 0 }}
                    className="p-0 border-0 m-0 leading-none"
                  />
                </tr>
              )}
              {visibleItems.map(({ item }) => {
                if (item.type === 'blank') {
                  const blankKey = `blank-${item.groupKey}`
                  return <tr key={blankKey} ref={(el) => rowRefCallback(blankKey, el)} aria-hidden="true">
                    <td colSpan={visibleColumns.length + (isEmbedded ? 0 : 1)} className="h-9 border-b border-grid-line p-0" style={{ background: 'var(--cell-ro-pale)' }} />
                  </tr>
                }
                if (item.type === 'group' || item.type === 'subtotal') {
                  const gKey = item.groupKey ?? item.label
                  const level = item.level ?? 0
                  const hasFrozenColumns = stickyLeft.size > 0
                  const groupRows = item.groupRows as Record<string, unknown>[]
                  // Subtotals sit in the header band, or below the group in a closing band of their own.
                  const isFooter = item.type === 'subtotal'
                  const showCells = showGroupSubtotals && (isFooter || !subtotalsAtBottom)
                  const bandKey = `${item.type}-${gKey}`
                  // A band accepts a dragged row: dropping on it is how a row changes group.
                  const bandDropTarget = canMoveBetweenGroups && rowDropIndicator?.key === bandKey
                  // A dragged group lands above or below another top-level group's header.
                  // Its closing totals row is part of the group too: dropping there, or "after" anywhere
                  // on the group, draws the line below the totals rather than between header and totals.
                  const groupDropKey = `move:${gKey}`
                  const groupDropPos = level === 0 && rowDropIndicator?.key === groupDropKey ? rowDropIndicator.position : null
                  const canDragGroup = canReorderGroups && !isFooter && level === 0
                  const acceptsGroup = canReorderGroups && level === 0
                  const lineAfter = groupDropPos === 'after' && (isFooter || !groupsWithFooter.has(gKey))
                  const groupHandle = canDragGroup && <span
                    draggable
                    onDragStart={(e) => startGroupDrag(e, gKey)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    title={t('grid.dragGroup')}
                    className="absolute left-0 top-0 z-[4] flex h-full w-3 cursor-grab items-center justify-center text-[11px] leading-none text-text-muted opacity-0 group-hover/band:opacity-50 hover:!opacity-100 active:cursor-grabbing"
                  >⠿</span>
                  return <tr
                    key={bandKey}
                    ref={(el) => rowRefCallback(bandKey, el)}
                    onDragOver={canMoveBetweenGroups || acceptsGroup ? (e) => {
                      if (groupDrag.current !== null) {
                        if (!acceptsGroup || groupDrag.current.key === gKey) return
                        e.preventDefault()
                        e.dataTransfer.dropEffect = 'move'
                        const rect = e.currentTarget.getBoundingClientRect()
                        const pos = isFooter || e.clientY >= rect.top + rect.height / 2 ? 'after' : 'before'
                        setRowDropIndicator((prev) => (prev?.key === groupDropKey && prev.position === pos ? prev : { key: groupDropKey, position: pos }))
                        return
                      }
                      if (!canMoveBetweenGroups || rowDrag.current === null) return
                      e.preventDefault()
                      e.dataTransfer.dropEffect = 'move'
                      setRowDropIndicator((prev) => (prev?.key === bandKey ? prev : { key: bandKey, position: 'after' }))
                    } : undefined}
                    onDragLeave={canMoveBetweenGroups || acceptsGroup ? () => setRowDropIndicator(null) : undefined}
                    onDrop={canMoveBetweenGroups || acceptsGroup ? (e) => {
                      if (groupDrag.current !== null) {
                        if (!acceptsGroup) return
                        e.preventDefault()
                        void dropGroup(gKey, groupDropPos ?? 'after')
                        return
                      }
                      if (!canMoveBetweenGroups || rowDrag.current === null) return
                      e.preventDefault()
                      moveRowsToGroup(groupValueOf(groupRows), groupRows[0] as T | undefined)
                    } : undefined}
                    style={bandDropTarget ? { boxShadow: 'inset 0 0 0 2px var(--brand)' }
                      : groupDropPos === 'before' && !isFooter ? { boxShadow: 'inset 0 2px 0 0 var(--brand)' }
                      : lineAfter ? { boxShadow: 'inset 0 -2px 0 0 var(--brand)' } : undefined}
                    className="nt-grid-group group/band bg-grid-group-bg text-grid-group-text border-b border-grid-group-line">
                    {!showCells ? (
                      <td colSpan={visibleColumns.length + (isEmbedded ? 0 : 1)} className="relative border-b border-grid-group-line p-0 text-left">
                        {groupHandle}
                        <button
                          onClick={() => setCollapsedGroups((s) => { const c = new Set(s); if (c.has(gKey)) c.delete(gKey); else c.add(gKey); return c })}
                          className={`text-sm font-bold text-grid-group-text inline-flex items-center py-2 pr-2 ${hasFrozenColumns ? 'sticky left-0' : ''}`}
                          style={{ paddingLeft: 8 + level * 16 }}
                        >
                          {groupOutline
                            ? <span aria-hidden className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border-strong bg-bg text-[10px] leading-none">{item.collapsed ? '+' : '−'}</span>
                            : item.collapsed ? '▸' : '▾'} <span className="ml-1">{item.label}{showGroupCounts ? ` (${groupCountLabel(item.count)})` : ''}</span>
                        </button>
                      </td>
                    ) : (
                      <>
                        {!isEmbedded && (
                          <td
                            style={{
                              width: 38,
                              minWidth: 38,
                              boxShadow: 'inset 0 -1px 0 0 var(--grid-group-line), inset -1px 0 0 0 var(--grid-group-line)',
                              ...(stickyLeft.size > 0 ? { position: 'sticky' as const, left: 0, zIndex: 3, background: 'var(--grid-group-bg)' } : {}),
                            }}
                            className="relative p-1 text-center text-xs text-text-muted select-none border-b border-grid-group-line"
                          >
                            {groupHandle}
                            {!isFooter && <button
                              type="button"
                              onClick={() => setCollapsedGroups((s) => { const c = new Set(s); if (c.has(gKey)) c.delete(gKey); else c.add(gKey); return c })}
                              className="text-xs font-bold text-grid-group-text"
                            >
                              {item.collapsed ? '▸' : '▾'}
                            </button>}
                          </td>
                        )}
                        {(() => {
                          const bandTexts = visibleColumns.map((index) => subtotalCellAt(groupRows, index).text)
                          return visibleColumns.map((index, colPosIdx) => {
                          const col = columns[index]
                          if (!col) return <td key={index} />
                          const isFirstCol = colPosIdx === 0
                          const card = configuredSummaryCards.find((c) => c.columnId === col.field)
                          const cellType = columnType.get(index) ?? 'text'
                          const calculatedVal = bandTexts[colPosIdx] ?? ''
                          // Subtotals align like their column; their wrapping follows it unless set apart.
                          const subtotalFormat = col.field ? dataFormatsConfig[gridKey]?.[col.field] : undefined
                          const subtotalWrap = subtotalFormat?.subtotalWrapMode ?? col.wrapMode
                          const subtotalWeight = subtotalFormat?.subtotalBold === false ? 'font-normal' : 'font-semibold'
                          const subtotalFill = col.field ? cellColors.get(cellColorKey(col.field, subtotalRowKey(gKey))) ?? columnColors[col.field] : undefined
                          const spill = subtotalWrap === 'overflow' ? overflowSpill(bandTexts, colPosIdx) : 0

                          const isStickyLeft = stickyLeft.has(index)
                          const isStickyRight = stickyRight.has(index)
                          const cellRule = alignmentConfig[cellType]
                          const alignClass = textClass(cellRule, col.align)
                          const bandPos = subtotalPosition.get(gKey)
                          const inSubtotalRange = subtotalRange !== null && bandPos !== undefined
                            && bandPos >= subtotalRange.rowMin && bandPos <= subtotalRange.rowMax
                            && colPosIdx >= subtotalRange.colMin && colPosIdx <= subtotalRange.colMax
                          const subtotalBorders = inSubtotalRange ? [
                            bandPos === subtotalRange.rowMin ? `inset 0 1px 0 0 ${SELECTION_COLOR}` : '',
                            bandPos === subtotalRange.rowMax ? `inset 0 -1px 0 0 ${SELECTION_COLOR}` : '',
                            colPosIdx === subtotalRange.colMin ? `inset 1px 0 0 0 ${SELECTION_COLOR}` : '',
                            colPosIdx === subtotalRange.colMax ? `inset -1px 0 0 0 ${SELECTION_COLOR}` : '',
                          ].filter(Boolean) : []

                          return (
                            <td
                              key={index}
                              onMouseDown={isEmbedded ? undefined : (e) => {
                                if (e.button !== 0) return
                                const cell = { groupKey: gKey, originalCol: index }
                                draggingSubtotalSelection.current = true
                                setAnchor(null)
                                setEnd(null)
                                setSelectedColumns(new Set())
                                setSelectedFullColumn(null)
                                setSubtotalEnd(cell)
                                if (!e.shiftKey || !subtotalAnchor) setSubtotalAnchor(cell)
                              }}
                              onMouseEnter={isEmbedded ? undefined : (e) => {
                                if (!draggingSubtotalSelection.current) return
                                if ((e.buttons & 1) === 0) { draggingSubtotalSelection.current = false; return }
                                setSubtotalEnd({ groupKey: gKey, originalCol: index })
                              }}
                              onContextMenu={isEmbedded ? undefined : (e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setTotalsContextMenu({ x: e.clientX, y: e.clientY, column: col, card, displayVal: calculatedVal })
                              }}
                              style={{
                                width: widthOf(index),
                                minWidth: widthOf(index),
                                boxShadow: [...subtotalBorders, 'inset 0 -1px 0 0 var(--grid-group-line), inset -1px 0 0 0 var(--grid-group-line)'].join(', '),
                                ...(inSubtotalRange ? { filter: 'brightness(0.95)' } : {}),
                                ...(subtotalFill ? { background: subtotalFill } : {}),
                                ...(spill > 0 ? { overflow: 'visible', position: 'relative' as const, zIndex: 1 } : {}),
                                ...(isStickyLeft ? { position: 'sticky' as const, left: stickyLeft.get(index), zIndex: 3, background: subtotalFill ?? 'var(--grid-group-bg)' } : {}),
                                ...(isStickyRight ? { position: 'sticky' as const, right: stickyRight.get(index), zIndex: 2, background: subtotalFill ?? 'var(--grid-group-bg)' } : {}),
                                ...(isFirstCol || spill > 0 ? {} : aggregateFitStyle(index, calculatedVal)),
                              }}
                              className={`p-1.5 text-sm sm:text-xs select-none cursor-cell bg-grid-group-bg border-b border-grid-group-line ${alignClass}`}
                            >
                              {isFirstCol ? (
                                <div
                                  className={`flex items-center gap-1.5 min-w-0 ${subtotalFormat?.subtotalBold === false ? 'font-normal' : 'font-bold'} ${spill > 0 ? 'whitespace-nowrap' : ''}`}
                                  style={{ paddingLeft: isEmbedded ? 8 + level * 16 : level * 16, ...(spill > 0 ? { width: `calc(100% + ${spill}px)` } : {}) }}
                                >
                                  {isEmbedded && !isFooter && (
                                    <button
                                      type="button"
                                      onClick={() => setCollapsedGroups((s) => { const c = new Set(s); if (c.has(gKey)) c.delete(gKey); else c.add(gKey); return c })}
                                      className="text-xs font-bold text-grid-group-text mr-0.5"
                                    >
                                      {item.collapsed ? '▸' : '▾'}
                                    </button>
                                  )}
                                  <span className="truncate">{isFooter ? t('grid.groupTotal', { label: item.label }).trim() : `${item.label}${showGroupCounts ? ` (${groupCountLabel(item.count)})` : ''}`}</span>
                                  {calculatedVal ? <span className={`ml-auto ${subtotalWeight} tabular-nums text-text shrink-0`}>{calculatedVal}</span> : null}
                                </div>
                              ) : calculatedVal ? (
                                spill > 0
                                  ? <div className={`${subtotalWeight} tabular-nums text-text whitespace-nowrap`} style={{ width: `calc(100% + ${spill}px)` }}>{calculatedVal}</div>
                                  : <span className={`${subtotalWeight} tabular-nums text-text`}>{calculatedVal}</span>
                              ) : null}
                            </td>
                          )
                        })
                        })()}
                      </>
                    )}
                  </tr>
                }
                return item.type === 'row' ? renderRow(item.row, item.index) : null
              })}
              {bottomSpacerHeight > 0 && (
                <tr style={{ height: bottomSpacerHeight }} className="p-0 border-0 m-0 leading-none pointer-events-none">
                  <td
                    colSpan={visibleColumns.length + (isEmbedded ? 0 : 1)}
                    style={{ height: bottomSpacerHeight, padding: 0, border: 0, margin: 0, lineHeight: 0, fontSize: 0 }}
                    className="p-0 border-0 m-0 leading-none"
                  />
                </tr>
              )}
              {!loading && sorted.length === 0 && <tr><td colSpan={visibleColumns.length + (isEmbedded ? 0 : 1)} className="p-0 text-sm text-text-muted">
                {/* Sticky and as wide as the visible area, so the label stays centered on screen however wide the table is or wherever it's scrolled. */}
                <div className="sticky left-0 p-6 text-center" style={viewportWidth > 0 ? { width: viewportWidth / (zoom / 100 / textScale) } : undefined}>{t('grid.noResults')}
                  {(search !== '' || Object.keys(filters).length > 0) && (
                    <div className="mt-3">
                      <button type="button" onClick={() => { setSearch(''); setFilters({}) }} className="rounded-token border border-border px-3 py-1.5 text-sm text-text hover:bg-surface-hover">
                        {t('grid.clearSearchAndFilters')}
                      </button>
                    </div>
                  )}
                </div>
              </td></tr>}
            </tbody>
            {summaryCardsContext?.totalsRowVisible && (
              <tfoot className="sticky bottom-0 z-10 border-t-2 border-grid-line-strong bg-grid-head font-bold text-grid-head-text shadow-[0_-2px_4px_rgba(0,0,0,0.05)]">
                <tr>
                  {!isEmbedded && (
                    <td
                      style={{
                        width: 38,
                        minWidth: 38,
                        boxShadow: 'inset 0 1px 0 0 var(--grid-line-strong), inset -1px 0 0 0 var(--grid-line-strong)',
                        ...(stickyLeft.size > 0 ? { position: 'sticky' as const, left: 0, zIndex: 12, background: 'var(--grid-head)' } : {}),
                      }}
                      className="p-2 text-center text-xs text-text-muted select-none"
                    >
                      ∑
                    </td>
                  )}
                  {visibleColumns.map((index, colPosIdx) => {
                    const col = columns[index]
                    if (!col) return <td key={index} />
                    const card = configuredSummaryCards.find((c) => c.columnId === col.field)
                    const cellType = columnType.get(index) ?? 'text'
                    const totalsPos = subtotalPosition.get(TOTALS_BAND_KEY)
                    const inTotalsRange = subtotalRange !== null && totalsPos !== undefined
                      && totalsPos >= subtotalRange.rowMin && totalsPos <= subtotalRange.rowMax
                      && colPosIdx >= subtotalRange.colMin && colPosIdx <= subtotalRange.colMax
                    const totalsBorders = inTotalsRange ? [
                      totalsPos === subtotalRange.rowMin ? `inset 0 1px 0 0 ${SELECTION_COLOR}` : '',
                      totalsPos === subtotalRange.rowMax ? `inset 0 -1px 0 0 ${SELECTION_COLOR}` : '',
                      colPosIdx === subtotalRange.colMin ? `inset 1px 0 0 0 ${SELECTION_COLOR}` : '',
                      colPosIdx === subtotalRange.colMax ? `inset -1px 0 0 0 ${SELECTION_COLOR}` : '',
                    ].filter(Boolean) : []
                    const totalsFill = col.field ? cellColors.get(cellColorKey(col.field, subtotalRowKey(TOTALS_BAND_KEY))) ?? columnColors[col.field] : undefined
                    const isNum = cellType === 'number' || col.cellType === 'number' || col.dataFormat?.type === 'number'
                    const calculatedVal = col.field && hiddenTotalFields.has(col.field) ? '' : aggregateCellText(col, card, isNum, headerOf(index), aggregationRowsOf(internalRows as Record<string, unknown>[]), formats)

                    const isStickyLeft = stickyLeft.has(index)
                    const isStickyRight = stickyRight.has(index)
                    const cellRule = alignmentConfig[cellType]
                    const alignClass = textClass(cellRule, col.align)

                    return (
                      <td
                        key={index}
                        onMouseDown={isEmbedded ? undefined : (e) => {
                          if (e.button !== 0) return
                          const cell = { groupKey: TOTALS_BAND_KEY, originalCol: index }
                          draggingSubtotalSelection.current = true
                          setAnchor(null)
                          setEnd(null)
                          setSelectedColumns(new Set())
                          setSelectedFullColumn(null)
                          setSubtotalEnd(cell)
                          if (!e.shiftKey || !subtotalAnchor) setSubtotalAnchor(cell)
                        }}
                        onMouseEnter={isEmbedded ? undefined : (e) => {
                          if (!draggingSubtotalSelection.current) return
                          if ((e.buttons & 1) === 0) { draggingSubtotalSelection.current = false; return }
                          setSubtotalEnd({ groupKey: TOTALS_BAND_KEY, originalCol: index })
                        }}
                        {...doubleTapProps(() => {
                          if (colPosIdx === 0 && canEditFormats) { setEditingTotalsLabel(true); return }
                          if (canEditFormats) {
                            setEditingTotalsColumn({ column: col, card })
                            setIsTotalsDialogOpen(true)
                          }
                        })}
                        onContextMenu={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setTotalsContextMenu({
                            x: e.clientX,
                            y: e.clientY,
                            column: col,
                            card,
                            displayVal: calculatedVal,
                          })
                        }}
                        style={{
                          width: widthOf(index),
                          minWidth: widthOf(index),
                          boxShadow: [...totalsBorders, 'inset 0 1px 0 0 var(--grid-line-strong), inset -1px 0 0 0 var(--grid-line-strong)'].join(', '),
                          ...(inTotalsRange ? { filter: 'brightness(0.95)' } : {}),
                          ...(totalsFill ? { background: totalsFill } : {}),
                          ...(isStickyLeft ? { position: 'sticky' as const, left: stickyLeft.get(index), zIndex: 12, background: totalsFill ?? 'var(--grid-head)' } : {}),
                          ...(isStickyRight ? { position: 'sticky' as const, right: stickyRight.get(index), zIndex: 11, background: totalsFill ?? 'var(--grid-head)' } : {}),
                          ...(colPosIdx === 0 ? {} : aggregateFitStyle(index, calculatedVal)),
                        }}
                        className={`p-2 text-sm sm:text-xs select-none cursor-cell bg-grid-head ${alignClass}`}
                      >
                        {colPosIdx === 0 ? (
                          <div className="flex items-center gap-1.5 min-w-0 font-bold">
                            {editingTotalsLabel ? (
                              <input
                                autoFocus
                                defaultValue={totalsLabel}
                                onMouseDown={(e) => e.stopPropagation()}
                                onBlur={(e) => void saveTotalsLabel(e.currentTarget.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') e.currentTarget.blur()
                                  if (e.key === 'Escape') setEditingTotalsLabel(false)
                                }}
                                className="min-w-0 flex-1 rounded border border-grid-line-strong bg-surface px-1 text-sm sm:text-xs font-bold text-text"
                              />
                            ) : <span className="truncate">{totalsLabel}</span>}
                            {calculatedVal ? <span className="ml-auto font-semibold tabular-nums text-text shrink-0">{calculatedVal}</span> : null}
                          </div>
                        ) : calculatedVal ? (
                          <span className="font-semibold tabular-nums text-text">{calculatedVal}</span>
                        ) : null}
                      </td>
                    )
                  })}
                </tr>
              </tfoot>
            )}
          </table>}
          {onAddRows && !isEmbedded && !loading && (
            <div className="flex items-center gap-2 p-2 px-10 text-xs border-t border-grid-line bg-surface">
              <button
                type="button"
                disabled={!editable}
                onClick={() => {
                  const count = parseInt(rowsToAdd, 10)
                  if (!isNaN(count) && count > 0) {
                    setLocalSort('none')
                    pendingActivateNewRow.current = true
                    void runWithSaveState(() => onAddRows(count))
                  }
                }}
                className="flex h-7 items-center rounded-md bg-surface-3 px-3 text-xs sm:text-sm font-medium text-text transition-colors hover:bg-surface-hover disabled:opacity-50"
              >
                {t('grid.addMoreRows')}
              </button>
              <input
                id={`add-rows-input-${gridKey}`}
                type="number"
                min={1}
                max={100}
                value={rowsToAdd}
                onChange={(e) => setRowsToAdd(e.target.value.replace(/[^0-9]/g, ''))}
                className="w-16 h-7 px-2 border border-border rounded focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand text-center disabled:opacity-50"
                disabled={!editable}
              />
              <span className="text-text-muted text-sm">{t('grid.moreRowsAtBottom')}</span>
            </div>
          )}
        </div>
      </div>
      </div>

      {optionsDialog && formatTarget !== '' && !isEmbedded && (() => {
        const selected = dataFormatsConfig[gridKey]?.[formatTarget]
        if (selected?.type !== 'smartchip' && selected?.type !== 'autofill') return null
        const column = columns.find((candidate) => candidate.field === formatTarget)
        const present = new Set<string>()
        if (column) internalRows.forEach((row) => { const value = cellText(column, row); if (value) present.add(value) })
        return <GridSidePanel id="grid-options-panel" label={t('grid.optionsPanelLabel')} width={optionsPanelWidth} minWidth={240} maxWidth={900} onWidthChange={resizeOptionsPanel} fullScreen={fullScreen}>
        <ColumnOptionsPanel key={formatTarget}
          initialOptions={selected.options ?? []}
          initialLink={selected.optionsLink}
          suggestions={Array.from(present).sort((a, b) => a.localeCompare(b))}
          title={t('chipSelect.editTitle', { name: (formulaTargets.find((target) => target.field === formatTarget)?.name ?? formatTarget).toLowerCase() })}
          orgId={gridConfig.orgId}
          workbook={optionsWorkbook}
          renames={gridHeadersConfig}
          calculated={calculatedColumnsByTab}
          resolveCell={resolveLinkedCell}
          onClose={() => setOptionsDialog(false)}
          onPersist={({ options, link }) => {
            const { options: _options, optionsLink: _link, ...rest } = selected
            void saveDataFormat({
              ...rest,
              ...(options.length > 0 ? { options } : {}),
              ...(link && link.field !== '' ? { optionsLink: link } : {}),
            })
          }}
        />
        </GridSidePanel>
      })()}

      {/* Right columns panel */}
      {columnsPanel && !optionsDialog && !isEmbedded && !bare && <GridSidePanel id="grid-columns-panel" label={t('grid.columnsPanelLabel')} width={columnsPanelWidth} minWidth={160} maxWidth={600} onWidthChange={resizeColumnsPanel} fullScreen={fullScreen}>
        <div className="shrink-0 space-y-1.5 border-b border-grid-line p-2">
          <div className="flex items-center justify-between gap-1 mb-1">
            <button
              type="button"
              onClick={() => { void runWithSaveState(() => handleAddColumn()) }}
              className="flex h-7 items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 text-xs font-medium text-fg transition-colors hover:bg-surface-hover"
            >
              <span className="text-sm font-bold">+</span>
              <span>{t('grid.addColumn')}</span>
            </button>
            <div className="flex items-center gap-1">
            {(canToggleColumns || canEditFormats) && (
              <DeviceSwitch value={columnsPanelScope} onChange={setColumnsPanelScope} label={t('device.editColumnsFor')} />
            )}
            <button
              type="button"
              onClick={() => {
                setColumnsPanel(false)
                savePref('columnas', '0')
              }}
              className="flex h-7 w-7 items-center justify-center rounded-full text-text-muted hover:bg-surface-3 hover:text-text"
              title={t('common.close')}
              aria-label={t('common.close')}
            >
              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            </div>
          </div>
          <input value={columnsSearch} onChange={(e) => setColumnsSearch(e.target.value)} placeholder={t('grid.searchColumn')}
            className="h-7 w-full rounded-full border border-border bg-bg px-2 text-sm sm:text-xs outline-none focus:border-focus" />
          <div className="flex flex-wrap justify-between gap-1 text-xs sm:text-[11px]">
            <button type="button" disabled={!canToggleColumns} onClick={() => void setColumnsVisibility(columnOrder.filter((i) => i < columns.length), true, columnsPanelScope)} className="rounded-full px-1 py-0.5 font-bold text-brand transition-colors hover:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-50">{t('grid.showAllF')}</button>
            <button type="button" disabled={!canToggleColumns} onClick={() => void setColumnsVisibility(columnOrder.filter((i) => i < columns.length), false, columnsPanelScope)} className="rounded-full px-1 py-0.5 font-bold text-brand transition-colors hover:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-50">{t('grid.hideAllF')}</button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-scroll p-2">
          {(() => {
            const term = columnsSearch.trim().toLowerCase()
            const matches = (index: number) => !term || headerOf(index).toLowerCase().includes(term)
            const toggle = (indices: number[], show: boolean) => { void setColumnsVisibility(indices, show, columnsPanelScope) }
            // Shown on the device being edited; under "all devices", shown on both.
            const shownHere = (index: number) => !hidden.has(index) && (columnsPanelScope === 'all' ? columnScopeOf(index) === 'all' : scopeIncludes(columnScopeOf(index), columnsPanelScope))
            const leafNode = (index: number, nested: boolean) => {
              const currentPos = columnOrder.indexOf(index)
              const canMoveLeft = canEditFormats && currentPos > 0
              const canMoveRight = canEditFormats && currentPos >= 0 && currentPos < columnOrder.length - 1

              return (
                <div key={index} className={`flex items-center gap-1.5 rounded-full p-1 text-sm sm:text-xs transition-colors hover:bg-surface-3 ${nested ? 'ml-3' : ''}`}>
                  <input type="checkbox" disabled={!canToggleColumns} aria-label={headerOf(index)} className="h-3.5 w-3.5 shrink-0 cursor-pointer rounded-full border-border disabled:cursor-not-allowed"
                    checked={shownHere(index)}
                    ref={(el) => { if (el) el.indeterminate = columnsPanelScope === 'all' && !hidden.has(index) && columnScopeOf(index) !== 'all' }}
                    onChange={() => toggle([index], !shownHere(index))} />
                  <button type="button" disabled={!canToggleColumns} title={headerOf(index)} onClick={() => toggle([index], !shownHere(index))} className="min-w-0 flex-1 truncate text-left font-medium disabled:cursor-not-allowed">{headerOf(index)}</button>
                  {canEditFormats && (
                    <div className="flex shrink-0 items-center gap-0.5">
                      <button
                        type="button"
                        disabled={!canMoveLeft}
                        onClick={() => {
                          if (canMoveLeft) {
                            const targetIndex = columnOrder[currentPos - 1]
                            if (targetIndex !== undefined) void moveColumn(index, targetIndex)
                          }
                        }}
                        aria-label={t('grid.moveColumnLeft', { column: headerOf(index) })}
                        title={t('grid.moveColumnLeft', { column: headerOf(index) })}
                        className="flex h-7 w-6 items-center justify-center rounded-full border border-border bg-surface text-xs text-text-muted hover:bg-surface-hover hover:text-text disabled:opacity-30 disabled:hover:bg-surface"
                      >
                        ←
                      </button>
                      <button
                        type="button"
                        disabled={!canMoveRight}
                        onClick={() => {
                          if (canMoveRight) {
                            const targetIndex = columnOrder[currentPos + 1]
                            if (targetIndex !== undefined) void moveColumn(index, targetIndex)
                          }
                        }}
                        aria-label={t('grid.moveColumnRight', { column: headerOf(index) })}
                        title={t('grid.moveColumnRight', { column: headerOf(index) })}
                        className="flex h-7 w-6 items-center justify-center rounded-full border border-border bg-surface text-xs text-text-muted hover:bg-surface-hover hover:text-text disabled:opacity-30 disabled:hover:bg-surface"
                      >
                        →
                      </button>
                    </div>
                  )}
                  {canEditFormats && (() => {
                    const key = columnKeys[index]
                    const entry = frozenColumnsConfig[gridKey]
                    const frozen = key !== undefined && (columnsPanelScope === 'all'
                      ? frozenScopeOf(entry, key) === 'all'
                      : frozenKeyFor(entry, columnsPanelScope) === key)
                    const label = frozen ? t('grid.unfreezeColumns') : t('grid.freezeColumnsThrough', { column: headerOf(index) })
                    return <button
                      type="button"
                      onClick={() => void setFrozenOn(frozen ? null : index, columnsPanelScope)}
                      aria-label={label}
                      title={label}
                      className={`h-7 shrink-0 rounded-full border px-2 text-xs sm:text-[11px] font-medium transition-colors ${frozen ? 'border-brand bg-brand/10 text-brand hover:bg-brand/15' : 'border-border bg-surface hover:bg-surface-hover'}`}
                    >{frozen ? t('grid.unfreeze') : t('grid.freeze')}</button>
                  })()}
                </div>
              )
            }

            const nodes = columnTree.map((node) => {
              if (node.group === null) return matches(node.cols[0]!) ? leafNode(node.cols[0]!, false) : null
              const groupMatches = node.group.toLowerCase().includes(term)
              const children = node.cols.filter((i) => groupMatches || matches(i))
              if (children.length === 0) return null
              const visible = node.cols.filter((i) => shownHere(i))
              return <div key={`group-${node.group}-${node.cols[0]}`} className="select-none">
                <div className="flex items-center gap-1.5 rounded-full p-1 text-sm sm:text-xs transition-colors hover:bg-surface-3">
                  <input type="checkbox" disabled={!canToggleColumns} aria-label={node.group} className="h-3.5 w-3.5 shrink-0 cursor-pointer rounded-full border-border disabled:cursor-not-allowed"
                    checked={visible.length === node.cols.length}
                    ref={(el) => { if (el) el.indeterminate = visible.length > 0 && visible.length < node.cols.length }}
                    onChange={() => toggle(node.cols, visible.length < node.cols.length)} />
                  <button type="button" disabled={!canToggleColumns} title={node.group} onClick={() => toggle(node.cols, visible.length < node.cols.length)} className="min-w-0 flex-1 truncate text-left font-bold disabled:cursor-not-allowed">{node.group}</button>
                  <span className="shrink-0 tabular-nums text-text-muted text-xs sm:text-[11px]">({formatNumber(visible.length)}/{formatNumber(node.cols.length)})</span>
                </div>
                <div className="border-l border-border/40 ml-2.5 pl-0.5">{children.map((i) => leafNode(i, true))}</div>
              </div>
            }).filter(Boolean)

            return nodes.length > 0 ? nodes : <p className="p-1 text-sm sm:text-xs text-text-muted">{t('grid.noColumns')}</p>
          })()}
        </div>
      </GridSidePanel>}

      {/* Active filters: a right sidebar from `sm` up */}
      {filtersPanel && !isEmbedded && !bare && !phoneLayout && <GridSidePanel id="grid-filters-panel" label={t('grid.filtersPanel.title')} width={filtersPanelWidth} minWidth={220} maxWidth={600} onWidthChange={resizeFiltersPanel} fullScreen={fullScreen}>
        {filtersPanelBody}
      </GridSidePanel>}

      {/* Right chart panel */}
      {chartPanel && !columnsPanel && !optionsDialog && !isEmbedded && !bare && <GridSidePanel id="grid-chart-panel" label={t('grid.chartPanelLabel')} width={chartPanelWidth} minWidth={240} maxWidth={900} onWidthChange={resizeChartPanel} fullScreen={fullScreen}>
        <GridChartPanel
          width={chartPanelWidth}
          categoryColumns={chartCategoryColumns}
          numberColumns={chartNumberColumns}
          buildPoints={buildChartPoints}
          settings={gridConfig.chart[gridKey]}
          onSettingsChange={saveChartSettings}
          readOnly={!canEditChart}
          onClose={canEditFormats || personalView ? toggleChartPanel : undefined}
          headerExtra={canEditFormats ? (
            <DeviceSwitch value={sharedChart?.openOn ?? 'all'} onChange={(scope) => void persistPanels({ openOn: scope })}
              label={t('device.showChartOn')} />
          ) : undefined}
        />
      </GridSidePanel>}

      {/* Expanded formula sidebar */}
      {canUseFormulas && formulaBar && formulaPanel && <aside style={{ width: formulaPanelWidth }}
        className={`relative hidden shrink-0 flex-col border border-grid-line bg-surface sm:flex ${fullScreen ? 'border-l-0' : 'rounded-r-lg border-l-0'}`}>
        <FormulaBar
          targets={formulaTargets}
          completionSchema={formulaCompletionSchema}
          referenceTargets={formulaReferenceTargets}
          selected={formulaTarget}
          onSelectTarget={setFormulaTarget}
          formula={formulaBarMode === 'cell' ? activeCellFormula : selectedFormula.formula}
          title={selectedFormula.title}
          scope={cellFormulaScope}
          locale={formulaLocale}
          onAccept={(formula, title) => formulaBarMode === 'cell' ? acceptActiveCellFormula(formula) : void saveFormula(formula, title)}
          onDelete={() => formulaBarMode === 'cell' ? deleteActiveCellFormula() : void deleteFormula()}
          mode={formulaBarMode}
          onModeChange={setFormulaBarMode}
          cellAvailable={activeCellIsEditable}
          insertRequest={formulaInsert}
          onPointRef={pointBarRef}
          pointRequest={barPointRequest}
          operandRefs={barOperandRefs}
          expanded
          onToggleExpanded={() => setFormulaPanel(false)}
          onClose={() => setInternalFormulaBar(false)}
          saveError={formulaSaveError}
          busy={formulaBusy}
          showAllowCellOverrides={formulaTarget !== NEW_COLUMN && isFormulaField(formulaTarget, formulaConfig)}
          allowCellOverrides={formulaTarget === NEW_COLUMN || cellOverridesAllowed(formulaConfig, formulaTarget)}
          onAllowCellOverridesChange={(allowed) => void setFormulaCellOverridesAllowed(allowed)}
        overrideTargetOptions={overrideTargetOptions}
        overrideTarget={overrideTargetOf(formulaConfig, formulaTarget) ?? SAME_COLUMN}
        onOverrideTargetChange={(field) => void setFormulaOverrideTarget(field)}
        />
        <div onMouseDown={startFormulaPanelResize}
          className="absolute top-0 left-0 bottom-0 w-2 cursor-col-resize hover:bg-brand/30 active:bg-brand/50 transition-colors z-10"
          title={t('grid.dragResize')} />
      </aside>}
    </div>

    {/* Phones, a cell selected but not being edited: the bar shows what the cell holds, without the
        keyboard or ✕ / ✓. Tapping it edits the cell, as a double tap on the cell does. */}
    {isPhone && editing === null && editingHeader === null && activeCell && activeCellRow && activeCellCol && <div className="flex shrink-0 items-center gap-1 border-t border-border bg-surface px-2 py-1.5">
      <button
        type="button"
        data-testid="grid-phone-bar-idle"
        disabled={!activeCellIsEditable || activeCellCol.cellType === 'checkbox'}
        onClick={() => setEditing({ rowKey: activeCell.rowKey, field: activeCellCol.field! })}
        className="h-9 min-w-0 flex-1 truncate rounded-lg border border-border bg-bg px-3 text-left text-sm disabled:text-text-muted"
      >
        {activeCellFormula
          ? `=${serializeCellFormula(fromStored(activeCellFormula), cellFormulaScope, formulaLocale)}`
          : cellText(activeCellCol, activeCellRow)}
      </button>
    </div>}
    {isPhone && (editing !== null || editingHeader !== null) && <div className="flex shrink-0 items-center gap-1 border-t border-border bg-surface px-2 py-1.5">
      <div
        ref={setPhoneBarSlot}
        className="nt-phone-edit-slot min-w-0 flex-1"
      />
      <button
        type="button"
        onPointerDown={(e) => e.preventDefault()}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          if (editingHeader !== null) pressInPhoneEditor('Escape')
          else { cancelingEdit.current = true; setEditing(null) }
        }}
        title={t('grid.cancel')}
        aria-label={t('grid.cancel')}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded text-sm font-bold transition-colors hover:bg-surface-hover"
      >
        ✕
      </button>
      <button
        type="button"
        onPointerDown={(e) => e.preventDefault()}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          if (editing && cellFormulaCommitRef.current) cellFormulaCommitRef.current()
          else pressInPhoneEditor('Enter')
        }}
        title={t('grid.accept')}
        aria-label={t('grid.accept')}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded text-sm font-bold text-success transition-colors hover:bg-success/10"
      >
        ✓
      </button>
    </div>}

    {!bare && <div id="grid-bottom-tabs-slot" data-hide-while-typing className="shrink-0" />}

    <GridStatusBar
      rowsCount={isEmbedded ? undefined : countedRows}
      stats={subtotalStats ?? selectionStats}
      selectionActions={rowSelectionActions}
      zoom={zoom}
      onZoomChange={isEmbedded ? undefined : setZoom}
      className={`${fullScreen || bare ? 'px-2 sm:px-3' : ''} ${splitView && splitSheets.length > 0 && !chartPanel && !columnsPanel && !optionsDialog && !isEmbedded && !bare ? 'sm:border-r sm:border-grid-line sm:pr-2' : ''}`}
    />
    </div>

      {splitView && !chartPanel && !columnsPanel && !optionsDialog && !isEmbedded && !bare && splitSheets.map((sheet, index) => (
        <GridSidePanel key={sheet.id} id={`grid-split-panel-${index + 1}`} label={`${t('grid.splitView')}: ${sheet.name}`} width={splitPanelWidths[index] ?? 520} minWidth={160} maxWidth={1400} onWidthChange={(width) => resizeSplitPanel(index, width)} fullScreen={fullScreen}>
          <div className="flex h-9 shrink-0 items-center gap-2 border-b border-grid-line pl-3 pr-2 text-sm sm:text-xs">
            <span className="min-w-0 truncate font-bold">{sheet.name}</span>
            <span ref={splitMenuSlotRef(sheet.id)} className="flex min-w-0 flex-1 items-center" />
            {canEditFormats && <button type="button" onClick={() => toggleSplitSheet(sheet.id)} aria-label={t('common.close')} title={t('common.close')} className="shrink-0 font-bold text-text-muted hover:text-text">✕</button>}
          </div>
          <div data-split-pane={sheet.id} className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-auto ${activePane === sheet.id ? 'ring-2 ring-inset ring-brand/30' : ''}`}>
            <SplitPaneContext.Provider value={{ toolbarSlot: splitToolbarSlot, active: activePane === sheet.id }}>
            {splitView.render(sheet.id, {
              hiddenFields: sharedChart?.splitHidden?.[sheet.id] ?? [],
              menuSlot: splitMenuSlots[sheet.id] ?? null,
              ...(canEditFormats || personalView ? {
                onHiddenFieldsChange: (fields: string[]) => void persistPanels({ splitHidden: { ...sharedChart?.splitHidden, [sheet.id]: fields } }),
              } : {}),
            })}
            </SplitPaneContext.Provider>
          </div>
        </GridSidePanel>
      ))}
    </div>

    {bare && splitSheetView?.menuSlot && createPortal(
      <button type="button" title={t('grid.splitColumns')} aria-label={t('grid.splitColumns')} aria-haspopup="menu" aria-expanded={splitColumnsMenu !== null}
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect()
          setSplitColumnsMenu((open) => open ? null : { left: Math.max(8, Math.min(rect.left, window.innerWidth - 248)), top: rect.bottom + 4 })
        }}
        className={`flex h-6 shrink-0 items-center gap-0.5 rounded px-1 transition-colors hover:bg-surface-hover ${(splitHiddenFields?.length ?? 0) > 0 ? 'text-brand' : 'text-text-muted'}`}>
        {MENU_ICONS.columns}
        <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
      </button>,
      splitSheetView.menuSlot,
    )}

    {bare && splitColumnsMenu && splitSheetView && createPortal(
      <div className="fixed inset-0 z-[90]" onClick={() => setSplitColumnsMenu(null)} onKeyDown={(e) => { if (e.key === 'Escape') setSplitColumnsMenu(null) }}>
        <div role="menu" aria-label={t('grid.splitColumns')} style={{ left: splitColumnsMenu.left, top: splitColumnsMenu.top }} onClick={(e) => e.stopPropagation()}
          className="fixed flex max-h-[60vh] w-60 flex-col overflow-y-auto rounded-xl border border-border bg-surface py-1 text-sm sm:text-xs text-fg shadow-xl">
          {columnOrder.filter((i) => i < columns.length && !hidden.has(i) && columns[i]?.field).map((i) => {
            const field = columns[i]!.field!
            const checked = !splitSheetView.hiddenFields.includes(field)
            const change = splitSheetView.onHiddenFieldsChange
            // The last shown column stays: a sidebar with no columns has nothing to uncheck back from.
            const last = checked && visibleColumns.length <= 1
            return (
              <button key={field} type="button" role="menuitemcheckbox" aria-checked={checked} disabled={!change || last}
                onClick={() => change?.(checked ? [...splitSheetView.hiddenFields, field] : splitSheetView.hiddenFields.filter((other) => other !== field))}
                className="flex min-w-0 items-center gap-2 px-3 py-2 text-left hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent">
                <span aria-hidden="true" className="flex w-3.5 shrink-0 justify-center">
                  {checked && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>}
                </span>
                <span className="min-w-0 flex-1 truncate">{headerOf(i)}</span>
              </button>
            )
          })}
        </div>
      </div>,
      document.body,
    )}

    {splitMenu && splitView && createPortal(
      <div className="fixed inset-0 z-[90]" onClick={() => setSplitMenu(null)} onKeyDown={(e) => { if (e.key === 'Escape') setSplitMenu(null) }}>
        <div role="menu" aria-label={t('grid.splitView')} style={{ left: splitMenu.left, top: splitMenu.top }} onClick={(e) => e.stopPropagation()}
          className="fixed flex max-h-[60vh] w-60 flex-col overflow-y-auto rounded-xl border border-border bg-surface py-1 text-sm sm:text-xs text-fg shadow-xl">
          {splitView.sheets.map((sheet) => {
            const checked = splitSheetIds.includes(sheet.id)
            // Two sheets beside the grid is the most: past that, only unchecking works.
            const full = !checked && splitSheetIds.length >= MAX_SPLIT_SHEETS
            return (
              <button key={sheet.id} type="button" role="menuitemcheckbox" aria-checked={checked} disabled={full}
                onClick={() => toggleSplitSheet(sheet.id)}
                className={`flex min-w-0 items-center gap-2 px-3 py-2 text-left hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent ${checked ? 'font-bold text-brand' : ''}`}>
                <span aria-hidden="true" className="flex w-3.5 shrink-0 justify-center">
                  {checked && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>}
                </span>
                <span className="min-w-0 flex-1 truncate">{sheet.name}</span>
              </button>
            )
          })}
          {canEditFormats && (
            <div className="mt-1 flex items-center justify-between gap-2 border-t border-border px-3 pb-1 pt-2">
              <span className="min-w-0 truncate text-text-muted">{t('device.showOn')}</span>
              <DeviceSwitch value={sharedChart?.splitsOn ?? 'all'} onChange={(scope) => void persistPanels({ splitsOn: scope })}
                label={t('device.showSplitOn')} />
            </div>
          )}
        </div>
      </div>,
      document.body,
    )}

    {/* Cell manual override popup menu via portal to document.body */}
    {overrideMenu && createPortal(
      <div
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        data-override-menu="1"
        role="menu"
        aria-label={t('grid.overrideMenuLabel')}
        style={{ position: 'fixed', top: overrideMenu.top + 4, left: overrideMenu.left, transform: 'translateX(-100%)' }}
        className="z-50 flex w-max flex-col radius-box border border-border bg-surface py-1 text-sm sm:text-xs shadow-card"
      >
        <button type="button" role="menuitem" onClick={() => void removeOverride(overrideMenu.rowKey, overrideMenu.field)} className="px-3 py-1.5 text-left hover:bg-surface-3">
          {t('grid.overrideRevertToFormula')}
        </button>
        <button type="button" role="menuitem" onClick={() => void clearOverrideCell(overrideMenu.rowKey, overrideMenu.field)} className="px-3 py-1.5 text-left hover:bg-surface-3">
          {t('grid.overrideLeaveEmpty')}
        </button>
      </div>,
      document.body,
    )}

    {/* Desktop floating filter menu via portal to document.body (positioned to stay fully on-screen) */}
    {openFilter !== null && createPortal(
      <div
        ref={filterMenuRef}
        data-grid-filter-modal="1"
        style={{
          position: 'fixed',
          top: filterMenuPosition.top,
          left: filterMenuPosition.left,
          width: filterMenuSize?.width ?? 208,
          height: filterMenuSize?.height ?? 256,
          zIndex: 50,
        }}
        className="hidden sm:flex flex-col rounded-lg border border-border bg-surface text-text shadow-card"
      >
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          <div className="mb-2 flex gap-1 border-b border-border pb-2 text-sm sm:text-xs">
            <button
              type="button"
              onClick={() => void applySort(sort?.index === openFilter && sort.dir === 1 ? null : { index: openFilter, dir: 1 })}
              className={`flex-1 rounded-full py-1.5 font-bold transition-colors hover:bg-surface-3 ${sort?.index === openFilter && sort.dir === 1 ? 'bg-brand/10 text-brand' : ''}`}
            >
              ▲ {t('grid.ascending')}
            </button>
            <button
              type="button"
              onClick={() => void applySort(sort?.index === openFilter && sort.dir === -1 ? null : { index: openFilter, dir: -1 })}
              className={`flex-1 rounded-full py-1.5 font-bold transition-colors hover:bg-surface-3 ${sort?.index === openFilter && sort.dir === -1 ? 'bg-brand/10 text-brand' : ''}`}
            >
              ▼ {t('grid.descending')}
            </button>
          </div>
          <input
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder={t('grid.search')}
            className="mb-2 h-7 w-full rounded-full border border-border bg-bg px-2 text-sm sm:text-xs outline-none focus:border-focus"
          />
          <div className="mb-1 flex justify-between gap-1 text-xs sm:text-[11px]">
            <button
              type="button"
              onClick={() => filterWithHistory((f) => { const c = { ...f }; delete c[openFilter]; return c })}
              className="rounded-full px-1.5 py-1 font-bold text-brand transition-colors hover:bg-brand/10"
            >
              {t('grid.selectAll')}
            </button>
            <button
              type="button"
              onClick={() => filterWithHistory((f) => ({ ...f, [openFilter]: new Set() }))}
              className="rounded-full px-1.5 py-1 font-bold text-brand transition-colors hover:bg-brand/10"
            >
              {t('grid.uncheckAll')}
            </button>
          </div>
          {uniqueValues(openFilter).filter(([value]) => value.toLowerCase().includes(filterSearch.trim().toLowerCase())).map(([value, count]) => (
            <label key={value} className="flex cursor-pointer items-center gap-2 rounded-full p-1.5 text-sm sm:text-xs font-normal transition-colors hover:bg-surface-3">
              <input
                type="checkbox"
                className="h-4 w-4 shrink-0 cursor-pointer rounded-full border-border"
                checked={!filters[openFilter] || filters[openFilter].has(value)}
                onChange={() => filterWithHistory((f) => {
                  const current = new Set(f[openFilter] ?? uniqueValues(openFilter).map(([v]) => v))
                  if (current.has(value)) current.delete(value); else current.add(value)
                  return { ...f, [openFilter]: current }
                })}
              />
              <span className="min-w-0 flex-1 truncate">{displayFilterValue(value)}</span>
              <span className="shrink-0 tabular-nums text-text-muted">({formatNumber(count)})</span>
            </label>
          ))}
        </div>
        <ResizeCorner onMouseDown={startFilterResize} />
      </div>,
      document.body,
    )}

    {/* Active filters as a full page on phones; the filter editor opens above it */}
    {filtersPanel && !isEmbedded && !bare && phoneLayout && createPortal(
      <div data-grid-filter-modal="1" role="dialog" aria-modal="true" aria-label={t('grid.filtersPanel.title')}
        className="fixed inset-0 z-50 flex flex-col bg-surface text-text">
        {filtersPanelBody}
      </div>,
      document.body,
    )}

    {/* Mobile full-screen filter menu via portal to document.body */}
    {openFilter !== null && createPortal(
      <div
        data-grid-filter-modal="1"
        className="fixed inset-0 z-50 flex flex-col bg-surface text-text sm:hidden"
        role="dialog"
        aria-modal="true"
        aria-label={`${t('grid.filterColumn')}: ${headerOf(openFilter)}`}
      >
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
          <div className="flex min-w-0 items-center gap-2">
            <svg aria-hidden="true" width="11" height="7" viewBox="0 0 22 14" fill="#105525" className="shrink-0">
              <rect x="0" y="0" width="22" height="3" />
              <rect x="5" y="6" width="13" height="2" />
              <rect x="9" y="11" width="4" height="3" />
            </svg>
            <span className="truncate text-base font-bold">{headerOf(openFilter)}</span>
          </div>
          <button
            type="button"
            onClick={() => setOpenFilter(null)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-text-muted hover:bg-surface-3 hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            aria-label={t('common.close')}
          >
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="mb-3 flex gap-2 border-b border-border pb-3">
            <button
              type="button"
              onClick={() => void applySort(sort?.index === openFilter && sort.dir === 1 ? null : { index: openFilter, dir: 1 })}
              className={`flex-1 rounded-full py-2.5 text-sm font-bold transition-colors border border-border hover:bg-surface-3 ${sort?.index === openFilter && sort.dir === 1 ? 'bg-brand/10 text-brand border-brand/30' : ''}`}
            >
              ▲ {t('grid.ascending')}
            </button>
            <button
              type="button"
              onClick={() => void applySort(sort?.index === openFilter && sort.dir === -1 ? null : { index: openFilter, dir: -1 })}
              className={`flex-1 rounded-full py-2.5 text-sm font-bold transition-colors border border-border hover:bg-surface-3 ${sort?.index === openFilter && sort.dir === -1 ? 'bg-brand/10 text-brand border-brand/30' : ''}`}
            >
              ▼ {t('grid.descending')}
            </button>
          </div>

          <div className="relative mb-3">
            <input
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              placeholder={t('grid.search')}
              className="h-10 w-full rounded-full border border-border bg-bg pl-3.5 pr-9 text-sm outline-none focus:border-focus"
            />
            {filterSearch && (
              <button
                type="button"
                onClick={() => setFilterSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text text-sm font-bold"
                aria-label={t('grid.clearSelection')}
              >
                ✕
              </button>
            )}
          </div>

          <div className="mb-2 flex justify-between gap-2 text-xs">
            <button
              type="button"
              onClick={() => filterWithHistory((f) => { const c = { ...f }; delete c[openFilter]; return c })}
              className="rounded-full px-2.5 py-1.5 font-bold text-brand transition-colors hover:bg-brand/10"
            >
              {t('grid.selectAll')}
            </button>
            <button
              type="button"
              onClick={() => filterWithHistory((f) => ({ ...f, [openFilter]: new Set() }))}
              className="rounded-full px-2.5 py-1.5 font-bold text-brand transition-colors hover:bg-brand/10"
            >
              {t('grid.uncheckAll')}
            </button>
          </div>

          <div className="space-y-1">
            {uniqueValues(openFilter).filter(([value]) => value.toLowerCase().includes(filterSearch.trim().toLowerCase())).map(([value, count]) => (
              <label
                key={value}
                className="flex cursor-pointer items-center gap-3 rounded-xl p-2.5 text-sm font-medium transition-colors hover:bg-surface-3 active:bg-surface-3"
              >
                <input
                  type="checkbox"
                  className="h-5 w-5 shrink-0 cursor-pointer rounded border-border text-brand"
                  checked={!filters[openFilter] || filters[openFilter].has(value)}
                  onChange={() => filterWithHistory((f) => {
                    const current = new Set(f[openFilter] ?? uniqueValues(openFilter).map(([v]) => v))
                    if (current.has(value)) current.delete(value); else current.add(value)
                    return { ...f, [openFilter]: current }
                  })}
                />
                <span className="min-w-0 flex-1 truncate">{displayFilterValue(value)}</span>
                <span className="shrink-0 tabular-nums text-text-muted text-xs font-normal">({formatNumber(count)})</span>
              </label>
            ))}
          </div>
        </div>
      </div>,
      document.body,
    )}

    {editable && (onAddRow || onAddRows) && !isEmbedded && !loading && addButtonInGrid && !keyboardOpen && !(editing && isPhone) && renderAddRowButton('grid')}

    {quoteRuleField && (() => {
      const column = columns.find((c) => c.field === quoteRuleField)
      const base = column?.dataFormat
      const fx = base?.exchangeRate
      if (!base || !fx) return null
      const current = fx.sideRule ? { field: fx.sideRule.field, positive: fx.sideRule.sell, negative: fx.sideRule.buy } : undefined
      return <SignRuleDialog
        columnName={column?.headerName ?? quoteRuleField}
        conditionColumns={columns
          .filter((c): c is typeof c & { field: string } => Boolean(c.field) && c.field !== quoteRuleField)
          .map((c) => ({ field: c.field, name: c.headerName }))}
        valuesOf={(field) => [
          ...(dataFormatsConfig[gridKey]?.[field]?.options ?? columns.find((c) => c.field === field)?.dataFormat?.options ?? []).map((o) => o.value),
          ...internalRows.map((row) => (row as Record<string, unknown>)[field])
            .filter((v): v is string | number => (typeof v === 'string' && v.trim() !== '') || typeof v === 'number')
            .map(String),
        ]}
        rule={current}
        affectedRows={() => 0}
        busy={formatBusy}
        labels={{
          title: t('grid.exchangeRate.rule.title', { column: column?.headerName ?? quoteRuleField }),
          description: t('grid.exchangeRate.rule.description'),
          follows: t('grid.exchangeRate.rule.follows'),
          positive: t('grid.exchangeRate.sides.sell'),
          negative: t('grid.exchangeRate.sides.buy'),
          keep: t('grid.exchangeRate.sides.avg'),
          remove: t('grid.exchangeRate.rule.remove'),
        }}
        onSave={(rule) => {
          const { sideRule: _previous, side: _side, ...rest } = fx
          const exchangeRate = rule
            ? { ...rest, side: 'rule' as const, sideRule: { field: rule.field, sell: [...rule.positive], buy: [...rule.negative] } }
            : { ...rest, side: 'avg' as const }
          const target = quoteRuleField
          setQuoteRuleField(null)
          void saveDataFormat({ ...base, exchangeRate })
            .then((saved) => { if (saved) void fillExchangeRateColumn(target, exchangeRate) })
        }}
        onClose={() => setQuoteRuleField(null)}
      />
    })()}
    {signDialogField && (() => {
      const target = columns.find((c) => c.field === signDialogField)
      return <SignRuleDialog
        columnName={target?.headerName ?? signDialogField}
        conditionColumns={columns
          .filter((c): c is typeof c & { field: string } => Boolean(c.field) && c.field !== signDialogField)
          .map((c) => ({ field: c.field, name: c.headerName }))}
        valuesOf={(field) => [
          ...(dataFormatsConfig[gridKey]?.[field]?.options ?? []).map((o) => o.value),
          ...internalRows.map((row) => (row as Record<string, unknown>)[field])
            .filter((v): v is string | number => (typeof v === 'string' && v.trim() !== '') || typeof v === 'number')
            .map(String),
        ]}
        rule={effectiveFormat(signDialogField)?.sign}
        affectedRows={(rule) => signRewrites(signDialogField, rule).length}
        busy={formatBusy}
        onSave={(rule) => void saveSignRule(signDialogField, rule)}
        onClose={() => setSignDialogField(null)}
      />
    })()}
    {noteEditor && <Dialog
      title={headerNotes[noteEditor.field] ? t('grid.menu.editNote') : t('grid.menu.addNote')}
      onClose={() => setNoteEditor(null)}
      footer={
        <>
          <button type="button" onClick={() => setNoteEditor(null)} className="tap-target rounded-full border border-border px-4 text-sm text-text hover:bg-surface-3">
            {t('common.cancel')}
          </button>
          <button type="button" onClick={() => { void saveHeaderNote(noteEditor.field, noteEditor.text).then((saved) => { if (saved) setNoteEditor(null) }) }} className="tap-target rounded-full bg-brand px-4 text-sm text-brand-fg hover:opacity-90 font-medium">
            {t('common.save')}
          </button>
        </>
      }
    >
      <textarea autoFocus rows={4} value={noteEditor.text} aria-label={t('grid.noteLabel')} onChange={(e) => setNoteEditor({ ...noteEditor, text: e.target.value })} className="nt-control w-full" />
    </Dialog>}
    {renameDialogOpen && (
      <Dialog
        title={t('grid.renameItemDialogTitle')}
        description={t('grid.renameItemDialogDesc')}
        onClose={() => setRenameDialogOpen(false)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setRenameDialogOpen(false)}
              className="tap-target rounded-full border border-border px-4 text-sm text-text hover:bg-surface-3"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => {
                const trimmed = renameValue.trim()
                const nextItemNames = { ...gridConfig.itemNames }
                if (trimmed) {
                  nextItemNames[gridKey] = trimmed
                } else {
                  delete nextItemNames[gridKey]
                }
                markSaving()
                void (async () => {
                  const error = await saveSettingRef.current(GRID_SETTING_KEYS.itemNames, nextItemNames)
                  if (error) {
                    markFailed()
                  } else {
                    markSaved()
                    setRenameDialogOpen(false)
                  }
                })()
              }}
              className="tap-target rounded-full bg-brand px-4 text-sm text-brand-fg hover:opacity-90 font-medium"
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            const trimmed = renameValue.trim()
            const nextItemNames = { ...gridConfig.itemNames }
            if (trimmed) {
              nextItemNames[gridKey] = trimmed
            } else {
              delete nextItemNames[gridKey]
            }
            markSaving()
            void (async () => {
              const error = await saveSettingRef.current(GRID_SETTING_KEYS.itemNames, nextItemNames)
              if (error) {
                markFailed()
              } else {
                markSaved()
                setRenameDialogOpen(false)
              }
            })()
          }}
          className="space-y-2 py-2"
        >
          <label className="block text-xs font-semibold text-text-muted">
            {t('grid.renameItemLabel')}
          </label>
          <input
            type="text"
            autoFocus
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder={t('grid.renameItemPlaceholder')}
            className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm text-text outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          />
        </form>
      </Dialog>
    )}

    {groupingDialogOpen && (
      <Dialog
        title={t('grid.chooseGroupingColumns')}
        onClose={() => setGroupingDialogOpen(false)}
        footer={
          <button
            type="button"
            onClick={() => setGroupingDialogOpen(false)}
            className="tap-target rounded-full bg-brand px-4 text-sm text-brand-fg hover:opacity-90 font-medium"
          >
            {t('common.close')}
          </button>
        }
      >
        <div className="space-y-4 py-1">
          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-text-muted">
              {t('grid.firstField')}
            </label>
            <div className="relative">
              <select
                value={groupColumns[0] === null ? '' : String(groupColumns[0])}
                disabled={!canChooseGrouping}
                onChange={(e) => void selectGroupColumn(0, e.target.value === '' ? null : Number(e.target.value))}
                className="h-10 w-full appearance-none rounded-full border border-border bg-surface px-4 pr-10 text-sm font-medium text-text outline-none transition-colors focus:border-focus disabled:cursor-not-allowed disabled:opacity-60"
              >
                <option value="">{t('grid.noGrouping')}</option>
                {columnOrder
                  .filter((index) => index < columns.length && index !== groupColumns[1])
                  .map((index) => (
                    <option key={index} value={index}>{headerOf(index)}</option>
                  ))}
              </select>
              <div className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-text-muted">
                <svg className="h-4 w-4 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </div>
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-text-muted">
              {t('grid.secondField')}
            </label>
            <div className="relative">
              <select
                value={groupColumns[1] === null ? '' : String(groupColumns[1])}
                disabled={!canChooseGrouping || groupColumns[0] === null}
                onChange={(e) => void selectGroupColumn(1, e.target.value === '' ? null : Number(e.target.value))}
                className="h-10 w-full appearance-none rounded-full border border-border bg-surface px-4 pr-10 text-sm font-medium text-text outline-none transition-colors focus:border-focus disabled:cursor-not-allowed disabled:opacity-60"
              >
                <option value="">{t('grid.noSubGrouping')}</option>
                {columnOrder
                  .filter((index) => index < columns.length && index !== groupColumns[0])
                  .map((index) => (
                    <option key={index} value={index}>{headerOf(index)}</option>
                  ))}
              </select>
              <div className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-text-muted">
                <svg className="h-4 w-4 fill-none stroke-current stroke-2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    )}

    {selectionToolbar && isPhone && range && !editing && renderSelectionToolbar()}

    {groupButtonMenu && groupButtonMenuItems.length > 0 && (
      <GridContextMenu
        x={groupButtonMenu.x}
        y={groupButtonMenu.y}
        items={groupButtonMenuItems}
        onClose={() => setGroupButtonMenu(null)}
        label={t('grid.menu.label')}
      />
    )}
    {contextMenu && (
      <GridContextMenu
        x={contextMenu.x}
        y={contextMenu.y}
        items={contextMenuItems}
        onClose={() => setContextMenu(null)}
        openedByTouch={contextMenu.touch}
        label={t('grid.menu.label')}
      />
    )}

    {linkDialogOpen && (
      <Dialog
        title={t('grid.linkDialogTitle')}
        description={t('grid.linkDialogDesc')}
        onClose={() => setLinkDialogOpen(false)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setLinkDialogOpen(false)}
              className="tap-target rounded-full border border-border px-4 text-sm text-text hover:bg-surface-3"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => saveActiveCellLink(linkDialogValue)}
              className="tap-target rounded-full bg-brand px-4 text-sm text-brand-fg hover:opacity-90 font-medium"
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            saveActiveCellLink(linkDialogValue)
          }}
          className="space-y-3 py-1"
        >
          <div>
            <label className="mb-1 block text-xs font-semibold text-text-muted">
              {t('grid.linkUrlLabel')}
            </label>
            <div className="flex gap-2">
              <input
                type="url"
                autoFocus
                value={linkDialogValue}
                onChange={(e) => setLinkDialogValue(e.target.value)}
                placeholder={t('grid.linkUrlPlaceholder')}
                className="h-10 flex-1 rounded-lg border border-border bg-bg px-3 text-sm text-text outline-none focus:border-brand focus:ring-1 focus:ring-brand"
              />
              <button
                type="button"
                aria-label={t('grid.pasteUrl')}
                title={t('grid.pasteUrl')}
                onClick={async () => {
                  try {
                    const text = await navigator.clipboard.readText()
                    if (text) setLinkDialogValue(text.trim())
                  } catch { /* clipboard not available */ }
                }}
                className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 text-xs font-medium text-text hover:bg-surface-3 transition-colors"
              >
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="8" y="2" width="8" height="4" rx="1" />
                  <path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-2" />
                  <path d="M16 4h2a2 2 0 0 1 2 2v4" />
                  <path d="M21 14H11" />
                  <path d="m15 10-4 4 4 4" />
                </svg>
                <span className="hidden sm:inline">{t('grid.pasteUrl')}</span>
              </button>
              <button
                type="button"
                aria-label={t('grid.copyUrl')}
                title={t('grid.copyUrl')}
                disabled={!linkDialogValue.trim()}
                onClick={async () => {
                  if (!linkDialogValue.trim()) return
                  try {
                    await navigator.clipboard.writeText(linkDialogValue.trim())
                    setLinkDialogCopied(true)
                    setTimeout(() => setLinkDialogCopied(false), 2000)
                  } catch { /* clipboard not available */ }
                }}
                className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-border px-3 text-xs font-medium text-text hover:bg-surface-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {linkDialogCopied ? (
                  <>
                    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-brand">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span className="hidden sm:inline text-brand">{t('grid.urlCopied')}</span>
                  </>
                ) : (
                  <>
                    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    <span className="hidden sm:inline">{t('grid.copyUrl')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </Dialog>
    )}
    {isTotalsDialogOpen && (
      <SummaryCardEditDialog
        isOpen={isTotalsDialogOpen}
        onClose={() => {
          setIsTotalsDialogOpen(false)
          setEditingTotalsColumn(null)
        }}
        card={editingTotalsColumn?.card ?? (editingTotalsColumn ? {
          id: `card-${editingTotalsColumn.column.field}`,
          label: headerOf(columns.indexOf(editingTotalsColumn.column)),
          columnId: editingTotalsColumn.column.field,
          calculationType: 'SUM',
          format: 'integer',
        } : null)}
        columns={visibleColumns.map((idx) => ({
          field: columns[idx]?.field,
          headerName: headerOf(idx),
        }))}
        onSave={(updatedCard) => {
          handleSaveTotalsCard(updatedCard)
          setIsTotalsDialogOpen(false)
          setEditingTotalsColumn(null)
        }}
        onDelete={editingTotalsColumn?.card ? (cardId) => {
          handleDeleteTotalsCard(cardId)
          setIsTotalsDialogOpen(false)
          setEditingTotalsColumn(null)
        } : undefined}
      />
    )}

    {totalsContextMenu && (
      <GridContextMenu
        x={totalsContextMenu.x}
        y={totalsContextMenu.y}
        items={[
          {
            kind: 'action',
            key: 'copy', icon: MENU_ICONS.copy,
            label: t('grid.copyValue'),
            onSelect: async () => {
              try {
                await navigator.clipboard.writeText(totalsContextMenu.displayVal)
              } catch {}
            },
          },
          ...(canEditFormats ? [
            {
              kind: 'action' as const,
              key: 'edit', icon: MENU_ICONS.rename,
              label: t('grid.aggregateMenu.edit'),
              onSelect: () => {
                setEditingTotalsColumn({ column: totalsContextMenu.column, card: totalsContextMenu.card })
                setIsTotalsDialogOpen(true)
              },
            },
            ...(totalsContextMenu.column.field ? (() => {
              const field = totalsContextMenu.column.field
              return [
                {
                  kind: 'action' as const,
                  key: 'toggle-subtotals',
                  icon: hiddenSubtotalFields.has(field) ? MENU_ICONS.show : MENU_ICONS.hide,
                  label: hiddenSubtotalFields.has(field) ? t('grid.aggregateMenu.showSubtotals') : t('grid.aggregateMenu.hideSubtotals'),
                  onSelect: () => { void toggleAggregateHidden('subtotals', field) },
                },
                {
                  kind: 'action' as const,
                  key: 'toggle-totals',
                  icon: hiddenTotalFields.has(field) ? MENU_ICONS.show : MENU_ICONS.hide,
                  label: hiddenTotalFields.has(field) ? t('grid.aggregateMenu.showTotals') : t('grid.aggregateMenu.hideTotals'),
                  onSelect: () => { void toggleAggregateHidden('totals', field) },
                },
              ]
            })() : []),
            ...(totalsContextMenu.card ? [
              {
                kind: 'action' as const,
                key: 'delete', icon: MENU_ICONS.trash,
                label: t('grid.delete'),
                onSelect: () => {
                  handleDeleteTotalsCard(totalsContextMenu.card!.id!)
                },
              },
            ] : []),
          ] : []),
        ]}
        onClose={() => setTotalsContextMenu(null)}
        label={t('grid.totalsRow')}
      />
    )}
  </div>
}

/**
 * Inline date cell editor with native date picker popover support.
 */
export function DateCellEditor({
  value,
  initialText,
  formats,
  dataFormat,
  cellRule,
  align,
  onConfirm,
  onCancel,
  onNavigate,
}: {
  value: unknown
  initialText?: string | undefined
  formats: GridFormats
  dataFormat?: GridDataFormat | undefined
  cellRule: CellRule
  align?: 'left' | 'center' | 'right' | 'auto' | undefined
  onConfirm: (val: string) => void
  onCancel: () => void
  onNavigate: (rowDelta: number, colDelta: number) => void
}) {
  const [text, setText] = useState(() => {
    if (initialText !== undefined) return initialText
    return value !== null && value !== undefined && value !== ''
      ? formatGridData(value, dataFormat ?? { type: 'date', formatKey: 'date' }, formats)
      : ''
  })
  const inputRef = useRef<HTMLInputElement>(null)
  const datePickerRef = useRef<HTMLInputElement>(null)
  const committedRef = useRef(false)
  const isPickerOpenRef = useRef(false)

  const isoValue = useMemo(() => {
    if (text.trim() === '') return ''
    const parsed = parseGridDate(text)
    return parsed ? toISODate(parsed) : getInitialIsoDate(value)
  }, [text, value])

  const confirmCurrentText = (val: string) => {
    if (committedRef.current) return
    committedRef.current = true
    onConfirm(val)
  }

  const openPicker = () => {
    const el = datePickerRef.current
    if (!el) return
    isPickerOpenRef.current = true
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker()
      } catch {
        el.click()
      }
    } else {
      el.click()
    }
  }

  useEffect(() => {
    inputRef.current?.focus()
    if (initialText === undefined) {
      inputRef.current?.select()
    }
  }, [initialText])

  return (
    <div
      className="relative flex items-center w-full min-h-7"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        type="text"
        value={text}
        placeholder={formats.date}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          setTimeout(() => {
            if (!committedRef.current && !isPickerOpenRef.current) {
              confirmCurrentText(text)
            }
          }, 150)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            confirmCurrentText(text)
            onNavigate(1, 0)
          } else if (e.key === 'Tab') {
            e.preventDefault()
            confirmCurrentText(text)
            onNavigate(0, e.shiftKey ? -1 : 1)
          } else if (e.key === 'Escape') {
            e.preventDefault()
            committedRef.current = true
            onCancel()
          } else if ((e.key === 'ArrowDown' && e.altKey) || e.key === 'F4') {
            e.preventDefault()
            openPicker()
          }
        }}
        className={`h-7 w-full rounded-none border border-transparent bg-bg pl-1 pr-6 text-sm sm:text-xs outline-none leading-snug ${textClass(cellRule, align)} tabular-nums`}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label="Calendar"
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        onClick={(e) => {
          e.stopPropagation()
          openPicker()
        }}
        className="absolute right-0 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center text-text-muted hover:text-text cursor-pointer transition-colors"
      >
        <Icon name="calendar" className="h-3.5 w-3.5" />
      </button>
      <input
        ref={datePickerRef}
        type="date"
        value={isoValue}
        onChange={(e) => {
          isPickerOpenRef.current = false
          const nextIso = e.target.value
          if (nextIso) {
            const formatted = formatGridData(nextIso, dataFormat ?? { type: 'date', formatKey: 'date' }, formats)
            setText(formatted)
            confirmCurrentText(nextIso)
          }
        }}
        onBlur={() => {
          setTimeout(() => {
            isPickerOpenRef.current = false
            if (!committedRef.current && document.activeElement !== inputRef.current) {
              confirmCurrentText(text)
            }
          }, 150)
        }}
        tabIndex={-1}
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 right-0 h-0 w-0 opacity-0"
      />
    </div>
  )
}

// ── `dropdown` feature: cell with searchable dropdown ────────────────────────

const WINDOW_MARGIN = 8

/**
 * Searchable dropdown cell, the `dropdown` column feature (see `DropdownConfig`).
 * Rendered standalone without `cellRenderer`.
 */
export function DropdownCell({
  options,
  value,
  onChange,
  placeholder,
  emptyLabel,
  textoVacio,
  sizeKey = null,
  claveTamano = null,
  editing,
  initialText,
  onStop,
  allowCustom,
}: {
  options: readonly DropdownOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string | undefined
  emptyLabel?: string | undefined
  textoVacio?: string | undefined
  /** Key in `localStorage` to persist the chosen size. */
  sizeKey?: string | null | undefined
  claveTamano?: string | null | undefined
  /** Controlled mode: editing is opened/closed by the Grid (double-click, F2, typing). */
  editing?: boolean | undefined
  /** Initial key stroke that triggered editing: replaces content and opens filtered list. */
  initialText?: string | undefined
  /** Notifies Grid when editing completes. */
  onStop?: (() => void) | undefined
  /** Allows typing custom text not present in options list. */
  allowCustom?: boolean
}) {
  const { t } = useI18n()
  const [text, setText] = useState('')
  const [filter, setFilter] = useState('')
  const [open, setOpen] = useState(false)
  // Ids estables por instancia: `aria-activedescendant` en el combobox tiene que apuntar
  // a la opción resaltada, y el foco nunca sale del input.
  const listId = useId()
  const optionId = (i: number) => `${listId}-option-${i}`
  const [isEditingInternal, setIsEditingInternal] = useState(false)
  const isEditing = editing ?? isEditingInternal
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, maxWidth: 0, maxHeight: 0, upward: false })
  const inputRef = useRef<HTMLInputElement>(null)
  const activeRef = useRef({ value, options })
  useEffect(() => { activeRef.current = { value, options } })

  const resolvedSizeKey = sizeKey ?? claveTamano
  const { panelRef: listRef, size: listSize, startResize: startListResize } = useResizableSize(
    null,
    { key: resolvedSizeKey, minWidth: 160, minHeight: 100 },
  )

  useEffect(() => {
    if (!isEditing) return
    const input = inputRef.current
    if (!input) return
    if (initialText !== undefined) { setText(initialText); setFilter(initialText) }
    else setFilter('')
    // Opening the editor shows the whole list: a dropdown cell that looks like a
    // bare text box until you type reads as having no options at all.
    setOpen(true)
    input.focus()
    const raf = requestAnimationFrame(() => input.setSelectionRange(input.value.length, input.value.length))
    return () => cancelAnimationFrame(raf)
  }, [isEditing, initialText])

  useEffect(() => { setText(dropdownLabel(options, value)) }, [value, options])

  useEffect(() => {
    if (!open) return
    const updatePosition = () => {
      const r = inputRef.current?.getBoundingClientRect()
      if (!r) return
      const spaceBelow = window.innerHeight - r.bottom - WINDOW_MARGIN
      const spaceAbove = r.top - WINDOW_MARGIN
      const upward = spaceAbove > spaceBelow
      setPosition({
        top: upward ? r.top : r.bottom,
        left: r.left,
        width: r.width,
        maxWidth: window.innerWidth - r.left - WINDOW_MARGIN,
        maxHeight: Math.max(upward ? spaceAbove : spaceBelow, 0),
        upward,
      })
    }
    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [open])

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [filter, options])

  const navigable = useMemo(() => ['', ...filtered.map((o) => o.value)], [filtered])

  const choose = (val: string) => {
    onChange(val)
    setText(dropdownLabel(options, val))
    setOpen(false)
  }

  const confirm = (val: string) => { choose(val); inputRef.current?.blur() }

  const { index, activeRef: keyboardActiveRef, onKeyDown } = useDropdownKeyboard({
    count: navigable.length,
    open,
    initialIndex: filtered.length > 0 && !allowCustom ? 1 : 0,
    resetWith: filter,
    onSelect: (i) => confirm(i === 0 && allowCustom ? text : (navigable[i] ?? (allowCustom ? text : ''))),
    onClose: () => { setOpen(false); inputRef.current?.blur() },
    onOpen: () => setOpen(true),
  })

  const resolvedEmptyLabel = emptyLabel ?? textoVacio

  return (
    <div className="relative" onMouseDown={isEditing ? (e) => e.stopPropagation() : undefined}>
      {isEditing ? (
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => { setText(e.target.value); setFilter(e.target.value); setOpen(true) }}
          onKeyDown={(e) => { e.stopPropagation(); onKeyDown(e) }}
          onBlur={() => {
            setTimeout(() => {
              setOpen(false)
              setIsEditingInternal(false)
              onStop?.()
              setText(dropdownLabel(activeRef.current.options, activeRef.current.value))
            }, 150)
          }}
          placeholder={placeholder}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open ? optionId(index) : undefined}
          className="h-8 w-full bg-transparent text-sm sm:text-xs outline-none"
        />
      ) : (
        <div
          onClick={editing === undefined ? () => setIsEditingInternal(true) : undefined}
          className={`min-h-[26px] w-full flex items-center rounded-[13px] border border-border/40 px-2.5 py-0.5 text-sm sm:text-xs font-semibold whitespace-normal break-words leading-tight ${editing === undefined ? 'cursor-text' : ''}`}
          style={{ backgroundColor: 'var(--chip-bg)' }}
        >
          {text || <span className="text-text-muted">{placeholder}</span>}
        </div>
      )}
      {open && createPortal(
        <div
          ref={listRef}
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            ...(listSize
              ? { width: listSize.width, height: listSize.height }
              : { width: 'max-content', minWidth: position.width, maxWidth: position.maxWidth, maxHeight: position.maxHeight }),
            transform: position.upward ? 'translateY(calc(-100% - 4px))' : undefined,
            marginTop: position.upward ? 0 : 4,
          }}
          id={listId}
          role="listbox"
          aria-label={t('grid.dropdownOptions')}
          className="z-50 flex flex-col radius-box border border-border bg-surface shadow-card"
        >
          <div className="min-h-0 flex-1 overflow-y-auto">
            {!allowCustom || text === '' ? (
              <button
                type="button"
                ref={index === 0 ? keyboardActiveRef : undefined}
                id={optionId(0)}
                role="option"
                aria-selected={index === 0}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose('')}
                className={`block w-full whitespace-nowrap p-2 text-left text-sm sm:text-xs text-text-muted hover:bg-brand-100 ${index === 0 ? 'bg-brand-100' : ''}`}
              >{resolvedEmptyLabel ?? placeholder}</button>
            ) : (
              <button
                type="button"
                ref={index === 0 ? keyboardActiveRef : undefined}
                id={optionId(0)}
                role="option"
                aria-selected={index === 0}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(text)}
                className={`block w-full whitespace-nowrap p-2 text-left text-sm sm:text-xs font-bold text-brand hover:bg-brand-100 ${index === 0 ? 'bg-brand-100' : ''}`}
              >{t('grid.dropdownUseCustom', { text })}</button>
            )}
            {filtered.length === 0 && !allowCustom && <p className="p-2 text-sm sm:text-xs text-text-muted">{t('grid.noMatches')}</p>}
            {filtered.map((o, i) => (
              <button
                key={o.value}
                type="button"
                ref={index === i + 1 ? keyboardActiveRef : undefined}
                id={optionId(i + 1)}
                role="option"
                aria-selected={index === i + 1}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(o.value)}
                className={`block w-full whitespace-nowrap p-2 text-left text-sm sm:text-xs hover:bg-brand-100 ${index === i + 1 ? 'bg-brand-100' : ''}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <ResizeCorner onMouseDown={startListResize} />
        </div>,
        document.body,
      )}
    </div>
  )
}

export const CeldaDropdown = DropdownCell

/**
 * The pattern box beside the format select when a column uses a custom format.
 * Same rules as the organization's global formats: typed with the active decimal
 * separator, stored canonically, saved on blur or Enter.
 */
function GridCustomPatternInput({ pattern, disabled, label, onCommit }: {
  pattern: string
  disabled: boolean
  label: string
  onCommit: (canonical: string) => void
}) {
  const separator = getGridDecimalSeparator()
  const [text, setText] = useState(() => patronADisplay(pattern, separator))
  const commit = () => {
    const canonical = patronACanonico(text, separator)
    if (canonical !== pattern) onCommit(canonical)
  }
  return (
    <input
      aria-label={label}
      value={text}
      disabled={disabled}
      placeholder="#,##0.00"
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur()
        if (event.key === 'Escape') setText(patronADisplay(pattern, separator))
      }}
      className="ml-1 h-7 w-40 shrink-0 rounded border border-border bg-bg px-2 font-mono text-sm sm:text-xs outline-none focus:border-focus disabled:opacity-50"
    />
  )
}
